const { useEffect, useMemo, useRef, useState } = React;
const EMPTY_ARRAY = [];
const AUTH_STORAGE_KEY = "fedasen.irap.auth.token";
const AUDIT_SESSION_STORAGE_KEY = "fedasen.irap.audit.sessionId";
const FEDASEN_LOGO_SRC = "/assets/fedasen-logo-white.png";
const IRAP_ACCESS_GROUP = {
  name: "irap",
  nid: "30107420-49bc-11f1-9bc4-1fe52b63df80"
};
const IRAP_EDITOR_GROUP = {
  name: "irapeditor",
  nid: "46574600-49bc-11f1-9bc4-1fe52b63df80"
};
const IRAP_RANKING_GROUP = {
  name: "irapranking",
  nid: ""
};
const DISABLED_LAYOUT_IDS = new Set(["grid"]);
const ADMIN_LAYOUT_IDS = new Set(["single", "agents", "logs", "combined", "feedback", "settings", "activity"]);
const IRAP_ACCESS_DENIED_CODE = "IRAP_ACCESS_DENIED";
const IRAP_ACCESS_DENIED_MESSAGE = "You don't have access.";
const INVALID_LOGIN_MESSAGE = "User/password not valid.";
const DEFAULT_PANO_HEADING_OFFSET_DEG = 45;
const PANO_FOV_MAP_RANGE_SCALE = 0.18;
const PANO_FOV_AUTO_RANGE_METERS = 10;
const MIN_PANO_CANDIDATE_SELECTION = 5;
const MAX_PANO_CANDIDATE_SELECTION = 10;
const GIS_LAYER_SETTINGS_STORAGE_KEY = "fedasen.irap.gisLayerSettings";
const DEFAULT_GIS_LAYER_SETTINGS = {
  evidenceEnabled: true,
  aiEnabled: true,
  evidenceSelectionMode: "selected",
  evidenceClassIds: [],
  aiSelectionMode: "selected",
  aiClassIds: [],
  limit: 500,
  aiLimit: 120
};

function parsePositiveIdArray(value) {
  const source = Array.isArray(value)
    ? value
    : String(value || "").trim()
    ? String(value).split(/[,\s]+/)
    : [];
  return Array.from(new Set(
    source
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item > 0)
  ));
}

function normalizeGisLayerSettings(value) {
  const source = value && typeof value === "object" ? value : {};
  const legacyClassIds = parsePositiveIdArray(source.selectedClassIds || source.classIds);
  const evidenceClassIds = parsePositiveIdArray(
    Object.prototype.hasOwnProperty.call(source, "evidenceClassIds")
      ? source.evidenceClassIds
      : legacyClassIds
  );
  const aiClassIds = parsePositiveIdArray(
    Object.prototype.hasOwnProperty.call(source, "aiClassIds")
      ? source.aiClassIds
      : legacyClassIds
  );
  return {
    evidenceEnabled: true,
    aiEnabled: true,
    evidenceSelectionMode: "selected",
    evidenceClassIds,
    aiSelectionMode: "selected",
    aiClassIds,
    limit: Math.max(1, Math.min(2500, Number(source.limit) || DEFAULT_GIS_LAYER_SETTINGS.limit)),
    aiLimit: Math.max(1, Math.min(500, Number(source.aiLimit) || DEFAULT_GIS_LAYER_SETTINGS.aiLimit))
  };
}

function readStoredGisLayerSettings() {
  try {
    return normalizeGisLayerSettings(JSON.parse(localStorage.getItem(GIS_LAYER_SETTINGS_STORAGE_KEY) || "null"));
  } catch (_error) {
    return normalizeGisLayerSettings(null);
  }
}

function storeGisLayerSettings(settings) {
  try {
    localStorage.setItem(GIS_LAYER_SETTINGS_STORAGE_KEY, JSON.stringify(normalizeGisLayerSettings(settings)));
  } catch (_error) {
    // Settings still apply for the current session if local storage is blocked.
  }
}

function isGenericGisLayerName(name, classId) {
  const normalized = String(name || "").trim().toLowerCase();
  return !normalized || normalized === String(classId) || normalized === `class ${classId}`;
}

function normalizeGisLayerOptions(data) {
  const groupedClasses = new Map();
  const seenClassRows = new Set();
  (Array.isArray(data && data.classes) ? data.classes : []).forEach((item) => {
    const classId = Number(item && item.classId);
    if (!Number.isInteger(classId) || classId <= 0) {
      return;
    }
    const className = String(item && item.className || "").trim() || `Class ${classId}`;
    const count = Math.max(0, Number(item && item.count) || 0);
    const rowKey = `${classId}|${className.toLowerCase()}|${count}`;
    const alreadyCounted = seenClassRows.has(rowKey);
    seenClassRows.add(rowKey);
    if (!groupedClasses.has(classId)) {
      groupedClasses.set(classId, {
        classId,
        className,
        count: 0,
        aliases: [],
        _bestNameScore: -1
      });
    }
    const entry = groupedClasses.get(classId);
    if (!alreadyCounted) {
      entry.count += count;
    }
    const nameScore = (isGenericGisLayerName(className, classId) ? 0 : 1000000) + count;
    if (nameScore > entry._bestNameScore) {
      if (entry.className && entry.className !== className && !isGenericGisLayerName(entry.className, classId)) {
        entry.aliases.push(entry.className);
      }
      entry.className = className;
      entry._bestNameScore = nameScore;
    } else if (className !== entry.className && !isGenericGisLayerName(className, classId)) {
      entry.aliases.push(className);
    }
  });
  const classes = Array.from(groupedClasses.values())
    .map((item) => ({
      classId: item.classId,
      className: item.className || `Class ${item.classId}`,
      count: item.count,
      aliases: Array.from(new Set(item.aliases)).filter((alias) => alias && alias !== item.className).sort()
    }))
    .sort((a, b) => b.count - a.count || a.className.localeCompare(b.className));
  return {
    database: String(data && data.database || ""),
    classes,
    assetSubTypes: Array.isArray(data && data.assetSubTypes) ? data.assetSubTypes : []
  };
}

function gisLayerTargetMode(settings, target = "evidence") {
  const normalized = normalizeGisLayerSettings(settings);
  return target === "ai" ? normalized.aiSelectionMode : normalized.evidenceSelectionMode;
}

function gisLayerTargetClassIds(settings, target = "evidence") {
  const normalized = normalizeGisLayerSettings(settings);
  return target === "ai" ? normalized.aiClassIds : normalized.evidenceClassIds;
}

function gisLayerSelectionIsEmpty(settings, target = "evidence") {
  return gisLayerTargetMode(settings, target) === "selected" && !gisLayerTargetClassIds(settings, target).length;
}

function buildGisLayerQueryString(settings, options = {}) {
  const normalized = normalizeGisLayerSettings(settings);
  if (!normalized.evidenceEnabled || gisLayerSelectionIsEmpty(normalized, "evidence")) {
    return null;
  }
  const params = new URLSearchParams();
  params.set("limit", String(Math.max(1, Math.min(2500, Number(options.limit || normalized.limit) || normalized.limit))));
  if (normalized.evidenceSelectionMode === "selected") {
    params.set("classIds", normalized.evidenceClassIds.join(","));
  }
  return params.toString();
}

function buildGisLayerSelection(settings, options = {}) {
  const normalized = normalizeGisLayerSettings(settings);
  if (!normalized.aiEnabled || gisLayerSelectionIsEmpty(normalized, "ai")) {
    return null;
  }
  const selection = {
    enabled: true,
    limit: Math.max(1, Math.min(500, Number(options.limit || normalized.aiLimit) || normalized.aiLimit))
  };
  if (normalized.aiSelectionMode === "selected") {
    selection.classIds = normalized.aiClassIds;
  }
  return selection;
}

function normalizeFieldPromptGisSelection(value) {
  const source = value && typeof value === "object" ? value : {};
  const classIds = parsePositiveIdArray(source.classIds || source.selectedClassIds || source.classes);
  const enabled = Boolean(source.enabled || source.include || source.useGisLayers || source.useGISLayers) && classIds.length > 0;
  return {
    enabled,
    classIds,
    limit: Math.max(1, Math.min(500, Number(source.limit) || DEFAULT_GIS_LAYER_SETTINGS.aiLimit))
  };
}

function gisLayerOptionsForTarget(settings, options = {}, target = "evidence") {
  const normalizedSettings = normalizeGisLayerSettings(settings);
  const normalizedOptions = normalizeGisLayerOptions(options);
  const mode = gisLayerTargetMode(normalizedSettings, target);
  if (mode !== "selected") {
    return normalizedOptions.classes;
  }
  const selectedIds = new Set(gisLayerTargetClassIds(normalizedSettings, target).map((classId) => String(classId)));
  if (!selectedIds.size) {
    return [];
  }
  return normalizedOptions.classes.filter((option) => selectedIds.has(String(option.classId)));
}

function gisLayerSelectedCount(settings, options = {}, target = "evidence") {
  const classes = Array.isArray(options.classes) ? options.classes : [];
  if (gisLayerTargetMode(settings, target) === "all") {
    return classes.length;
  }
  return gisLayerTargetClassIds(settings, target).length;
}

function notifyPanoAnnotationsChanged() {
  requestAnimationFrame(() => {
    window.dispatchEvent(new Event("irap-pano-annotations-changed"));
  });
}

function readStoredAuthToken() {
  try {
    return localStorage.getItem(AUTH_STORAGE_KEY) || "";
  } catch (_error) {
    return "";
  }
}

function storeAuthToken(token) {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, token);
  } catch (_error) {
    // The app can still run for this browser session if storage is blocked.
  }
}

function clearStoredAuthToken() {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch (_error) {
    // Ignore storage failures during sign out.
  }
}

function randomAuditSessionId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `audit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function auditClientSessionId() {
  try {
    const existing = localStorage.getItem(AUDIT_SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const next = randomAuditSessionId();
    localStorage.setItem(AUDIT_SESSION_STORAGE_KEY, next);
    return next;
  } catch (_error) {
    if (!window.__irapAuditSessionId) {
      window.__irapAuditSessionId = randomAuditSessionId();
    }
    return window.__irapAuditSessionId;
  }
}

function authTokenFromLoginResponse(response) {
  return String(
    (response && response.data && response.data.token) ||
      (response && response.token) ||
      (response && response.accessToken) ||
      ""
  ).trim();
}

function userInfoFromResponse(response) {
  if (!response) {
    return null;
  }
  if (response.data && response.data.user) {
    return response.data.user;
  }
  if (response.data) {
    return response.data;
  }
  if (response.user) {
    return response.user;
  }
  return response;
}

function normalizeRoleText(value) {
  return String(value || "").trim().toLowerCase();
}

function userGroupRoles(user) {
  if (!user || typeof user !== "object") {
    return [];
  }
  return [
    user.groupRoles,
    user.gropRoles,
    user.groups,
    user.roles,
    user.roleGroups
  ].flatMap((value) => {
    if (Array.isArray(value)) {
      return value;
    }
    return value && typeof value === "object" ? [value] : [];
  });
}

function roleMatchesIrapGroup(role, group) {
  if (!role || typeof role !== "object") {
    return false;
  }
  const roleId = normalizeRoleText(role.NidGroup || role.nidGroup || role.groupId || role.id);
  if (roleId && roleId === group.nid) {
    return true;
  }
  const names = [role.name, role.title, role.label, role.attribute]
    .map(normalizeRoleText)
    .filter(Boolean);
  return names.includes(group.name);
}

function userHasIrapGroup(user, group) {
  return userGroupRoles(user).some((role) => roleMatchesIrapGroup(role, group));
}

function userHasIrapAccess(user) {
  return userHasIrapGroup(user, IRAP_ACCESS_GROUP);
}

function userHasIrapEditorAccess(user) {
  return userHasIrapGroup(user, IRAP_EDITOR_GROUP);
}

function userHasIrapRankingAccess(user) {
  return userHasIrapGroup(user, IRAP_RANKING_GROUP);
}

function authorizedIrapUser(user) {
  if (!userHasIrapAccess(user)) {
    const error = new Error(IRAP_ACCESS_DENIED_MESSAGE);
    error.code = IRAP_ACCESS_DENIED_CODE;
    throw error;
  }
  const permissions = user && user.irapPermissions || {};
  return {
    ...user,
    irapPermissions: {
      canAccess: true,
      canEdit: Boolean(permissions.canEdit) || userHasIrapEditorAccess(user),
      canViewRanking: Boolean(permissions.canViewRanking) || userHasIrapRankingAccess(user)
    }
  };
}

function userCanEditIrap(user) {
  return Boolean(user && user.irapPermissions && user.irapPermissions.canEdit);
}

function userCanViewIrapRanking(user) {
  return Boolean(user && user.irapPermissions && user.irapPermissions.canViewRanking) || userHasIrapRankingAccess(user);
}

function isIrapAccessDeniedError(error) {
  return Boolean(
    error &&
      (
        error.code === IRAP_ACCESS_DENIED_CODE ||
        String(error.message || "").includes(IRAP_ACCESS_DENIED_MESSAGE)
      )
  );
}

function userDisplayName(user) {
  if (!user) {
    return "Signed in";
  }
  return String(
    user.fullName ||
      user.name ||
      user.displayName ||
      user.username ||
      user.userName ||
      user.email ||
      "Signed in"
  );
}

function FedasenLogo({ className = "" }) {
  const classes = ["fedasen-logo", className].filter(Boolean).join(" ");
  return (
    <a className={classes} href="https://fedasen.com.au" target="_blank" rel="noreferrer" title="Fedasen">
      <img
        src={FEDASEN_LOGO_SRC}
        alt="Fedasen"
        className="site-logo-image"
        width="160"
        data-tf-not-load="1"
        fetchpriority="high"
      />
    </a>
  );
}

function AiRapMark({ className = "" }) {
  const classes = ["airap-mark", className].filter(Boolean).join(" ");
  return (
    <svg className={classes} viewBox="0 0 128 96" role="img" aria-label="AiRAP">
      <path className="airap-mark__sweep" d="M16 49C29 17 75 5 111 22C75 15 44 26 28 52C24 58 21 65 20 72C16 66 14 58 16 49Z" />
      <path className="airap-mark__road" d="M18 71C34 42 59 30 94 35C74 45 60 58 53 76C69 80 91 78 116 66C93 92 44 94 18 71Z" />
      <path className="airap-mark__lane" d="M35 63C41 57 47 52 54 49" />
      <path className="airap-mark__lane" d="M28 75C36 78 44 80 53 80" />
      <rect className="airap-mark__pixel airap-mark__pixel--green" x="73" y="36" width="8" height="8" rx="1" />
      <rect className="airap-mark__pixel airap-mark__pixel--green" x="96" y="29" width="6" height="6" rx="1" />
      <rect className="airap-mark__pixel" x="84" y="49" width="9" height="9" rx="1" />
      <rect className="airap-mark__pixel" x="64" y="56" width="7" height="7" rx="1" />
      <rect className="airap-mark__pixel airap-mark__pixel--green" x="101" y="50" width="7" height="7" rx="1" />
    </svg>
  );
}

function AiRapBrand({ className = "", compact = false }) {
  const classes = ["airap-brand", compact ? "airap-brand--compact" : "", className].filter(Boolean).join(" ");
  return (
    <div className={classes}>
      <AiRapMark />
      <div className="airap-brand__word">
        <strong><span>Ai</span>RAP</strong>
        <small>Attribute Intelligence for Safer Roads</small>
      </div>
    </div>
  );
}

function LoginAiAnimation() {
  return (
    <div className="login-ai-animation" aria-hidden="true">
      <svg viewBox="0 0 460 128" focusable="false">
        <defs>
          <radialGradient id="login-airap-scan-glow" cx="50%" cy="50%" r="62%">
            <stop offset="0%" stopColor="#0b7f47" stopOpacity="0.2" />
            <stop offset="68%" stopColor="#0b7f47" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#0b7f47" stopOpacity="0" />
          </radialGradient>
          <path id="login-airap-road-path" d="M40 88C105 33 172 25 232 67C283 102 342 96 420 42" />
          <path id="login-airap-scan-path" d="M70 78C124 50 178 51 226 72C278 94 331 87 384 54" />
        </defs>
        <path className="login-ai-map-line login-ai-map-line--one" d="M56 32H148L188 54H292L348 30H418" />
        <path className="login-ai-map-line login-ai-map-line--two" d="M54 106H140L190 86H286L354 104H412" />
        <path className="login-ai-road-shadow" d="M40 88C105 33 172 25 232 67C283 102 342 96 420 42" />
        <path id="login-airap-road-visible" className="login-ai-road" d="M40 88C105 33 172 25 232 67C283 102 342 96 420 42" />
        <path className="login-ai-road-lane" d="M74 75C120 43 170 42 214 68C268 99 326 88 386 53" />
        <g className="login-ai-scan-zone">
          <circle cx="232" cy="67" r="42" fill="url(#login-airap-scan-glow)" />
          <path d="M194 52v-15h15M270 52v-15h-15M194 82v15h15M270 82v15h-15" />
          <rect x="219" y="54" width="26" height="26" rx="6" />
          <path d="M225 67h14M232 60v-11M232 88v-11M212 67h-10M262 67h-10" />
        </g>
        <g className="login-ai-road-segment login-ai-pulse login-ai-pulse--one">
          <circle cx="102" cy="59" r="6" />
          <rect x="97" y="68" width="10" height="10" rx="2" />
        </g>
        <g className="login-ai-road-segment login-ai-pulse login-ai-pulse--two">
          <circle cx="168" cy="50" r="6" />
          <rect x="163" y="59" width="10" height="10" rx="2" />
        </g>
        <g className="login-ai-road-segment login-ai-pulse login-ai-pulse--three">
          <circle cx="308" cy="87" r="6" />
          <rect x="303" y="70" width="10" height="10" rx="2" />
        </g>
        <g className="login-ai-road-segment login-ai-pulse login-ai-pulse--four">
          <circle cx="372" cy="60" r="6" />
          <rect x="367" y="43" width="10" height="10" rx="2" />
        </g>
        <rect className="login-ai-spark login-ai-spark--one" x="132" y="32" width="7" height="7" rx="2" />
        <rect className="login-ai-spark login-ai-spark--two" x="286" y="42" width="6" height="6" rx="2" />
        <rect className="login-ai-spark login-ai-spark--three" x="398" y="80" width="5" height="5" rx="1" />
        <circle className="login-ai-packet login-ai-packet--one" r="4">
          <animateMotion dur="7.2s" repeatCount="indefinite" rotate="auto">
            <mpath href="#login-airap-road-path" />
          </animateMotion>
        </circle>
        <circle className="login-ai-packet login-ai-packet--two" r="3">
          <animateMotion begin="1.4s" dur="6.8s" repeatCount="indefinite" rotate="auto">
            <mpath href="#login-airap-scan-path" />
          </animateMotion>
        </circle>
      </svg>
    </div>
  );
}

const CATEGORY_META = {
  "Segment info": {
    short: "Segment info",
    icon: "info",
    color: "#0891b2",
    note: "Road metadata and context"
  },
  "Mid-block": {
    short: "Mid-block",
    icon: "road",
    color: "#0b7f47",
    note: "Geometry, lanes, road condition, lighting"
  },
  Intersections: {
    short: "Intersections",
    icon: "git-fork",
    color: "#dc6b19",
    note: "Intersection form, channelisation, access"
  },
  Flow: {
    short: "Flow",
    icon: "activity",
    color: "#0f766e",
    note: "AADT and vulnerable-user flows"
  },
  "VRU facilities and land use": {
    short: "VRU",
    icon: "accessibility",
    color: "#7c3aed",
    note: "Pedestrian, bicycle, school, land use"
  },
  Speeds: {
    short: "Speeds",
    icon: "gauge",
    color: "#c2410c",
    note: "Speed limits and speed management"
  },
  Roadside: {
    short: "Roadside",
    icon: "trees",
    color: "#15803d",
    note: "Roadside severity, shoulders, objects"
  },
  "Old system outputs": {
    short: "Legacy",
    icon: "archive",
    color: "#64748b",
    note: "Legacy supporting fields, SRS, star ratings, and output values"
  }
};

const CATEGORY_ORDER = [
  "Mid-block",
  "Intersections",
  "Roadside",
  "VRU facilities and land use",
  "Speeds",
  "Flow"
];
const AGENT_ORDER = CATEGORY_ORDER.slice();
const OPENAI_CONCURRENCY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const MAX_BATCH_RANGE_SEGMENTS = 200;
const AGENT_RUNNER_MODEL_OPTIONS = [
  "gpt-5.5",
  "gpt-5.5-pro",
  "gpt-5.4",
  "gpt-5.4-mini",
  "gemini-2.5-flash",
  "gemini-2.5-pro",
  "gemini-2.5-flash-lite",
  "gemini-3-flash-preview",
  "gemini-3-pro-preview"
];
const AGENT_RUNNER_REASONING_OPTIONS = ["low", "medium", "high", "xhigh"];

function agentRunnerModelLabel(modelName) {
  const text = String(modelName || "").trim();
  if (/^models\/gemini-|^gemini-/i.test(text)) {
    return `Gemini ${text.replace(/^models\//i, "").replace(/^gemini-/i, "").replace(/[-_]+/g, " ")}`;
  }
  const version = text.replace(/^gpt[-_]?/i, "").replace(/[-_]+/g, " ").trim();
  return version ? `OpenAI ${version}` : text;
}

const IMAGE_PROCESSING_OPTIONS = [
  { value: "full360", label: "360 image" },
  { value: "frontCube", label: "Front camera" }
];
const DEFAULT_FIELD_IMAGE_SOURCE_MODE = "panorama";
const AERIAL_OVERLAY_IMAGE_FILE = "segment-aerial-overlay.png";
const SEGMENT_BATCH_EXCLUDED_KEY = "_batchExcluded";
const SEGMENT_BATCH_EXCLUDED_REASON_KEY = "_batchExcludedReason";
const SEGMENT_BATCH_EXCLUDED_AT_KEY = "_batchExcludedAt";
const SEGMENT_BATCH_EXCLUDED_KEYS = [
  SEGMENT_BATCH_EXCLUDED_KEY,
  "batchExcluded",
  "BatchExcluded",
  "excludeFromBatch",
  "ExcludeFromBatch",
  "batchExclude",
  "BatchExclude",
  "skipBatch",
  "SkipBatch"
];
const FIELD_IMAGE_SOURCE_OPTIONS = [
  { value: "panorama", label: "Panorama only", icon: "image" },
  { value: "aerial", label: "Aerial only", icon: "map" },
  { value: "both", label: "Aerial + Panorama", icon: "layers" }
];
const PANO_ANNOTATION_TYPES = [
  { value: "sign", label: "Sign", icon: "traffic-cone" },
  { value: "road_marking", label: "Road marking", icon: "route" },
  { value: "lane", label: "Lane", icon: "panel-top" },
  { value: "roadside", label: "Roadside", icon: "trees" },
  { value: "signal", label: "Signal", icon: "traffic-cone" },
  { value: "barrier", label: "Barrier", icon: "shield" },
  { value: "surface", label: "Surface", icon: "layers" },
  { value: "measurement", label: "Measurement", icon: "ruler" },
  { value: "other", label: "Other", icon: "map-pin" }
];
const PANO_ANNOTATION_SHAPES = [
  { value: "point", label: "Point", icon: "map-pin", minPoints: 1 },
  { value: "line", label: "Line", icon: "route", minPoints: 2 },
  { value: "polygon", label: "Area", icon: "pentagon", minPoints: 3 },
  { value: "sam", label: "Segment Anything", icon: "scan-search", minPoints: 1 }
];
const MANUAL_PANO_ANNOTATION_SHAPES = PANO_ANNOTATION_SHAPES;
function imageProcessingLabel(mode) {
  const option = IMAGE_PROCESSING_OPTIONS.find((candidate) => candidate.value === mode);
  return option ? option.label : IMAGE_PROCESSING_OPTIONS[0].label;
}

function normalizeFieldImageSourceMode(value) {
  const compact = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
  if (["both", "all", "aerialpanorama", "panoramaaerial", "aerialpano", "panoaerial"].includes(compact)) {
    return "both";
  }
  if (["aerial", "aerialonly", "air", "map", "overlay", "aerialoverlay", "satellite"].includes(compact)) {
    return "aerial";
  }
  return DEFAULT_FIELD_IMAGE_SOURCE_MODE;
}

function makeFieldTitleMap(fields = []) {
  const map = new Map();
  (fields || []).forEach((field) => {
    const title = field && (field.caption || field.label || field.field);
    if (!title) {
      return;
    }
    [field.field, field.geoProperty, field.propertyName]
      .filter(Boolean)
      .forEach((key) => {
        map.set(String(key), {
          title,
          code: field.field || "",
          group: field.group || ""
        });
      });
  });
  return map;
}

function displayFieldTitle(fieldName, fieldTitleMap) {
  const key = String(fieldName || "");
  if (!key) {
    return "Field";
  }
  const meta = fieldTitleMap && fieldTitleMap.get(key);
  return meta && meta.title ? meta.title : key.replace(/_/g, " ");
}

function singleSegmentRange(segmentId) {
  const text = String(Number(segmentId));
  return { start: text, end: text, ranges: text };
}

const CORE_SEGMENT_FIELDS = new Set([
  "Coder_name",
  "Coding_date",
  "Road_survey_date",
  "Image_reference",
  "Road_name",
  "Section",
  "Distance",
  "Length",
  "Landmark",
  "Comments",
  "Location_ID",
  "Latitude",
  "Longitude",
  "End_Latitude",
  "End_Longitude",
  "Smoothed_Section_ID",
  "__centroid"
]);

function lucideComponentName(name) {
  return String(name || "")
    .split(/[-_\s]+/g)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join("");
}

function reactSvgAttrName(name) {
  if (name === "class") {
    return "className";
  }
  return String(name || "").replace(/-([a-z])/g, (_match, letter) => letter.toUpperCase());
}

function lucideNodeToReact(node, key) {
  const [tag, attrs = {}, children = []] = node;
  const props = { key };
  Object.keys(attrs || {}).forEach((attrName) => {
    props[reactSvgAttrName(attrName)] = attrs[attrName];
  });
  return React.createElement(
    tag,
    props,
    (children || []).map((child, index) => lucideNodeToReact(child, `${key}-${index}`))
  );
}

function icon(name) {
  const iconName = String(name || "").trim();
  const icons = window.lucide && window.lucide.icons;
  const iconNode = icons && icons[lucideComponentName(iconName)];
  if (!iconNode) {
    return <span className={`lucide lucide-${iconName}`} data-lucide={iconName} aria-hidden="true"></span>;
  }
  return (
    <svg
      className={`lucide lucide-${iconName}`}
      data-lucide={iconName}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {iconNode.map((child, index) => lucideNodeToReact(child, index))}
    </svg>
  );
}

function safeText(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  return String(value);
}

function panoAnnotationTypeLabel(value) {
  const type = PANO_ANNOTATION_TYPES.find((candidate) => candidate.value === value);
  return type ? type.label : "Other";
}

function panoAnnotationTypeIcon(value) {
  const type = PANO_ANNOTATION_TYPES.find((candidate) => candidate.value === value);
  return type ? type.icon : "map-pin";
}

function panoAnnotationShapeLabel(value) {
  const shape = PANO_ANNOTATION_SHAPES.find((candidate) => candidate.value === value);
  return shape ? shape.label : "Point";
}

function panoAnnotationShapeIcon(value) {
  const shape = PANO_ANNOTATION_SHAPES.find((candidate) => candidate.value === value);
  return shape ? shape.icon : "map-pin";
}

function minimumPointsForPanoShape(value) {
  const shape = PANO_ANNOTATION_SHAPES.find((candidate) => candidate.value === value);
  return shape ? shape.minPoints : 1;
}

function panoAnnotationShape(annotation) {
  if (annotation && annotation.shape && Array.isArray(annotation.shape.points)) {
    return annotation.shape;
  }
  return {
    kind: "point",
    points: annotation ? [{
      yaw: annotation.yaw,
      pitch: annotation.pitch,
      textureX: annotation.textureX,
      textureY: annotation.textureY
    }] : []
  };
}

function panoAnnotationFocusPoint(annotation) {
  const shape = panoAnnotationShape(annotation);
  const points = Array.isArray(shape.points)
    ? shape.points.map(panoAnnotationSphericalPoint).filter(Boolean)
    : [];
  if (!points.length) {
    return null;
  }
  const vector = points.reduce((sum, point) => ({
    sin: sum.sin + Math.sin(point.yaw),
    cos: sum.cos + Math.cos(point.yaw),
    pitch: sum.pitch + point.pitch
  }), { sin: 0, cos: 0, pitch: 0 });
  return {
    yaw: Math.atan2(vector.sin / points.length, vector.cos / points.length),
    pitch: vector.pitch / points.length
  };
}

function panoAnnotationTexturePoint(point) {
  const x = Number(point && point.textureX);
  const y = Number(point && point.textureY);
  return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
}

function panoAnnotationSphericalPoint(point) {
  const yaw = Number(point && point.yaw);
  const pitch = Number(point && point.pitch);
  return Number.isFinite(yaw) && Number.isFinite(pitch) ? { yaw, pitch } : null;
}

function panoAnnotationMeasurementMeta(annotation) {
  return annotation && annotation.meta && typeof annotation.meta === "object"
    ? annotation.meta
    : {};
}

function panoCalibrationMeta(calibration) {
  if (!calibration || typeof calibration !== "object") {
    return {};
  }
  if (
    Object.prototype.hasOwnProperty.call(calibration, "knownMeters") ||
    Object.prototype.hasOwnProperty.call(calibration, "metersPerRadian")
  ) {
    return calibration;
  }
  return panoAnnotationMeasurementMeta(calibration);
}

function panoAnnotationMeasurementRole(annotation) {
  const role = String(panoAnnotationMeasurementMeta(annotation).measurementRole || "").trim().toLowerCase();
  return role === "calibration" || role === "measurement" ? role : "";
}

function panoAnnotationPointVector(point) {
  const yaw = Number(point && point.yaw);
  const pitch = Number(point && point.pitch);
  if (!Number.isFinite(yaw) || !Number.isFinite(pitch)) {
    return null;
  }
  const cosPitch = Math.cos(pitch);
  return {
    x: cosPitch * Math.sin(yaw),
    y: Math.sin(pitch),
    z: cosPitch * Math.cos(yaw)
  };
}

function panoAngularDistance(pointA, pointB) {
  const a = panoAnnotationPointVector(pointA);
  const b = panoAnnotationPointVector(pointB);
  if (!a || !b) {
    return null;
  }
  const dot = Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z));
  return Math.acos(dot);
}

function panoLineMidpoint(pointA, pointB) {
  const a = panoAnnotationPointVector(pointA);
  const b = panoAnnotationPointVector(pointB);
  if (!a || !b) {
    return null;
  }
  const x = a.x + b.x;
  const y = a.y + b.y;
  const z = a.z + b.z;
  const length = Math.sqrt(x * x + y * y + z * z);
  if (!Number.isFinite(length) || length <= 1e-9) {
    return null;
  }
  return {
    yaw: Math.atan2(x / length, z / length),
    pitch: Math.asin(Math.max(-1, Math.min(1, y / length)))
  };
}

function formatPanoMeters(value) {
  const meters = Number(value);
  if (!Number.isFinite(meters)) {
    return "-";
  }
  if (Math.abs(meters) >= 100) {
    return `${Math.round(meters)} m`;
  }
  if (Math.abs(meters) >= 10) {
    return `${meters.toFixed(1)} m`;
  }
  return `${meters.toFixed(2)} m`;
}

function formatPanoCalibrationScale(value) {
  const metersPerRadian = Number(value);
  if (!Number.isFinite(metersPerRadian) || metersPerRadian <= 0) {
    return "No calibration";
  }
  return `${formatPanoMeters(metersPerRadian * (Math.PI / 180))}/deg`;
}

function panoCalibrationScale(calibration) {
  const meta = panoCalibrationMeta(calibration);
  const scale = Number(meta.metersPerRadian);
  return Number.isFinite(scale) && scale > 0 ? scale : null;
}

function panoCalibrationDisplayLabel(calibration) {
  const meta = panoCalibrationMeta(calibration);
  const name = calibration && (calibration.name || calibration.label)
    ? calibration.name || calibration.label
    : "Calibration";
  return `${name} (${formatPanoMeters(meta.knownMeters)})`;
}

function sanitizePanoViewCalibration(profile) {
  const headingOffsetDeg = numericPanoValue(profile && (profile.headingOffsetDeg ?? profile.viewHeadingOffsetDeg));
  const fovDeg = numericPanoValue(profile && (profile.fovDeg ?? profile.viewFovDeg));
  const rangeMeters = numericPanoValue(profile && (profile.rangeMeters ?? profile.viewRangeMeters));
  return {
    headingOffsetDeg: Number.isFinite(headingOffsetDeg)
      ? Math.max(-180, Math.min(180, headingOffsetDeg))
      : DEFAULT_PANO_HEADING_OFFSET_DEG,
    fovDeg: Number.isFinite(fovDeg) && fovDeg > 0
      ? Math.max(18, Math.min(140, fovDeg))
      : null,
    rangeMeters: Number.isFinite(rangeMeters) && rangeMeters > 0
      ? Math.max(10, Math.min(300, rangeMeters))
      : null
  };
}

function panoViewCalibrationForImage(calibrationState, image, detail) {
  const state = normalizePanoCalibrationState(calibrationState);
  return state.viewCalibration;
}

function panoMeasurementAnnotationLabel(annotation) {
  const meta = panoAnnotationMeasurementMeta(annotation);
  const role = panoAnnotationMeasurementRole(annotation);
  if (role === "calibration") {
    return `${annotation.label || "Calibration"} (${formatPanoMeters(meta.knownMeters)})`;
  }
  if (role === "measurement") {
    const title = String(meta.measurementTitle || annotation.label || "").trim();
    const normalizedTitle = title.toLowerCase();
    const cleanTitle = normalizedTitle === "measurement" || normalizedTitle.startsWith("measurement evidence")
      ? ""
      : title;
    const distance = formatPanoMeters(meta.distanceMeters);
    return cleanTitle ? `${cleanTitle} (${distance})` : distance;
  }
  if (meta.showMeasurement && Number.isFinite(Number(meta.distanceMeters))) {
    return `${annotation && annotation.label ? annotation.label : "Annotation"} (${formatPanoMeters(meta.distanceMeters)})`;
  }
  return annotation && annotation.label ? annotation.label : "Annotation";
}

function isPanoCalibrationAnnotation(annotation) {
  return panoAnnotationMeasurementRole(annotation) === "calibration" && panoCalibrationScale(annotation);
}

function isPanoDistanceMeasurementAnnotation(annotation) {
  return panoAnnotationMeasurementRole(annotation) === "measurement";
}

function normalizePanoCalibrationState(input) {
  const calibrations = Array.isArray(input && input.calibrations) ? input.calibrations : [];
  const ids = new Set(calibrations.map((calibration) => String(calibration.id)));
  const requestedDefaultId = String(input && input.defaultCalibrationId ? input.defaultCalibrationId : "");
  const fallbackDefault = calibrations.find((calibration) => calibration && calibration.isDefault) || calibrations[0] || null;
  const defaultCalibrationId = ids.has(requestedDefaultId)
    ? requestedDefaultId
    : fallbackDefault
    ? String(fallbackDefault.id)
    : "";
  const assignments = {};
  const rawAssignments = input && input.assignments && typeof input.assignments === "object"
    ? input.assignments
    : {};
  Object.entries(rawAssignments).forEach(([videoId, calibrationId]) => {
    if (videoId && ids.has(String(calibrationId))) {
      assignments[String(videoId)] = String(calibrationId);
    }
  });
  const legacyViewCalibration = fallbackDefault && (
    fallbackDefault.viewHeadingOffsetDeg !== undefined ||
    fallbackDefault.viewFovDeg !== undefined ||
    fallbackDefault.viewRangeMeters !== undefined
  )
    ? fallbackDefault
    : null;
  const viewCalibration = sanitizePanoViewCalibration(
    input && input.viewCalibration
      ? input.viewCalibration
      : input && (
        input.viewHeadingOffsetDeg !== undefined ||
        input.viewFovDeg !== undefined ||
        input.viewRangeMeters !== undefined
      )
      ? input
      : legacyViewCalibration
  );
  return {
    version: 1,
    updatedAt: input && input.updatedAt ? input.updatedAt : "",
    viewCalibration,
    calibrations: calibrations.map((calibration) => ({
      ...calibration,
      isDefault: String(calibration.id) === String(defaultCalibrationId)
    })),
    assignments,
    defaultCalibrationId
  };
}

function makePanoCalibrationClientId() {
  return `cal-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 10)}`;
}

function panoImageStem(fileName) {
  return String(fileName || "").replace(/^.*[\\/]/, "").replace(/\.[^.]+$/, "");
}

function panoVideoIdForImage(image, detail) {
  const fileName = String(image && image.file ? image.file : "");
  const explicitVideo = image && (image.videoName || image.VideoName || image.video || image.Video);
  if (explicitVideo) {
    return String(explicitVideo).trim();
  }
  const stem = panoImageStem(fileName);
  const match = stem.match(/^(.*)_([^_]+)$/);
  if (match && match[1]) {
    return match[1];
  }
  if (detail && detail.id) {
    return `segment-${detail.id}`;
  }
  return stem || "default";
}

function panoVideoLabel(videoId) {
  const text = String(videoId || "").trim();
  if (!text) {
    return "Current video";
  }
  const segmentMatch = text.match(/^segment-(.+)$/);
  return segmentMatch ? `Segment ${segmentMatch[1]}` : text;
}

function panoCalibrationProfileToAnnotation(profile, { segmentId, imageFile } = {}) {
  if (!profile || !profile.shape || !Array.isArray(profile.shape.points)) {
    return null;
  }
  return {
    id: `calibration-profile-${profile.id}`,
    segmentId,
    imageFile,
    label: profile.name || "Calibration",
    type: "measurement",
    note: profile.note || "",
    shape: profile.shape,
    meta: {
      source: "pano-calibration-profile",
      measurementRole: "calibration",
      profileId: profile.id,
      knownMeters: profile.knownMeters,
      angularDistanceRad: profile.angularDistanceRad,
      metersPerRadian: profile.metersPerRadian
    }
  };
}

function panoAnnotationMarkerStyle({ active = false, draft = false, kind = "point", role = "" } = {}) {
  const isAreaShape = kind === "polygon" || kind === "sam";
  if (draft) {
    return {
      fill: kind === "line" ? "none" : "rgba(16,185,129,0.16)",
      stroke: "#10b981",
      strokeWidth: isAreaShape ? "1px" : "2px",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeDasharray: "7 5"
    };
  }
  if (active) {
    return {
      fill: kind === "line" ? "none" : "rgba(34,211,238,0.18)",
      stroke: "#22d3ee",
      strokeWidth: isAreaShape ? "1px" : "2px",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    };
  }
  if (role === "calibration") {
    return {
      fill: kind === "line" ? "none" : "rgba(234,179,8,0.14)",
      stroke: "#eab308",
      strokeWidth: "2px",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    };
  }
  if (role === "measurement") {
    return {
      fill: kind === "line" ? "none" : "rgba(14,165,233,0.14)",
      stroke: "#0ea5e9",
      strokeWidth: "2px",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    };
  }
  if (kind === "sam") {
    return {
      fill: "rgba(124,58,237,0.18)",
      stroke: "#7c3aed",
      strokeWidth: "1px",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    };
  }
  return {
    fill: kind === "line" ? "none" : "rgba(249,115,22,0.18)",
    stroke: "#f97316",
    strokeWidth: isAreaShape ? "1px" : "1.5px",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  };
}

function panoAnnotationToViewerMarker(annotation, { active = false, draft = false } = {}) {
  const shape = panoAnnotationShape(annotation);
  const kind = shape.kind || "point";
  const points = Array.isArray(shape.points) ? shape.points : [];
  const texturePoints = points.map(panoAnnotationTexturePoint);
  const hasTexturePoints = texturePoints.every(Boolean);
  const sphericalPoints = points.map(panoAnnotationSphericalPoint);
  const hasSphericalPoints = sphericalPoints.every(Boolean);
  const marker = {
    id: draft ? "__pano-annotation-draft" : `pano-annotation-${annotation.id}`,
    className: `irap-pano-marker ${active ? "active" : ""} ${draft ? "draft" : ""}`,
    data: { annotationId: annotation.id, draft },
    hideList: true,
    svgStyle: panoAnnotationMarkerStyle({ active, draft, kind, role: panoAnnotationMeasurementRole(annotation) }),
    tooltip: {
      content: panoMeasurementAnnotationLabel(annotation),
      position: "bottom right"
    },
    zIndex: active || draft ? 20 : 10
  };

  if (kind === "line") {
    if (hasTexturePoints && texturePoints.length >= 2) {
      return { ...marker, polylinePixels: texturePoints };
    }
    if (hasSphericalPoints && sphericalPoints.length >= 2) {
      return { ...marker, polyline: sphericalPoints };
    }
    return null;
  }

  if (kind === "polygon" || kind === "sam") {
    if (hasTexturePoints && texturePoints.length >= 3) {
      return { ...marker, polygonPixels: texturePoints };
    }
    if (hasSphericalPoints && sphericalPoints.length >= 3) {
      return { ...marker, polygon: sphericalPoints };
    }
    if (draft && hasTexturePoints && texturePoints.length >= 2) {
      return { ...marker, polylinePixels: texturePoints };
    }
    if (draft && hasSphericalPoints && sphericalPoints.length >= 2) {
      return { ...marker, polyline: sphericalPoints };
    }
    return null;
  }

  const point = points[0];
  const position = panoAnnotationSphericalPoint(point);
  if (!position) {
    return null;
  }
  return {
    ...marker,
    circle: active || draft ? 16 : 12,
    position
  };
}

function formatPanoAngle(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${Math.round((numeric * 180) / Math.PI)} deg` : "-";
}

function hasDisplayValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function truthySegmentProperty(value) {
  if (value === true || value === 1) {
    return true;
  }
  const text = String(value === null || value === undefined ? "" : value).trim().toLowerCase();
  return ["1", "true", "yes", "y", "on", "blocked", "black", "blacklisted", "exclude", "excluded", "skip", "skipped"].includes(text);
}

function segmentBatchExcludedFromProps(props = {}) {
  return SEGMENT_BATCH_EXCLUDED_KEYS.some((key) => truthySegmentProperty(props[key]));
}

function segmentBatchExcluded(segment) {
  const props = segment && segment.properties
    ? segment.properties
    : (segment && segment.feature && segment.feature.properties) || {};
  return segmentBatchExcludedFromProps(props);
}

function segmentBatchExcludedReason(segment) {
  const props = segment && segment.properties
    ? segment.properties
    : (segment && segment.feature && segment.feature.properties) || {};
  return String(props[SEGMENT_BATCH_EXCLUDED_REASON_KEY] || "").trim();
}

const SEGMENT_STAR_RATING_USERS = [
  {
    key: "vehicle",
    label: "Vehicle Occupant",
    short: "VO",
    raw: "Vehicle_Star_Rating_Raw",
    smoothed: "Vehicle_Star_Rating_Smoothed",
    rawSrs: "Vehicle_SRS_Total",
    smoothedSrs: "Vehicle_SRS_Total_Smoothed"
  },
  {
    key: "motorcyclist",
    label: "Motorcyclist",
    short: "MC",
    raw: "Motorcyclist_Star_Rating_Raw",
    smoothed: "Motorcyclist_Star_Rating_Smoothed",
    rawSrs: "Motorcyclist_SRS_Total",
    smoothedSrs: "Motorcyclist_SRS_Total_Smoothed"
  },
  {
    key: "pedestrian",
    label: "Pedestrian",
    short: "Ped",
    raw: "Pedestrian_Star_Rating_Raw",
    smoothed: "Pedestrian_Star_Rating_Smoothed",
    rawSrs: "Pedestrian_SRS_Total",
    smoothedSrs: "Pedestrian_SRS_Total_Smoothed"
  },
  {
    key: "bicyclist",
    label: "Bicyclist",
    short: "Bike",
    raw: "Bicyclist_Star_Rating_Raw",
    smoothed: "Bicyclist_Star_Rating_Smoothed",
    rawSrs: "Bicyclist_SRS_Total",
    smoothedSrs: "Bicyclist_SRS_Total_Smoothed"
  }
];
const SEGMENT_STAR_RANKING_PROPERTY_KEYS = new Set(
  SEGMENT_STAR_RATING_USERS.flatMap((user) => [user.raw, user.smoothed, user.rawSrs, user.smoothedSrs])
);
const STAR_RATING_DEFAULT_CHAT_QUESTION =
  "Which coded iRAP/AusRAP road attributes are driving this segment's star-rating risk? Please use the attribute titles, coded option labels, and AI evidence. Do not use old score/result fields.";

function isStarRankingPropertyName(value) {
  const text = String(value || "").trim();
  if (!text) {
    return false;
  }
  const normalized = text.toLowerCase();
  return (
    SEGMENT_STAR_RANKING_PROPERTY_KEYS.has(text) ||
    normalized.includes("star_rating") ||
    normalized.includes("star rating") ||
    normalized.includes("_srs_") ||
    normalized.endsWith("_srs_total") ||
    normalized.endsWith("_srs_total_smoothed")
  );
}

function isStarRankingField(field) {
  if (!field || typeof field !== "object") {
    return false;
  }
  return [field.field, field.geoProperty, field.property, field.propertyName]
    .some((value) => isStarRankingPropertyName(value));
}

function segmentProps(segmentOrDetail) {
  if (!segmentOrDetail) {
    return {};
  }
  if (segmentOrDetail.properties) {
    return segmentOrDetail.properties || {};
  }
  if (segmentOrDetail.feature && segmentOrDetail.feature.properties) {
    return segmentOrDetail.feature.properties || {};
  }
  return {};
}

function parseStarRatingValue(value) {
  if (!hasDisplayValue(value)) {
    return null;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return Math.max(0, Math.min(5, numeric));
}

function formatStarRatingValue(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "-";
  }
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1);
}

function formatSrsValue(value) {
  if (!hasDisplayValue(value)) {
    return "-";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return String(value);
  }
  return numeric.toFixed(numeric >= 100 ? 0 : 1);
}

function starRatingTone(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 1) {
    return "very-low";
  }
  if (numeric < 3) {
    return "low";
  }
  if (numeric < 4) {
    return "target";
  }
  if (numeric < 5) {
    return "good";
  }
  return "best";
}

function segmentStarRatings(segmentOrDetail) {
  const props = segmentProps(segmentOrDetail);
  return SEGMENT_STAR_RATING_USERS
    .map((user) => {
      const smoothed = parseStarRatingValue(props[user.smoothed]);
      const raw = parseStarRatingValue(props[user.raw]);
      const rating = smoothed !== null ? smoothed : raw;
      if (rating === null) {
        return null;
      }
      return {
        ...user,
        rating,
        raw,
        smoothed,
        basis: smoothed !== null ? "Smoothed" : "Raw",
        rawSrs: props[user.rawSrs],
        smoothedSrs: props[user.smoothedSrs],
        tone: starRatingTone(rating)
      };
    })
    .filter(Boolean);
}

function segmentStarRatingForUser(segmentOrDetail, userKey) {
  const safeUserKey = String(userKey || "");
  return segmentStarRatings(segmentOrDetail).find((item) => item.key === safeUserKey) || null;
}

function segmentStarRatingTitle(item) {
  return [
    item.label,
    `${item.basis}: ${formatStarRatingValue(item.rating)} star`,
    `Raw: ${item.raw === null ? "-" : formatStarRatingValue(item.raw)} star`,
    "Displayed legacy rating only; not used as a calculation input"
  ].join("\n");
}

function segmentStarRatingsText(segmentOrDetail) {
  const ratings = Array.isArray(segmentOrDetail)
    ? segmentOrDetail
    : segmentStarRatings(segmentOrDetail);
  return ratings.length
    ? ratings.map((item) => `${item.short} ${formatStarRatingValue(item.rating)}`).join(" | ")
    : "-";
}

function segmentStarRatingsTitle(segmentOrDetail) {
  const ratings = segmentStarRatings(segmentOrDetail);
  return ratings.length
    ? `iRAP/AusRAP Star Rating\n${ratings.map(segmentStarRatingTitle).join("\n\n")}`
    : "No iRAP/AusRAP Star Rating values";
}

function starRatingChatResponseText(response = {}) {
  const sections = [String(response.reply || "").trim()];
  [
    ["Key drivers", response.keyDrivers],
    ["Limiting users", response.limitingRoadUsers],
    ["Improvement ideas", response.improvementIdeas],
    ["Data notes", response.dataNotes]
  ].forEach(([label, values]) => {
    const items = (Array.isArray(values) ? values : [])
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    if (items.length) {
      sections.push(`${label}:\n${items.map((item) => `- ${item}`).join("\n")}`);
    }
  });
  return sections.filter(Boolean).join("\n\n");
}

function starRatingBand(value) {
  const rating = parseStarRatingValue(value);
  if (rating === null) {
    return null;
  }
  if (rating >= 5) {
    return 5;
  }
  return Math.max(0, Math.floor(rating));
}

function segmentMatchesStarRatingFilter(segment, filterValue = "all") {
  const value = String(filterValue || "all");
  if (value === "all") {
    return true;
  }
  const ratings = segmentStarRatings(segment);
  if (!ratings.length) {
    return false;
  }
  if (value === "lowest:1") {
    return ratings.some((item) => item.rating <= 1);
  }
  if (value === "lowest:2") {
    return ratings.some((item) => item.rating <= 2);
  }
  if (value === "all:3plus") {
    return ratings.every((item) => item.rating >= 3);
  }
  const [userKey, bandText] = value.split(":");
  const targetBand = Number(bandText);
  if (!userKey || !Number.isFinite(targetBand)) {
    return true;
  }
  const item = ratings.find((candidate) => candidate.key === userKey);
  return item ? starRatingBand(item.rating) === targetBand : false;
}

function segmentMatchesStarRatingFilters(segment, filters = []) {
  const safeFilters = (Array.isArray(filters) ? filters : [])
    .map((value) => String(value || "").trim())
    .filter((value) => value && value !== "all");
  return !safeFilters.length || safeFilters.some((value) => segmentMatchesStarRatingFilter(segment, value));
}

function starRatingFilterLabel(value) {
  if (value === "lowest:1") {
    return "Any 1-star";
  }
  if (value === "lowest:2") {
    return "Any 1-2 star";
  }
  if (value === "all:3plus") {
    return "All 3+";
  }
  const [userKey, bandText] = String(value || "").split(":");
  const user = SEGMENT_STAR_RATING_USERS.find((item) => item.key === userKey);
  if (user && hasDisplayValue(bandText)) {
    return `${user.short} ${bandText}`;
  }
  return "All";
}

function starRatingFiltersLabel(filters = []) {
  const safeFilters = (Array.isArray(filters) ? filters : [])
    .map((value) => String(value || "").trim())
    .filter((value) => value && value !== "all");
  if (!safeFilters.length) {
    return "All";
  }
  if (safeFilters.length === 1) {
    return starRatingFilterLabel(safeFilters[0]);
  }
  return `${safeFilters.length} selected`;
}

function StarRatingSymbols({ value }) {
  const rating = parseStarRatingValue(value) || 0;
  return (
    <span className="segment-star-symbols" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((star) => {
        const fillPct = Math.max(0, Math.min(100, (rating - (star - 1)) * 100));
        return (
          <span key={star} className="segment-star-symbol">
            {icon("star")}
            <span className="segment-star-symbol-fill" style={{ width: `${fillPct}%` }}>
              {icon("star")}
            </span>
          </span>
        );
      })}
    </span>
  );
}

function SegmentStarRatingSummary({ ratings = [], onOpenChat = null }) {
  if (!Array.isArray(ratings) || !ratings.length) {
    return null;
  }
  const worstRating = ratings.reduce((lowest, item) => Math.min(lowest, item.rating), ratings[0].rating);
  return (
    <section className="segment-star-rating-strip" aria-label="iRAP/AusRAP Star Rating">
      <header>
        <strong>{icon("star")} Star Rating</strong>
        <div className="segment-star-rating-actions">
          {typeof onOpenChat === "function" && (
            <button
              type="button"
              className="segment-star-rating-chat-button"
              title="Chat about why this segment received these star ratings"
              aria-label="Chat about star rating"
              onClick={onOpenChat}
            >
              {icon("message-circle-question")}
            </button>
          )}
          <span className={`segment-star-rating-worst star-tone-${starRatingTone(worstRating)}`} title="Lowest road-user rating on this segment">
            Lowest {formatStarRatingValue(worstRating)}
          </span>
        </div>
      </header>
      <div className="segment-star-rating-list">
        {ratings.map((item) => (
          <div
            key={item.key}
            className={`segment-star-rating-item star-tone-${item.tone}`}
            title={segmentStarRatingTitle(item)}
          >
            <span>{item.label}</span>
            <strong>{formatStarRatingValue(item.rating)}</strong>
            <StarRatingSymbols value={item.rating} />
          </div>
        ))}
      </div>
    </section>
  );
}

function SegmentStarRatingMini({ ratings = [] }) {
  if (!Array.isArray(ratings) || !ratings.length) {
    return "-";
  }
  return (
    <span className="specs-star-rating-mini" title={segmentStarRatingsText(ratings)}>
      {ratings.map((item) => (
        <span key={item.key} className={`specs-star-rating-mini__item star-tone-${item.tone}`} title={segmentStarRatingTitle(item)}>
          <b>{item.short}</b>
          <em>{formatStarRatingValue(item.rating)}</em>
        </span>
      ))}
    </span>
  );
}

function normalizeSegmentApproveValue(value) {
  return segmentApprovalState(value) === "approved";
}

function segmentApprovalState(value) {
  if (value === true || value === 1) {
    return "approved";
  }
  if (value === false || value === 0 || value === null || value === undefined) {
    return "pending";
  }
  const text = String(value).trim().toLowerCase();
  if (!text) {
    return "pending";
  }
  if (["review", "needs review", "needs_review", "need review", "needs-review", "2"].includes(text)) {
    return "review";
  }
  if (["1", "true", "yes", "approved", "approve", "on"].includes(text)) {
    return "approved";
  }
  return "pending";
}

function segmentApprovalValueForState(state) {
  if (state === "approved") {
    return 1;
  }
  if (state === "review") {
    return "Review";
  }
  return 0;
}

function segmentApproveDisplayText(value) {
  const state = segmentApprovalState(value);
  if (state === "approved") {
    return "Approved";
  }
  if (state === "review") {
    return "Review";
  }
  return "Pending";
}

function segmentReviewValueState(value) {
  if (value === true || value === 1) {
    return "review";
  }
  if (value === false || value === 0 || value === null || value === undefined) {
    return "pending";
  }
  const text = String(value).trim().toLowerCase();
  if (!text || ["false", "no", "none", "pending", "0"].includes(text)) {
    return "pending";
  }
  if (["true", "yes", "review", "needs review", "needs_review", "need review", "needs-review", "1", "2"].includes(text)) {
    return "review";
  }
  return segmentApprovalState(value);
}

function segmentApprovalStateFromProps(props = {}) {
  const approvalKey = ["approve", "Approve", "approved", "Approved"].find((key) =>
    Object.prototype.hasOwnProperty.call(props, key) && hasDisplayValue(props[key])
  );
  const reviewKey = ["review", "Review"].find((key) =>
    Object.prototype.hasOwnProperty.call(props, key) && hasDisplayValue(props[key])
  );
  const approvalState = approvalKey ? segmentApprovalState(props[approvalKey]) : "";
  const reviewState = reviewKey ? segmentReviewValueState(props[reviewKey]) : "";
  if (approvalState === "approved") {
    return "approved";
  }
  if (approvalState === "review" || reviewState === "review") {
    return "review";
  }
  return approvalState || reviewState || "pending";
}

function segmentApprovalUpdateFieldsFromProps(props = {}, nextState) {
  const approvalKey = ["approve", "Approve", "approved", "Approved"].find((key) =>
    Object.prototype.hasOwnProperty.call(props, key)
  );
  const reviewKey = ["review", "Review"].find((key) =>
    Object.prototype.hasOwnProperty.call(props, key)
  );
  const updates = [];
  const addUpdate = (fieldName, value) => {
    if (!fieldName || updates.some((update) => update.fieldName === fieldName)) {
      return;
    }
    updates.push({ fieldName, value });
  };

  if (approvalKey && reviewKey) {
    addUpdate(approvalKey, nextState === "approved" ? 1 : 0);
    addUpdate(reviewKey, nextState === "review" ? 1 : 0);
    return updates;
  }

  if (reviewKey) {
    const statusText =
      nextState === "approved" ? "Approved" : nextState === "review" ? "Review" : "Pending";
    addUpdate(reviewKey, statusText);
    return updates;
  }

  addUpdate(approvalKey || "approve", segmentApprovalValueForState(nextState));
  return updates;
}

function normalizeIconFileValue(value) {
  if (!hasDisplayValue(value)) {
    return "";
  }

  const text = String(value).trim();
  const numericMatch = text.match(/^-?\d+(?:\.\d+)?/);
  const numericValue = numericMatch ? Number(numericMatch[0]) : Number(text);
  if (Number.isFinite(numericValue)) {
    const wholeValue = Math.trunc(numericValue);
    if (wholeValue >= 0 && wholeValue < 10) {
      return String(wholeValue).padStart(2, "0");
    }
    return String(wholeValue);
  }

  return text;
}

function irapIconUrl(field, value) {
  if (!field || !hasDisplayValue(field.iconCategory)) {
    return "";
  }
  const iconValue = normalizeIconFileValue(value);
  if (!iconValue) {
    return "";
  }
  return `/irap-icons/${encodeURIComponent(String(field.iconCategory))}/${encodeURIComponent(iconValue)}.png`;
}

function fieldOptionLabel(field, value) {
  if (!field || !Array.isArray(field.options) || !hasDisplayValue(value)) {
    return "";
  }
  const normalized = String(value).trim();
  const numeric = Number(normalized);
  const option = field.options.find((candidate) => {
    return String(candidate.id) === normalized || (Number.isFinite(numeric) && Number(candidate.id) === numeric);
  });
  return option ? option.label : "";
}

function dateOnly(value) {
  if (!value) {
    return "";
  }
  const text = String(value);
  return text.includes("T") ? text.slice(0, 10) : text;
}

function normalizeSearchDigits(value) {
  return String(value || "").replace(/[\u06f0-\u06f9\u0660-\u0669]/g, (digit) => {
    const code = digit.charCodeAt(0);
    if (code >= 0x06f0 && code <= 0x06f9) {
      return String(code - 0x06f0);
    }
    return String(code - 0x0660);
  });
}

function parseSegmentSearch(rawSearch) {
  const query = normalizeSearchDigits(rawSearch).trim().toLowerCase();
  if (!query) {
    return { type: "empty", query: "" };
  }

  const normalized = query.replace(/[\u2013\u2014\u2212]/g, "-").replace(/\s+/g, " ");
  const rangeMatch = normalized.match(/^#?(\d+)\s*-\s*#?(\d+)$/);
  if (rangeMatch) {
    const first = Number(rangeMatch[1]);
    const second = Number(rangeMatch[2]);
    return {
      type: "range",
      min: Math.min(first, second),
      max: Math.max(first, second),
      query: normalized
    };
  }

  const comparisons = [
    { pattern: /^>=\s*#?(\d+)$/, type: "gte" },
    { pattern: /^#?(\d+)\s*>=$/, type: "gte" },
    { pattern: /^>\s*#?(\d+)$/, type: "gt" },
    { pattern: /^#?(\d+)\s*>$/, type: "gt" },
    { pattern: /^<=\s*#?(\d+)$/, type: "lte" },
    { pattern: /^#?(\d+)\s*<=$/, type: "lte" },
    { pattern: /^<\s*#?(\d+)$/, type: "lt" },
    { pattern: /^#?(\d+)\s*<$/, type: "lt" }
  ];

  for (const comparison of comparisons) {
    const match = normalized.match(comparison.pattern);
    if (match) {
      return { type: comparison.type, value: Number(match[1]), query: normalized };
    }
  }

  return { type: "text", query: normalized };
}

function segmentMatchesSearch(segment, rawSearch) {
  const parsed = parseSegmentSearch(rawSearch);
  if (parsed.type === "empty") {
    return true;
  }

  const id = Number(segment.id);
  if (parsed.type === "range") {
    return Number.isFinite(id) && id >= parsed.min && id <= parsed.max;
  }
  if (parsed.type === "gt") {
    return Number.isFinite(id) && id > parsed.value;
  }
  if (parsed.type === "gte") {
    return Number.isFinite(id) && id >= parsed.value;
  }
  if (parsed.type === "lt") {
    return Number.isFinite(id) && id < parsed.value;
  }
  if (parsed.type === "lte") {
    return Number.isFinite(id) && id <= parsed.value;
  }

  return (
    String(segment.id).includes(parsed.query) ||
    String(segment.road || "").toLowerCase().includes(parsed.query) ||
    String(segment.distance || "").toLowerCase().includes(parsed.query)
  );
}

function evidenceItemsFromResult(result) {
  if (!result) {
    return [];
  }
  const allItems = [
    ...(Array.isArray(result.items) ? result.items : []),
    ...(Array.isArray(result.signs) ? result.signs : []),
    ...(Array.isArray(result.segmentedItems) ? result.segmentedItems : [])
  ];
  const seen = new Set();
  return allItems.filter((item, index) => {
    const key = item && (item.id || item.markedCropUrl || item.cropUrl || `${item.value || item.text || item.description || "item"}:${index}`);
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function bboxForEvidence(item) {
  const bbox = item && (item.bbox || (item.crop && item.crop.bbox));
  if (!bbox) {
    return null;
  }
  const x = Number(bbox.x);
  const y = Number(bbox.y);
  const width = Number(bbox.width);
  const height = Number(bbox.height);
  if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) {
    return null;
  }
  return {
    x: Math.max(0, Math.min(1, x)),
    y: Math.max(0, Math.min(1, y)),
    width: Math.max(0.001, Math.min(1 - Math.max(0, Math.min(1, x)), width)),
    height: Math.max(0.001, Math.min(1 - Math.max(0, Math.min(1, y)), height))
  };
}

function sourceImageForEvidence(item) {
  if (!item) {
    return "";
  }
  if (item.cropSourceImage) {
    return item.cropSourceImage;
  }
  if (item.crop && item.crop.sourceImage) {
    return item.crop.sourceImage;
  }
  return Array.isArray(item.sourceImages) && item.sourceImages.length ? item.sourceImages[0] : "";
}

function isPromptSegmentationItem(item) {
  const source = item && item.segmentation && String(item.segmentation.source || "").toLowerCase();
  return Boolean(
    item &&
      (source === "replicate" ||
        String(item.type || "").toLowerCase() === "prompt_segmentation")
  );
}

function isOpenAiVisualEvidenceItem(item) {
  return Boolean(
    item &&
      String(item.source || "").toLowerCase() === "openai_visual_evidence" &&
      String(item.type || "").toLowerCase() === "visual_annotation"
  );
}

function humanizeEvidenceText(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function segmentationEvidenceKey(item) {
  const segmentation = item && item.segmentation ? item.segmentation : {};
  return humanizeEvidenceText(
    segmentation.promptLabel ||
      item.value ||
      item.text ||
      item.description ||
      item.category ||
      "evidence"
  );
}

function bestUniqueSegmentationItems(items) {
  const bestByKey = new Map();
  items.forEach((item) => {
    const key = segmentationEvidenceKey(item);
    if (!key) {
      return;
    }
    const current = bestByKey.get(key);
    if (!current || Number(item.confidence || 0) > Number(current.confidence || 0)) {
      bestByKey.set(key, item);
    }
  });
  return Array.from(bestByKey.values()).sort((a, b) =>
    segmentationEvidenceKey(a).localeCompare(segmentationEvidenceKey(b))
  );
}

function isSameEvidenceTarget(target, item, segmentId) {
  if (!target || !item) {
    return false;
  }
  return Number(target.segmentId) === Number(segmentId) && String(target.itemId) === String(item.id);
}

function evidenceOverlaysForImage(detail, signResults, currentImage, activeEvidenceTarget) {
  if (!detail || !Array.isArray(detail.images) || !detail.images[currentImage]) {
    return [];
  }
  const imageFile = detail.images[currentImage].file;
  const result = signResults && signResults[detail.id];
  return evidenceItemsFromResult(result)
    .filter((item) => !isOpenAiVisualEvidenceItem(item))
    .filter((item) => !isPromptSegmentationItem(item))
    .filter((item) => bboxForEvidence(item) && sourceImageForEvidence(item) === imageFile)
    .map((item) => ({
      item,
      bbox: bboxForEvidence(item),
      label: item.value || item.text || item.description || "Evidence",
      active: isSameEvidenceTarget(activeEvidenceTarget, item, detail.id)
    }));
}

function panoCandidateId(candidate) {
  if (!candidate) {
    return "";
  }
  return String(
    candidate.candidateId ||
      candidate.pointId ||
      candidate.fileName ||
      (candidate.feature && candidate.feature.properties && (
        candidate.feature.properties.PointID ||
        candidate.feature.properties.FileName ||
        `${candidate.feature.properties.VideoName || ""}:${candidate.feature.properties.FID || ""}`
      )) ||
      `${candidate.videoName || ""}:${candidate.fid || ""}`
  ).trim();
}

function panoCandidateFileLabel(candidate) {
  const props = (candidate && candidate.feature && candidate.feature.properties) || {};
  return String(candidate && (candidate.fileName || props.FileName) || "").trim();
}

function panoCandidateImageUrl(candidate) {
  const props = (candidate && candidate.feature && candidate.feature.properties) || {};
  return String(
    candidate && (
      candidate.previewUrl ||
      candidate.panoPreviewUrl ||
      candidate.url ||
      candidate.panoImageUrl ||
      candidate.PanoImageUrl ||
      props.previewUrl ||
      props.PanoImageUrl
    ) || ""
  ).trim();
}

function panoCandidatePreviewImage(candidate, segmentId) {
  const url = panoCandidateImageUrl(candidate);
  if (!candidate || !url) {
    return null;
  }
  const props = (candidate.feature && candidate.feature.properties) || {};
  const file = panoCandidateFileLabel(candidate) ||
    `${panoCandidateId(candidate) || "candidate"}.jpg`;
  return {
    file,
    url,
    thumbUrl: url,
    sourceUrl: candidate.panoImageUrl || candidate.PanoImageUrl || props.PanoImageUrl || "",
    videoName: candidate.videoName || candidate.VideoName || props.VideoName || props.VideoNameRaw || "",
    fid: candidate.fid ?? candidate.FID ?? props.FID,
    segmentId,
    candidateId: panoCandidateId(candidate),
    isCandidatePreview: true
  };
}

function panoCandidateFeature(candidate) {
  if (!candidate) {
    return null;
  }
  const sourceFeature = candidate.feature && candidate.feature.type === "Feature"
    ? candidate.feature
    : null;
  const sourceGeometry = sourceFeature && sourceFeature.geometry;
  if (sourceGeometry && Array.isArray(sourceGeometry.coordinates) && sourceGeometry.coordinates.length >= 2) {
    return {
      ...sourceFeature,
      properties: {
        ...(sourceFeature.properties || {}),
        __candidateId: panoCandidateId(candidate)
      }
    };
  }
  const lng = Number(candidate.lng ?? candidate.Lng ?? candidate.lon ?? candidate.Lon);
  const lat = Number(candidate.lat ?? candidate.Lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [lng, lat]
    },
    properties: {
      ...(candidate.properties || {}),
      FileName: candidate.fileName || candidate.FileName,
      FID: candidate.fid ?? candidate.FID,
      VideoName: candidate.videoName || candidate.VideoName,
      __candidateId: panoCandidateId(candidate)
    }
  };
}

function gisAssetId(asset) {
  return String(asset && (asset.assetId || asset.nidAsset || asset.refId || "") || "").trim();
}

function gisLayerClassKey(item) {
  if (!item) {
    return "";
  }
  const classId = item.classId ?? item.ClassID;
  if (classId !== null && classId !== undefined && String(classId).trim() !== "") {
    const numericClassId = Number(classId);
    return Number.isFinite(numericClassId) ? String(numericClassId) : String(classId).trim();
  }
  return String(item.key || item.className || item.ClassName || "unknown").trim().toLowerCase();
}

function gisAssetLabel(asset) {
  if (!asset) {
    return "GIS asset";
  }
  return String(
    asset.assetName ||
      asset.className ||
      asset.assetSubType ||
      (asset.classId ? `Class ${asset.classId}` : "") ||
      (asset.assetId ? `Asset ${asset.assetId}` : "GIS asset")
  ).trim();
}

function gisAssetMetaLabel(asset) {
  if (!asset) {
    return "";
  }
  return [
    asset.className,
    asset.assetType,
    asset.assetSubType
  ].map((value) => String(value || "").trim()).filter(Boolean).join(" - ");
}

function gisFeatureFromAsset(asset) {
  if (!asset) {
    return null;
  }
  const sourceFeature = asset.feature && asset.feature.type === "Feature"
    ? asset.feature
    : null;
  const geometry = sourceFeature && sourceFeature.geometry
    ? sourceFeature.geometry
    : asset.geoJson;
  if (!geometry || !geometry.type || !Array.isArray(geometry.coordinates)) {
    return null;
  }
  return {
    type: "Feature",
    geometry,
    properties: {
      ...(sourceFeature ? sourceFeature.properties || {} : {}),
      assetId: asset.assetId,
      assetSubTypeId: asset.assetSubTypeId,
      assetSubType: asset.assetSubType,
      assetType: asset.assetType,
      assetName: asset.assetName,
      classId: asset.classId,
      className: asset.className,
      geometryType: asset.geometryType,
      distanceToSegment: asset.distanceToSegment,
      lineColor: asset.lineColor,
      fillColor: asset.fillColor,
      __gisAssetId: gisAssetId(asset)
    }
  };
}

function gisLayerClassOptionsFromAssets(assets) {
  const map = new Map();
  (Array.isArray(assets) ? assets : []).forEach((asset) => {
    const classId = Number(asset && asset.classId);
    const key = Number.isFinite(classId) ? String(classId) : String(asset && asset.className || "unknown");
    if (!map.has(key)) {
      map.set(key, {
        key,
        classId: Number.isFinite(classId) ? classId : null,
        className: String(asset && asset.className || (Number.isFinite(classId) ? `Class ${classId}` : "GIS asset")).trim(),
        count: 0
      });
    }
    map.get(key).count += 1;
  });
  return Array.from(map.values()).sort((a, b) => b.count - a.count || a.className.localeCompare(b.className));
}

function gisLayerColor(value) {
  const palette = [
    "#ff007f",
    "#00a6ff",
    "#ffb000",
    "#00c853",
    "#f50057",
    "#7c4dff",
    "#00b8a9",
    "#ff5c00"
  ];
  const text = String(value || "gis");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  }
  return palette[hash % palette.length];
}

function panoCandidateIsSelected(candidate, selectedIds) {
  const id = panoCandidateId(candidate);
  return Boolean(id && selectedIds && selectedIds.has(id));
}

function jsonFetch(url, options) {
  return fetch(url, options).then(async (response) => {
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(body.error || body.msg || `Request failed: ${response.status}`);
      error.statusCode = response.status;
      error.code = body.code || "";
      error.details = body;
      throw error;
    }
    return body;
  });
}

function apiFetch(url, options) {
  const requestOptions = { ...(options || {}) };
  const headers = { ...(requestOptions.headers || {}) };
  const token = readStoredAuthToken();
  if (token && String(url || "").startsWith("/api/")) {
    if (!headers.Token && !headers.Authorization) {
      headers.Token = token;
    }
    const method = String(requestOptions.method || "GET").toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method) && !headers["X-CSRF-Token"]) {
      headers["X-CSRF-Token"] = token;
    }
    if (!headers["X-Requested-With"]) {
      headers["X-Requested-With"] = "XMLHttpRequest";
    }
    if (!headers["X-Client-Session-Id"]) {
      headers["X-Client-Session-Id"] = auditClientSessionId();
    }
  }
  requestOptions.headers = headers;
  requestOptions.credentials = requestOptions.credentials || "same-origin";
  return jsonFetch(url, requestOptions);
}

function openSegmentAerialOverlay(segmentId) {
  const numericSegmentId = Number(segmentId);
  if (!Number.isFinite(numericSegmentId) || numericSegmentId <= 0) {
    return;
  }
  window.open(`/api/segments/${numericSegmentId}/aerial-overlay?zoom=19`, "_blank", "noopener,noreferrer");
}

function authFetch(path, options) {
  return apiFetch(`/api/auth${path}`, options);
}

function loginErrorMessage(error) {
  const message = String(error && error.message || "");
  if (
    (error && error.code === "INVALID_CREDENTIALS") ||
    /no security token|no token was returned|invalid credential|invalid username|invalid password/i.test(message)
  ) {
    return INVALID_LOGIN_MESSAGE;
  }
  return message || "Login failed.";
}

function isActiveBatchJob(job) {
  return job && ["queued", "running", "cancelling"].includes(job.status);
}

function normalizeSegmentRangeText(value) {
  const persianDigits = "\u06f0\u06f1\u06f2\u06f3\u06f4\u06f5\u06f6\u06f7\u06f8\u06f9";
  const arabicDigits = "\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669";
  return String(value || "")
    .replace(/[\u06f0-\u06f9]/g, (digit) => String(persianDigits.indexOf(digit)))
    .replace(/[\u0660-\u0669]/g, (digit) => String(arabicDigits.indexOf(digit)))
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[\u060c\u061b]/g, ",")
    .replace(/\bto\b/gi, "-")
    .replace(/\u062a\u0627/g, "-")
    .replace(/\u0648/g, ",");
}

function parseSegmentRangeText(value) {
  const normalized = normalizeSegmentRangeText(value);
  const entries = normalized.split(/[,\n;]+/).map((entry) => entry.trim()).filter(Boolean);
  const ranges = [];

  for (const entry of entries) {
    const match = entry.match(/^(\d+)(?:\s*-\s*(\d+))?$/);
    if (!match) {
      return { error: "Use ranges like 1-10, 100-110, 200-210." };
    }
    const startId = Number(match[1]);
    const endId = Number(match[2] || match[1]);
    if (!Number.isInteger(startId) || !Number.isInteger(endId) || startId < 1 || endId < 1) {
      return { error: "Segment IDs must be whole numbers greater than zero." };
    }
    if (startId > endId) {
      return { error: "Each segment range start must be less than or equal to its end." };
    }
    ranges.push({ startId, endId });
  }

  const requestedCount = ranges.reduce((sum, range) => sum + (range.endId - range.startId + 1), 0);
  if (requestedCount > MAX_BATCH_RANGE_SEGMENTS) {
    return { error: `Batch ranges are too large. Maximum is ${MAX_BATCH_RANGE_SEGMENTS} requested segment IDs.` };
  }

  const seenIds = new Set();
  ranges.forEach((range) => {
    for (let segmentId = range.startId; segmentId <= range.endId; segmentId += 1) {
      seenIds.add(segmentId);
    }
  });

  return {
    ranges,
    segmentCount: seenIds.size,
    label: ranges.map((range) => range.startId === range.endId ? String(range.startId) : `${range.startId}-${range.endId}`).join(", ")
  };
}

function batchProgressText(job) {
  if (!job) {
    return "No batch job running.";
  }

  const completed = Number(job.completed || 0);
  const total = Number(job.total || 0);
  const success = Number(job.success || 0);
  const failed = Number(job.failed || 0);
  const excluded = Number(job.excludedSegmentCount || 0);
  const range = job.rangeLabel || (job.range && job.range.label) || (job.range ? `${job.range.startId}-${job.range.endId}` : "");
  const successLabel =
    job.mode === "segmentEvidence"
      ? "processed"
      : job.mode === "extractSigns"
      ? "extracted"
      : "analyzed";
  return `Batch ${range}: ${completed}/${total} complete, ${success} ${successLabel}, ${failed} failed${excluded ? `, ${excluded} blocked skipped` : ""}.`;
}

function initialLayoutMode() {
  const view = new URLSearchParams(window.location.search).get("view");
  return view === "single" || view === "logs" || view === "combined" || view === "agents" || view === "specs" || view === "feedback" || view === "dashboard" || view === "settings" || view === "activity" ? view : "dashboard";
}

function App() {
  const [authToken, setAuthToken] = useState(readStoredAuthToken);
  const [authUser, setAuthUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(() => Boolean(readStoredAuthToken()));
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authAccessDenied, setAuthAccessDenied] = useState(false);
  const [authChallenge, setAuthChallenge] = useState(null);
  const [authTermsChallenge, setAuthTermsChallenge] = useState(null);
  const [authResending, setAuthResending] = useState(false);
  const [health, setHealth] = useState(null);
  const [fields, setFields] = useState([]);
  const [segments, setSegments] = useState(null);
  const [allSpeedRecords, setAllSpeedRecords] = useState([]);
  const [gisLayerOptions, setGisLayerOptions] = useState(() => normalizeGisLayerOptions(null));
  const [gisLayerOptionsLoading, setGisLayerOptionsLoading] = useState(false);
  const [gisLayerOptionsError, setGisLayerOptionsError] = useState("");
  const [gisLayerSettings, setGisLayerSettingsState] = useState(readStoredGisLayerSettings);
  const [signResults, setSignResults] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draftValues, setDraftValues] = useState({});
  const [activeCategory, setActiveCategory] = useState("All");
  const [viewMode, setViewMode] = useState("map");
  const [layoutMode, setLayoutMode] = useState(initialLayoutMode); // "single", "agents", "feedback", "dashboard", "activity", "settings", "specs", "combined", or "logs"
  const [specsPanels, setSpecsPanels] = useState({ segments: true, info: true });
  const [dashboardSegmentFilterRequest, setDashboardSegmentFilterRequest] = useState(null);
  const [search, setSearch] = useState("");
  const [currentImage, setCurrentImage] = useState(0);
  const [analysisImageCount, setAnalysisImageCount] = useState(1);
  const [imageProcessingMode, setImageProcessingMode] = useState("full360");
  const [agentRunnerModel, setAgentRunnerModel] = useState("gpt-5.5");
  const [agentRunnerReasoningEffort, setAgentRunnerReasoningEffort] = useState("low");
  const [includeFieldPromptsInAgentPrompt, setIncludeFieldPromptsInAgentPrompt] = useState(true);
  const [agentConcurrency, setAgentConcurrency] = useState(5);
  const [openAiCallConcurrency, setOpenAiCallConcurrency] = useState(10);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("Loading iRAP data...");
  const [error, setError] = useState("");
  const [analysisResults, setAnalysisResults] = useState({}); // Store AI results by segment ID
  const [logs, setLogs] = useState([]); // Store OpenAI API logs
  const [batchRange, setBatchRange] = useState({ start: "", end: "", ranges: "" });
  const [batchSegmentConcurrency, setBatchSegmentConcurrency] = useState(2);
  const [batchJob, setBatchJob] = useState(null);
  const [activeEvidenceTarget, setActiveEvidenceTarget] = useState(null);
  const [previewEvidenceTarget, setPreviewEvidenceTarget] = useState(null);
  const [panoAnnotations, setPanoAnnotations] = useState([]);
  const [panoCalibrationState, setPanoCalibrationState] = useState(() => normalizePanoCalibrationState(null));
  const [panoCalibrationLoaded, setPanoCalibrationLoaded] = useState(false);
  const [activePanoAnnotationId, setActivePanoAnnotationId] = useState(null);
  const [selectedAgentGroups, setSelectedAgentGroups] = useState([]);
  const segmentLoadIdRef = useRef(0);
  const authPushPollInFlightRef = useRef(false);
  const lastPanoAuditKeyRef = useRef("");

  const batchBusy = isActiveBatchJob(batchJob);
  const canEditIrap = userCanEditIrap(authUser);
  const layoutModeForUser = DISABLED_LAYOUT_IDS.has(layoutMode)
    ? "specs"
    : authUser && !canEditIrap && ADMIN_LAYOUT_IDS.has(layoutMode)
    ? "specs"
    : layoutMode;

  const setGisLayerSettings = (updater) => {
    setGisLayerSettingsState((current) => {
      const nextValue = typeof updater === "function" ? updater(current) : updater;
      const normalized = normalizeGisLayerSettings(nextValue);
      storeGisLayerSettings(normalized);
      return normalized;
    });
  };

  const trackAuditEvent = (eventType, details = {}) => {
    if (!authUser || !readStoredAuthToken()) {
      return;
    }
    apiFetch("/api/audit/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType,
        clientSessionId: auditClientSessionId(),
        details
      })
    }).catch(() => {
      // User activity logging should never interrupt the review workflow.
    });
  };

  const loadAuthenticatedUser = async (token, options = {}) => {
    const userResponse = await authFetch("/me", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Token: token
      },
      body: JSON.stringify({ clientSessionId: auditClientSessionId() })
    });
    const user = authorizedIrapUser(userInfoFromResponse(userResponse));
    if (!options.skipSet) {
      setAuthUser(user);
    }
    return user;
  };

  const setMfaChallengeFromResponse = (response, fallbackMethod = "email") => {
    setAuthChallenge({
      challengeId: response.challengeId,
      method: response.method || fallbackMethod || "email",
      destinationMasked: response.destinationMasked || response.phoneMasked || response.emailMasked || response.pushMasked || "",
      phoneMasked: response.phoneMasked || "",
      emailMasked: response.emailMasked || "",
      pushMasked: response.pushMasked || "",
      expiresAt: response.expiresAt || "",
      resendAfterSeconds: response.resendAfterSeconds || 60
    });
  };

  const completeAuthenticatedLogin = async (authResponse) => {
    const token = authTokenFromLoginResponse(authResponse);
    if (!token) {
      throw new Error("Login succeeded but no token was returned.");
    }
    const responseUser = authResponse && (authResponse.user || (authResponse.data && authResponse.data.user))
      ? userInfoFromResponse(authResponse)
      : null;
    const user = responseUser ? authorizedIrapUser(responseUser) : await loadAuthenticatedUser(token);
    setAuthUser(user);
    storeAuthToken(token);
    setAuthToken(token);
    setAuthChallenge(null);
    setAuthTermsChallenge(null);
    setLayoutMode("dashboard");
    setSpecsPanels({ segments: true, info: true });
    setAuthAccessDenied(false);
    setMessage("Signed in. Loading iRAP data...");
  };

  const handleLogin = async ({ username, password, method = "email" }) => {
    setAuthSubmitting(true);
    setAuthError("");
    setAuthAccessDenied(false);
    setAuthTermsChallenge(null);
    try {
      const loginResponse = await authFetch("/login", {
        method: "POST",
        headers: { "Content-Type": "application/json;charset=UTF-8" },
        body: JSON.stringify({ username, password, method, clientSessionId: auditClientSessionId() })
      });
      if (loginResponse && loginResponse.termsRequired) {
        clearStoredAuthToken();
        setAuthToken("");
        setAuthUser(null);
        setAuthChallenge(null);
        setAuthTermsChallenge({
          termsChallengeId: loginResponse.termsChallengeId,
          terms: loginResponse.terms || null,
          expiresAt: loginResponse.expiresAt || "",
          method: loginResponse.method || method || "email"
        });
        setMessage("Terms acceptance required.");
        return;
      }
      if (loginResponse && loginResponse.mfaRequired) {
        clearStoredAuthToken();
        setAuthToken("");
        setAuthUser(null);
        setAuthTermsChallenge(null);
        setMfaChallengeFromResponse(loginResponse, method);
        setMessage(loginResponse.method === "push" ? "Push approval request sent." : "Verification code sent.");
        return;
      }
      await completeAuthenticatedLogin(loginResponse);
    } catch (loginError) {
      const accessDenied = isIrapAccessDeniedError(loginError);
      clearStoredAuthToken();
      setAuthToken("");
      setAuthUser(null);
      setAuthChallenge(null);
      setAuthTermsChallenge(null);
      setAuthAccessDenied(accessDenied);
      setAuthError(accessDenied ? IRAP_ACCESS_DENIED_MESSAGE : loginErrorMessage(loginError));
    } finally {
      setAuthSubmitting(false);
      setAuthChecking(false);
    }
  };

  const handleAcceptTerms = async ({ termsChallengeId }) => {
    if (!termsChallengeId || authSubmitting) {
      return;
    }
    setAuthSubmitting(true);
    setAuthError("");
    setAuthAccessDenied(false);
    try {
      const termsResponse = await authFetch("/accept-terms", {
        method: "POST",
        headers: { "Content-Type": "application/json;charset=UTF-8" },
        body: JSON.stringify({ termsChallengeId, accepted: true, clientSessionId: auditClientSessionId() })
      });
      setAuthTermsChallenge(null);
      if (termsResponse && termsResponse.mfaRequired) {
        setMfaChallengeFromResponse(termsResponse, authTermsChallenge && authTermsChallenge.method || "email");
        setMessage(termsResponse.method === "push" ? "Push approval request sent." : "Verification code sent.");
        return;
      }
      await completeAuthenticatedLogin(termsResponse);
    } catch (termsError) {
      setAuthError(termsError.message || "Could not accept terms.");
    } finally {
      setAuthSubmitting(false);
      setAuthChecking(false);
    }
  };

  const handleCancelTerms = () => {
    setAuthTermsChallenge(null);
    setAuthChallenge(null);
    setAuthError("");
    setAuthAccessDenied(false);
    setMessage("Terms were not accepted.");
  };

  const handleVerifyMfa = async ({ code }) => {
    if (!authChallenge || !authChallenge.challengeId) {
      setAuthError("Start sign in again before entering a verification code.");
      return;
    }
    setAuthSubmitting(true);
    setAuthError("");
    setAuthAccessDenied(false);
    try {
      const verifyResponse = await authFetch("/verify-mfa", {
        method: "POST",
        headers: { "Content-Type": "application/json;charset=UTF-8" },
        body: JSON.stringify({
          challengeId: authChallenge.challengeId,
          code,
          clientSessionId: auditClientSessionId()
        })
      });
      await completeAuthenticatedLogin(verifyResponse);
    } catch (verifyError) {
      setAuthError(verifyError.message || "Verification failed.");
    } finally {
      setAuthSubmitting(false);
      setAuthChecking(false);
    }
  };

  const handleResendMfa = async () => {
    if (!authChallenge || !authChallenge.challengeId || authResending) {
      return;
    }
    setAuthResending(true);
    setAuthError("");
    try {
      const resendResponse = await authFetch("/resend-mfa", {
        method: "POST",
        headers: { "Content-Type": "application/json;charset=UTF-8" },
        body: JSON.stringify({ challengeId: authChallenge.challengeId, clientSessionId: auditClientSessionId() })
      });
      setAuthChallenge({
        challengeId: resendResponse.challengeId || authChallenge.challengeId,
        method: resendResponse.method || authChallenge.method || "email",
        destinationMasked: resendResponse.destinationMasked || authChallenge.destinationMasked || "",
        phoneMasked: resendResponse.phoneMasked || authChallenge.phoneMasked || "",
        emailMasked: resendResponse.emailMasked || authChallenge.emailMasked || "",
        pushMasked: resendResponse.pushMasked || authChallenge.pushMasked || "",
        expiresAt: resendResponse.expiresAt || authChallenge.expiresAt || "",
        resendAfterSeconds: resendResponse.resendAfterSeconds || authChallenge.resendAfterSeconds || 60
      });
      setMessage((resendResponse.method || authChallenge.method) === "push" ? "Push approval request sent again." : "Verification code sent again.");
    } catch (resendError) {
      setAuthError(resendError.message || "Could not resend the verification request.");
    } finally {
      setAuthResending(false);
    }
  };

  const handleCheckPushApproval = async () => {
    if (!authChallenge || authChallenge.method !== "push" || !authChallenge.challengeId || authPushPollInFlightRef.current) {
      return;
    }
    authPushPollInFlightRef.current = true;
    try {
      const pushResponse = await authFetch("/push-status", {
        method: "POST",
        headers: { "Content-Type": "application/json;charset=UTF-8" },
        body: JSON.stringify({ challengeId: authChallenge.challengeId, clientSessionId: auditClientSessionId() })
      });
      if (authTokenFromLoginResponse(pushResponse)) {
        await completeAuthenticatedLogin(pushResponse);
        return;
      }
      if (pushResponse && pushResponse.status === "expired") {
        setAuthError("The push approval request has expired.");
        setAuthChallenge(null);
      }
    } catch (pushError) {
      setAuthError(pushError.message || "Push approval failed.");
      if (/denied|expired|no longer valid/i.test(pushError.message || "")) {
        setAuthChallenge(null);
      }
    } finally {
      authPushPollInFlightRef.current = false;
    }
  };

  const handleCancelMfa = () => {
    setAuthChallenge(null);
    setAuthError("");
    setAuthAccessDenied(false);
  };

  const handleLogout = () => {
    const token = readStoredAuthToken();
    if (token) {
      authFetch("/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Token: token
        },
        body: JSON.stringify({ clientSessionId: auditClientSessionId() })
      }).catch(() => {});
    }
    clearStoredAuthToken();
    setAuthToken("");
    setAuthUser(null);
    setAuthAccessDenied(false);
    setAuthChallenge(null);
    setAuthTermsChallenge(null);
    setHealth(null);
    setFields([]);
    setSegments(null);
    setDetail(null);
    setSelectedId(null);
    setDraftValues({});
    setSignResults({});
    setAnalysisResults({});
    setLogs([]);
    setPanoAnnotations([]);
    setMessage("Signed out.");
    setError("");
  };

  useEffect(() => {
    let cancelled = false;
    const token = readStoredAuthToken();
    if (!token) {
      setAuthAccessDenied(false);
      setAuthChecking(false);
      return () => {
        cancelled = true;
      };
    }

    setAuthChecking(true);
    loadAuthenticatedUser(token, { skipSet: true })
      .then((user) => {
        if (cancelled) {
          return;
        }
        setAuthToken(token);
        setAuthUser(user);
        setAuthError("");
        setAuthAccessDenied(false);
      })
      .catch((authLoadError) => {
        if (cancelled) {
          return;
        }
        const accessDenied = isIrapAccessDeniedError(authLoadError);
        clearStoredAuthToken();
        setAuthToken("");
        setAuthUser(null);
        setAuthAccessDenied(accessDenied);
        setAuthError(accessDenied ? IRAP_ACCESS_DENIED_MESSAGE : authLoadError.message || "Your session has expired. Please sign in again.");
      })
      .finally(() => {
        if (!cancelled) {
          setAuthChecking(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (authUser && layoutModeForUser !== layoutMode) {
      setLayoutMode(layoutModeForUser);
    }
  }, [authUser, layoutMode, layoutModeForUser]);

  const setSpecsPanelOpen = (panel, open) => {
    setSpecsPanels((current) => ({
      ...current,
      [panel]: Boolean(open)
    }));
  };

  const agentGroupNames = useMemo(() => {
    if (!fields.length) {
      return [];
    }
    const names = Array.from(new Set(fields.map((field) => field.group)));
    const availableAgents = Array.from(new Set(names));
    const categoryAgents = AGENT_ORDER.filter((name) => availableAgents.includes(name)).concat(
      availableAgents.filter((name) => !AGENT_ORDER.includes(name))
    );
    return categoryAgents;
  }, [fields]);

  useEffect(() => {
    if (!authUser) {
      setPanoCalibrationLoaded(false);
      return;
    }

    Promise.all([
      apiFetch("/api/health"),
      apiFetch("/api/fields"),
      apiFetch("/api/segments"),
      apiFetch("/api/speed-records")
    ])
      .then(([healthData, fieldData, segmentData, speedData]) => {
        setHealth(healthData);
        setFields(fieldData.fields || []);
        setSegments(segmentData);
        setAllSpeedRecords(speedData.records || []);
        if (healthData && Number.isFinite(Number(healthData.agentConcurrency))) {
          setAgentConcurrency(Math.max(1, Math.min(10, Number(healthData.agentConcurrency))));
        }
        if (healthData && Number.isFinite(Number(healthData.openAiCallConcurrency))) {
          setOpenAiCallConcurrency(Math.max(1, Math.min(10, Number(healthData.openAiCallConcurrency))));
        }
        if (healthData && (healthData.agentRunnerModel || healthData.model)) {
          setAgentRunnerModel(String(healthData.agentRunnerModel || healthData.model));
        }
        if (healthData && (healthData.agentRunnerReasoningEffort || healthData.reasoningEffort)) {
          setAgentRunnerReasoningEffort(String(healthData.agentRunnerReasoningEffort || healthData.reasoningEffort));
        }
        setMessage("Select AI agents, choose a segment range, then run the range processor.");
      })
      .catch((loadError) => setError(loadError.message));
  }, [authUser]);

  useEffect(() => {
    if (!authUser) {
      setGisLayerOptionsLoading(false);
      return;
    }
    let cancelled = false;
    setGisLayerOptionsLoading(true);
    setGisLayerOptionsError("");
    apiFetch("/api/gis-layer-options?limit=2000")
      .then((data) => {
        if (cancelled) {
          return;
        }
        setGisLayerOptions(normalizeGisLayerOptions(data));
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }
        setGisLayerOptions(normalizeGisLayerOptions(null));
        setGisLayerOptionsError(loadError.message || "Could not load GIS layer list.");
      })
      .finally(() => {
        if (!cancelled) {
          setGisLayerOptionsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authUser]);

  useEffect(() => {
    if (!authUser) {
      return;
    }

    setPanoCalibrationLoaded(false);
    Promise.allSettled([
      apiFetch("/api/pano-annotations"),
      apiFetch("/api/pano-calibrations")
    ])
      .then(([annotationResult, calibrationResult]) => {
        if (annotationResult.status === "fulfilled") {
          setPanoAnnotations(Array.isArray(annotationResult.value.annotations) ? annotationResult.value.annotations : []);
        } else {
          console.error("Error loading pano annotations:", annotationResult.reason);
        }

        if (calibrationResult.status === "fulfilled") {
          setPanoCalibrationState(normalizePanoCalibrationState(calibrationResult.value));
        } else {
          console.error("Error loading pano calibrations:", calibrationResult.reason);
        }
        setPanoCalibrationLoaded(true);
      })
      .catch((loadError) => {
        console.error("Error loading pano annotations/calibrations:", loadError);
        setPanoCalibrationLoaded(true);
      });
  }, [authUser]);

  useEffect(() => {
    const features = (segments && Array.isArray(segments.features)) ? segments.features : [];
    const visibleFeatures = canEditIrap
      ? features
      : features.filter((feature) => !segmentBatchExcluded({ feature }));
    const selectedFeature = selectedId
      ? features.find((feature) => Number(feature && feature.properties && (feature.properties.SegmentId || feature.properties.ID)) === Number(selectedId))
      : null;
    const selectedIsVisible = selectedId
      ? visibleFeatures.some((feature) => Number(feature && feature.properties && (feature.properties.SegmentId || feature.properties.ID)) === Number(selectedId))
      : false;
    const selectedIsBlocked = Boolean(selectedFeature && segmentBatchExcluded({ feature: selectedFeature }));
    if (visibleFeatures.length && (!selectedId || (!canEditIrap && (selectedIsBlocked || !selectedIsVisible)))) {
      const firstId = Number(visibleFeatures[0].properties.SegmentId || visibleFeatures[0].properties.ID);
      selectSegment(firstId, { source: "auto" });
    }
  }, [segments, selectedId, canEditIrap]);

  useEffect(() => {
    if (selectedId && !batchRange.start && !batchRange.end && !batchRange.ranges) {
      setBatchRange({ start: String(selectedId), end: String(selectedId), ranges: String(selectedId) });
    }
  }, [selectedId, batchRange.start, batchRange.end, batchRange.ranges]);

  useEffect(() => {
    if (!agentGroupNames.length) {
      return;
    }

    setSelectedAgentGroups((current) => {
      const validCurrent = current.filter((name) => agentGroupNames.includes(name));
      return validCurrent.length
        ? validCurrent
        : agentGroupNames;
    });
  }, [agentGroupNames.join("|")]);

  const loadLogs = (options = {}) => {
    const silent = Boolean(options.silent);
    apiFetch("/api/logs?limit=100")
      .then((logData) => {
        setLogs(logData);
        if (!silent) {
          setMessage(`Loaded ${logData.length} log entries.`);
        }
      })
      .catch((loadError) => {
        console.error("Error loading logs:", loadError);
        if (!silent) {
          setMessage("Failed to load logs.");
        }
      });
  };

  const clearAllLogs = async () => {
    if (!confirm("Clear all logs? This cannot be undone.")) {
      return;
    }
    try {
      await apiFetch("/api/logs", { method: "DELETE" });
      setLogs([]);
      setMessage("All logs cleared.");
    } catch (err) {
      setError("Failed to clear logs: " + err.message);
    }
  };

  const loadSignResults = (options = {}) => {
    const silent = Boolean(options.silent);
    Promise.all([apiFetch("/api/sign-results"), apiFetch("/api/health")])
      .then(([data, healthData]) => {
        setSignResults(data.results || {});
        setHealth(healthData);
        if (!silent) {
          setMessage(`Loaded evidence inventory for ${Object.keys(data.results || {}).length} segment${Object.keys(data.results || {}).length === 1 ? "" : "s"}.`);
        }
      })
      .catch((loadError) => {
        console.error("Error loading sign results:", loadError);
        if (!silent) {
          setMessage("Evidence inventory loaded with no saved results.");
        }
      });
  };

  const loadSegmentSignResult = (segmentId, options = {}) => {
    const numericSegmentId = Number(segmentId);
    if (!Number.isFinite(numericSegmentId)) {
      return Promise.resolve(null);
    }

    return apiFetch(`/api/segments/${numericSegmentId}/signs`)
      .then((result) => {
        if (options.loadId && options.loadId !== segmentLoadIdRef.current) {
          return result;
        }
        setSignResults((current) => ({
          ...current,
          [numericSegmentId]: result
        }));
        return result;
      })
      .catch((loadError) => {
        console.error(`Error loading evidence for segment ${numericSegmentId}:`, loadError);
        return null;
      });
  };

  useEffect(() => {
    if (!authUser) {
      return;
    }

    const activeLayoutMode = layoutModeForUser;
    if (activeLayoutMode === "grid" || activeLayoutMode === "combined" || activeLayoutMode === "dashboard" || activeLayoutMode === "specs") {
      apiFetch("/api/analysis-results")
        .then((results) => {
          setAnalysisResults(results);
          const viewLabel = activeLayoutMode === "dashboard"
            ? "Dashboard"
            : activeLayoutMode === "specs"
            ? "Segments view"
            : "Grid view";
          setMessage(`${viewLabel} loaded with ${Object.keys(results).length} analysis results.`);
        })
        .catch((loadError) => {
          console.error("Error loading analysis results:", loadError);
          const viewLabel = activeLayoutMode === "dashboard"
            ? "Dashboard"
            : activeLayoutMode === "specs"
            ? "Segments view"
            : "Grid view";
          setMessage(`${viewLabel} loaded (no analysis results found).`);
        });
      if (activeLayoutMode === "grid" || activeLayoutMode === "combined" || activeLayoutMode === "specs") {
        loadSignResults({ silent: true });
      }
    } else if (activeLayoutMode === "logs") {
      loadLogs();
    } else if (activeLayoutMode === "single") {
      loadLogs({ silent: true });
    }
  }, [layoutModeForUser, authUser]);

  useEffect(() => {
    if (!authUser) {
      return;
    }

    if (!isActiveBatchJob(batchJob)) {
      return;
    }

    let stopped = false;

    function pollBatchJob() {
      apiFetch(`/api/batch-analyze/${batchJob.jobId}`)
        .then((updatedJob) => {
          if (stopped) {
            return;
          }

          setBatchJob(updatedJob);
          setMessage(batchProgressText(updatedJob));

          if (!isActiveBatchJob(updatedJob)) {
            if (layoutModeForUser === "grid" || layoutModeForUser === "combined" || layoutModeForUser === "dashboard" || layoutModeForUser === "specs") {
              apiFetch("/api/analysis-results")
                .then((results) => setAnalysisResults(results))
                .catch(() => {});
            }
            if (selectedId) {
              apiFetch(`/api/segments/${selectedId}`)
                .then((segmentDetail) => setDetail(segmentDetail))
                .catch(() => {});
            }
            loadSignResults({ silent: true });
            loadLogs({ silent: true });
          }
        })
        .catch((loadError) => {
          if (!stopped) {
            setError(loadError.message);
          }
        });
    }

    pollBatchJob();
    const timer = setInterval(pollBatchJob, 3500);

    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [authUser, batchJob && batchJob.jobId, batchJob && batchJob.status, layoutModeForUser, selectedId]);

  const visibleSegmentsForUser = useMemo(() => {
    if (!segments || canEditIrap || !Array.isArray(segments.features)) {
      return segments;
    }
    return {
      ...segments,
      features: segments.features.filter((feature) => !segmentBatchExcluded({ feature }))
    };
  }, [segments, canEditIrap]);

  const segmentIndex = useMemo(() => {
    const features = (visibleSegmentsForUser && visibleSegmentsForUser.features) || [];
    return features.map((feature) => ({
      id: Number(feature.properties.SegmentId || feature.properties.ID),
      road: feature.properties.Road_name || "",
      distance: feature.properties.Distance || "",
      feature
    }));
  }, [visibleSegmentsForUser]);

  const selectSegment = (segmentId, options = {}) => {
    const numericSegmentId = Number(segmentId);
    if (!Number.isFinite(numericSegmentId)) {
      setError("Could not select this map segment.");
      return;
    }
    const knownFeature = segments && Array.isArray(segments.features)
      ? segments.features.find((feature) => Number(feature && feature.properties && (feature.properties.SegmentId || feature.properties.ID)) === numericSegmentId)
      : null;
    if (!canEditIrap && knownFeature && segmentBatchExcluded({ feature: knownFeature })) {
      setError("This segment is blocked and is not available.");
      return;
    }
    const targetImageFile = options.targetImageFile ? String(options.targetImageFile) : "";
    const knownProps = (knownFeature && knownFeature.properties) || {};
    trackAuditEvent("segment_click", {
      segmentId: numericSegmentId,
      roadName: knownProps.Road_name || knownProps.roadName || "",
      distance: knownProps.Distance || knownProps.distance || "",
      source: options.source || "ui",
      targetImageFile
    });
    const loadId = segmentLoadIdRef.current + 1;
    segmentLoadIdRef.current = loadId;
    setSelectedId(numericSegmentId);
    if (!batchBusy && options.syncBatchRange !== false) {
      setBatchRange(singleSegmentRange(numericSegmentId));
    }
    setDetail(null);
    setCurrentImage(0);
    if (!options.preserveEvidenceTarget) {
      setActiveEvidenceTarget(null);
    }
    setActivePanoAnnotationId(null);
    setError("");
    setMessage(`Loading segment ${numericSegmentId}...`);
    loadSegmentSignResult(numericSegmentId, { loadId });
    apiFetch(`/api/segments/${numericSegmentId}`)
      .then((segmentDetail) => {
        if (loadId !== segmentLoadIdRef.current) {
          return;
        }
        const values = {};
        (segmentDetail.fields || []).forEach((field) => {
          values[field.field] = field.rawValue ?? "";
        });
        const targetImageIndex = targetImageFile && Array.isArray(segmentDetail.images)
          ? segmentDetail.images.findIndex((image) => image && image.file === targetImageFile)
          : -1;
        setDetail(segmentDetail);
        setDraftValues(values);
        setCurrentImage(targetImageIndex >= 0 ? targetImageIndex : 0);
        if (!options.preserveViewMode) {
          setViewMode(segmentDetail.images.length ? "pano" : "map");
        }
        setMessage(
          segmentDetail.images.length
            ? `Segment ${numericSegmentId} selected for AI processing. ${segmentDetail.images.length} pano images ready.`
            : "No pano images found for this segment."
        );
      })
      .catch((loadError) => {
        if (loadId !== segmentLoadIdRef.current) {
          return;
        }
        setError(loadError.message);
        setMessage("");
      });
  };

  const reloadSelectedSegmentDetail = async (options = {}) => {
    const numericSegmentId = Number(options.segmentId || selectedId);
    if (!Number.isFinite(numericSegmentId)) {
      return null;
    }
    const segmentDetail = await apiFetch(`/api/segments/${numericSegmentId}`);
    const values = {};
    (segmentDetail.fields || []).forEach((field) => {
      values[field.field] = field.rawValue ?? "";
    });
    setDetail(segmentDetail);
    setDraftValues(values);
    setSegments((current) => {
      if (!current || !Array.isArray(current.features)) {
        return current;
      }
      return {
        ...current,
        features: current.features.map((feature) => {
          const featureId = Number(feature && feature.properties && (feature.properties.SegmentId || feature.properties.ID));
          if (featureId !== numericSegmentId) {
            return feature;
          }
          return {
            ...feature,
            properties: {
              ...(feature.properties || {}),
              ...(segmentDetail.properties || {}),
              SegmentId: numericSegmentId
            }
          };
        })
      };
    });
    if (segmentDetail.analysis && typeof setAnalysisResults === "function") {
      setAnalysisResults((current) => ({
        ...current,
        [numericSegmentId]: segmentDetail.analysis
      }));
    }
    if (!options.silent) {
      setMessage(`Segment ${numericSegmentId} reloaded.`);
    }
    return segmentDetail;
  };

  const selectPanoImage = (imageIndex) => {
    setActiveEvidenceTarget(null);
    setActivePanoAnnotationId(null);
    setCurrentImage(Number(imageIndex));
    setViewMode("pano");
  };

  useEffect(() => {
    if (!detail || !activeEvidenceTarget || Number(activeEvidenceTarget.segmentId) !== Number(detail.id)) {
      return;
    }
    const sourceImage = sourceImageForEvidence(activeEvidenceTarget.item);
    if (!sourceImage || !Array.isArray(detail.images)) {
      return;
    }
    const imageIndex = detail.images.findIndex((image) => image.file === sourceImage);
    if (imageIndex >= 0 && imageIndex !== currentImage) {
      setCurrentImage(imageIndex);
    }
    if (viewMode !== "pano") {
      setViewMode("pano");
    }
  }, [detail, activeEvidenceTarget, currentImage, viewMode]);

  useEffect(() => {
    if (!authUser || viewMode !== "pano" || !detail || !Array.isArray(detail.images) || !detail.images[currentImage]) {
      return;
    }
    const image = detail.images[currentImage];
    const auditKey = `${detail.id}:${image.file}`;
    if (lastPanoAuditKeyRef.current === auditKey) {
      return;
    }
    lastPanoAuditKeyRef.current = auditKey;
    trackAuditEvent("panorama_view", {
      segmentId: Number(detail.id),
      roadName: detail.properties && (detail.properties.Road_name || detail.properties.roadName) || "",
      distance: detail.properties && (detail.properties.Distance || detail.properties.distance) || "",
      imageFile: image.file,
      imageIndex: Number(currentImage),
      source: activeEvidenceTarget ? "evidence" : "pano"
    });
  }, [authUser, detail, currentImage, viewMode, activeEvidenceTarget]);

  const runSearch = (event) => {
    event.preventDefault();
    if (!search.trim()) {
      return;
    }

    const found = segmentIndex.find((item) => {
      return segmentMatchesSearch(item, search);
    });

    if (found) {
      selectSegment(found.id, { source: "search" });
    } else {
      setMessage("No segment matched the search.");
    }
  };

  const analyzeSelected = async () => {
    if (!detail) {
      return;
    }

    setBusy(true);
    setError("");
    if (!selectedAgentGroups.length) {
      setError("Select at least one AI agent to run.");
      setBusy(false);
      return;
    }

    setMessage(
      `Running ${selectedAgentGroups.length} selected AI agent${selectedAgentGroups.length === 1 ? "" : "s"} for segment ${detail.id}...`
    );
    try {
      const gisLayerSelection = buildGisLayerSelection(gisLayerSettings);
      const result = await apiFetch(`/api/segments/${detail.id}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          maxImages: analysisImageCount,
          imageProcessingMode,
          model: agentRunnerModel,
          reasoningEffort: agentRunnerReasoningEffort,
          includeFieldPrompts: includeFieldPromptsInAgentPrompt,
          agentConcurrency,
          openAiCallConcurrency,
          groups: selectedAgentGroups,
          gisLayerSelection
        })
      });
      const analysisResult = result;
      setDetail({ ...detail, analysis: analysisResult });
      setAnalysisResults((current) => ({
        ...current,
        [detail.id]: analysisResult
      }));
      setMessage(
        `AI analysis complete. ${Object.keys(analysisResult.saveJson || {}).length} fields passed confidence checks. Add visual evidence manually from the Map/Pano panel.`
      );
      loadLogs({ silent: true });
    } catch (analysisError) {
      setError(analysisError.message);
      setMessage("Analysis stopped.");
      loadLogs({ silent: true });
    } finally {
      setBusy(false);
    }
  };

  const startBatchAnalyze = async () => {
    const rangeInput = String(
      batchRange.ranges || (batchRange.start && batchRange.end ? `${batchRange.start}-${batchRange.end}` : "")
    ).trim();
    const parsedRanges = parseSegmentRangeText(rangeInput);
    if (!rangeInput || parsedRanges.error || !parsedRanges.ranges.length) {
      setError(parsedRanges.error || "Enter at least one segment range.");
      return;
    }
    if (!selectedAgentGroups.length) {
      setError("Select at least one AI agent to run.");
      return;
    }

    setError("");
    setMessage(
      `Starting AI agent range processing for segments ${parsedRanges.label} with ${selectedAgentGroups.length} selected agent${selectedAgentGroups.length === 1 ? "" : "s"}...`
    );
    try {
      const gisLayerSelection = buildGisLayerSelection(gisLayerSettings);
      const job = await apiFetch("/api/batch-analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ranges: parsedRanges.ranges,
          maxImages: analysisImageCount,
          imageProcessingMode,
          model: agentRunnerModel,
          reasoningEffort: agentRunnerReasoningEffort,
          includeFieldPrompts: includeFieldPromptsInAgentPrompt,
          segmentConcurrency: batchSegmentConcurrency,
          agentConcurrency,
          openAiCallConcurrency,
          groups: selectedAgentGroups,
          gisLayerSelection
        })
      });
      setBatchJob(job);
      setMessage(batchProgressText(job));
    } catch (batchError) {
      setError(batchError.message);
      setMessage("AI agent range processing was not started.");
    }
  };

  const cancelBatchAnalyze = async () => {
    if (!batchJob || !isActiveBatchJob(batchJob)) {
      return;
    }

    try {
      const updatedJob = await apiFetch(`/api/batch-analyze/${batchJob.jobId}`, {
        method: "DELETE"
      });
      setBatchJob(updatedJob);
      setMessage("Batch cancellation requested. Current segment may finish first.");
    } catch (batchError) {
      setError(batchError.message);
    }
  };

  const openEvidenceInPano = (item, segment) => {
    if (!item || !segment) {
      return;
    }
    const targetImageFile = sourceImageForEvidence(item);
    setPreviewEvidenceTarget(null);
    setActiveEvidenceTarget({
      segmentId: Number(segment.id),
      itemId: item.id,
      item
    });
    setLayoutMode(canEditIrap ? "single" : "specs");
    setViewMode("pano");
    if (Number(selectedId) !== Number(segment.id)) {
      selectSegment(segment.id, { preserveEvidenceTarget: true, targetImageFile });
    } else if (targetImageFile && detail && Array.isArray(detail.images)) {
      const targetImageIndex = detail.images.findIndex((image) => image && image.file === targetImageFile);
      if (targetImageIndex >= 0) {
        setCurrentImage(targetImageIndex);
      }
    }
  };

  const openEvidencePreview = (item, segment) => {
    if (!item || !segment) {
      return;
    }
    setPreviewEvidenceTarget({ item, segment });
  };

  const applyAiSuggestions = () => {
    if (!detail || !detail.analysis || !detail.analysis.saveJson) {
      return;
    }

    setDraftValues({
      ...draftValues,
      ...detail.analysis.saveJson
    });
    setMessage("Applied high-confidence AI values to the draft JSON.");
  };

  const updateDraftValue = (fieldName, value) => {
    setDraftValues({
      ...draftValues,
      [fieldName]: value
    });
  };

  const saveJson = useMemo(() => {
    const result = {};
    fields.forEach((field) => {
      const value = draftValues[field.field];
      if (value !== "" && value !== null && value !== undefined) {
        const numberValue = Number(value);
        result[field.field] = Number.isFinite(numberValue) ? numberValue : value;
      }
    });
    return result;
  }, [draftValues, fields]);

  const updateGridCell = async ({ segmentId, source, fieldName, value }) => {
    setError("");
    let result;
    try {
      result = await apiFetch(`/api/segments/${segmentId}/grid-field`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source, fieldName, value })
      });
    } catch (gridEditError) {
      setError(gridEditError.message);
      throw gridEditError;
    }

    if (result.source === "old") {
      setSegments((current) => {
        if (!current || !Array.isArray(current.features)) {
          return current;
        }

        return {
          ...current,
          features: current.features.map((feature) => {
            const featureId = Number(feature.properties && feature.properties.SegmentId);
            if (featureId !== Number(segmentId)) {
              return feature;
            }
            return {
              ...feature,
              properties: {
                ...(feature.properties || {}),
                [result.propertyName]: result.value
              }
            };
          })
        };
      });

      setDetail((current) => {
        if (!current || Number(current.id) !== Number(segmentId)) {
          return current;
        }
        return {
          ...current,
          properties: {
            ...(current.properties || {}),
            [result.propertyName]: result.value
          },
          fields: (current.fields || []).map((field) => {
            if (field.field !== result.fieldCode) {
              return field;
            }
            return {
              ...field,
              rawValue: result.value,
              decodedValue: result.decodedValue || ""
            };
          })
        };
      });
    }

    if (result.source === "ai" && result.analysis) {
      setAnalysisResults((current) => ({
        ...current,
        [segmentId]: result.analysis
      }));
      setDetail((current) => {
        if (!current || Number(current.id) !== Number(segmentId)) {
          return current;
        }
        return {
          ...current,
          analysis: result.analysis
        };
      });
    }

    setMessage(`Updated ${source === "old" ? "old" : "AI"} ${fieldName} for segment ${segmentId}.`);
    return result;
  };

  const updateSegmentBatchExclusion = async ({ segmentId, excluded, reason = "" }) => {
    const numericSegmentId = Number(segmentId);
    if (!Number.isFinite(numericSegmentId) || numericSegmentId <= 0) {
      return null;
    }
    setError("");
    let result;
    try {
      result = await apiFetch(`/api/segments/${numericSegmentId}/batch-exclusion`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ excluded: Boolean(excluded), reason })
      });
    } catch (batchExcludeError) {
      setError(batchExcludeError.message || "Could not update batch exclusion.");
      throw batchExcludeError;
    }

    const properties = (result && result.properties) || {};
    const applyProperties = (currentProps = {}) => ({
      ...currentProps,
      ...properties
    });
    setSegments((current) => {
      if (!current || !Array.isArray(current.features)) {
        return current;
      }
      return {
        ...current,
        features: current.features.map((feature) => {
          const featureId = Number(feature.properties && feature.properties.SegmentId);
          if (featureId !== numericSegmentId) {
            return feature;
          }
          return {
            ...feature,
            properties: applyProperties(feature.properties || {})
          };
        })
      };
    });
    setDetail((current) => {
      if (!current || Number(current.id) !== numericSegmentId) {
        return current;
      }
      return {
        ...current,
        ...(result && result.detail ? result.detail : {}),
        properties: applyProperties((result && result.detail && result.detail.properties) || current.properties || {})
      };
    });
    setMessage(
      result && result.excluded
        ? `Segment ${numericSegmentId} blocked from batch processing.`
        : `Segment ${numericSegmentId} unblocked for batch processing.`
    );
    return result;
  };

  const createPanoAnnotation = async (annotationInput) => {
    if (!detail || !detail.images || !detail.images.length) {
      setError("Select a pano image before adding an annotation.");
      return null;
    }

    const selectedImage = annotationInput.imageFile
      ? detail.images.find((image) => image && image.file === annotationInput.imageFile)
      : detail.images[currentImage];
    if (!selectedImage) {
      setError("Selected pano image is not part of this segment.");
      return null;
    }
    try {
      const result = await apiFetch(`/api/segments/${detail.id}/pano-annotations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          imageFile: selectedImage.file,
          label: annotationInput.label,
          type: annotationInput.type,
          note: annotationInput.note,
          yaw: annotationInput.yaw,
          pitch: annotationInput.pitch,
          textureX: annotationInput.textureX,
          textureY: annotationInput.textureY,
          shape: annotationInput.shape,
          meta: annotationInput.meta || null
        })
      });
      const annotation = result.annotation;
      setPanoAnnotations((current) => current.concat(annotation));
      setActivePanoAnnotationId(annotation.id);
      notifyPanoAnnotationsChanged();
      setMessage(`Added manual evidence "${annotation.label}" on segment ${detail.id}.`);
      return annotation;
    } catch (annotationError) {
      setError(annotationError.message);
      return null;
    }
  };

  const createSamPanoAnnotation = async (annotationInput) => {
    if (!detail || !detail.images || !detail.images.length) {
      setError("Select a pano image before using Segment Anything.");
      return null;
    }

    const selectedImage = annotationInput.imageFile
      ? detail.images.find((image) => image && image.file === annotationInput.imageFile)
      : detail.images[currentImage];
    if (!selectedImage) {
      setError("Selected pano image is not part of this segment.");
      return null;
    }
    try {
      const result = await apiFetch(`/api/segments/${detail.id}/sam-annotation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          imageFile: selectedImage.file,
          label: annotationInput.label,
          type: annotationInput.type,
          note: annotationInput.note,
          point: {
            yaw: annotationInput.yaw,
            pitch: annotationInput.pitch,
            textureX: annotationInput.textureX,
            textureY: annotationInput.textureY
          },
          meta: annotationInput.meta || null
        })
      });
      const annotation = result.annotation;
      setPanoAnnotations((current) => current.concat(annotation));
      setActivePanoAnnotationId(annotation.id);
      notifyPanoAnnotationsChanged();
      setMessage(
        `Segment Anything saved "${annotation.label}" on segment ${detail.id}${result.sam && Number.isFinite(Number(result.sam.score)) ? ` (${Math.round(Number(result.sam.score) * 100)}% mask score)` : ""}.`
      );
      return annotation;
    } catch (annotationError) {
      setError(annotationError.message);
      return null;
    }
  };

  const updatePanoAnnotation = async (annotationId, patch) => {
    const annotation = panoAnnotations.find((candidate) => String(candidate.id) === String(annotationId));
    if (!annotation) {
      return null;
    }

    try {
      const result = await apiFetch(`/api/segments/${annotation.segmentId}/pano-annotations/${encodeURIComponent(annotation.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch)
      });
      setPanoAnnotations((current) => current.map((candidate) => (
        String(candidate.id) === String(annotation.id) ? result.annotation : candidate
      )));
      notifyPanoAnnotationsChanged();
      setMessage(`Updated pano annotation "${result.annotation.label}".`);
      return result.annotation;
    } catch (annotationError) {
      setError(annotationError.message);
      return null;
    }
  };

  const deletePanoAnnotation = async (annotationId, options = {}) => {
    const annotation = panoAnnotations.find((candidate) => String(candidate.id) === String(annotationId));
    if (!annotation) {
      return;
    }
    if (!options.skipConfirm && !confirm(`Delete annotation "${annotation.label}"?`)) {
      return;
    }

    try {
      await apiFetch(`/api/segments/${annotation.segmentId}/pano-annotations/${encodeURIComponent(annotation.id)}`, {
        method: "DELETE"
      });
      setPanoAnnotations((current) => current.filter((candidate) => String(candidate.id) !== String(annotation.id)));
      if (String(activePanoAnnotationId) === String(annotation.id)) {
        setActivePanoAnnotationId(null);
      }
      notifyPanoAnnotationsChanged();
      setMessage(`Deleted pano annotation "${annotation.label}".`);
    } catch (annotationError) {
      setError(annotationError.message);
    }
  };

  const savePanoCalibrationState = async (nextState, successMessage = "") => {
    try {
      const savedState = await apiFetch("/api/pano-calibrations", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nextState)
      });
      const normalizedState = normalizePanoCalibrationState(savedState);
      setPanoCalibrationState(normalizedState);
      if (successMessage) {
        setMessage(successMessage);
      }
      return normalizedState;
    } catch (calibrationError) {
      setError(calibrationError.message);
      return null;
    }
  };

  const applyDashboardSegmentFilter = (filterRequest) => {
    if (!filterRequest || !filterRequest.fieldCode || !filterRequest.mode) {
      return;
    }
    setDashboardSegmentFilterRequest({
      ...filterRequest,
      requestId: `${Date.now()}-${Math.random().toString(16).slice(2)}`
    });
    setSpecsPanels((current) => ({
      ...current,
      segments: true
    }));
    setLayoutMode("specs");
    setMessage(`Segments filtered for ${filterRequest.fieldCaption || filterRequest.fieldCode}: ${filterRequest.label || filterRequest.mode}.`);
  };

  if (authChecking) {
    return <AuthLoadingScreen />;
  }

  if (!authToken || !authUser) {
    return (
      <LoginView
        error={authError}
        accessDenied={authAccessDenied}
        busy={authSubmitting}
        mfaChallenge={authChallenge}
        termsChallenge={authTermsChallenge}
        resendBusy={authResending}
        onLogin={handleLogin}
        onAcceptTerms={handleAcceptTerms}
        onCancelTerms={handleCancelTerms}
        onVerifyMfa={handleVerifyMfa}
        onResendMfa={handleResendMfa}
        onCheckPushApproval={handleCheckPushApproval}
        onCancelMfa={handleCancelMfa}
      />
    );
  }

  return (
    <>
      <Topbar
        search={search}
        setSearch={setSearch}
        runSearch={runSearch}
        user={authUser}
      />
      <ViewSidebar
        layoutMode={layoutModeForUser}
        setLayoutMode={setLayoutMode}
        specsPanels={specsPanels}
        setSpecsPanelOpen={setSpecsPanelOpen}
        canAdmin={canEditIrap}
        onLogout={handleLogout}
      />
      {layoutModeForUser === "single" ? (
        <main className="workspace">
          <CodingWorkbench
            canEditIrap={canEditIrap}
            segments={visibleSegmentsForUser}
            detail={detail}
            fields={fields}
            selectedId={selectedId}
            selectSegment={selectSegment}
            allSpeedRecords={allSpeedRecords}
            viewMode={viewMode}
            setViewMode={setViewMode}
            currentImage={currentImage}
            setCurrentImage={selectPanoImage}
            signResults={signResults}
            activeEvidenceTarget={activeEvidenceTarget}
            panoAnnotations={panoAnnotations}
            activePanoAnnotationId={activePanoAnnotationId}
            setActivePanoAnnotationId={setActivePanoAnnotationId}
            deletePanoAnnotation={deletePanoAnnotation}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            agentGroupNames={agentGroupNames}
            selectedAgentGroups={selectedAgentGroups}
            setSelectedAgentGroups={setSelectedAgentGroups}
            draftValues={draftValues}
            updateDraftValue={updateDraftValue}
            analyzeSelected={analyzeSelected}
            applyAiSuggestions={applyAiSuggestions}
            busy={busy}
            batchBusy={batchBusy}
            message={message}
            error={error}
            saveJson={saveJson}
            analysisImageCount={analysisImageCount}
            setAnalysisImageCount={setAnalysisImageCount}
            imageProcessingMode={imageProcessingMode}
            setImageProcessingMode={setImageProcessingMode}
            agentRunnerModel={agentRunnerModel}
            setAgentRunnerModel={setAgentRunnerModel}
            agentRunnerReasoningEffort={agentRunnerReasoningEffort}
            setAgentRunnerReasoningEffort={setAgentRunnerReasoningEffort}
            includeFieldPromptsInAgentPrompt={includeFieldPromptsInAgentPrompt}
            setIncludeFieldPromptsInAgentPrompt={setIncludeFieldPromptsInAgentPrompt}
            agentConcurrency={agentConcurrency}
            setAgentConcurrency={setAgentConcurrency}
            openAiCallConcurrency={openAiCallConcurrency}
            setOpenAiCallConcurrency={setOpenAiCallConcurrency}
            batchRange={batchRange}
            setBatchRange={setBatchRange}
            batchSegmentConcurrency={batchSegmentConcurrency}
            setBatchSegmentConcurrency={setBatchSegmentConcurrency}
            batchJob={batchJob}
            startBatchAnalyze={startBatchAnalyze}
            cancelBatchAnalyze={cancelBatchAnalyze}
            logs={logs}
            loadLogs={loadLogs}
            clearLogs={clearAllLogs}
          />
        </main>
      ) : layoutModeForUser === "dashboard" ? (
        <DashboardView
          health={health}
          segments={segmentIndex}
          fields={fields}
          analysisResults={analysisResults}
          search={search}
          onApplySegmentFilter={applyDashboardSegmentFilter}
        />
      ) : layoutModeForUser === "activity" ? (
        <UserActivityDashboardView />
      ) : layoutModeForUser === "agents" ? (
        <AgentManagerView
          health={health}
          message={message}
          setMessage={setMessage}
          error={error}
          setError={setError}
        />
      ) : layoutModeForUser === "settings" ? (
        <SettingsView
          gisLayerOptions={gisLayerOptions}
          gisLayerOptionsLoading={gisLayerOptionsLoading}
          gisLayerOptionsError={gisLayerOptionsError}
          gisLayerSettings={gisLayerSettings}
          setGisLayerSettings={setGisLayerSettings}
        />
      ) : layoutModeForUser === "feedback" ? (
        <main className="workspace-feedback">
          <SegmentFeedbackView
            authUser={authUser}
            fields={fields}
            selectSegment={selectSegment}
            setLayoutMode={setLayoutMode}
            setMessage={setMessage}
            setError={setError}
          />
        </main>
      ) : layoutModeForUser === "specs" ? (
        <SegmentSpecsView
          authUser={authUser}
          segments={visibleSegmentsForUser}
          segmentIndex={segmentIndex}
          selectedId={selectedId}
          selectSegment={selectSegment}
          detail={detail}
          fields={fields}
          analysisResults={analysisResults}
          setAnalysisResults={setAnalysisResults}
          draftValues={draftValues}
          allSpeedRecords={allSpeedRecords}
          search={search}
          isSegmentListOpen={specsPanels.segments}
          setIsSegmentListOpen={(open) => setSpecsPanelOpen("segments", open)}
          isSegmentInfoOpen={specsPanels.info}
          setIsSegmentInfoOpen={(open) => setSpecsPanelOpen("info", open)}
          signResults={signResults}
          openEvidencePreview={openEvidencePreview}
          updateGridCell={canEditIrap ? updateGridCell : null}
          updateSegmentBatchExclusion={canEditIrap ? updateSegmentBatchExclusion : null}
          panoAnnotations={panoAnnotations}
          panoCalibrationState={panoCalibrationState}
          panoCalibrationLoaded={panoCalibrationLoaded}
          activePanoAnnotationId={activePanoAnnotationId}
          setActivePanoAnnotationId={setActivePanoAnnotationId}
          createPanoAnnotation={canEditIrap ? createPanoAnnotation : null}
          createSamPanoAnnotation={canEditIrap ? createSamPanoAnnotation : null}
          deletePanoAnnotation={canEditIrap ? deletePanoAnnotation : null}
          savePanoCalibrationState={canEditIrap ? savePanoCalibrationState : null}
          setMessage={setMessage}
          setError={setError}
          analysisImageCount={analysisImageCount}
          imageProcessingMode={imageProcessingMode}
          selectedAgentGroups={selectedAgentGroups}
          agentRunnerModel={agentRunnerModel}
          agentRunnerReasoningEffort={agentRunnerReasoningEffort}
          includeFieldPromptsInAgentPrompt={includeFieldPromptsInAgentPrompt}
          agentConcurrency={agentConcurrency}
          openAiCallConcurrency={openAiCallConcurrency}
          batchSegmentConcurrency={batchSegmentConcurrency}
          setBatchJob={setBatchJob}
          batchJob={batchJob}
          externalAdvancedFilterRequest={dashboardSegmentFilterRequest}
          reloadSelectedSegment={reloadSelectedSegmentDetail}
          gisLayerSettings={gisLayerSettings}
          gisLayerOptions={gisLayerOptions}
          logs={logs}
          loadLogs={loadLogs}
        />
      ) : layoutModeForUser === "grid" ? (
        <main className="workspace-grid">
          <SegmentsGridView
            health={health}
            segments={segmentIndex}
            fields={fields}
            analysisResults={analysisResults}
            search={search}
            updateGridCell={updateGridCell}
            signResults={signResults}
            openEvidencePreview={openEvidencePreview}
            openEvidenceInPano={openEvidenceInPano}
          />
        </main>
      ) : layoutModeForUser === "combined" ? (
        <CombinedWorkspace
          health={health}
          segments={visibleSegmentsForUser}
          segmentIndex={segmentIndex}
          fields={fields}
          analysisResults={analysisResults}
          search={search}
          selectedId={selectedId}
          selectSegment={selectSegment}
          detail={detail}
          allSpeedRecords={allSpeedRecords}
          currentImage={currentImage}
          setCurrentImage={selectPanoImage}
          imageProcessingMode={imageProcessingMode}
          updateGridCell={updateGridCell}
          signResults={signResults}
          activeEvidenceTarget={activeEvidenceTarget}
          panoCalibrationState={panoCalibrationState}
          panoCalibrationLoaded={panoCalibrationLoaded}
          panoAnnotations={panoAnnotations}
          activePanoAnnotationId={activePanoAnnotationId}
          setActivePanoAnnotationId={setActivePanoAnnotationId}
          deletePanoAnnotation={deletePanoAnnotation}
          openEvidencePreview={openEvidencePreview}
          openEvidenceInPano={openEvidenceInPano}
        />
      ) : (
        <main className="workspace-logs">
          <LogViewer
            logs={logs}
            fields={fields}
            loadLogs={loadLogs}
            clearLogs={clearAllLogs}
          />
        </main>
      )}
      {previewEvidenceTarget && (
        <EvidencePreview
          item={previewEvidenceTarget.item}
          segment={previewEvidenceTarget.segment}
          onOpenInPano={openEvidenceInPano}
          onClose={() => setPreviewEvidenceTarget(null)}
        />
      )}
    </>
  );
}

const MAIN_VIEWS = [
  { id: "single", label: "Agent Runner", icon: "square", title: "AI agent runner", adminOnly: true },
  { id: "agents", label: "Agents", icon: "bot", title: "Edit agent prompts, documents, and workflow", adminOnly: true },
  { id: "specs", label: "Segments", icon: "image", title: "Segments" },
  { id: "dashboard", label: "Dashboard", icon: "layout-dashboard", title: "Dashboard" },
  { id: "activity", label: "User Logs", icon: "activity", title: "User activity dashboard", adminOnly: true },
  { id: "settings", label: "Settings", icon: "settings", title: "Settings", adminOnly: true },
  { id: "feedback", label: "Segment feedback", icon: "messages-square", title: "Review segment field feedback", adminOnly: true },
  { id: "logs", label: "Logs", icon: "scroll-text", title: "OpenAI prompt logs", adminOnly: true }
];

function ViewSidebar({ layoutMode, setLayoutMode, specsPanels, setSpecsPanelOpen, canAdmin = false, onLogout = null }) {
  const canToggleSpecsPanels = layoutMode === "specs" && typeof setSpecsPanelOpen === "function";
  const visibleViews = MAIN_VIEWS.filter((view) => !view.adminOnly || canAdmin);

  return (
    <nav className="view-sidebar" aria-label="Main views">
      {visibleViews.map((view) => (
        <button
          key={view.id}
          className={layoutMode === view.id ? "active" : ""}
          type="button"
          onClick={() => setLayoutMode(view.id)}
          title={view.title}
        >
          {icon(view.icon)}
          <span>{view.label}</span>
        </button>
      ))}
      {canToggleSpecsPanels && (
        <div className="view-sidebar-panel-group" aria-label="Specs panels">
          <button
            className={specsPanels && specsPanels.segments ? "active" : ""}
            type="button"
            aria-pressed={Boolean(specsPanels && specsPanels.segments)}
            onClick={() => setSpecsPanelOpen("segments", !(specsPanels && specsPanels.segments))}
            title="Show or hide segment list"
          >
            {icon("list")}
            <span>Segments</span>
          </button>
          <button
            className={specsPanels && specsPanels.info ? "active" : ""}
            type="button"
            aria-pressed={Boolean(specsPanels && specsPanels.info)}
            onClick={() => setSpecsPanelOpen("info", !(specsPanels && specsPanels.info))}
            title="Show or hide segment info"
          >
            {icon("info")}
            <span>Info</span>
          </button>
        </div>
      )}
      <div className="view-sidebar-footer">
        <a
          className="view-sidebar-action view-sidebar-help"
          href="/help/airap/"
          target="_blank"
          rel="noreferrer"
          title="Open AiRAP user guide"
        >
          {icon("circle-help")}
          <span>Help</span>
        </a>
        <button
          type="button"
          className="view-sidebar-logout"
          onClick={onLogout}
          title="Sign out"
          disabled={typeof onLogout !== "function"}
        >
          {icon("log-out")}
          <span>Sign out</span>
        </button>
      </div>
    </nav>
  );
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error("AiRAP UI error:", error);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <main className="login-shell">
        <section className="login-card login-card--compact" role="alert">
          <AiRapBrand className="login-card-brand" compact />
          <h1>AiRAP</h1>
          <div className="login-error">
            <strong>Could not load the page</strong>
            <span>{this.state.error.message || "Please refresh and try again."}</span>
          </div>
          <button className="login-submit" type="button" onClick={() => window.location.reload()}>
            <span>Refresh</span>
          </button>
        </section>
      </main>
    );
  }
}

function AuthLoadingScreen() {
  return (
    <main className="login-shell">
      <section className="login-card login-card--compact" aria-live="polite">
        <AiRapBrand className="login-card-brand" compact />
        <h1>AiRAP</h1>
        <p>Checking your session...</p>
      </section>
    </main>
  );
}

function LoginView({
  error,
  accessDenied = false,
  busy,
  mfaChallenge = null,
  termsChallenge = null,
  resendBusy = false,
  onLogin,
  onAcceptTerms,
  onCancelTerms,
  onVerifyMfa,
  onResendMfa,
  onCheckPushApproval,
  onCancelMfa
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaMethod, setMfaMethod] = useState("email");
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const isTermsStep = Boolean(termsChallenge && termsChallenge.termsChallengeId);
  const isMfaStep = Boolean(mfaChallenge && mfaChallenge.challengeId);
  const activeMfaMethod = String(mfaChallenge && mfaChallenge.method || mfaMethod || "email").toLowerCase();
  const isPushStep = isMfaStep && activeMfaMethod === "push";
  const terms = termsChallenge && termsChallenge.terms ? termsChallenge.terms : {};
  const termsTitle = terms.title || "Terms & Conditions";
  const termsParagraphs = String(terms.bodyText || "").split(/\n+/).map((item) => item.trim()).filter(Boolean);
  const destinationMasked =
    (mfaChallenge && (
      mfaChallenge.destinationMasked ||
      mfaChallenge.phoneMasked ||
      mfaChallenge.emailMasked ||
      mfaChallenge.pushMasked
    )) ||
    "";
  const methodLabel = activeMfaMethod === "email" ? "Email" : activeMfaMethod === "push" ? "App approval" : "SMS";
  const mfaIntroText = isMfaStep
    ? isPushStep
      ? `Push sent to ${destinationMasked || "your app"}`
      : `${methodLabel} code sent to ${destinationMasked || "your account"}`
    : isTermsStep
    ? "Please review and accept before verification."
    : "Sign in to access your AiRAP platform";

  useEffect(() => {
    setTermsAccepted(false);
  }, [termsChallenge && termsChallenge.termsChallengeId]);

  useEffect(() => {
    if (isMfaStep) {
      setMfaCode("");
    }
  }, [isMfaStep, mfaChallenge && mfaChallenge.challengeId]);

  useEffect(() => {
    if (!isPushStep || !onCheckPushApproval) {
      return undefined;
    }
    let cancelled = false;
    const poll = () => {
      if (!cancelled) {
        onCheckPushApproval();
      }
    };
    const initialTimer = window.setTimeout(poll, 800);
    const interval = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
      window.clearInterval(interval);
    };
  }, [isPushStep, mfaChallenge && mfaChallenge.challengeId]);

  const submitLogin = (event) => {
    event.preventDefault();
    if (busy) {
      return;
    }
    onLogin({ username: username.trim(), password, method: mfaMethod });
  };

  const submitTerms = (event) => {
    event.preventDefault();
    if (busy || !termsAccepted || !termsChallenge || !termsChallenge.termsChallengeId) {
      return;
    }
    onAcceptTerms({ termsChallengeId: termsChallenge.termsChallengeId });
  };

  const submitMfaCode = (event) => {
    event.preventDefault();
    if (busy || isPushStep) {
      return;
    }
    onVerifyMfa({ code: mfaCode });
  };

  return (
    <main className="login-shell">
      <section className="login-brand-panel" aria-label="AiRAP platform">
        <AiRapBrand className="login-hero-brand" />
        <p className="login-brand-line">Automated. Accurate. iRAP Accredited.</p>
        <LoginAiAnimation />
        <div className="login-feature-row" aria-label="Platform capabilities">
          <span>{icon("rotate-3d")}<small>360 image processing</small></span>
          <span>{icon("brain-circuit")}<small>AI/ML automation</small></span>
          <span>{icon("shield-check")}<small>iRAP compliant</small></span>
          <span>{icon("chart-no-axes-combined")}<small>Actionable insights</small></span>
        </div>
        <div className="login-fedasen-badge">
          <FedasenLogo className="login-fedasen-logo" />
        </div>
      </section>

      <section className="login-card">
        <div className="login-card__glass">
          <AiRapBrand className="login-card-brand" compact />
          <div className="login-card__intro">
            <h1>{isTermsStep ? "Terms & Conditions" : isMfaStep ? "Verify sign in" : "Welcome back"}</h1>
            <p>{mfaIntroText}</p>
          </div>
          {isTermsStep ? (
            <form className="login-form login-terms-form" onSubmit={submitTerms}>
              <section className="login-terms-panel" aria-label="Portal access terms">
                <strong>{termsTitle}</strong>
                <div className="login-terms-scroll" tabIndex="0">
                  {termsParagraphs.length ? (
                    termsParagraphs.map((paragraph, index) => <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>)
                  ) : (
                    <p>Review and accept the portal access terms before continuing.</p>
                  )}
                </div>
              </section>
              <label className="login-terms-accept">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => setTermsAccepted(event.target.checked)}
                />
                <span>I have read, understood and agree to these Terms & Conditions.</span>
              </label>
              {error && (
                <div className={`login-error ${accessDenied ? "login-error--denied" : ""}`} role="alert">
                  <strong>{accessDenied ? "You don't have access" : "Terms acceptance failed"}</strong>
                  <span>{error}</span>
                </div>
              )}
              <button className="login-submit" type="submit" disabled={busy || !termsAccepted}>
                {busy && icon("loader-circle")}
                <span>{busy ? "Continuing..." : "Accept and continue"}</span>
              </button>
              <div className="login-secondary-actions">
                <button type="button" onClick={onCancelTerms} disabled={busy}>
                  Cancel
                </button>
              </div>
            </form>
          ) : isMfaStep ? (
            <form className="login-form" onSubmit={submitMfaCode}>
              {isPushStep ? (
                <div className="login-push-panel" aria-live="polite">
                  <span>{icon("smartphone")}</span>
                  <div>
                    <strong>Waiting for app approval</strong>
                    <small>{destinationMasked || "Approve the sign-in request in your app"}</small>
                  </div>
                </div>
              ) : (
                <label className="login-field login-field--otp">
                  <span className="login-field__icon">{icon(activeMfaMethod === "email" ? "mail" : "message-circle-code")}</span>
                  <input
                    autoComplete="one-time-code"
                    autoFocus
                    inputMode="numeric"
                    maxLength="8"
                    value={mfaCode}
                    onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, ""))}
                    placeholder="Verification code"
                    required
                  />
                </label>
              )}
              {error && (
                <div className={`login-error ${accessDenied ? "login-error--denied" : ""}`} role="alert">
                  <strong>{accessDenied ? "You don't have access" : "Verification failed"}</strong>
                  <span>{error}</span>
                </div>
              )}
              <button className="login-submit" type={isPushStep ? "button" : "submit"} onClick={isPushStep ? onCheckPushApproval : undefined} disabled={busy}>
                {busy && icon("loader-circle")}
                <span>{isPushStep ? "Check approval" : busy ? "Verifying..." : "Verify"}</span>
              </button>
              <div className="login-secondary-actions">
                <button type="button" onClick={onCancelMfa} disabled={busy || resendBusy}>
                  Back
                </button>
                <button type="button" onClick={onResendMfa} disabled={busy || resendBusy}>
                  {resendBusy ? "Sending..." : isPushStep ? "Send again" : "Resend code"}
                </button>
              </div>
            </form>
          ) : (
            <form className="login-form" onSubmit={submitLogin}>
              <label className="login-field">
                <span className="login-field__icon">{icon("user-round")}</span>
                <input
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="Username or email"
                  required
                />
              </label>
              <label className="login-field">
                <span className="login-field__icon">{icon("lock-keyhole")}</span>
                <input
                  autoComplete="current-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Password"
                  required
                />
                <button
                  className="login-password-toggle"
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  title={showPassword ? "Hide password" : "Show password"}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {icon(showPassword ? "eye-off" : "eye")}
                </button>
              </label>
              <div className="login-mfa-methods" role="group" aria-label="Verification method">
                {[
                  { id: "sms", label: "SMS", iconName: "message-circle-code", disabled: true },
                  { id: "email", label: "Email", iconName: "mail" },
                  { id: "push", label: "App", iconName: "smartphone", disabled: true }
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={mfaMethod === option.id ? "is-active" : ""}
                    disabled={option.disabled}
                    onClick={() => {
                      if (!option.disabled) {
                        setMfaMethod(option.id);
                      }
                    }}
                    aria-pressed={mfaMethod === option.id}
                    title={option.disabled ? "Email verification is currently required" : undefined}
                  >
                    {icon(option.iconName)}
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
              <div className="login-form-options">
                <label className="login-remember">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <button type="button">Forgot password?</button>
              </div>
              {error && (
                <div className={`login-error ${accessDenied ? "login-error--denied" : ""}`} role="alert">
                  <strong>{accessDenied ? "You don't have access" : "Sign in failed"}</strong>
                  <span>{error}</span>
                </div>
              )}
              <button className="login-submit" type="submit" disabled={busy}>
                {busy && icon("loader-circle")}
                <span>{busy ? "Signing in..." : "Sign In"}</span>
              </button>
            </form>
          )}
          <div className="login-trust-strip">
            <span className="login-trust-label">{icon("shield-check")} iRAP Accredited Solution</span>
            <div className="login-accreditation-links" aria-label="iRAP accreditation links">
              <a href="https://irap.org/accreditation/" target="_blank" rel="noreferrer">
                Accreditation
              </a>
              <a href="https://irap.org/accreditation/airap-accreditation/" target="_blank" rel="noreferrer">
                AiRAP Accreditation
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Topbar({ search, setSearch, runSearch, user }) {
  return (
    <header className="topbar">
      <div className="brand-block">
        <div className="topbar-airap-lockup" title="AiRAP">
          <AiRapMark className="topbar-airap-mark" />
          <div>
            <h1><span>Ai</span>RAP</h1>
          </div>
        </div>
      </div>

      <form className="global-search" onSubmit={runSearch}>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Find segment ID, road, distance, >427, or 100-150"
        />
        <button className="icon-action" type="submit" title="Search">
          {icon("search")}
        </button>
      </form>

      <div className="topbar-user">
        <div className="topbar-fedasen-badge">
          <FedasenLogo className="topbar-fedasen-logo" />
        </div>
        <span title={userDisplayName(user)}>{userDisplayName(user)}</span>
      </div>
    </header>
  );
}

function segmentPropertyValue(detail, field) {
  const props = (detail && detail.properties) || {};
  const keys = [field && field.geoProperty, field && field.field].filter(Boolean);
  for (const key of keys) {
    if (hasDisplayValue(props[key])) {
      return props[key];
    }
  }
  return null;
}

function resolvedSpecValue(detail, field, draftValues = {}, options = {}) {
  const analysis = options.analysis || (detail && detail.analysis);
  const includeDraft = options.includeDraft !== false;
  const includeSegmentFallback = options.includeSegmentFallback !== false;
  const aiField = analysis && analysis.fields ? analysis.fields[field.field] : null;
  const saveJson = (analysis && analysis.saveJson) || {};
  if (Object.prototype.hasOwnProperty.call(saveJson, field.field) && hasDisplayValue(saveJson[field.field])) {
    return { value: saveJson[field.field], source: "AI" };
  }
  if (aiField && hasDisplayValue(aiField.value)) {
    return { value: aiField.value, decodedValue: aiField.decodedValue, source: "AI" };
  }
  if (includeDraft && Object.prototype.hasOwnProperty.call(draftValues, field.field) && hasDisplayValue(draftValues[field.field])) {
    return { value: draftValues[field.field], source: "Draft" };
  }
  if (!includeSegmentFallback) {
    return { value: null, source: "" };
  }
  const propertyValue = segmentPropertyValue(detail, field);
  if (hasDisplayValue(propertyValue)) {
    return { value: propertyValue, source: "Segment" };
  }
  if (hasDisplayValue(field.rawValue)) {
    return { value: field.rawValue, decodedValue: field.decodedValue, source: "Segment" };
  }
  return { value: null, source: "" };
}

function specDisplayValue(field, value, decodedValue = "") {
  if (!hasDisplayValue(value)) {
    return "Blank";
  }
  const label = decodedValue || fieldOptionLabel(field, value);
  return label ? `${value}: ${label}` : String(value);
}

function normalizeSpecComparableValue(value) {
  if (!hasDisplayValue(value)) {
    return "";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value).trim();
}

function specOldFieldValue(detail, field) {
  const props = (detail && detail.properties) || {};
  const propertyName = field && (field.geoProperty || field.field) ? field.geoProperty || field.field : "";
  const hasPropertyValue = propertyName && Object.prototype.hasOwnProperty.call(props, propertyName);
  const value = hasPropertyValue ? props[propertyName] : field ? field.rawValue : null;
  const decodedValue = fieldOptionLabel(field, value) || (hasDisplayValue(value) ? field && field.decodedValue : "");
  return {
    propertyName,
    value,
    decodedValue,
    display: specDisplayValue(field, value, decodedValue)
  };
}

function specFieldComparison(resolved, oldValue) {
  const aiHasValue = hasDisplayValue(resolved && resolved.value);
  const oldHasValue = hasDisplayValue(oldValue && oldValue.value);
  if (!aiHasValue) {
    return {
      status: "missing",
      label: "Missing AI"
    };
  }
  if (normalizeSpecComparableValue(resolved.value) === normalizeSpecComparableValue(oldValue && oldValue.value)) {
    return {
      status: oldHasValue ? "same" : "different",
      label: oldHasValue ? "Same" : "No old value"
    };
  }
  return {
    status: "different",
    label: oldHasValue ? "Changed" : "No old value"
  };
}

function metadataValue(detail, fieldCode, propertyNames = []) {
  const props = (detail && detail.properties) || {};
  const field = detail && Array.isArray(detail.fields)
    ? detail.fields.find((candidate) => candidate.field === fieldCode)
    : null;
  if (field) {
    const resolved = resolvedSpecValue(detail, field);
    if (hasDisplayValue(resolved.value)) {
      return resolved.decodedValue || fieldOptionLabel(field, resolved.value) || resolved.value;
    }
  }
  for (const propertyName of propertyNames) {
    if (hasDisplayValue(props[propertyName])) {
      return props[propertyName];
    }
  }
  return "";
}

const SPECS_SEGMENT_LIST_DEFAULT_WIDTH = 340;
const SPECS_SEGMENT_LIST_MIN_WIDTH = 250;
const SPECS_SEGMENT_LIST_MAX_RATIO = 0.9;
const SPECS_SEGMENT_LIST_HANDLE_WIDTH = 10;
const SPECS_DEFAULT_WIDTHS = { info: 58, map: 42 };
const SPECS_PANEL_MIN_WIDTHS = { info: 300, map: 340 };
const SPECS_RESIZE_HANDLE_WIDTH = 10;
const SPECS_MAP_PANO_DEFAULT_WIDTHS = { map: 56, pano: 44 };
const SPECS_EVIDENCE_MAP_PANO_WIDTHS = { map: 34, pano: 66 };
const SPECS_MAP_PANO_MIN_WIDTHS = { map: 240, pano: 320 };
const SPECS_MAP_PANO_HANDLE_WIDTH = 10;
const SPECS_ICON_SIZE_DEFAULT = 0.74;
const SPECS_ICON_SIZE_MIN = 0.55;
const SPECS_ICON_SIZE_MAX = 1.45;
const SPECS_EVIDENCE_TAB = "Evidence";
const SPECS_MAP_MAX_ZOOM = 23;
const SPECS_FIELD_VIEW_MODES = [
  { mode: "valued", label: "Values", icon: "list-checks" },
  { mode: "different", label: "Changed", icon: "git-compare-arrows" },
  { mode: "missing", label: "Missing", icon: "circle-off" },
  { mode: "same", label: "Same", icon: "equal" },
  { mode: "all", label: "All fields", icon: "layout-grid" }
];
const SPECS_MAP_VIEW_MODES = [
  { mode: "mini", icon: "minimize-2", label: "Mini map" },
  { mode: "side", icon: "columns-2", label: "Side by side" },
  { mode: "map", icon: "map", label: "Map full" },
  { mode: "pano", icon: "image", label: "Panorama full" }
];
const MAP_BASE_LAYER_DEFS = [
  {
    id: "googleRoad",
    label: "Google Road",
    url: "https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
    options: {
      maxZoom: SPECS_MAP_MAX_ZOOM,
      attribution: ""
    }
  },
  {
    id: "googleClean",
    label: "Google Clean",
    url: "https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}",
    options: {
      maxZoom: SPECS_MAP_MAX_ZOOM,
      attribution: ""
    }
  },
  {
    id: "googleSatellite",
    label: "Google Satellite",
    url: "http://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
    options: {
      maxZoom: SPECS_MAP_MAX_ZOOM,
      attribution: ""
    }
  },
  {
    id: "vicAerial2020",
    label: "Vic Aerial 2020",
    // The AERIAL_VG2020 sample URL uses EPSG:7899; this service layer is the matching Web Mercator WMTS for Leaflet.
    url: "https://base.maps.vic.gov.au/service?layer=AERIAL_WM_256&style=default&tilematrixset=EPSG%3A3857%3A256&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fpng&TileMatrix={z}&TileCol={x}&TileRow={y}",
    options: {
      maxZoom: SPECS_MAP_MAX_ZOOM,
      maxNativeZoom: 20,
      tileSize: 256,
      attribution: ""
    }
  }
];

function resizeSpecsWidths(startWidths, deltaPct, availableWidth) {
  const safeAvailableWidth = Math.max(1, availableWidth || 1);
  const minInfo = (SPECS_PANEL_MIN_WIDTHS.info / safeAvailableWidth) * 100;
  const minMap = (SPECS_PANEL_MIN_WIDTHS.map / safeAvailableWidth) * 100;
  const delta = clampResizeDelta(deltaPct, minInfo - startWidths.info, startWidths.map - minMap);
  return {
    info: startWidths.info + delta,
    map: startWidths.map - delta
  };
}

function resizeSpecsSegmentListWidth(startWidth, deltaPx, availableWidth) {
  const safeAvailableWidth = Math.max(1, availableWidth || 1);
  const maxWidth = Math.max(
    SPECS_SEGMENT_LIST_MIN_WIDTH,
    Math.floor(safeAvailableWidth * SPECS_SEGMENT_LIST_MAX_RATIO)
  );
  return Math.max(
    SPECS_SEGMENT_LIST_MIN_WIDTH,
    Math.min(maxWidth, Math.round(startWidth + deltaPx))
  );
}

function resizeSpecsMapPanoWidths(startWidths, deltaPct, availableWidth) {
  const safeAvailableWidth = Math.max(1, availableWidth || 1);
  const minMap = (SPECS_MAP_PANO_MIN_WIDTHS.map / safeAvailableWidth) * 100;
  const minPano = (SPECS_MAP_PANO_MIN_WIDTHS.pano / safeAvailableWidth) * 100;
  const delta = clampResizeDelta(deltaPct, minMap - startWidths.map, startWidths.pano - minPano);
  return {
    map: startWidths.map + delta,
    pano: startWidths.pano - delta
  };
}

function specsGridColumnWidth(label, min = 118, max = 220) {
  const textLength = String(label || "").replace(/\s+/g, " ").trim().length;
  return Math.max(min, Math.min(max, Math.ceil(textLength * 7.2) + 28));
}

function specsGridColumnMinWidth(column) {
  if (!column) {
    return 72;
  }
  if (column.key === "__id") {
    return 82;
  }
  if (column.oldMismatchFocus) {
    return 220;
  }
  if (column.gridFocusField) {
    return 180;
  }
  return column.kind === "field" ? 96 : 82;
}

function clampSpecsGridColumnWidth(column, width) {
  const min = specsGridColumnMinWidth(column);
  const max = column && column.oldMismatchFocus ? 620 : 520;
  const numeric = Number(width);
  if (!Number.isFinite(numeric)) {
    return Math.max(min, Number(column && column.width) || min);
  }
  return Math.max(min, Math.min(max, Math.round(numeric)));
}

function specsFieldValueFilterKey(fieldCode, value) {
  return `${String(fieldCode || "").trim()}::${normalizeSpecComparableValue(value)}`;
}

function specsFieldValueFilterFromItem(item) {
  const field = item && item.field ? item.field : null;
  const fieldCode = field && field.field ? String(field.field).trim() : "";
  if (!fieldCode) {
    return null;
  }
  const value = hasDisplayValue(item.value) ? item.value : "";
  const decodedValue = item.decodedValue || fieldOptionLabel(field, value) || "";
  return {
    key: specsFieldValueFilterKey(fieldCode, value),
    fieldCode,
    value,
    display: specDisplayValue(field, value, decodedValue),
    fieldCaption: field.caption || field.field || fieldCode,
    groupName: field.group || ""
  };
}

function specsFieldValueFilterKeyFromItem(item) {
  const filter = specsFieldValueFilterFromItem(item);
  return filter ? filter.key : "";
}

function specsSegmentMatchesFieldValueFilters(segment, filters, columns, analysisResults = {}) {
  const activeFilters = Array.isArray(filters)
    ? filters.filter((filter) => filter && filter.fieldCode)
    : [];
  if (!activeFilters.length) {
    return true;
  }

  const columnsByField = new Map(
    (columns || [])
      .filter((column) => column && column.kind === "field" && column.field && column.field.field)
      .map((column) => [String(column.field.field), column])
  );
  const valuesByField = new Map();
  activeFilters.forEach((filter) => {
    const fieldCode = String(filter.fieldCode);
    if (!valuesByField.has(fieldCode)) {
      valuesByField.set(fieldCode, new Set());
    }
    valuesByField.get(fieldCode).add(normalizeSpecComparableValue(filter.value));
  });

  return Array.from(valuesByField.entries()).every(([fieldCode, acceptedValues]) => {
    const column = columnsByField.get(fieldCode);
    if (!column) {
      return false;
    }
    const rawValue = getSpecsAiFieldValue(segment, column, analysisResults);
    return acceptedValues.has(normalizeSpecComparableValue(rawValue));
  });
}

const FIELD_PROMPT_MAX_PASTED_IMAGES = 4;
const PROMPT_TRAINER_MAX_IMAGES = 12;
const FIELD_PROMPT_MAX_PASTED_IMAGE_BYTES = 8 * 1024 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read pasted image."));
    reader.readAsDataURL(file);
  });
}

async function imageAttachmentsFromFiles(files, existingCount = 0, maxImages = FIELD_PROMPT_MAX_PASTED_IMAGES) {
  const imageFiles = Array.from(files || [])
    .filter((file) => file && /^image\//i.test(file.type || ""));
  if (!imageFiles.length) {
    return [];
  }
  const remainingSlots = Math.max(0, Number(maxImages || FIELD_PROMPT_MAX_PASTED_IMAGES) - existingCount);
  const selectedFiles = imageFiles.slice(0, remainingSlots);
  const attachments = [];
  for (const file of selectedFiles) {
    if (file.size > FIELD_PROMPT_MAX_PASTED_IMAGE_BYTES) {
      continue;
    }
    const dataUrl = await readFileAsDataUrl(file);
    if (!dataUrl) {
      continue;
    }
    const id = `paste-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`;
    attachments.push({
      id,
      name: file.name || `pasted-image-${attachments.length + 1}.png`,
      type: file.type || "image/png",
      size: file.size,
      dataUrl
    });
  }
  return attachments;
}

async function pastedImagesFromClipboardEvent(event, existingCount = 0, maxImages = FIELD_PROMPT_MAX_PASTED_IMAGES) {
  const items = Array.from((event.clipboardData && event.clipboardData.items) || []);
  const imageFiles = items
    .filter((item) => item && item.kind === "file" && /^image\//i.test(item.type || ""))
    .map((item) => item.getAsFile())
    .filter(Boolean);
  return imageAttachmentsFromFiles(imageFiles, existingCount, maxImages);
}

function SegmentSpecsView({
  authUser = null,
  segments,
  segmentIndex,
  selectedId,
  selectSegment,
  detail,
  fields,
  draftValues,
  allSpeedRecords,
  search,
  isSegmentListOpen,
  setIsSegmentListOpen,
  isSegmentInfoOpen,
  setIsSegmentInfoOpen,
  analysisResults = {},
  setAnalysisResults = null,
  signResults = {},
  openEvidencePreview = null,
  updateGridCell = null,
  updateSegmentBatchExclusion = null,
  panoAnnotations = [],
  panoCalibrationState = null,
  panoCalibrationLoaded = true,
  activePanoAnnotationId = null,
  setActivePanoAnnotationId = null,
  createPanoAnnotation = null,
  createSamPanoAnnotation = null,
  deletePanoAnnotation = null,
  savePanoCalibrationState = null,
  setMessage = null,
  setError = null,
  analysisImageCount = 1,
  imageProcessingMode = "full360",
  selectedAgentGroups = [],
  agentRunnerModel = "gpt-5.5",
  agentRunnerReasoningEffort = "low",
  includeFieldPromptsInAgentPrompt = true,
  agentConcurrency = 5,
  openAiCallConcurrency = 10,
  batchSegmentConcurrency = 2,
  setBatchJob = null,
  batchJob = null,
  externalAdvancedFilterRequest = null,
  reloadSelectedSegment = null,
  gisLayerSettings = DEFAULT_GIS_LAYER_SETTINGS,
  gisLayerOptions = null,
  logs = [],
  loadLogs = null
}) {
  const [activeGroup, setActiveGroup] = useState("All");
  const [specSegmentListWidth, setSpecSegmentListWidth] = useState(SPECS_SEGMENT_LIST_DEFAULT_WIDTH);
  const [resizingSegmentList, setResizingSegmentList] = useState(false);
  const [specPanelWidths, setSpecPanelWidths] = useState(SPECS_DEFAULT_WIDTHS);
  const [resizingSpecs, setResizingSpecs] = useState(false);
  const [specsCurrentImage, setSpecsCurrentImage] = useState(0);
  const [specsPanoViewState, setSpecsPanoViewState] = useState(null);
  const [specsMapBaseLayerId, setSpecsMapBaseLayerId] = useState("googleRoad");
  const [specsMapMeasureActive, setSpecsMapMeasureActive] = useState(false);
  const [specsMapArcActive, setSpecsMapArcActive] = useState(false);
  const [specsMapArcClearToken, setSpecsMapArcClearToken] = useState(0);
  const [specsMapTooltipsEnabled, setSpecsMapTooltipsEnabled] = useState(false);
  const [specsMapLimitToList, setSpecsMapLimitToList] = useState(false);
  const [specsListedSegmentIds, setSpecsListedSegmentIds] = useState([]);
  const [isSpecsLayoutMenuOpen, setIsSpecsLayoutMenuOpen] = useState(false);
  const [isSpecsBaseLayerMenuOpen, setIsSpecsBaseLayerMenuOpen] = useState(false);
  const [isSpecsPanoOpen, setIsSpecsPanoOpen] = useState(false);
  const [specMapPanoWidths, setSpecMapPanoWidths] = useState(SPECS_MAP_PANO_DEFAULT_WIDTHS);
  const [specMapViewMode, setSpecMapViewMode] = useState("side");
  const [showSpecsImageStrip, setShowSpecsImageStrip] = useState(true);
  const [isSpecsFieldEditMode, setIsSpecsFieldEditMode] = useState(false);
  const [specsFieldEvidenceTarget, setSpecsFieldEvidenceTarget] = useState(null);
  const [specsEvidenceShapeKind, setSpecsEvidenceShapeKind] = useState("point");
  const [specsEvidenceNote, setSpecsEvidenceNote] = useState("");
  const [fieldPromptMap, setFieldPromptMap] = useState({});
  const [fieldPromptBusy, setFieldPromptBusy] = useState(false);
  const [fieldPromptRunningField, setFieldPromptRunningField] = useState("");
  const [fieldBatchSetup, setFieldBatchSetup] = useState(null);
  const [specsRunningSegmentAgentId, setSpecsRunningSegmentAgentId] = useState(null);
  const [specsEvidenceShowMeasurement, setSpecsEvidenceShowMeasurement] = useState(false);
  const [specsEvidenceDraftPoints, setSpecsEvidenceDraftPoints] = useState([]);
  const [specsSamBusy, setSpecsSamBusy] = useState(false);
  const [specsSamPendingAnnotation, setSpecsSamPendingAnnotation] = useState(null);
  const [panoMeasurementMode, setPanoMeasurementMode] = useState(null);
  const [panoMeasurementDraftPoints, setPanoMeasurementDraftPoints] = useState([]);
  const [panoTransientMeasurements, setPanoTransientMeasurements] = useState([]);
  const [isPanoMeasurementListOpen, setIsPanoMeasurementListOpen] = useState(false);
  const [isSavingPanoMeasurementEvidence, setIsSavingPanoMeasurementEvidence] = useState(false);
  const [panoMeasurementTitle, setPanoMeasurementTitle] = useState("");
  const [panoCalibrationMeters, setPanoCalibrationMeters] = useState("1");
  const [panoCalibrationProfileName, setPanoCalibrationProfileName] = useState("");
  const [panoViewHeadingOffsetDeg, setPanoViewHeadingOffsetDeg] = useState(String(DEFAULT_PANO_HEADING_OFFSET_DEG));
  const [panoViewFovDeg, setPanoViewFovDeg] = useState("");
  const [panoViewRangeMeters, setPanoViewRangeMeters] = useState("");
  const [assignNewPanoCalibrationToVideo, setAssignNewPanoCalibrationToVideo] = useState(true);
  const [makeNewPanoCalibrationDefault, setMakeNewPanoCalibrationDefault] = useState(false);
  const [isPanoCalibrationPanelOpen, setIsPanoCalibrationPanelOpen] = useState(false);
  const [isPanoMeasurementPanelOpen, setIsPanoMeasurementPanelOpen] = useState(false);
  const [selectedPanoCalibrationId, setSelectedPanoCalibrationId] = useState("");
  const [panoMeasurementStatus, setPanoMeasurementStatus] = useState("");
  const [resizingSpecsPano, setResizingSpecsPano] = useState(false);
  const [specIconSize, setSpecIconSize] = useState(SPECS_ICON_SIZE_DEFAULT);
  const [specsFieldViewMode, setSpecsFieldViewMode] = useState("valued");
  const [showSpecsOldValues, setShowSpecsOldValues] = useState(false);
  const [specsFieldValueFilterEnabled, setSpecsFieldValueFilterEnabled] = useState(false);
  const [specsFieldValueFilters, setSpecsFieldValueFilters] = useState([]);
  const [specsFocusedFieldCodes, setSpecsFocusedFieldCodes] = useState([]);
  const [specsEvidenceTarget, setSpecsEvidenceTarget] = useState(null);
  const [segmentApproveBusy, setSegmentApproveBusy] = useState(false);
  const [segmentBatchExcludeBusy, setSegmentBatchExcludeBusy] = useState(false);
  const [panoCandidatePanelOpen, setPanoCandidatePanelOpen] = useState(false);
  const [panoCandidateLoading, setPanoCandidateLoading] = useState(false);
  const [panoCandidateSaving, setPanoCandidateSaving] = useState(false);
  const [panoCandidatePoints, setPanoCandidatePoints] = useState([]);
  const [panoCandidateSelectedIds, setPanoCandidateSelectedIds] = useState([]);
  const [panoCandidateSaveMode, setPanoCandidateSaveMode] = useState("merge");
  const [panoCandidateBuffer, setPanoCandidateBuffer] = useState("0.0000050");
  const [panoCandidatePreview, setPanoCandidatePreview] = useState(null);
  const [segmentGisAssets, setSegmentGisAssets] = useState([]);
  const [segmentGisSummary, setSegmentGisSummary] = useState([]);
  const [segmentGisLoading, setSegmentGisLoading] = useState(false);
  const [segmentGisError, setSegmentGisError] = useState("");
  const [segmentGisMapVisible, setSegmentGisMapVisible] = useState(true);
  const [segmentGisHiddenLayerKeys, setSegmentGisHiddenLayerKeys] = useState([]);
  const specsWorkspaceRef = useRef(null);
  const segmentListResizeStateRef = useRef(null);
  const specLayoutRef = useRef(null);
  const specResizeStateRef = useRef(null);
  const specMapPanoRef = useRef(null);
  const specMapPanoResizeStateRef = useRef(null);
  const canEditSpecsFields = typeof updateGridCell === "function";
  const canUsePanoMeasurementTools = canEditSpecsFields && typeof savePanoCalibrationState === "function";
  const canViewStarRanking = userCanViewIrapRanking(authUser);
  const normalizedGisLayerSettings = normalizeGisLayerSettings(gisLayerSettings);
  const globalGisLayerSelection = buildGisLayerSelection(normalizedGisLayerSettings);
  const segmentGisQueryString = buildGisLayerQueryString(normalizedGisLayerSettings, {
    limit: normalizedGisLayerSettings.limit
  });
  const segmentGisHiddenLayerKey = segmentGisHiddenLayerKeys.join("|");
  const segmentGisHiddenLayerSet = useMemo(
    () => new Set(segmentGisHiddenLayerKeys.map((layerKey) => String(layerKey))),
    [segmentGisHiddenLayerKey]
  );
  const visibleSegmentGisAssets = useMemo(
    () => segmentGisAssets.filter((asset) => !segmentGisHiddenLayerSet.has(gisLayerClassKey(asset))),
    [segmentGisAssets, segmentGisHiddenLayerKey]
  );
  const selectedPanoCandidates = useMemo(
    () => panoCandidateSelectedIds
      .map((candidateId) => panoCandidatePoints.find((candidate) => panoCandidateId(candidate) === candidateId))
      .filter(Boolean),
    [panoCandidateSelectedIds.join("|"), panoCandidatePoints]
  );
  const filteredSegments = useMemo(() => {
    return segmentIndex.filter((segment) => segmentMatchesSearch(segment, search));
  }, [segmentIndex, search]);
  const segmentGridColumns = useMemo(() => {
    const safeFields = (Array.isArray(fields) ? fields : [])
      .filter((field) => canViewStarRanking || !isStarRankingField(field));
    const propertyNames = Array.from(new Set(
      segmentIndex.flatMap((segment) => Object.keys((segment.feature && segment.feature.properties) || {}))
    ));
    const usedProperties = new Set([
      "SegmentId",
      "ID",
      "Road_name",
      "Distance",
      "approve",
      "Approve",
      "approved",
      "Approved",
      "review",
      "Review",
      ...SEGMENT_BATCH_EXCLUDED_KEYS,
      SEGMENT_BATCH_EXCLUDED_REASON_KEY,
      SEGMENT_BATCH_EXCLUDED_AT_KEY
    ]);
    const columns = [
      { key: "__id", label: "ID", title: "Segment ID", width: 88, kind: "id", stickyLeft: 0 },
      ...(canViewStarRanking ? SEGMENT_STAR_RATING_USERS.map((user) => ({
        key: `__starRating_${user.key}`,
        label: user.short,
        title: `${user.label} Star Rating`,
        width: 82,
        kind: "starRatingUser",
        starUserKey: user.key
      })) : []),
      { key: "Road_name", label: "Road", title: "Road name", width: 156, kind: "core", property: "Road_name" },
      { key: "Distance", label: "Distance", title: "Distance", width: 116, kind: "core", property: "Distance" },
      { key: "approve", label: "Approve", title: "Segment approval", width: 108, kind: "core", property: "approve" }
    ];

    safeFields.forEach((field) => {
      const property = field.geoProperty || field.field;
      const label = field.caption || field.field;
      if (field.field) {
        usedProperties.add(field.field);
      }
      if (property) {
        usedProperties.add(property);
      }
      columns.push({
        key: field.field,
        label,
        title: field.field ? `${label} (${field.field})` : label,
        width: specsGridColumnWidth(label),
        kind: "field",
        property,
        field
      });
    });

    propertyNames
      .filter((propertyName) => !usedProperties.has(propertyName))
      .filter((propertyName) => canViewStarRanking || !isStarRankingPropertyName(propertyName))
      .sort((a, b) => a.localeCompare(b))
      .forEach((propertyName) => {
        columns.push({
          key: propertyName,
          label: propertyName.replace(/_/g, " "),
          title: propertyName,
          width: 126,
          kind: "legacy",
          property: propertyName
        });
      });

    return columns;
  }, [fields, segmentIndex, canViewStarRanking]);
  const segmentGridTemplateColumns = useMemo(
    () => segmentGridColumns.map((column) => `${column.width}px`).join(" "),
    [segmentGridColumns]
  );

  const iconItems = useMemo(() => {
    if (!detail || !Array.isArray(detail.fields)) {
      return [];
    }
    const analysis = analysisResults[detail.id] || analysisResults[String(detail.id)] || detail.analysis;
    return detail.fields
      .filter((field) => canViewStarRanking || !isStarRankingField(field))
      .map((field, index) => {
        const resolved = resolvedSpecValue(detail, field, draftValues, {
          analysis,
          includeDraft: false,
          includeSegmentFallback: false
        });
        const analysisField = analysis && analysis.fields ? analysis.fields[field.field] : null;
        const oldValue = specOldFieldValue(detail, field);
        const comparison = specFieldComparison(resolved, oldValue);
        const iconUrl = irapIconUrl(field, resolved.value);
        const decodedValue = resolved.decodedValue || fieldOptionLabel(field, resolved.value) || field.decodedValue || "";
        const rawSourceImages = analysisField && Array.isArray(analysisField.sourceImages)
          ? analysisField.sourceImages
          : analysisField && analysisField.visualEvidence && Array.isArray(analysisField.visualEvidence.sourceImages)
          ? analysisField.visualEvidence.sourceImages
          : [];
        const rawVisualEvidence = analysisField ? analysisField.visualEvidence || null : null;
        const normalizedSourceImages = normalizeFieldSourceImages(rawSourceImages, detail);
        const normalizedVisualSourceImages = rawVisualEvidence
          ? normalizeFieldSourceImages([
              rawVisualEvidence.sourceImage,
              ...(Array.isArray(rawVisualEvidence.sourceImages) ? rawVisualEvidence.sourceImages : [])
            ], detail)
          : [];
        const visualEvidence = rawVisualEvidence
          ? {
              ...rawVisualEvidence,
              sourceImage: normalizedVisualSourceImages[0] || "",
              sourceImages: normalizedVisualSourceImages
            }
          : null;
        return {
          field,
          index,
          value: resolved.value,
          decodedValue,
          source: resolved.source,
          iconUrl,
          oldValue,
          comparison,
          confidence: analysisField ? analysisField.confidence : null,
          needsReview: analysisField ? Boolean(analysisField.needsReview) : false,
          validOption: analysisField ? analysisField.validOption : null,
          evidence: analysisField ? analysisField.evidence || analysisField.reason || "" : "",
          sourceImages: normalizedSourceImages,
          visualEvidence
        };
      })
      .filter((item) => hasDisplayValue(item.field.iconCategory))
      .sort((a, b) => {
        const orderA = CATEGORY_ORDER.indexOf(a.field.group);
        const orderB = CATEGORY_ORDER.indexOf(b.field.group);
        const safeOrderA = orderA >= 0 ? orderA : 999;
        const safeOrderB = orderB >= 0 ? orderB : 999;
        return safeOrderA - safeOrderB || (a.field.id || a.index) - (b.field.id || b.index);
      });
  }, [detail, draftValues, analysisResults, canViewStarRanking]);

  const specsEvidenceItems = useMemo(() => {
    if (!detail) {
      return [];
    }
    const result = signResults && (signResults[detail.id] || signResults[String(detail.id)]);
    return markedEvidenceItemsFromResult(result);
  }, [signResults, detail && detail.id]);

  const specsPanoAnnotations = useMemo(() => {
    if (!detail || !Array.isArray(panoAnnotations)) {
      return [];
    }
    return panoAnnotations.filter((annotation) => Number(annotation.segmentId) === Number(detail.id));
  }, [panoAnnotations, detail && detail.id]);

  const specsFieldViewCounts = useMemo(() => {
    return iconItems.reduce((counts, item) => {
      counts.all += 1;
      if (hasDisplayValue(item.value)) {
        counts.valued += 1;
      }
      const status = item.comparison && item.comparison.status;
      if (status === "different") {
        counts.different += 1;
      }
      if (status === "missing") {
        counts.missing += 1;
      }
      if (status === "same") {
        counts.same += 1;
      }
      return counts;
    }, {
      all: 0,
      valued: 0,
      different: 0,
      missing: 0,
      same: 0
    });
  }, [iconItems]);

  const iconItemsWithEvidence = useMemo(() => {
    if (!detail) {
      return iconItems;
    }
    return iconItems.map((item) => {
      const evidenceItems = markedEvidenceForField(
        signResults,
        detail.id,
        item.field.field,
        item.field.field
      );
      const annotationEvidenceItems = specsPanoAnnotations.filter((annotation) => panoAnnotationMatchesField(annotation, item.field));
      return {
        ...item,
        evidenceItems,
        annotationEvidenceItems
      };
    }).filter((item) => {
      const hasEvidence =
        (Array.isArray(item.evidenceItems) && item.evidenceItems.length > 0) ||
        (Array.isArray(item.annotationEvidenceItems) && item.annotationEvidenceItems.length > 0);
      if (specsFieldViewMode === "all") {
        return true;
      }
      if (specsFieldViewMode === "valued") {
        return hasDisplayValue(item.value) || hasEvidence;
      }
      return item.comparison && item.comparison.status === specsFieldViewMode;
    });
  }, [iconItems, signResults, specsPanoAnnotations, detail && detail.id, specsFieldViewMode]);

  const groupNames = useMemo(() => {
    const names = Array.from(new Set(iconItemsWithEvidence.map((item) => item.field.group)));
    return CATEGORY_ORDER.filter((name) => names.includes(name)).concat(
      names.filter((name) => !CATEGORY_ORDER.includes(name))
    );
  }, [iconItemsWithEvidence]);
  const allSpecsAgentGroups = useMemo(() => {
    const sourceFields = Array.isArray(fields) && fields.length
      ? fields
      : (detail && Array.isArray(detail.fields) ? detail.fields : []);
    const names = Array.from(new Set(sourceFields.map((field) => field && field.group).filter(Boolean)));
    return CATEGORY_ORDER.filter((name) => names.includes(name)).concat(
      names.filter((name) => !CATEGORY_ORDER.includes(name))
    );
  }, [fields, detail && detail.id]);
  const specsFieldValueFilterKeys = useMemo(
    () => new Set(specsFieldValueFilters.map((filter) => filter && filter.key).filter(Boolean)),
    [specsFieldValueFilters]
  );
  const toggleSpecsFieldValueFilter = (item) => {
    const nextFilter = specsFieldValueFilterFromItem(item);
    if (!nextFilter) {
      return;
    }
    const nextFilterWithContext = detail && detail.id !== undefined
      ? { ...nextFilter, segmentId: Number(detail.id) }
      : nextFilter;
    setSpecsFieldValueFilterEnabled(true);
    setSpecsFieldValueFilters((current) => {
      const exists = current.some((filter) => filter && filter.key === nextFilter.key);
      return exists
        ? current.filter((filter) => filter && filter.key !== nextFilter.key)
        : current.concat(nextFilterWithContext);
    });
  };
  const clearSpecsFieldValueFilters = () => {
    setSpecsFieldValueFilters([]);
  };
  const updateSpecsFocusedFieldCodes = (fieldCodes) => {
    const nextCodes = Array.from(new Set((Array.isArray(fieldCodes) ? fieldCodes : [])
      .map((fieldCode) => String(fieldCode || "").trim())
      .filter(Boolean)));
    const nextKey = nextCodes.join("|");
    setSpecsFocusedFieldCodes((current) => (
      (Array.isArray(current) ? current : []).join("|") === nextKey ? current : nextCodes
    ));
  };

  useEffect(() => {
    if (!detail) {
      return;
    }
    if (activeGroup !== "All" && activeGroup !== SPECS_EVIDENCE_TAB && !groupNames.includes(activeGroup)) {
      setActiveGroup("All");
    }
  }, [activeGroup, groupNames.join("|"), detail && detail.id]);

  useEffect(() => {
    if (!canEditSpecsFields && isSpecsFieldEditMode) {
      setIsSpecsFieldEditMode(false);
    }
  }, [canEditSpecsFields, isSpecsFieldEditMode]);

  useEffect(() => {
    if (canUsePanoMeasurementTools) {
      return;
    }
    setSpecsEvidenceShowMeasurement(false);
    setSpecsMapMeasureActive(false);
    setPanoMeasurementMode(null);
    setPanoMeasurementDraftPoints([]);
    setPanoTransientMeasurements([]);
    setIsPanoMeasurementListOpen(false);
    setIsPanoCalibrationPanelOpen(false);
    setIsPanoMeasurementPanelOpen(false);
  }, [canUsePanoMeasurementTools]);

  useEffect(() => {
    if (!detail) {
      return;
    }
    setSpecsCurrentImage(0);
    setSpecsPanoViewState(null);
    setSpecsMapMeasureActive(false);
    setIsSpecsLayoutMenuOpen(false);
    setIsSpecsBaseLayerMenuOpen(false);
    if (!Array.isArray(detail.images) || !detail.images.length) {
      setIsSpecsPanoOpen(false);
      setSpecMapViewMode("map");
    }
    setSpecsEvidenceTarget(null);
    setIsSpecsFieldEditMode(false);
    setSpecsFieldEvidenceTarget(null);
    setSpecsEvidenceDraftPoints([]);
    setSpecsEvidenceShowMeasurement(false);
    setSpecsSamBusy(false);
    setPanoMeasurementMode(null);
    setPanoMeasurementDraftPoints([]);
    setPanoTransientMeasurements([]);
    setIsPanoMeasurementListOpen(false);
    setPanoMeasurementStatus("");
    setSelectedPanoCalibrationId("");
    setPanoCalibrationProfileName("");
    setPanoViewHeadingOffsetDeg(String(DEFAULT_PANO_HEADING_OFFSET_DEG));
    setPanoViewFovDeg("");
    setPanoViewRangeMeters("");
    setPanoCandidatePanelOpen(false);
    setPanoCandidateLoading(false);
    setPanoCandidateSaving(false);
    setPanoCandidatePoints([]);
    setPanoCandidateSelectedIds([]);
    setPanoCandidatePreview(null);
    setSegmentGisAssets([]);
    setSegmentGisSummary([]);
    setSegmentGisError("");
    setSegmentGisMapVisible(true);
    setSegmentGisHiddenLayerKeys([]);
    setIsPanoCalibrationPanelOpen(false);
    setIsPanoMeasurementPanelOpen(false);
  }, [detail && detail.id]);

  const toggleSegmentGisLayerVisibility = (layerKey) => {
    const normalizedLayerKey = String(layerKey || "").trim();
    if (!normalizedLayerKey) {
      return;
    }
    setSegmentGisHiddenLayerKeys((current) => {
      const next = new Set((Array.isArray(current) ? current : []).map((item) => String(item)));
      if (next.has(normalizedLayerKey)) {
        next.delete(normalizedLayerKey);
      } else {
        next.add(normalizedLayerKey);
      }
      return Array.from(next).sort();
    });
  };

  useEffect(() => {
    const segmentId = detail && detail.id;
    if (!segmentId || !normalizedGisLayerSettings.evidenceEnabled || !segmentGisQueryString) {
      setSegmentGisAssets([]);
      setSegmentGisSummary([]);
      setSegmentGisLoading(false);
      setSegmentGisError(segmentId && normalizedGisLayerSettings.evidenceEnabled && !segmentGisQueryString ? "No GIS layers selected." : "");
      return undefined;
    }
    let cancelled = false;
    setSegmentGisLoading(true);
    setSegmentGisError("");
    apiFetch(`/api/segments/${Number(segmentId)}/gis-assets?${segmentGisQueryString}`)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setSegmentGisAssets(Array.isArray(data && data.assets) ? data.assets : []);
        setSegmentGisSummary(Array.isArray(data && data.summaryByClass) ? data.summaryByClass : []);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setSegmentGisAssets([]);
        setSegmentGisSummary([]);
        setSegmentGisError(error.message || "Could not load GIS layers.");
      })
      .finally(() => {
        if (!cancelled) {
          setSegmentGisLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    detail && detail.id,
    normalizedGisLayerSettings.evidenceEnabled,
    normalizedGisLayerSettings.evidenceSelectionMode,
    normalizedGisLayerSettings.evidenceClassIds.join(","),
    normalizedGisLayerSettings.limit
  ]);

  const getSpecAvailableWidth = () => {
    return Math.max(1, (specLayoutRef.current?.clientWidth || 1) - SPECS_RESIZE_HANDLE_WIDTH);
  };

  const getSpecWorkspaceAvailableWidth = () => {
    return Math.max(1, specsWorkspaceRef.current?.clientWidth || 1);
  };

  const startSpecSegmentListResize = (event) => {
    event.preventDefault();
    segmentListResizeStateRef.current = {
      startX: event.clientX,
      width: specSegmentListWidth,
      availableWidth: getSpecWorkspaceAvailableWidth()
    };
    setResizingSegmentList(true);
    document.body.classList.add("is-resizing-spec-segment-list");
  };

  const nudgeSpecSegmentListResize = (event) => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 24 : -24;
    const availableWidth = getSpecWorkspaceAvailableWidth();
    setSpecSegmentListWidth((current) => resizeSpecsSegmentListWidth(current, delta, availableWidth));
    window.dispatchEvent(new Event("resize"));
  };

  useEffect(() => {
    const handlePointerMove = (event) => {
      const resizeState = segmentListResizeStateRef.current;
      if (!resizeState) {
        return;
      }
      setSpecSegmentListWidth(
        resizeSpecsSegmentListWidth(
          resizeState.width,
          event.clientX - resizeState.startX,
          resizeState.availableWidth
        )
      );
      window.dispatchEvent(new Event("resize"));
    };

    const handlePointerUp = () => {
      if (!segmentListResizeStateRef.current) {
        return;
      }
      segmentListResizeStateRef.current = null;
      setResizingSegmentList(false);
      document.body.classList.remove("is-resizing-spec-segment-list");
      window.dispatchEvent(new Event("resize"));
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      document.body.classList.remove("is-resizing-spec-segment-list");
    };
  }, []);

  const startSpecResize = (event) => {
    event.preventDefault();
    specResizeStateRef.current = {
      startX: event.clientX,
      widths: specPanelWidths,
      availableWidth: getSpecAvailableWidth()
    };
    setResizingSpecs(true);
    document.body.classList.add("is-resizing-specs");
  };

  const nudgeSpecResize = (event) => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 2 : -2;
    const availableWidth = getSpecAvailableWidth();
    setSpecPanelWidths((current) => resizeSpecsWidths(current, delta, availableWidth));
    window.dispatchEvent(new Event("resize"));
  };

  useEffect(() => {
    const handlePointerMove = (event) => {
      const resizeState = specResizeStateRef.current;
      if (!resizeState) {
        return;
      }
      const deltaPct = ((event.clientX - resizeState.startX) / resizeState.availableWidth) * 100;
      setSpecPanelWidths(resizeSpecsWidths(resizeState.widths, deltaPct, resizeState.availableWidth));
      window.dispatchEvent(new Event("resize"));
    };

    const handlePointerUp = () => {
      if (!specResizeStateRef.current) {
        return;
      }
      specResizeStateRef.current = null;
      setResizingSpecs(false);
      document.body.classList.remove("is-resizing-specs");
      window.dispatchEvent(new Event("resize"));
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      document.body.classList.remove("is-resizing-specs");
    };
  }, []);

  const getSpecMapPanoAvailableWidth = () => {
    return Math.max(1, (specMapPanoRef.current?.clientWidth || 1) - SPECS_MAP_PANO_HANDLE_WIDTH);
  };

  const startSpecMapPanoResize = (event) => {
    event.preventDefault();
    specMapPanoResizeStateRef.current = {
      startX: event.clientX,
      widths: specMapPanoWidths,
      availableWidth: getSpecMapPanoAvailableWidth()
    };
    setResizingSpecsPano(true);
    document.body.classList.add("is-resizing-spec-pano");
  };

  const nudgeSpecMapPanoResize = (event) => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 2 : -2;
    const availableWidth = getSpecMapPanoAvailableWidth();
    setSpecMapPanoWidths((current) => resizeSpecsMapPanoWidths(current, delta, availableWidth));
    window.dispatchEvent(new Event("resize"));
  };

  useEffect(() => {
    const handlePointerMove = (event) => {
      const resizeState = specMapPanoResizeStateRef.current;
      if (!resizeState) {
        return;
      }
      const deltaPct = ((event.clientX - resizeState.startX) / resizeState.availableWidth) * 100;
      setSpecMapPanoWidths(resizeSpecsMapPanoWidths(resizeState.widths, deltaPct, resizeState.availableWidth));
      window.dispatchEvent(new Event("resize"));
    };

    const handlePointerUp = () => {
      if (!specMapPanoResizeStateRef.current) {
        return;
      }
      specMapPanoResizeStateRef.current = null;
      setResizingSpecsPano(false);
      document.body.classList.remove("is-resizing-spec-pano");
      window.dispatchEvent(new Event("resize"));
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      document.body.classList.remove("is-resizing-spec-pano");
    };
  }, []);

  const visibleIconItems = activeGroup === "All" || activeGroup === SPECS_EVIDENCE_TAB
    ? iconItemsWithEvidence
    : iconItemsWithEvidence.filter((item) => item.field.group === activeGroup);
  const groupedItems = groupNames
    .map((groupName) => ({
      groupName,
      items: visibleIconItems.filter((item) => item.field.group === groupName)
    }))
    .filter((group) => group.items.length);
  const allGroupedItems = groupNames
    .map((groupName) => ({
      groupName,
      items: iconItemsWithEvidence.filter((item) => item.field.group === groupName)
    }))
    .filter((group) => group.items.length);

  const selectedSegment = segmentIndex.find((segment) => Number(segment.id) === Number(selectedId));
  const specsNeighborInsight = useMemo(() => specsNeighborAiInsight({
    currentSegment: selectedSegment,
    allSegments: segmentIndex,
    fields: detail && Array.isArray(detail.fields) ? detail.fields : fields,
    analysisResults,
    currentItems: iconItems
  }), [selectedSegment, segmentIndex, detail && detail.id, detail && detail.fields, fields, analysisResults, iconItems]);
  const segmentApproval = segmentApprovalStateFromProps((detail && detail.properties) || {});
  const segmentIsBatchExcluded = segmentBatchExcluded(detail || selectedSegment);
  const segmentBatchExclusionReason = segmentBatchExcludedReason(detail || selectedSegment);
  const starRatings = canViewStarRanking ? segmentStarRatings(detail || selectedSegment) : [];
  const metadata = [
    { label: "Segment", value: detail ? detail.id : selectedId },
    { label: "Road name", value: metadataValue(detail, "Road_name", ["Road_name", "ROAD_NAME", "Road Name"]) || selectedSegment?.road },
    segmentIsBatchExcluded
      ? { label: "Batch", value: segmentBatchExclusionReason ? `Blocked - ${segmentBatchExclusionReason}` : "Blocked" }
      : null
  ].filter((item) => item && hasDisplayValue(item.value));
  const specsEvidenceSegment = selectedSegment || (detail ? {
    id: detail.id,
    road: metadataValue(detail, "Road_name", ["Road_name", "ROAD_NAME", "Road Name"]),
    distance: metadataValue(detail, "Distance", ["Distance"])
  } : null);
  const changeSpecsMapViewMode = (mode) => {
    setSpecMapViewMode(mode);
    setIsSpecsLayoutMenuOpen(false);
    if (mode !== "map" && detail && Array.isArray(detail.images) && detail.images.length) {
      setSpecsCurrentImage((current) => detail.images[current] ? current : 0);
      setIsSpecsPanoOpen(true);
      if (mode === "side") {
        setSpecMapPanoWidths(SPECS_MAP_PANO_DEFAULT_WIDTHS);
      }
    }
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };
  const toggleSpecsImageStrip = () => {
    setIsSpecsLayoutMenuOpen(false);
    setIsSpecsBaseLayerMenuOpen(false);
    setShowSpecsImageStrip((current) => !current);
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };
  const findSpecsField = (fieldName) => {
    const fieldCode = String(fieldName || "").trim();
    const detailField = detail && Array.isArray(detail.fields)
      ? detail.fields.find((field) => String(field.field || "").trim() === fieldCode)
      : null;
    if (detailField) {
      return detailField;
    }
    return (fields || []).find((field) => String(field.field || "").trim() === fieldCode) || null;
  };
  const refreshEditedSpecsFieldValueFilter = (fieldName, previousValue, nextValue, decodedValue = "") => {
    const field = findSpecsField(fieldName);
    const fieldCode = String((field && field.field) || fieldName || "").trim();
    if (!fieldCode) {
      return;
    }
    const previousKey = specsFieldValueFilterKey(fieldCode, previousValue);
    const nextFilter = specsFieldValueFilterFromItem({
      field: field || { field: fieldCode },
      value: nextValue,
      decodedValue
    });
    if (!nextFilter) {
      return;
    }
    setSpecsFieldValueFilters((current) => {
      let replaced = false;
      const seenKeys = new Set();
      const nextFilters = [];
      current.forEach((filter) => {
        if (!filter || !filter.key) {
          return;
        }
        const replacement = filter.key === previousKey
          ? {
              ...nextFilter,
              segmentId: filter.segmentId
            }
          : filter;
        if (filter.key === previousKey) {
          replaced = true;
        }
        if (!seenKeys.has(replacement.key)) {
          seenKeys.add(replacement.key);
          nextFilters.push(replacement);
        }
      });
      return replaced ? nextFilters : current;
    });
  };
  const saveSpecsFieldValue = async (fieldName, value, editContext = {}) => {
    if (!detail || typeof updateGridCell !== "function") {
      return null;
    }
    const result = await updateGridCell({
      segmentId: detail.id,
      source: "ai",
      fieldName,
      value
    });
    refreshEditedSpecsFieldValueFilter(
      fieldName,
      editContext.previousValue,
      result && Object.prototype.hasOwnProperty.call(result, "value") ? result.value : value,
      result && result.decodedValue
    );
    return result;
  };
  const loadFieldPrompts = () => {
    apiFetch("/api/field-prompts")
      .then((data) => setFieldPromptMap(data && data.prompts ? data.prompts : {}))
      .catch((error) => {
        if (typeof setError === "function") {
          setError(error.message || "Could not load field prompts.");
        }
      });
  };

  useEffect(() => {
    loadFieldPrompts();
  }, []);

  const fieldPromptImageSourceModeForCode = (fieldCode) => normalizeFieldImageSourceMode(
    fieldCode && fieldPromptMap[fieldCode] && fieldPromptMap[fieldCode].imageSourceMode
  );
  const filterGisSelectionToConfiguredAiLayers = (selection) => {
    const normalized = normalizeFieldPromptGisSelection(selection);
    if (normalizedGisLayerSettings.aiSelectionMode !== "selected") {
      return normalized;
    }
    const allowedIds = new Set(normalizedGisLayerSettings.aiClassIds.map((classId) => String(classId)));
    const classIds = normalized.classIds.filter((classId) => allowedIds.has(String(classId)));
    return {
      ...normalized,
      enabled: Boolean(normalized.enabled && classIds.length),
      classIds
    };
  };
  const fieldPromptGisSelectionForCode = (fieldCode) => filterGisSelectionToConfiguredAiLayers(
    fieldCode && fieldPromptMap[fieldCode] && fieldPromptMap[fieldCode].gisLayerSelection
  );

  const saveSpecsFieldPrompt = async (item, prompt, options = {}) => {
    const fieldCode = item && item.field && item.field.field;
    if (!fieldCode) {
      return null;
    }
    setFieldPromptBusy(true);
    try {
      const data = await apiFetch(`/api/field-prompts/${encodeURIComponent(fieldCode)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          applyToAgentPrompt: Boolean(options.applyToAgentPrompt),
          imageSourceMode: normalizeFieldImageSourceMode(options.imageSourceMode || fieldPromptImageSourceModeForCode(fieldCode)),
          gisLayerSelection: Object.prototype.hasOwnProperty.call(options, "gisLayerSelection")
            ? normalizeFieldPromptGisSelection(options.gisLayerSelection)
            : fieldPromptGisSelectionForCode(fieldCode)
        })
      });
      setFieldPromptMap(data && data.prompts ? data.prompts : {});
      if (typeof setMessage === "function") {
        setMessage(options.applyToAgentPrompt
          ? `Saved ${fieldCode} prompt and added it to the ${item.field.group} agent.`
          : `Saved field prompt for ${fieldCode}.`);
      }
      return data;
    } catch (error) {
      if (typeof setError === "function") {
        setError(error.message || "Could not save field prompt.");
      }
      throw error;
    } finally {
      setFieldPromptBusy(false);
    }
  };

  const runSpecsSegmentAgents = async (segment, options = {}) => {
    const segmentId = Number(segment && segment.id !== undefined ? segment.id : segment);
    if (!Number.isFinite(segmentId) || specsRunningSegmentAgentId !== null) {
      return null;
    }
    const selectedGroups = Array.isArray(selectedAgentGroups)
      ? selectedAgentGroups.filter(Boolean)
      : [];
    const groupsForRun = (options.runAll ? allSpecsAgentGroups : selectedGroups).filter(Boolean);
    if (!groupsForRun.length) {
      if (typeof setError === "function") {
        setError("Select at least one AI agent, or choose Run all AI agents from the row menu.");
      }
      return null;
    }

    setSpecsRunningSegmentAgentId(segmentId);
    if (typeof setError === "function") {
      setError("");
    }
    if (typeof selectSegment === "function") {
      selectSegment(segmentId);
    }
    if (typeof setMessage === "function") {
      setMessage(
        `Running ${groupsForRun.length} AI agent${groupsForRun.length === 1 ? "" : "s"} for segment ${segmentId} from the grid...`
      );
    }

    try {
      const analysisResult = await apiFetch(`/api/segments/${segmentId}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          maxImages: analysisImageCount,
          imageProcessingMode,
          model: agentRunnerModel,
          reasoningEffort: agentRunnerReasoningEffort,
          includeFieldPrompts: includeFieldPromptsInAgentPrompt,
          agentConcurrency,
          openAiCallConcurrency,
          groups: groupsForRun,
          gisLayerSelection: globalGisLayerSelection,
          resetSegmentProcessing: false
        })
      });
      if (typeof setAnalysisResults === "function" && analysisResult) {
        setAnalysisResults((current) => ({
          ...current,
          [segmentId]: analysisResult
        }));
      }
      if (typeof reloadSelectedSegment === "function") {
        const refreshedDetail = await reloadSelectedSegment({
          segmentId,
          silent: true
        });
        if (refreshedDetail && refreshedDetail.analysis && typeof setAnalysisResults === "function") {
          setAnalysisResults((current) => ({
            ...current,
            [segmentId]: refreshedDetail.analysis
          }));
        }
      }
      if (typeof loadLogs === "function") {
        loadLogs({ silent: true });
      }
      if (typeof setMessage === "function") {
        const savedCount = Object.keys((analysisResult && analysisResult.saveJson) || {}).length;
        setMessage(`AI agents complete for segment ${segmentId}. ${savedCount} fields passed confidence checks.`);
      }
      return analysisResult;
    } catch (error) {
      if (typeof setError === "function") {
        setError(error.message || "Could not run AI agents for this segment.");
      }
      if (typeof setMessage === "function") {
        setMessage("Analysis stopped.");
      }
      if (typeof loadLogs === "function") {
        loadLogs({ silent: true });
      }
      return null;
    } finally {
      setSpecsRunningSegmentAgentId(null);
    }
  };

  const runSpecsFieldPrompt = async (item, options = {}) => {
    if (!detail || !item || !item.field || fieldPromptBusy) {
      return null;
    }
    const fieldCode = item.field.field;
    const promptText = Object.prototype.hasOwnProperty.call(options, "prompt")
      ? String(options.prompt || "").trim()
      : String((fieldPromptMap[fieldCode] && fieldPromptMap[fieldCode].prompt) || "").trim();
    const imageSourceMode = normalizeFieldImageSourceMode(options.imageSourceMode || fieldPromptImageSourceModeForCode(fieldCode));
    if (!promptText) {
      if (typeof setError === "function") {
        setError(`Field prompt is empty for ${fieldCode}. Add a field prompt before running this field.`);
      }
      if (typeof setMessage === "function") {
        setMessage("Run blocked because this field prompt is empty.");
      }
      return null;
    }
    if (!options.confirmed) {
      setFieldBatchSetup({
        mode: "single",
        item,
        fieldCode,
        fieldLabel: item.field.caption || item.field.label || fieldCode,
        segmentIds: [detail.id],
        prompt: promptText,
        applyToAgentPrompt: Boolean(options.applyToAgentPrompt),
        fieldPromptOnly: options.fieldPromptOnly !== false,
        includeAgentInstructions: Boolean(options.includeAgentInstructions),
        includeAgentPromptAddendum: Boolean(options.includeAgentPromptAddendum),
        imageSourceMode,
        maxImages: Number(options.maxImages) || analysisImageCount,
        imageProcessingMode: options.imageProcessingMode || imageProcessingMode,
        model: options.model || agentRunnerModel,
        reasoningEffort: options.reasoningEffort || agentRunnerReasoningEffort,
        segmentConcurrency: 1,
        openAiCallConcurrency: Number(options.openAiCallConcurrency) || openAiCallConcurrency,
        gisLayerSelection: Object.prototype.hasOwnProperty.call(options, "gisLayerSelection")
          ? options.gisLayerSelection
          : fieldPromptGisSelectionForCode(fieldCode)
      });
      if (typeof setMessage === "function") {
        setMessage(`Review run settings for ${fieldCode} on segment ${detail.id}.`);
      }
      return { pendingConfirmation: true, segmentCount: 1 };
    }
    setFieldPromptRunningField(fieldCode);
    setFieldPromptBusy(true);
    if (typeof setMessage === "function") {
      setMessage(`Running AI for ${fieldCode} on segment ${detail.id}...`);
    }
    try {
      const body = {
        fieldName: fieldCode,
        maxImages: Number(options.maxImages) || analysisImageCount,
        imageProcessingMode: options.imageProcessingMode || imageProcessingMode,
        model: options.model || agentRunnerModel,
        reasoningEffort: options.reasoningEffort || agentRunnerReasoningEffort,
        agentConcurrency: Number(options.agentConcurrency) || agentConcurrency,
        openAiCallConcurrency: Number(options.openAiCallConcurrency) || openAiCallConcurrency,
        resetSegmentProcessing: false
      };
      body.prompt = promptText;
      body.applyToAgentPrompt = Boolean(options.applyToAgentPrompt);
      body.fieldPromptOnly = options.fieldPromptOnly !== false;
      body.applyConsistencyRules = body.fieldPromptOnly ? false : options.applyConsistencyRules !== false;
      body.includeAgentInstructions = Boolean(options.includeAgentInstructions);
      body.includeAgentPromptAddendum = Boolean(options.includeAgentPromptAddendum);
      body.imageSourceMode = imageSourceMode;
      const effectiveGisLayerSelection = Object.prototype.hasOwnProperty.call(options, "gisLayerSelection")
        ? options.gisLayerSelection
        : fieldPromptGisSelectionForCode(fieldCode);
      if (effectiveGisLayerSelection) {
        body.gisLayerSelection = effectiveGisLayerSelection;
      }
      const data = await apiFetch(`/api/segments/${detail.id}/analyze-field`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const analysisResult = data && data.result ? data.result : data;
      if (data && data.prompts) {
        setFieldPromptMap(data.prompts);
      }
      if (typeof setAnalysisResults === "function" && analysisResult) {
        setAnalysisResults((current) => ({
          ...current,
          [detail.id]: analysisResult
        }));
      }
      if (typeof reloadSelectedSegment === "function") {
        const refreshedDetail = await reloadSelectedSegment({
          segmentId: detail.id,
          silent: true
        });
        if (refreshedDetail && refreshedDetail.analysis && typeof setAnalysisResults === "function") {
          setAnalysisResults((current) => ({
            ...current,
            [detail.id]: refreshedDetail.analysis
          }));
        }
      }
      if (typeof setMessage === "function") {
        const fieldResult = analysisResult && analysisResult.fields ? analysisResult.fields[fieldCode] : null;
        const valueText = fieldResult && hasDisplayValue(fieldResult.value)
          ? specDisplayValue(item.field, fieldResult.value, fieldResult.decodedValue)
          : "Blank";
        setMessage(`Updated ${fieldCode} for segment ${detail.id}: ${valueText}. Segment form reloaded.`);
      }
      if (typeof loadLogs === "function") {
        loadLogs({ silent: true });
      }
      return analysisResult;
    } catch (error) {
      if (typeof setError === "function") {
        setError(error.message || "Could not run field prompt.");
      }
      throw error;
    } finally {
      setFieldPromptBusy(false);
      setFieldPromptRunningField("");
    }
  };

  const runSpecsFieldPromptBatch = async (item, options = {}) => {
    if (!item || !item.field || fieldPromptBusy) {
      return null;
    }
    if (isActiveBatchJob(batchJob)) {
      if (typeof setError === "function") {
        setError("A batch is already running. Wait for it to finish before starting another field list run.");
      }
      return null;
    }
    const fieldCode = item.field.field;
    const promptText = Object.prototype.hasOwnProperty.call(options, "prompt")
      ? String(options.prompt || "").trim()
      : String((fieldPromptMap[fieldCode] && fieldPromptMap[fieldCode].prompt) || "").trim();
    const imageSourceMode = normalizeFieldImageSourceMode(options.imageSourceMode || fieldPromptImageSourceModeForCode(fieldCode));
    if (!promptText) {
      if (typeof setError === "function") {
        setError(`Field prompt is empty for ${fieldCode}. Add a field prompt before running this field for the list.`);
      }
      if (typeof setMessage === "function") {
        setMessage("List run blocked because this field prompt is empty.");
      }
      return null;
    }
    const visibleIds = Array.isArray(specsListedSegmentIds) && specsListedSegmentIds.length
      ? specsListedSegmentIds
      : detail
      ? [detail.id]
      : [];
    if (!visibleIds.length) {
      return null;
    }
    setFieldBatchSetup({
      mode: "batch",
      item,
      fieldCode,
      fieldLabel: item.field.caption || item.field.label || fieldCode,
      segmentIds: visibleIds,
      prompt: promptText,
      applyToAgentPrompt: Boolean(options.applyToAgentPrompt),
      fieldPromptOnly: options.fieldPromptOnly !== false,
      includeAgentInstructions: Boolean(options.includeAgentInstructions),
      includeAgentPromptAddendum: Boolean(options.includeAgentPromptAddendum),
      imageSourceMode,
      maxImages: analysisImageCount,
      imageProcessingMode,
      model: agentRunnerModel,
      reasoningEffort: agentRunnerReasoningEffort,
      segmentConcurrency: batchSegmentConcurrency,
      openAiCallConcurrency,
      gisLayerSelection: Object.prototype.hasOwnProperty.call(options, "gisLayerSelection")
        ? options.gisLayerSelection
        : fieldPromptGisSelectionForCode(fieldCode)
    });
    if (typeof setMessage === "function") {
      setMessage(`Review batch settings for ${fieldCode}: ${visibleIds.length} filtered grid segment${visibleIds.length === 1 ? "" : "s"}.`);
    }
    return { pendingConfirmation: true, segmentCount: visibleIds.length };
  };

  const startSpecsFieldPromptBatch = async (setup) => {
    if (!setup || !setup.item || !setup.fieldCode || fieldPromptBusy) {
      return null;
    }
    if (isActiveBatchJob(batchJob)) {
      if (typeof setError === "function") {
        setError("A batch is already running. Wait for it to finish before starting another field list run.");
      }
      return null;
    }
    const visibleIds = Array.isArray(setup.segmentIds)
      ? setup.segmentIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
      : [];
    if (!visibleIds.length) {
      if (typeof setError === "function") {
        setError("No filtered grid segments are available for this field batch.");
      }
      return null;
    }
    if (visibleIds.length > MAX_BATCH_RANGE_SEGMENTS) {
      if (typeof setError === "function") {
        setError(`This filtered grid has ${visibleIds.length} segments. Narrow the grid to ${MAX_BATCH_RANGE_SEGMENTS} segments or fewer.`);
      }
      return null;
    }
    const fieldCode = setup.fieldCode;
    const promptText = String(setup.prompt || "").trim();
    if (!promptText) {
      if (typeof setError === "function") {
        setError(`Field prompt is empty for ${fieldCode}. Add a field prompt before starting this batch.`);
      }
      return null;
    }
    setFieldPromptRunningField(fieldCode);
    setFieldPromptBusy(true);
    if (typeof setMessage === "function") {
      setMessage(`Starting batch AI for ${fieldCode} on ${visibleIds.length} segment${visibleIds.length === 1 ? "" : "s"}...`);
    }
    try {
      const data = await apiFetch(`/api/field-prompts/${encodeURIComponent(fieldCode)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: promptText,
          applyToAgentPrompt: Boolean(setup.applyToAgentPrompt),
          imageSourceMode: normalizeFieldImageSourceMode(setup.imageSourceMode),
          gisLayerSelection: normalizeFieldPromptGisSelection(setup.gisLayerSelection)
        })
      });
      setFieldPromptMap(data && data.prompts ? data.prompts : {});
      const job = await apiFetch("/api/batch-analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          segmentIds: visibleIds,
          fields: [fieldCode],
          maxImages: Number(setup.maxImages) || analysisImageCount,
          imageProcessingMode: setup.imageProcessingMode || imageProcessingMode,
          model: setup.model || agentRunnerModel,
          reasoningEffort: setup.reasoningEffort || agentRunnerReasoningEffort,
          fieldPromptOnly: Boolean(setup.fieldPromptOnly),
          applyConsistencyRules: setup.fieldPromptOnly ? false : setup.applyConsistencyRules !== false,
          includeAgentInstructions: Boolean(setup.includeAgentInstructions),
          includeAgentPromptAddendum: Boolean(setup.includeAgentPromptAddendum),
          segmentConcurrency: Number(setup.segmentConcurrency) || batchSegmentConcurrency,
          agentConcurrency,
          openAiCallConcurrency: Number(setup.openAiCallConcurrency) || openAiCallConcurrency,
          gisLayerSelection: setup.gisLayerSelection || null,
          resetSegmentProcessing: false
        })
      });
      if (typeof setBatchJob === "function") {
        setBatchJob(job);
      }
      setFieldBatchSetup(null);
      if (typeof setMessage === "function") {
        const excludedCount = Number(job && job.excludedSegmentCount || 0);
        setMessage(`Batch queued for ${fieldCode}: ${job && job.total ? job.total : visibleIds.length} segment${(job && job.total) === 1 ? "" : "s"}${excludedCount ? `, ${excludedCount} blocked skipped` : ""}.`);
      }
      return job;
    } catch (error) {
      if (typeof setError === "function") {
        setError(error.message || "Could not start field batch.");
      }
      throw error;
    } finally {
      setFieldPromptBusy(false);
      setFieldPromptRunningField("");
    }
  };
  const startSpecsFieldPromptSetup = async (setup) => {
    if (setup && setup.mode === "single") {
      try {
        const result = await runSpecsFieldPrompt(setup.item, {
          prompt: setup.prompt,
          applyToAgentPrompt: Boolean(setup.applyToAgentPrompt),
          fieldPromptOnly: Boolean(setup.fieldPromptOnly),
          includeAgentInstructions: Boolean(setup.includeAgentInstructions),
          includeAgentPromptAddendum: Boolean(setup.includeAgentPromptAddendum),
          imageSourceMode: setup.imageSourceMode,
          maxImages: setup.maxImages,
          imageProcessingMode: setup.imageProcessingMode,
          model: setup.model,
          reasoningEffort: setup.reasoningEffort,
          openAiCallConcurrency: setup.openAiCallConcurrency,
          gisLayerSelection: setup.gisLayerSelection,
          confirmed: true
        });
        setFieldBatchSetup(null);
        return result;
      } catch (error) {
        throw error;
      }
    }
    return startSpecsFieldPromptBatch(setup);
  };
  const setSegmentApproval = async (nextState) => {
    if (!detail || typeof updateGridCell !== "function" || segmentApproveBusy) {
      return;
    }
    setSegmentApproveBusy(true);
    try {
      const props = (detail && detail.properties) || {};
      const updates = segmentApprovalUpdateFieldsFromProps(props, nextState);
      for (const update of updates) {
        await updateGridCell({
          segmentId: detail.id,
          source: "old",
          fieldName: update.fieldName,
          value: update.value
        });
      }
    } finally {
      setSegmentApproveBusy(false);
    }
  };
  const setSegmentBatchExclusion = async (excluded) => {
    if (!detail || typeof updateSegmentBatchExclusion !== "function" || segmentBatchExcludeBusy) {
      return;
    }
    setSegmentBatchExcludeBusy(true);
    try {
      await updateSegmentBatchExclusion({
        segmentId: detail.id,
        excluded: Boolean(excluded),
        reason: excluded ? "Data issue" : ""
      });
    } catch (error) {
      if (typeof setError === "function") {
        setError(error.message || "Could not update segment batch block.");
      }
    } finally {
      setSegmentBatchExcludeBusy(false);
    }
  };
  const resetSpecsFieldEvidenceDraft = () => {
    setSpecsFieldEvidenceTarget(null);
    setSpecsEvidenceDraftPoints([]);
    setSpecsEvidenceNote("");
    setSpecsEvidenceShowMeasurement(false);
    setSpecsSamBusy(false);
    setSpecsSamPendingAnnotation(null);
  };

  const cancelSpecsSamEvidence = async () => {
    const pending = specsSamPendingAnnotation;
    resetSpecsFieldEvidenceDraft();
    if (pending && pending.id && typeof deletePanoAnnotation === "function") {
      await deletePanoAnnotation(pending.id, { skipConfirm: true });
    }
  };

  const clearSpecsFieldEvidenceDraft = () => {
    if (specsSamPendingAnnotation) {
      cancelSpecsSamEvidence();
      return;
    }
    resetSpecsFieldEvidenceDraft();
  };

  const startSpecsFieldEvidence = (item) => {
    if (!detail || !Array.isArray(detail.images) || !detail.images.length || !item || !item.field) {
      return;
    }
    setPanoCandidatePreview(null);
    setSpecsFieldEvidenceTarget({
      field: item.field,
      value: item.value,
      decodedValue: item.decodedValue
    });
    setSpecsEvidenceDraftPoints([]);
    setSpecsEvidenceNote("");
    setSpecsEvidenceShowMeasurement(false);
    setSpecsSamBusy(false);
    setSpecsSamPendingAnnotation(null);
    setPanoMeasurementMode(null);
    setPanoMeasurementDraftPoints([]);
    setPanoMeasurementStatus("");
    setIsSpecsPanoOpen(true);
    setSpecMapViewMode("mini");
    setSpecMapPanoWidths(SPECS_EVIDENCE_MAP_PANO_WIDTHS);
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };

  const buildSpecsFieldEvidenceAnnotation = (shape) => {
    if (!detail || !selectedSpecsImage || !specsFieldEvidenceTarget || !specsFieldEvidenceTarget.field) {
      return null;
    }
    const field = specsFieldEvidenceTarget.field;
    const label = `${field.caption || "Evidence"}`.trim();
    const shouldMeasureLine = specsEvidenceShowMeasurement && shape && shape.kind === "line";
    const metersPerRadian = shouldMeasureLine ? panoCalibrationScale(selectedPanoCalibration) : null;
    const angularDistanceRad = shouldMeasureLine && shape.points && shape.points.length >= 2
      ? panoAngularDistance(shape.points[0], shape.points[1])
      : null;
    const distanceMeters = shouldMeasureLine &&
      Number.isFinite(Number(metersPerRadian)) &&
      Number.isFinite(Number(angularDistanceRad))
      ? Number(metersPerRadian) * Number(angularDistanceRad)
      : null;
    return {
      imageFile: selectedSpecsImage.file,
      label,
      type: "other",
      note: specsEvidenceNote,
      shape,
      meta: {
        source: "manual-field-evidence",
        fieldCode: field.field,
        fieldCaption: field.caption || "",
        groupName: field.group || "",
        showMeasurement: Boolean(shouldMeasureLine && Number.isFinite(Number(distanceMeters))),
        distanceMeters: Number.isFinite(Number(distanceMeters)) ? distanceMeters : null,
        angularDistanceRad: Number.isFinite(Number(angularDistanceRad)) ? angularDistanceRad : null,
        metersPerRadian: Number.isFinite(Number(metersPerRadian)) ? Number(metersPerRadian) : null,
        calibrationId: shouldMeasureLine && selectedPanoCalibration ? selectedPanoCalibration.id : "",
        calibrationLabel: shouldMeasureLine && selectedPanoCalibration ? selectedPanoCalibration.name || selectedPanoCalibration.label || "" : ""
      }
    };
  };

  const saveSpecsFieldEvidenceShape = async (shape) => {
    if (!shape || !createPanoAnnotation) {
      return null;
    }
    const annotationInput = buildSpecsFieldEvidenceAnnotation(shape);
    if (!annotationInput) {
      return null;
    }
    const saved = await createPanoAnnotation(annotationInput);
    if (saved) {
      if (typeof setActivePanoAnnotationId === "function") {
        setActivePanoAnnotationId(saved.id);
      }
      resetSpecsFieldEvidenceDraft();
    }
    return saved;
  };

  const saveSpecsFieldEvidenceDraft = () => {
    const minPoints = minimumPointsForPanoShape(specsEvidenceShapeKind);
    if (specsEvidenceDraftPoints.length < minPoints) {
      return;
    }
    saveSpecsFieldEvidenceShape({
      kind: specsEvidenceShapeKind,
      points: specsEvidenceDraftPoints
    });
  };

  const finishSpecsSamEvidence = () => {
    resetSpecsFieldEvidenceDraft();
  };

  const placeSpecsFieldEvidence = (position) => {
    if (!specsFieldEvidenceTarget || !selectedSpecsImage) {
      return;
    }
    if (specsEvidenceShapeKind === "sam") {
      if (!createSamPanoAnnotation || specsSamBusy || specsSamPendingAnnotation) {
        return;
      }
      const annotationInput = buildSpecsFieldEvidenceAnnotation({
        kind: "point",
        points: [position]
      });
      if (!annotationInput) {
        return;
      }
      setSpecsSamBusy(true);
      createSamPanoAnnotation({
        ...annotationInput,
        yaw: position.yaw,
        pitch: position.pitch,
        textureX: position.textureX,
        textureY: position.textureY
      }).then((saved) => {
        if (saved && typeof setActivePanoAnnotationId === "function") {
          setActivePanoAnnotationId(saved.id);
        }
        if (saved) {
          setSpecsSamPendingAnnotation(saved);
        }
      }).finally(() => setSpecsSamBusy(false));
      return;
    }
    if (specsEvidenceShapeKind === "point") {
      saveSpecsFieldEvidenceShape({
        kind: "point",
        points: [position]
      });
      return;
    }
    setSpecsEvidenceDraftPoints((current) => current.concat(position));
  };

  const openSpecsEvidencePreview = (item) => {
    if (!item || !detail || !Array.isArray(detail.images) || !detail.images.length) {
      if (typeof openEvidencePreview === "function" && specsEvidenceSegment) {
        openEvidencePreview(item, specsEvidenceSegment);
      }
      return;
    }
    const sourceImage = sourceImageForEvidence(item);
    const imageIndex = sourceImage
      ? detail.images.findIndex((image) => image && image.file === sourceImage)
      : -1;
    setSpecsEvidenceTarget({
      segmentId: Number(detail.id),
      itemId: item.id,
      item
    });
    setSpecsCurrentImage(imageIndex >= 0 ? imageIndex : 0);
    setIsSpecsPanoOpen(true);
    setSpecMapViewMode("mini");
    setSpecMapPanoWidths(SPECS_EVIDENCE_MAP_PANO_WIDTHS);
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };

  const closeSpecsPano = () => {
    setIsSpecsPanoOpen(false);
    setSpecMapViewMode("side");
    setPanoCandidatePreview(null);
    setSpecsEvidenceTarget(null);
    setSpecsPanoViewState(null);
    setPanoMeasurementMode(null);
    setPanoMeasurementDraftPoints([]);
    setPanoTransientMeasurements([]);
    setIsPanoMeasurementListOpen(false);
    setPanoMeasurementStatus("");
    setIsPanoCalibrationPanelOpen(false);
    setIsPanoMeasurementPanelOpen(false);
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };

  const openSpecsPanoImage = (imageIndex) => {
    if (!Number.isFinite(Number(imageIndex))) {
      return;
    }
    setPanoCandidatePreview(null);
    const shouldUseDefaultLayout = !isSpecsPanoOpen;
    setSpecsCurrentImage(Number(imageIndex));
    setSpecsPanoViewState(null);
    setIsSpecsPanoOpen(true);
    if (shouldUseDefaultLayout) {
      setSpecMapViewMode("side");
      setSpecMapPanoWidths(SPECS_MAP_PANO_DEFAULT_WIDTHS);
    }
    setSpecsEvidenceTarget(null);
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };
  const openSpecsAnnotationEvidence = (annotation) => {
    if (!annotation || !detail || !Array.isArray(detail.images)) {
      return;
    }
    const imageIndex = detail.images.findIndex((image) => image && image.file === annotation.imageFile);
    if (imageIndex >= 0) {
      setPanoCandidatePreview(null);
      setSpecsCurrentImage(imageIndex);
    }
    setIsSpecsPanoOpen(true);
    setSpecMapViewMode("mini");
    if (typeof setActivePanoAnnotationId === "function") {
      setActivePanoAnnotationId(annotation.id);
    }
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };
  const openSpecsFieldSourceImage = (item) => {
    if (!item || !detail || !Array.isArray(detail.images) || !detail.images.length) {
      return;
    }
    const sourceImage = fieldSourceImages(item)[0] || "";
    if (!sourceImage) {
      return;
    }
    const imageIndex = detail.images.findIndex((image) => image && image.file === sourceImage);
    if (imageIndex < 0) {
      return;
    }
    setPanoCandidatePreview(null);
    setSpecsCurrentImage(imageIndex);
    setSpecsEvidenceTarget(null);
    setActivePanoAnnotationId && setActivePanoAnnotationId(null);
    setIsSpecsPanoOpen(true);
    setSpecMapViewMode("mini");
    setSpecMapPanoWidths(SPECS_EVIDENCE_MAP_PANO_WIDTHS);
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  };
  const loadPanoCandidatePoints = async () => {
    if (!detail || !canEditSpecsFields || panoCandidateLoading) {
      return;
    }
    setPanoCandidatePanelOpen(true);
    setPanoCandidateLoading(true);
    setPanoCandidateSelectedIds([]);
    if (typeof setError === "function") {
      setError("");
    }
    try {
      const params = new URLSearchParams();
      const bufferValue = Number(panoCandidateBuffer);
      if (Number.isFinite(bufferValue) && bufferValue > 0) {
        params.set("buffer", String(bufferValue));
      }
      params.set("limit", "500");
      const data = await apiFetch(`/api/segments/${detail.id}/pano-candidates?${params.toString()}`);
      const candidates = Array.isArray(data && data.candidates) ? data.candidates : [];
      setPanoCandidatePoints(candidates);
      setPanoCandidatePreview(null);
      if (typeof setMessage === "function") {
        setMessage(`Loaded ${candidates.length} AssetAi pano candidate${candidates.length === 1 ? "" : "s"} for segment ${detail.id}.`);
      }
    } catch (error) {
      if (typeof setError === "function") {
        setError(error.message || "Could not load AssetAi pano points.");
      }
    } finally {
      setPanoCandidateLoading(false);
    }
  };
  const previewPanoCandidate = (candidate) => {
    if (!isSpecsPanoOpen || !detail) {
      return;
    }
    const previewImage = panoCandidatePreviewImage(candidate, detail.id);
    if (!previewImage) {
      if (typeof setError === "function") {
        setError("This pano candidate does not have a preview image URL.");
      }
      return;
    }
    setPanoCandidatePreview(previewImage);
    setSpecsPanoViewState(null);
    setSpecsEvidenceTarget(null);
    setSpecsFieldEvidenceTarget(null);
    setSpecsEvidenceDraftPoints([]);
    setPanoMeasurementMode(null);
    setPanoMeasurementDraftPoints([]);
    setPanoTransientMeasurements([]);
    setIsPanoMeasurementListOpen(false);
    setIsPanoMeasurementPanelOpen(false);
    setIsPanoCalibrationPanelOpen(false);
  };
  const togglePanoCandidate = (candidate) => {
    const id = panoCandidateId(candidate);
    if (!id) {
      return;
    }
    previewPanoCandidate(candidate);
    setPanoCandidateSelectedIds((current) => {
      if (current.includes(id)) {
        return current.filter((candidateId) => candidateId !== id);
      }
      if (current.length >= MAX_PANO_CANDIDATE_SELECTION) {
        if (typeof setError === "function") {
          setError(`Select ${MAX_PANO_CANDIDATE_SELECTION} pano points or fewer.`);
        }
        return current;
      }
      if (typeof setError === "function") {
        setError("");
      }
      return current.concat(id);
    });
  };
  const clearPanoCandidates = () => {
    setPanoCandidatePoints([]);
    setPanoCandidateSelectedIds([]);
    setPanoCandidatePreview(null);
    setPanoCandidatePanelOpen(false);
  };
  const savePanoCandidateSelection = async () => {
    if (!detail || panoCandidateSaving) {
      return;
    }
    if (
      selectedPanoCandidates.length < MIN_PANO_CANDIDATE_SELECTION ||
      selectedPanoCandidates.length > MAX_PANO_CANDIDATE_SELECTION
    ) {
      if (typeof setError === "function") {
        setError(`Select ${MIN_PANO_CANDIDATE_SELECTION} to ${MAX_PANO_CANDIDATE_SELECTION} pano points.`);
      }
      return;
    }
    setPanoCandidateSaving(true);
    if (typeof setError === "function") {
      setError("");
    }
    try {
      const result = await apiFetch(`/api/segments/${detail.id}/pano-points`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: panoCandidateSaveMode,
          points: selectedPanoCandidates
        })
      });
      setPanoCandidateSelectedIds([]);
      setPanoCandidatePoints([]);
      setPanoCandidatePreview(null);
      setPanoCandidatePanelOpen(false);
      if (typeof reloadSelectedSegment === "function") {
        await reloadSelectedSegment({ segmentId: detail.id, silent: true });
      }
      if (typeof setMessage === "function") {
        const saved = Number(result && result.savedCount || 0);
        const skipped = Number(result && result.skippedDuplicateCount || 0);
        setMessage(`Saved ${saved} pano point${saved === 1 ? "" : "s"} for segment ${detail.id}${skipped ? `, ${skipped} duplicate skipped` : ""}.`);
      }
    } catch (error) {
      if (typeof setError === "function") {
        setError(error.message || "Could not save pano points.");
      }
    } finally {
      setPanoCandidateSaving(false);
    }
  };
  const specsWorkspaceStyle = {
    "--specs-segment-list-width": `${specSegmentListWidth}px`
  };
  const specsLayoutStyle = {
    "--spec-info-column": `minmax(${SPECS_PANEL_MIN_WIDTHS.info}px, calc(${specPanelWidths.info}% - ${(SPECS_RESIZE_HANDLE_WIDTH * specPanelWidths.info) / 100}px))`,
    "--spec-map-column": `minmax(${SPECS_PANEL_MIN_WIDTHS.map}px, calc(${specPanelWidths.map}% - ${(SPECS_RESIZE_HANDLE_WIDTH * specPanelWidths.map) / 100}px))`
  };
  const savedSpecsImage = detail && Array.isArray(detail.images)
    ? detail.images[specsCurrentImage] || detail.images[0] || null
    : null;
  const selectedSpecsImage = panoCandidatePreview || savedSpecsImage;
  const selectedSpecsImageIsCandidatePreview = Boolean(selectedSpecsImage && selectedSpecsImage.isCandidatePreview);
  const isSpecsSegmentLoading = Boolean(selectedId && !detail);
  const showSpecsPano = isSpecsPanoOpen && (selectedSpecsImage || isSpecsSegmentLoading);
  const showSpecsDock = Boolean(selectedSpecsImage || showSpecsPano);
  const specDockActiveMode = showSpecsPano ? specMapViewMode : "map";
  const activeSpecMapViewMode = SPECS_MAP_VIEW_MODES.find((item) => item.mode === specDockActiveMode) || SPECS_MAP_VIEW_MODES[0];
  const activeSpecsBaseLayer = MAP_BASE_LAYER_DEFS.find((item) => item.id === specsMapBaseLayerId) || MAP_BASE_LAYER_DEFS[0];
  const specsEvidenceOverlays = selectedSpecsImageIsCandidatePreview
    ? []
    : evidenceOverlaysForImage(
        detail,
        signResults,
        specsCurrentImage,
        specsEvidenceTarget
      );
  const specsMapSegmentFilterIds = specsMapLimitToList ? specsListedSegmentIds : null;
  const specsImageAnnotations = selectedSpecsImage && !selectedSpecsImageIsCandidatePreview
    ? specsPanoAnnotations.filter((annotation) => annotation.imageFile === selectedSpecsImage.file)
    : [];
  const currentPanoCalibrationState = useMemo(
    () => normalizePanoCalibrationState(panoCalibrationState),
    [panoCalibrationState]
  );
  const panoCalibrationProfiles = currentPanoCalibrationState.calibrations;
  const currentPanoVideoId = selectedSpecsImage ? panoVideoIdForImage(selectedSpecsImage, detail) : "";
  const assignedPanoCalibrationId = currentPanoVideoId
    ? currentPanoCalibrationState.assignments[currentPanoVideoId] || ""
    : "";
  const defaultPanoCalibrationId = currentPanoCalibrationState.defaultCalibrationId || "";
  const legacyPanoCalibrationAnnotations = specsImageAnnotations.filter(isPanoCalibrationAnnotation);
  const panoDistanceMeasurements = specsImageAnnotations.filter(isPanoDistanceMeasurementAnnotation);
  const selectedPanoCalibrationProfile = panoCalibrationProfiles.find(
    (profile) => String(profile.id) === String(selectedPanoCalibrationId)
  ) || panoCalibrationProfiles.find(
    (profile) => String(profile.id) === String(assignedPanoCalibrationId)
  ) || panoCalibrationProfiles.find(
    (profile) => String(profile.id) === String(defaultPanoCalibrationId)
  ) || null;
  const legacySelectedPanoCalibration = legacyPanoCalibrationAnnotations.find(
    (annotation) => String(annotation.id) === String(selectedPanoCalibrationId)
  ) || legacyPanoCalibrationAnnotations[legacyPanoCalibrationAnnotations.length - 1] || null;
  const selectedPanoCalibration = selectedPanoCalibrationProfile || legacySelectedPanoCalibration;
  const selectedPanoViewCalibration = useMemo(() => {
    return {
      viewHeadingOffsetDeg: panoViewHeadingOffsetDeg,
      viewFovDeg: String(panoViewFovDeg || "").trim() ? panoViewFovDeg : null,
      viewRangeMeters: String(panoViewRangeMeters || "").trim() ? panoViewRangeMeters : null
    };
  }, [
    selectedPanoCalibrationProfile,
    panoViewHeadingOffsetDeg,
    panoViewFovDeg,
    panoViewRangeMeters
  ]);
  const effectivePanoCalibrationAnnotation = canUsePanoMeasurementTools && selectedPanoCalibrationProfile && isPanoCalibrationPanelOpen && !selectedSpecsImageIsCandidatePreview
    ? panoCalibrationProfileToAnnotation(selectedPanoCalibrationProfile, {
        segmentId: detail && detail.id,
        imageFile: selectedSpecsImage && selectedSpecsImage.file
      })
    : null;
  const effectiveTransientMeasurements = canUsePanoMeasurementTools && isPanoMeasurementPanelOpen && selectedSpecsImage && !selectedSpecsImageIsCandidatePreview
    ? panoTransientMeasurements.filter((measurement) => measurement && measurement.imageFile === selectedSpecsImage.file)
    : [];
  const viewerSpecsImageAnnotations = specsImageAnnotations
    .concat(effectivePanoCalibrationAnnotation ? [effectivePanoCalibrationAnnotation] : [])
    .concat(effectiveTransientMeasurements);
  const panoCalibrationKey = [
    panoCalibrationProfiles.map((profile) => String(profile.id)).join("|"),
    legacyPanoCalibrationAnnotations.map((annotation) => String(annotation.id)).join("|"),
    assignedPanoCalibrationId,
    defaultPanoCalibrationId
  ].join("::");
  useEffect(() => {
    if (!selectedSpecsImage) {
      return;
    }
    const preferredId = assignedPanoCalibrationId || defaultPanoCalibrationId || (
      legacyPanoCalibrationAnnotations.length
        ? String(legacyPanoCalibrationAnnotations[legacyPanoCalibrationAnnotations.length - 1].id)
        : ""
    );
    setSelectedPanoCalibrationId(preferredId);
  }, [selectedSpecsImage && selectedSpecsImage.file, assignedPanoCalibrationId, defaultPanoCalibrationId, panoCalibrationKey]);
  useEffect(() => {
    const viewCalibration = sanitizePanoViewCalibration(currentPanoCalibrationState.viewCalibration);
    setPanoViewHeadingOffsetDeg(String(viewCalibration.headingOffsetDeg));
    setPanoViewFovDeg(Number.isFinite(viewCalibration.fovDeg) ? String(viewCalibration.fovDeg) : "");
    setPanoViewRangeMeters(Number.isFinite(viewCalibration.rangeMeters) ? String(viewCalibration.rangeMeters) : "");
  }, [
    currentPanoCalibrationState.viewCalibration &&
      currentPanoCalibrationState.viewCalibration.headingOffsetDeg,
    currentPanoCalibrationState.viewCalibration &&
      currentPanoCalibrationState.viewCalibration.fovDeg,
    currentPanoCalibrationState.viewCalibration &&
      currentPanoCalibrationState.viewCalibration.rangeMeters
  ]);
  useEffect(() => {
    setSpecsPanoViewState(null);
    setPanoMeasurementMode(null);
    setPanoMeasurementDraftPoints([]);
    setPanoTransientMeasurements([]);
    setIsPanoMeasurementListOpen(false);
    setIsSavingPanoMeasurementEvidence(false);
    setPanoMeasurementTitle("");
    setPanoMeasurementStatus("");
  }, [selectedSpecsImage && selectedSpecsImage.file]);

  const saveNextPanoCalibrationState = async (nextState, successMessage) => {
    if (!canUsePanoMeasurementTools || typeof savePanoCalibrationState !== "function") {
      setPanoMeasurementStatus("Calibration storage is not available");
      return null;
    }
    return savePanoCalibrationState(nextState, successMessage);
  };

  const savePanoViewCalibration = async (reset = false) => {
    if (!canUsePanoMeasurementTools) {
      return null;
    }
    const headingOffsetDeg = reset ? DEFAULT_PANO_HEADING_OFFSET_DEG : Number(panoViewHeadingOffsetDeg || 0);
    const fovText = String(panoViewFovDeg || "").trim();
    const rangeText = String(panoViewRangeMeters || "").trim();
    const fovDeg = reset || !fovText ? null : Number(fovText);
    const rangeMeters = reset || !rangeText ? null : Number(rangeText);
    if (!Number.isFinite(headingOffsetDeg) || headingOffsetDeg < -180 || headingOffsetDeg > 180) {
      setPanoMeasurementStatus("Heading offset must be between -180 and 180 deg");
      return null;
    }
    if (fovDeg !== null && (!Number.isFinite(fovDeg) || fovDeg < 18 || fovDeg > 140)) {
      setPanoMeasurementStatus("FOV must be between 18 and 140 deg");
      return null;
    }
    if (rangeMeters !== null && (!Number.isFinite(rangeMeters) || rangeMeters < 10 || rangeMeters > 300)) {
      setPanoMeasurementStatus("Map range must be between 10 and 300 m");
      return null;
    }
    const updatedAt = new Date().toISOString();
    const result = await saveNextPanoCalibrationState({
      ...currentPanoCalibrationState,
      updatedAt,
      viewCalibration: {
        headingOffsetDeg: reset ? DEFAULT_PANO_HEADING_OFFSET_DEG : headingOffsetDeg,
        fovDeg: reset ? null : fovDeg,
        rangeMeters: reset ? null : rangeMeters
      }
    }, reset ? "Field of view calibration reset." : "Field of view calibration saved.");
    if (result && reset) {
      setPanoViewHeadingOffsetDeg(String(DEFAULT_PANO_HEADING_OFFSET_DEG));
      setPanoViewFovDeg("");
      setPanoViewRangeMeters("");
    }
    return result;
  };

  const startPanoCalibration = () => {
    if (!canUsePanoMeasurementTools || !selectedSpecsImage || typeof savePanoCalibrationState !== "function") {
      return;
    }
    setSpecsFieldEvidenceTarget(null);
    setPanoMeasurementDraftPoints([]);
    setPanoTransientMeasurements([]);
    setIsPanoMeasurementListOpen(false);
    setPanoMeasurementStatus("");
    setIsPanoCalibrationPanelOpen(true);
    setIsPanoMeasurementPanelOpen(false);
    setPanoMeasurementMode((current) => current === "calibration" ? null : "calibration");
  };

  const startPanoMeasurement = () => {
    if (!canUsePanoMeasurementTools) {
      return;
    }
    if (!selectedSpecsImage || !selectedPanoCalibration) {
      setIsPanoMeasurementPanelOpen(true);
      setIsPanoCalibrationPanelOpen(false);
      setIsPanoMeasurementListOpen(false);
      setPanoTransientMeasurements([]);
      setPanoMeasurementStatus("Calibration required");
      return;
    }
    setSpecsFieldEvidenceTarget(null);
    setPanoMeasurementDraftPoints([]);
    setPanoMeasurementStatus("");
    setIsPanoMeasurementPanelOpen(true);
    setIsPanoCalibrationPanelOpen(false);
    setIsPanoMeasurementListOpen(false);
    setPanoMeasurementMode("measurement");
  };

  const cancelPanoMeasurement = () => {
    setPanoMeasurementMode(null);
    setPanoMeasurementDraftPoints([]);
    setPanoMeasurementStatus("");
  };

  const updatePanoMeasurementTitle = (value) => {
    const nextTitle = String(value || "").slice(0, 40);
    setPanoMeasurementTitle(nextTitle);
    setPanoTransientMeasurements((current) => current.map((measurement) => {
      if (!measurement || panoAnnotationMeasurementMeta(measurement).source !== "pano-measurement-preview") {
        return measurement;
      }
      return {
        ...measurement,
        label: nextTitle,
        meta: {
          ...(measurement.meta || {}),
          measurementTitle: nextTitle
        }
      };
    }));
  };

  const closePanoMeasurementPanel = () => {
    cancelPanoMeasurement();
    setPanoTransientMeasurements([]);
    setIsPanoMeasurementListOpen(false);
    if (typeof setActivePanoAnnotationId === "function") {
      setActivePanoAnnotationId(null);
    }
    setIsPanoMeasurementPanelOpen(false);
  };

  const togglePanoMeasurementPanel = () => {
    if (!canUsePanoMeasurementTools) {
      closePanoMeasurementPanel();
      return;
    }
    if (isPanoMeasurementPanelOpen) {
      closePanoMeasurementPanel();
      return;
    }
    if (panoMeasurementMode === "calibration") {
      cancelPanoMeasurement();
    }
    startPanoMeasurement();
  };

  const togglePanoCalibrationPanel = () => {
    if (!canUsePanoMeasurementTools) {
      cancelPanoMeasurement();
      setIsPanoCalibrationPanelOpen(false);
      return;
    }
    if (isPanoCalibrationPanelOpen) {
      if (panoMeasurementMode === "calibration") {
        cancelPanoMeasurement();
      }
      setIsPanoCalibrationPanelOpen(false);
      return;
    }
    if (panoMeasurementMode === "measurement") {
      cancelPanoMeasurement();
    }
    setPanoTransientMeasurements([]);
    setIsPanoMeasurementListOpen(false);
    if (typeof setActivePanoAnnotationId === "function") {
      setActivePanoAnnotationId(null);
    }
    setIsPanoMeasurementPanelOpen(false);
    setIsPanoCalibrationPanelOpen(true);
  };

  const savePanoCalibration = async (points) => {
    if (!canUsePanoMeasurementTools) {
      return null;
    }
    const knownMeters = Number(panoCalibrationMeters);
    const angularDistanceRad = panoAngularDistance(points[0], points[1]);
    if (!selectedSpecsImage || !Number.isFinite(knownMeters) || knownMeters <= 0) {
      setPanoMeasurementStatus("Enter a valid calibration length");
      return null;
    }
    if (!Number.isFinite(angularDistanceRad) || angularDistanceRad <= 0) {
      setPanoMeasurementStatus("Calibration points are too close");
      return null;
    }
    const metersPerRadian = knownMeters / angularDistanceRad;
    const calibrationId = makePanoCalibrationClientId();
    const calibrationName = String(panoCalibrationProfileName || "").trim() ||
      `Calibration ${panoCalibrationProfiles.length + 1}`;
    const shouldMakeDefault = makeNewPanoCalibrationDefault || !currentPanoCalibrationState.defaultCalibrationId;
    const nextAssignments = { ...currentPanoCalibrationState.assignments };
    if (assignNewPanoCalibrationToVideo && currentPanoVideoId) {
      nextAssignments[currentPanoVideoId] = calibrationId;
    }
    const nextState = {
      ...currentPanoCalibrationState,
      defaultCalibrationId: shouldMakeDefault
        ? calibrationId
        : currentPanoCalibrationState.defaultCalibrationId,
      assignments: nextAssignments,
      calibrations: currentPanoCalibrationState.calibrations.concat({
        id: calibrationId,
        name: calibrationName,
        knownMeters,
        angularDistanceRad,
        metersPerRadian,
        referenceSegmentId: detail && detail.id,
        referenceImageFile: selectedSpecsImage.file,
        referenceVideoId: currentPanoVideoId,
        note: currentPanoVideoId ? `Captured from ${panoVideoLabel(currentPanoVideoId)}` : "",
        shape: {
          kind: "line",
          points
        }
      })
    };
    const savedState = await saveNextPanoCalibrationState(
      nextState,
      `Saved calibration "${calibrationName}".`
    );
    if (savedState) {
      setSelectedPanoCalibrationId(calibrationId);
      setPanoCalibrationProfileName("");
      setPanoMeasurementDraftPoints([]);
      setPanoMeasurementMode("measurement");
      setPanoMeasurementStatus(`Calibration ${formatPanoCalibrationScale(metersPerRadian)}`);
      if (typeof setActivePanoAnnotationId === "function") {
        setActivePanoAnnotationId(`calibration-profile-${calibrationId}`);
      }
    }
    return savedState;
  };

  const setSelectedPanoCalibrationAsDefault = () => {
    if (!canUsePanoMeasurementTools || !selectedPanoCalibrationProfile) {
      return;
    }
    saveNextPanoCalibrationState(
      {
        ...currentPanoCalibrationState,
        defaultCalibrationId: selectedPanoCalibrationProfile.id
      },
      `Default calibration set to "${selectedPanoCalibrationProfile.name}".`
    );
  };

  const assignSelectedPanoCalibrationToVideo = () => {
    if (!canUsePanoMeasurementTools || !selectedPanoCalibrationProfile || !currentPanoVideoId) {
      return;
    }
    saveNextPanoCalibrationState(
      {
        ...currentPanoCalibrationState,
        assignments: {
          ...currentPanoCalibrationState.assignments,
          [currentPanoVideoId]: selectedPanoCalibrationProfile.id
        }
      },
      `Assigned "${selectedPanoCalibrationProfile.name}" to ${panoVideoLabel(currentPanoVideoId)}.`
    );
  };

  const clearPanoCalibrationForVideo = () => {
    if (!canUsePanoMeasurementTools || !currentPanoVideoId || !currentPanoCalibrationState.assignments[currentPanoVideoId]) {
      return;
    }
    const nextAssignments = { ...currentPanoCalibrationState.assignments };
    delete nextAssignments[currentPanoVideoId];
    saveNextPanoCalibrationState(
      {
        ...currentPanoCalibrationState,
        assignments: nextAssignments
      },
      `Cleared calibration assignment for ${panoVideoLabel(currentPanoVideoId)}.`
    );
  };

  const deleteSelectedPanoCalibrationProfile = () => {
    if (!canUsePanoMeasurementTools || !selectedPanoCalibrationProfile) {
      return;
    }
    if (!confirm(`Delete calibration "${selectedPanoCalibrationProfile.name}"?`)) {
      return;
    }
    const deletedId = String(selectedPanoCalibrationProfile.id);
    const remainingCalibrations = currentPanoCalibrationState.calibrations.filter(
      (profile) => String(profile.id) !== deletedId
    );
    const nextAssignments = {};
    Object.entries(currentPanoCalibrationState.assignments).forEach(([videoId, calibrationId]) => {
      if (String(calibrationId) !== deletedId) {
        nextAssignments[videoId] = calibrationId;
      }
    });
    const nextDefaultId = String(currentPanoCalibrationState.defaultCalibrationId) === deletedId
      ? (remainingCalibrations[0] ? remainingCalibrations[0].id : "")
      : currentPanoCalibrationState.defaultCalibrationId;
    saveNextPanoCalibrationState(
      {
        ...currentPanoCalibrationState,
        calibrations: remainingCalibrations,
        assignments: nextAssignments,
        defaultCalibrationId: nextDefaultId
      },
      `Deleted calibration "${selectedPanoCalibrationProfile.name}".`
    );
  };

  const savePanoMeasurement = async (points) => {
    if (!canUsePanoMeasurementTools) {
      return null;
    }
    const calibration = selectedPanoCalibration;
    const metersPerRadian = panoCalibrationScale(calibration);
    const angularDistanceRad = panoAngularDistance(points[0], points[1]);
    if (!selectedSpecsImage || !calibration || !metersPerRadian) {
      setPanoMeasurementStatus("Calibration required");
      return null;
    }
    if (!Number.isFinite(angularDistanceRad) || angularDistanceRad <= 0) {
      setPanoMeasurementStatus("Measurement points are too close");
      return null;
    }
    const distanceMeters = angularDistanceRad * metersPerRadian;
    const title = String(panoMeasurementTitle || "").trim();
    const preview = {
      id: `measurement-preview-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`,
      segmentId: detail && detail.id,
      imageFile: selectedSpecsImage.file,
      label: title,
      type: "measurement",
      note: `Measured with ${calibration.name || calibration.label || "calibration"}`,
      shape: {
        kind: "line",
        points
      },
      meta: {
        source: "pano-measurement-preview",
        measurementRole: "measurement",
        measurementTitle: title,
        distanceMeters,
        angularDistanceRad,
        calibrationId: calibration.id,
        calibrationProfileId: selectedPanoCalibrationProfile ? selectedPanoCalibrationProfile.id : "",
        calibrationLabel: calibration.name || calibration.label || "",
        calibrationKnownMeters: panoCalibrationMeta(calibration).knownMeters,
        metersPerRadian
      }
    };
    setPanoTransientMeasurements((current) => current.concat(preview));
    setPanoMeasurementDraftPoints([]);
    setPanoMeasurementMode(null);
    setPanoMeasurementStatus(formatPanoMeters(distanceMeters));
    if (typeof setActivePanoAnnotationId === "function") {
      setActivePanoAnnotationId(preview.id);
    }
    return preview;
  };

  const savePanoMeasurementListAsEvidence = async () => {
    if (!canUsePanoMeasurementTools) {
      return;
    }
    const measurements = effectiveTransientMeasurements.filter((measurement) => (
      measurement &&
      measurement.shape &&
      measurement.shape.kind === "line" &&
      Array.isArray(measurement.shape.points) &&
      measurement.shape.points.length >= 2
    ));
    if (!measurements.length || !selectedSpecsImage || !createPanoAnnotation || isSavingPanoMeasurementEvidence) {
      return;
    }
    setIsSavingPanoMeasurementEvidence(true);
    setPanoMeasurementStatus("Saving evidence...");
    const groupId = `measurement-evidence-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`;
    const title = String(panoMeasurementTitle || "").trim();
    const groupLabel = title || `Distance set (${measurements.length})`;
    const summary = measurements
      .map((measurement, index) => {
        const meta = panoAnnotationMeasurementMeta(measurement);
        return `${index + 1}: ${formatPanoMeters(meta.distanceMeters)}`;
      })
      .join(", ");
    const saved = [];
    for (let index = 0; index < measurements.length; index += 1) {
      const measurement = measurements[index];
      const meta = panoAnnotationMeasurementMeta(measurement);
      const measurementTitle = String(meta.measurementTitle || title || "").trim();
      const annotation = await createPanoAnnotation({
        imageFile: selectedSpecsImage.file,
        label: measurementTitle || `${formatPanoMeters(meta.distanceMeters)} ${index + 1}/${measurements.length}`,
        type: "measurement",
        note: summary,
        shape: measurement.shape,
        meta: {
          ...meta,
          source: "pano-measurement-evidence",
          measurementRole: "measurement",
          measurementTitle,
          measurementGroupId: groupId,
          measurementGroupLabel: groupLabel,
          measurementGroupIndex: index + 1,
          measurementGroupCount: measurements.length,
          temporaryMeasurementId: measurement.id || ""
        }
      });
      if (annotation) {
        saved.push({ annotation, temporaryId: measurement.id || "" });
      }
    }
    setIsSavingPanoMeasurementEvidence(false);
    if (!saved.length) {
      setPanoMeasurementStatus("Evidence was not saved");
      return;
    }
    const savedTemporaryIds = new Set(saved.map((item) => String(item.temporaryId || "")).filter(Boolean));
    setPanoTransientMeasurements((current) => current.filter((measurement) => (
      !savedTemporaryIds.has(String(measurement && measurement.id ? measurement.id : ""))
    )));
    setPanoMeasurementDraftPoints([]);
    setPanoMeasurementMode(null);
    setIsPanoMeasurementListOpen(false);
    setPanoMeasurementStatus(`Saved ${saved.length}/${measurements.length} evidence`);
    if (typeof setActivePanoAnnotationId === "function") {
      setActivePanoAnnotationId(saved[saved.length - 1].annotation.id);
    }
  };

  const placePanoMeasurementPoint = (position) => {
    if (!canUsePanoMeasurementTools || !panoMeasurementMode || !selectedSpecsImage) {
      return;
    }
    if (panoMeasurementMode === "calibration" && typeof savePanoCalibrationState !== "function") {
      return;
    }
    const nextPoints = panoMeasurementDraftPoints.concat(position);
    if (nextPoints.length < 2) {
      setPanoMeasurementDraftPoints(nextPoints);
      setPanoMeasurementStatus("1/2 points");
      return;
    }
    if (panoMeasurementMode === "calibration") {
      savePanoCalibration(nextPoints);
    } else {
      savePanoMeasurement(nextPoints);
    }
  };

  const specsSamSelectionLocked = specsEvidenceShapeKind === "sam" && (specsSamBusy || Boolean(specsSamPendingAnnotation));
  const specsMeasurementPlacementMode = canUsePanoMeasurementTools && Boolean(panoMeasurementMode) && !specsFieldEvidenceTarget;
  const specsAnnotationPlacementMode = (Boolean(specsFieldEvidenceTarget) && !specsSamSelectionLocked) || specsMeasurementPlacementMode;
  const specsFieldEvidenceDraft = specsFieldEvidenceTarget && specsEvidenceDraftPoints.length
    ? {
        kind: specsEvidenceShapeKind,
        points: specsEvidenceDraftPoints
      }
    : null;
  const specsMeasurementDraft = specsMeasurementPlacementMode && panoMeasurementDraftPoints.length
    ? {
        kind: "line",
        points: panoMeasurementDraftPoints
      }
    : null;
  const specsActiveDraft = specsFieldEvidenceDraft || specsMeasurementDraft;
  const specsMapPanoAnnotations = viewerSpecsImageAnnotations.concat(
    specsActiveDraft && selectedSpecsImage
      ? [{
          id: "__pano-map-draft",
          segmentId: detail && detail.id,
          imageFile: selectedSpecsImage.file,
          label: "Drawing",
          type: "other",
          shape: specsActiveDraft,
          meta: { source: "pano-draft" }
        }]
      : []
  );
  const placeSpecsPanoAnnotation = (position) => {
    if (specsFieldEvidenceTarget) {
      placeSpecsFieldEvidence(position);
      return;
    }
    if (canUsePanoMeasurementTools && panoMeasurementMode) {
      placePanoMeasurementPoint(position);
    }
  };
  const specMapPanoStyle = {
    "--spec-inner-map-column": `minmax(${SPECS_MAP_PANO_MIN_WIDTHS.map}px, calc(${specMapPanoWidths.map}% - ${(SPECS_MAP_PANO_HANDLE_WIDTH * specMapPanoWidths.map) / 100}px))`,
    "--spec-pano-column": `minmax(${SPECS_MAP_PANO_MIN_WIDTHS.pano}px, calc(${specMapPanoWidths.pano}% - ${(SPECS_MAP_PANO_HANDLE_WIDTH * specMapPanoWidths.pano) / 100}px))`
  };
  useEffect(() => {
    if (!showSpecsPano) {
      return undefined;
    }
    const frame = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    return () => cancelAnimationFrame(frame);
  }, [showSpecsPano, specMapViewMode]);

  const specMapPanoClassName = [
    "segment-map-pano-layout",
    showSpecsPano ? "is-pano-open" : "",
    showSpecsPano ? `map-mode-${specMapViewMode}` : "",
    showSpecsPano && showSpecsImageStrip && selectedSpecsImage ? "has-image-strip" : ""
  ].filter(Boolean).join(" ");
  const iconSize = Math.max(SPECS_ICON_SIZE_MIN, Math.min(SPECS_ICON_SIZE_MAX, Number(specIconSize) || SPECS_ICON_SIZE_DEFAULT));
  const iconSizingStyle = {
    "--spec-icon-grid-min": `${Math.round(150 * iconSize)}px`,
    "--spec-icon-card-min-height": `${Math.round(184 * iconSize)}px`,
    "--spec-icon-frame-height": `${Math.round(82 * iconSize)}px`,
    "--spec-icon-frame-width": `${Math.round(126 * iconSize)}px`,
    "--spec-icon-card-padding": `${Math.max(4, Math.round(6 * iconSize))}px`,
    "--spec-icon-card-gap": `${Math.max(3, Math.round(5 * iconSize))}px`,
    "--spec-icon-title-size": `${Math.max(10, 11 * iconSize).toFixed(1)}px`,
    "--spec-icon-title-height": `${Math.max(22, 26 * iconSize).toFixed(1)}px`,
    "--spec-icon-value-size": `${Math.max(9, 10 * iconSize).toFixed(1)}px`,
    "--spec-icon-source-size": `${Math.max(8, 9 * iconSize).toFixed(1)}px`
  };
  return (
    <main
      ref={specsWorkspaceRef}
      className={`workspace-specs ${isSegmentListOpen ? "" : "is-segment-list-closed"} ${isSegmentInfoOpen ? "" : "is-segment-info-closed"} ${resizingSegmentList ? "is-resizing-segment-list" : ""} ${resizingSpecs ? "is-resizing" : ""} ${resizingSpecsPano ? "is-resizing-pano" : ""}`}
      style={specsWorkspaceStyle}
    >
      {isSegmentListOpen && (
        <SpecsSegmentListPanel
          filteredSegments={filteredSegments}
          selectedId={selectedId}
          search={search}
          selectSegment={selectSegment}
          onClose={() => setIsSegmentListOpen(false)}
          columns={segmentGridColumns}
          gridTemplateColumns={segmentGridTemplateColumns}
          analysisResults={analysisResults}
          limitMapToList={specsMapLimitToList}
          setLimitMapToList={setSpecsMapLimitToList}
          onVisibleSegmentIdsChange={setSpecsListedSegmentIds}
          fieldValueFilterEnabled={specsFieldValueFilterEnabled}
          setFieldValueFilterEnabled={setSpecsFieldValueFilterEnabled}
          fieldValueFilters={specsFieldValueFilters}
          clearFieldValueFilters={clearSpecsFieldValueFilters}
          selectedAgentGroups={selectedAgentGroups}
          allAgentGroups={allSpecsAgentGroups}
          runningSegmentAgentId={specsRunningSegmentAgentId}
          onRunSegmentAgents={runSpecsSegmentAgents}
          batchJob={batchJob}
          externalAdvancedFilterRequest={externalAdvancedFilterRequest}
          onFocusFieldCodesChange={updateSpecsFocusedFieldCodes}
          canEdit={canEditSpecsFields}
          canViewStarRanking={canViewStarRanking}
        />
      )}
      {isSegmentListOpen && (
        <div
          className="specs-segment-list-resize-handle"
          role="separator"
          aria-label="Resize segment grid and workspace"
          aria-orientation="vertical"
          tabIndex="0"
          title="Drag to resize segment grid"
          onPointerDown={startSpecSegmentListResize}
          onKeyDown={nudgeSpecSegmentListResize}
        >
          <span></span>
        </div>
      )}

      <section className="segment-spec-panel">
        <div
          ref={specLayoutRef}
          className="segment-spec-layout"
          style={specsLayoutStyle}
        >
          {isSegmentInfoOpen && (
            <SpecsSegmentInfoPanel
              activeGroup={activeGroup}
              setActiveGroup={setActiveGroup}
              groupNames={groupNames}
              groupedItems={groupedItems}
              allGroupedItems={allGroupedItems}
              aiInsightItems={iconItems}
              neighborInsight={specsNeighborInsight}
              iconSize={iconSize}
              setSpecIconSize={setSpecIconSize}
              iconSizingStyle={iconSizingStyle}
              fieldViewMode={specsFieldViewMode}
              setFieldViewMode={setSpecsFieldViewMode}
              fieldViewCounts={specsFieldViewCounts}
              showOldValues={showSpecsOldValues}
              setShowOldValues={setShowSpecsOldValues}
              fieldValueFilterEnabled={specsFieldValueFilterEnabled}
              fieldValueFilterKeys={specsFieldValueFilterKeys}
              focusedFieldCodes={specsFocusedFieldCodes}
              onToggleFieldValueFilter={toggleSpecsFieldValueFilter}
              metadata={metadata}
              starRatings={starRatings}
              canViewStarRanking={canViewStarRanking}
              evidenceItems={specsEvidenceItems}
              gisAssets={segmentGisAssets}
              gisSummary={segmentGisSummary}
              gisLoading={segmentGisLoading}
              gisError={segmentGisError}
              gisMapVisible={segmentGisMapVisible}
              onToggleGisMapVisible={setSegmentGisMapVisible}
              gisHiddenLayerKeys={segmentGisHiddenLayerKeys}
              onToggleGisLayerVisible={toggleSegmentGisLayerVisibility}
              gisLayerSettings={normalizedGisLayerSettings}
              gisLayerOptions={gisLayerOptions}
              selectedSegment={specsEvidenceSegment}
              allSegments={segmentIndex}
              searchSegments={filteredSegments}
              gridSegmentIds={specsListedSegmentIds}
              analysisResults={analysisResults}
              segmentImages={detail && Array.isArray(detail.images) ? detail.images : []}
              selectedImageFile={detail && detail.images && detail.images[specsCurrentImage] ? detail.images[specsCurrentImage].file : ""}
              onOpenEvidence={openSpecsEvidencePreview}
              onOpenFieldSourceImage={openSpecsFieldSourceImage}
              onClose={() => setIsSegmentInfoOpen(false)}
              editMode={isSpecsFieldEditMode}
              setEditMode={setIsSpecsFieldEditMode}
              canEdit={canEditSpecsFields}
              onSaveField={saveSpecsFieldValue}
              onAddFieldEvidence={startSpecsFieldEvidence}
              onOpenAnnotationEvidence={openSpecsAnnotationEvidence}
              fieldPrompts={fieldPromptMap}
              fieldPromptBusy={fieldPromptBusy}
              fieldPromptRunningField={fieldPromptRunningField}
              onFieldPromptsUpdated={setFieldPromptMap}
              onSaveFieldPrompt={saveSpecsFieldPrompt}
              onRunFieldPrompt={runSpecsFieldPrompt}
              onRunFieldPromptBatch={runSpecsFieldPromptBatch}
              agentRunnerModel={agentRunnerModel}
              agentRunnerReasoningEffort={agentRunnerReasoningEffort}
              authUser={authUser}
              logs={logs}
              loadLogs={loadLogs}
              approvalState={segmentApproval}
              approveBusy={segmentApproveBusy}
              onSetApprovalState={canEditSpecsFields ? setSegmentApproval : null}
              batchExcluded={segmentIsBatchExcluded}
              batchExclusionReason={segmentBatchExclusionReason}
              batchExcludeBusy={segmentBatchExcludeBusy}
              onSetBatchExcluded={canEditSpecsFields ? setSegmentBatchExclusion : null}
            />
          )}

          {isSegmentInfoOpen && (
            <div
              className="segment-spec-resize-handle"
              role="separator"
              aria-label="Resize segment info and map panels"
              aria-orientation="vertical"
              tabIndex="0"
              title="Drag to resize panels"
              onPointerDown={startSpecResize}
              onKeyDown={nudgeSpecResize}
            >
              <span></span>
            </div>
          )}

          <aside className="segment-spec-map">
            <div
              ref={specMapPanoRef}
              className={specMapPanoClassName}
              style={specMapPanoStyle}
            >
              {showSpecsDock && (
                <div className="segment-map-mode-tabs" role="toolbar" aria-label="Map and panorama layout">
                  <div className="segment-toolbar-dropdown">
                    <button
                      className="icon-action segment-map-mode-button active"
                      type="button"
                      title={`Layout: ${activeSpecMapViewMode.label}`}
                      aria-label={`Layout: ${activeSpecMapViewMode.label}`}
                      aria-expanded={isSpecsLayoutMenuOpen}
                      onClick={() => {
                        setIsSpecsLayoutMenuOpen((current) => !current);
                        setIsSpecsBaseLayerMenuOpen(false);
                      }}
                    >
                      {icon(activeSpecMapViewMode.icon)}
                      {icon("chevron-down")}
                    </button>
                    {isSpecsLayoutMenuOpen && (
                      <div className="segment-toolbar-menu" role="menu">
                        {SPECS_MAP_VIEW_MODES.map(({ mode, icon: iconName, label }) => (
                          <button
                            key={mode}
                            className={specDockActiveMode === mode ? "active" : ""}
                            type="button"
                            role="menuitem"
                            onClick={() => changeSpecsMapViewMode(mode)}
                          >
                            {icon(iconName)}
                            <span>{label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    className={`icon-action segment-map-mode-button is-street-view-toggle ${specDockActiveMode === "mini" ? "active" : ""}`}
                    type="button"
                    title="Street view"
                    aria-label="Street view"
                    aria-pressed={specDockActiveMode === "mini"}
                    onClick={() => {
                      setIsSpecsLayoutMenuOpen(false);
                      setIsSpecsBaseLayerMenuOpen(false);
                      changeSpecsMapViewMode("mini");
                    }}
                  >
                    {icon("person-standing")}
                  </button>
                  <div className="segment-toolbar-dropdown">
                    <button
                      className="icon-action segment-map-mode-button"
                      type="button"
                      title={`Base map: ${activeSpecsBaseLayer.label}`}
                      aria-label={`Base map: ${activeSpecsBaseLayer.label}`}
                      aria-expanded={isSpecsBaseLayerMenuOpen}
                      onClick={() => {
                        setIsSpecsBaseLayerMenuOpen((current) => !current);
                        setIsSpecsLayoutMenuOpen(false);
                      }}
                    >
                      {icon("layers")}
                      {icon("chevron-down")}
                    </button>
                    {isSpecsBaseLayerMenuOpen && (
                      <div className="segment-toolbar-menu segment-toolbar-menu--right" role="menu">
                        {MAP_BASE_LAYER_DEFS.map((baseLayer) => (
                          <button
                            key={baseLayer.id}
                            className={activeSpecsBaseLayer.id === baseLayer.id ? "active" : ""}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setSpecsMapBaseLayerId(baseLayer.id);
                              setIsSpecsBaseLayerMenuOpen(false);
                            }}
                          >
                            {icon(baseLayer.id === "googleSatellite" || baseLayer.id === "vicAerial2020" ? "satellite" : "map")}
                            <span>{baseLayer.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {canUsePanoMeasurementTools && (
                    <button
                      className={`icon-action segment-map-mode-button is-map-measure-toggle ${specsMapMeasureActive ? "active" : ""}`}
                      type="button"
                      title={specsMapMeasureActive ? "Stop map measurement" : "Measure distance on map"}
                      aria-label={specsMapMeasureActive ? "Stop map measurement" : "Measure distance on map"}
                      aria-pressed={specsMapMeasureActive}
                      onClick={() => {
                        setSpecsMapMeasureActive((current) => {
                          const next = !current;
                          if (next) {
                            setSpecsMapArcActive(false);
                          }
                          return next;
                        });
                        setIsSpecsLayoutMenuOpen(false);
                        setIsSpecsBaseLayerMenuOpen(false);
                      }}
                    >
                      {icon("ruler")}
                    </button>
                  )}
                  {canUsePanoMeasurementTools && (
                    <>
                      <button
                        className={`icon-action segment-map-mode-button is-map-arc-toggle ${specsMapArcActive ? "active" : ""}`}
                        type="button"
                        title={specsMapArcActive ? "Stop arc radius tool" : "Arc radius from three map points"}
                        aria-label={specsMapArcActive ? "Stop arc radius tool" : "Arc radius from three map points"}
                        aria-pressed={specsMapArcActive}
                        onClick={() => {
                          setSpecsMapArcActive((current) => {
                            const next = !current;
                            if (next) {
                              setSpecsMapMeasureActive(false);
                            }
                            return next;
                          });
                          setIsSpecsLayoutMenuOpen(false);
                          setIsSpecsBaseLayerMenuOpen(false);
                        }}
                      >
                        {icon("circle-dot")}
                      </button>
                      <button
                        className="icon-action segment-map-mode-button is-map-arc-clear"
                        type="button"
                        title="Clear arc radius drawing"
                        aria-label="Clear arc radius drawing"
                        onClick={() => {
                          setSpecsMapArcClearToken((current) => current + 1);
                          setIsSpecsLayoutMenuOpen(false);
                          setIsSpecsBaseLayerMenuOpen(false);
                        }}
                      >
                        {icon("eraser")}
                      </button>
                    </>
                  )}
                  <button
                    className={`icon-action segment-map-mode-button is-tooltip-toggle ${specsMapTooltipsEnabled ? "active" : ""}`}
                    type="button"
                    title={specsMapTooltipsEnabled ? "Hide map hover tooltips" : "Show map hover tooltips"}
                    aria-label={specsMapTooltipsEnabled ? "Hide map hover tooltips" : "Show map hover tooltips"}
                    aria-pressed={specsMapTooltipsEnabled}
                    onClick={() => {
                      setSpecsMapTooltipsEnabled((current) => !current);
                      setIsSpecsLayoutMenuOpen(false);
                      setIsSpecsBaseLayerMenuOpen(false);
                    }}
                  >
                    {icon("message-square-text")}
                  </button>
                  {showSpecsPano && (
                    <>
                      {canUsePanoMeasurementTools && (
                        <>
                          <button
                            className={`icon-action segment-map-mode-button is-measurement-toggle ${isPanoMeasurementPanelOpen ? "active" : ""}`}
                            type="button"
                            title={isPanoMeasurementPanelOpen ? "Hide measurement" : "Measurement"}
                            aria-label={isPanoMeasurementPanelOpen ? "Hide measurement" : "Measurement"}
                            aria-pressed={isPanoMeasurementPanelOpen}
                            onClick={() => {
                              setIsSpecsLayoutMenuOpen(false);
                              setIsSpecsBaseLayerMenuOpen(false);
                              togglePanoMeasurementPanel();
                            }}
                          >
                            {icon("ruler")}
                          </button>
                          <button
                            className={`icon-action segment-map-mode-button is-calibration-toggle ${isPanoCalibrationPanelOpen ? "active" : ""}`}
                            type="button"
                            title={isPanoCalibrationPanelOpen ? "Hide calibration settings" : "Calibration settings"}
                            aria-label={isPanoCalibrationPanelOpen ? "Hide calibration settings" : "Calibration settings"}
                            aria-pressed={isPanoCalibrationPanelOpen}
                            onClick={() => {
                              setIsSpecsLayoutMenuOpen(false);
                              setIsSpecsBaseLayerMenuOpen(false);
                              togglePanoCalibrationPanel();
                            }}
                          >
                            {icon("settings-2")}
                          </button>
                        </>
                      )}
                      <button
                        className={`icon-action segment-map-mode-button is-strip-toggle ${showSpecsImageStrip ? "active" : ""}`}
                        type="button"
                        title={showSpecsImageStrip ? "Hide image thumbnails" : "Show image thumbnails"}
                        aria-label={showSpecsImageStrip ? "Hide image thumbnails" : "Show image thumbnails"}
                        aria-pressed={showSpecsImageStrip}
                        onClick={toggleSpecsImageStrip}
                      >
                        {icon(showSpecsImageStrip ? "list" : "image-off")}
                      </button>
                      <button
                        className="icon-action segment-map-mode-button is-close"
                        type="button"
                        title="Close panorama viewer"
                        aria-label="Close panorama viewer"
                        onClick={() => {
                          setIsSpecsLayoutMenuOpen(false);
                          setIsSpecsBaseLayerMenuOpen(false);
                          closeSpecsPano();
                        }}
                      >
                        {icon("x")}
                      </button>
                    </>
                  )}
                </div>
              )}
              <div className="segment-spec-map-stage">
                <MapPanel
                  elementId="segmentSpecsMap"
                  segments={segments}
                  selectedId={selectedId}
                  selectSegment={selectSegment}
                  detail={detail}
                  allSpeedRecords={allSpeedRecords}
                  currentImage={specsCurrentImage}
                  setCurrentImage={openSpecsPanoImage}
                  segmentFilterIds={specsMapSegmentFilterIds}
                  showSegmentTooltips={specsMapTooltipsEnabled}
                  panoMapAnnotations={specsMapPanoAnnotations}
                  activePanoAnnotationId={activePanoAnnotationId}
                  panoMapCalibration={selectedPanoCalibration}
                  panoViewState={showSpecsPano ? specsPanoViewState : null}
                  panoViewCalibration={selectedPanoViewCalibration}
                  baseLayerId={specsMapBaseLayerId}
                  onBaseLayerChange={setSpecsMapBaseLayerId}
                  showBaseControl={!showSpecsDock}
                  mapMeasureActive={specsMapMeasureActive}
                  onMapMeasureActiveChange={setSpecsMapMeasureActive}
                  showMapMeasureControl={canUsePanoMeasurementTools && !showSpecsDock}
                  mapArcMeasureActive={specsMapArcActive}
                  onMapArcMeasureActiveChange={setSpecsMapArcActive}
                  mapArcMeasureClearToken={specsMapArcClearToken}
                  showMapArcMeasureControl={canUsePanoMeasurementTools && !showSpecsDock}
                  panoCandidatePoints={panoCandidatePanelOpen ? panoCandidatePoints : []}
                  selectedPanoCandidateIds={panoCandidateSelectedIds}
                  onTogglePanoCandidate={togglePanoCandidate}
                  gisAssets={segmentGisMapVisible ? visibleSegmentGisAssets : []}
                />
                {canEditSpecsFields && detail && (
                  <section className={`pano-candidate-panel ${panoCandidatePanelOpen ? "active" : ""}`}>
                    <header>
                      <div>
                        <strong>{icon("map-pin-plus")} Pano points</strong>
                        <span>{panoCandidatePoints.length} found - {panoCandidateSelectedIds.length}/{MIN_PANO_CANDIDATE_SELECTION}-{MAX_PANO_CANDIDATE_SELECTION} selected</span>
                      </div>
                      <button
                        className="icon-action"
                        type="button"
                        title={panoCandidatePanelOpen ? "Hide pano point picker" : "Show pano point picker"}
                        onClick={() => setPanoCandidatePanelOpen((current) => !current)}
                      >
                        {icon(panoCandidatePanelOpen ? "chevron-up" : "chevron-down")}
                      </button>
                    </header>
                    {panoCandidatePanelOpen && (
                      <>
                        <div className="pano-candidate-controls">
                          <label>
                            <span>Buffer</span>
                            <input
                              type="number"
                              step="0.000001"
                              min="0.0000001"
                              max="0.001"
                              value={panoCandidateBuffer}
                              onChange={(event) => setPanoCandidateBuffer(event.target.value)}
                            />
                          </label>
                          <select
                            value={panoCandidateSaveMode}
                            onChange={(event) => setPanoCandidateSaveMode(event.target.value)}
                            aria-label="Save mode"
                          >
                            <option value="merge">Merge</option>
                            <option value="replace">Replace</option>
                          </select>
                        </div>
                        <div className="pano-candidate-actions">
                          <button type="button" onClick={loadPanoCandidatePoints} disabled={panoCandidateLoading || panoCandidateSaving}>
                            {icon(panoCandidateLoading ? "loader-circle" : "database")} {panoCandidateLoading ? "Loading" : "Find"}
                          </button>
                          <button
                            type="button"
                            onClick={savePanoCandidateSelection}
                            disabled={panoCandidateSaving || selectedPanoCandidates.length < MIN_PANO_CANDIDATE_SELECTION}
                            title={selectedPanoCandidates.length >= MIN_PANO_CANDIDATE_SELECTION ? "Save selected pano points" : "Select 5 to 10 pano points on the map"}
                          >
                            {icon(panoCandidateSaving ? "loader-circle" : "save")} {panoCandidateSaving ? "Saving" : "Save"}
                          </button>
                          <button type="button" onClick={clearPanoCandidates} disabled={panoCandidateLoading || panoCandidateSaving || (!panoCandidatePoints.length && !panoCandidateSelectedIds.length)}>
                            {icon("x")} Clear
                          </button>
                        </div>
                        {selectedPanoCandidates.length > 0 && (
                          <div className="pano-candidate-selected-list">
                            {selectedPanoCandidates.slice(0, MAX_PANO_CANDIDATE_SELECTION).map((candidate) => (
                              <button
                                key={panoCandidateId(candidate)}
                                type="button"
                                title="Remove selected pano point"
                                onClick={() => togglePanoCandidate(candidate)}
                              >
                                {icon("circle-x")}
                                <span>{panoCandidateFileLabel(candidate) || panoCandidateId(candidate)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </section>
                )}
              </div>
              {showSpecsPano && (
                <>
                  <div
                    className="segment-pano-resize-handle"
                    role="separator"
                    aria-label="Resize map and panorama panels"
                    aria-orientation="vertical"
                    tabIndex="0"
                    title="Drag to resize panels"
                    onPointerDown={startSpecMapPanoResize}
                    onKeyDown={nudgeSpecMapPanoResize}
                  >
                    <span></span>
                  </div>
                  <section className="segment-spec-pano-panel">
                    {specsFieldEvidenceTarget && (
                      <div className="segment-field-evidence-capture">
                        <header>
                          <div>
                            <strong>{specsFieldEvidenceTarget.field.caption || "Field"} Evidence</strong>
                            <span>{specsFieldEvidenceTarget.field.caption}</span>
                          </div>
                          <button
                            className="icon-action"
                            type="button"
                            title="Stop adding field evidence"
                            onClick={clearSpecsFieldEvidenceDraft}
                          >
                            {icon("x")}
                          </button>
                        </header>
                        <label>
                          <span>Draw</span>
                          <select
                            value={specsEvidenceShapeKind}
                            disabled={specsSamBusy || Boolean(specsSamPendingAnnotation)}
                            onChange={(event) => {
                              const nextShapeKind = event.target.value;
                              setSpecsEvidenceShapeKind(nextShapeKind);
                              setSpecsEvidenceDraftPoints([]);
                              setSpecsSamPendingAnnotation(null);
                              if (nextShapeKind !== "line") {
                                setSpecsEvidenceShowMeasurement(false);
                              }
                            }}
                          >
                            {MANUAL_PANO_ANNOTATION_SHAPES.map((shape) => (
                              <option key={shape.value} value={shape.value}>{shape.label}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Comment</span>
                          <textarea
                            value={specsEvidenceNote}
                            disabled={specsSamBusy || Boolean(specsSamPendingAnnotation)}
                            placeholder="Why this field value is supported"
                            onChange={(event) => setSpecsEvidenceNote(event.target.value)}
                          />
                        </label>
                        {specsEvidenceShapeKind === "line" && (
                          <label className="segment-field-evidence-check">
                            <input
                              type="checkbox"
                              checked={specsEvidenceShowMeasurement}
                              disabled={!selectedPanoCalibration || specsSamBusy || Boolean(specsSamPendingAnnotation)}
                              onChange={(event) => setSpecsEvidenceShowMeasurement(event.target.checked)}
                            />
                            <span>
                              Show and save length
                              {!selectedPanoCalibration ? " (calibration required)" : ""}
                            </span>
                          </label>
                        )}
                        <p>
                          {specsEvidenceShapeKind === "sam"
                            ? specsSamPendingAnnotation
                              ? "Object selected. Save this evidence or cancel it."
                              : specsSamBusy
                              ? "SAM is selecting the object."
                              : "Click once on the object in the panorama."
                            : specsEvidenceShapeKind === "point"
                            ? "Click the panorama to save a point evidence."
                            : `Click ${panoAnnotationShapeLabel(specsEvidenceShapeKind).toLowerCase()} vertices, then save.`}
                        </p>
                        {specsEvidenceShapeKind === "sam" && (
                          <div className="segment-field-evidence-actions">
                            <span>
                              {specsSamPendingAnnotation
                                ? "Object selected"
                                : specsSamBusy
                                ? "Selecting object..."
                                : "Waiting for one panorama click"}
                            </span>
                            <button
                              className="secondary-button compact"
                              type="button"
                              disabled={specsSamBusy && !specsSamPendingAnnotation}
                              onClick={specsSamPendingAnnotation ? cancelSpecsSamEvidence : clearSpecsFieldEvidenceDraft}
                            >
                              {icon("x")} Cancel
                            </button>
                            <button
                              className="primary-button compact"
                              type="button"
                              disabled={!specsSamPendingAnnotation || specsSamBusy}
                              onClick={finishSpecsSamEvidence}
                            >
                              {icon("save")} Save
                            </button>
                          </div>
                        )}
                        {specsEvidenceShapeKind !== "point" && specsEvidenceShapeKind !== "sam" && (
                          <div className="segment-field-evidence-actions">
                            <span>{specsEvidenceDraftPoints.length}/{minimumPointsForPanoShape(specsEvidenceShapeKind)}+ points</span>
                            <button
                              className="secondary-button compact"
                              type="button"
                              disabled={!specsEvidenceDraftPoints.length || specsSamBusy}
                              onClick={() => setSpecsEvidenceDraftPoints((current) => current.slice(0, -1))}
                            >
                              {icon("undo-2")} Undo
                            </button>
                            <button
                              className="secondary-button compact"
                              type="button"
                              disabled={!specsEvidenceDraftPoints.length || specsSamBusy}
                              onClick={() => setSpecsEvidenceDraftPoints([])}
                            >
                              {icon("eraser")} Clear
                            </button>
                            <button
                              className="primary-button compact"
                              type="button"
                              disabled={specsEvidenceDraftPoints.length < minimumPointsForPanoShape(specsEvidenceShapeKind) || specsSamBusy}
                              onClick={saveSpecsFieldEvidenceDraft}
                            >
                              {icon("save")} Save
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {canUsePanoMeasurementTools && selectedSpecsImage && !specsFieldEvidenceTarget && isPanoCalibrationPanelOpen && (
                      <PanoCalibrationPanel
                        profiles={panoCalibrationProfiles}
                        selectedProfile={selectedPanoCalibrationProfile}
                        selectedProfileId={selectedPanoCalibrationProfile ? selectedPanoCalibrationProfile.id : selectedPanoCalibrationId}
                        defaultProfileId={defaultPanoCalibrationId}
                        assignedProfileId={assignedPanoCalibrationId}
                        videoId={currentPanoVideoId}
                        activeMode={panoMeasurementMode}
                        profileName={panoCalibrationProfileName}
                        setProfileName={setPanoCalibrationProfileName}
                        knownMeters={panoCalibrationMeters}
                        setKnownMeters={setPanoCalibrationMeters}
                        viewHeadingOffsetDeg={panoViewHeadingOffsetDeg}
                        setViewHeadingOffsetDeg={setPanoViewHeadingOffsetDeg}
                        viewFovDeg={panoViewFovDeg}
                        setViewFovDeg={setPanoViewFovDeg}
                        viewRangeMeters={panoViewRangeMeters}
                        setViewRangeMeters={setPanoViewRangeMeters}
                        assignToVideo={assignNewPanoCalibrationToVideo}
                        setAssignToVideo={setAssignNewPanoCalibrationToVideo}
                        makeDefault={makeNewPanoCalibrationDefault}
                        setMakeDefault={setMakeNewPanoCalibrationDefault}
                        draftPoints={panoMeasurementDraftPoints}
                        status={panoMeasurementStatus}
                        onSelectProfile={setSelectedPanoCalibrationId}
                        onStartCalibration={startPanoCalibration}
                        onAssignToVideo={assignSelectedPanoCalibrationToVideo}
                        onClearVideo={clearPanoCalibrationForVideo}
                        onSetDefault={setSelectedPanoCalibrationAsDefault}
                        onDeleteProfile={deleteSelectedPanoCalibrationProfile}
                        onSaveViewCalibration={() => savePanoViewCalibration(false)}
                        onResetViewCalibration={() => savePanoViewCalibration(true)}
                        onUndo={() => setPanoMeasurementDraftPoints((current) => current.slice(0, -1))}
                        onClear={() => {
                          setPanoMeasurementDraftPoints([]);
                          setPanoMeasurementStatus("");
                        }}
                        onCancel={cancelPanoMeasurement}
                      />
                    )}
                    {canUsePanoMeasurementTools && selectedSpecsImage && !specsFieldEvidenceTarget && isPanoMeasurementPanelOpen && !isPanoCalibrationPanelOpen && (
                      <PanoMeasurementPanel
                        calibrations={panoCalibrationProfiles.length ? panoCalibrationProfiles : legacyPanoCalibrationAnnotations}
                        selectedCalibration={selectedPanoCalibration}
                        selectedCalibrationId={selectedPanoCalibration ? selectedPanoCalibration.id : selectedPanoCalibrationId}
                        onSelectCalibration={setSelectedPanoCalibrationId}
                        activeMode={panoMeasurementMode}
                        title={panoMeasurementTitle}
                        setTitle={updatePanoMeasurementTitle}
                        videoId={currentPanoVideoId}
                        assignedCalibrationId={assignedPanoCalibrationId}
                        defaultCalibrationId={defaultPanoCalibrationId}
                        draftPoints={panoMeasurementDraftPoints}
                        status={panoMeasurementStatus}
                        measurementCount={panoDistanceMeasurements.length}
                        temporaryMeasurements={effectiveTransientMeasurements}
                        savingTemporaryMeasurements={isSavingPanoMeasurementEvidence}
                        isListOpen={isPanoMeasurementListOpen}
                        onToggleList={() => setIsPanoMeasurementListOpen((current) => !current)}
                        onNewMeasurement={startPanoMeasurement}
                        onSaveTemporaryMeasurements={savePanoMeasurementListAsEvidence}
                        onSelectTemporaryMeasurement={(measurement) => {
                          if (measurement && typeof setActivePanoAnnotationId === "function") {
                            setActivePanoAnnotationId(measurement.id);
                          }
                        }}
                        onClose={closePanoMeasurementPanel}
                        onUndo={() => setPanoMeasurementDraftPoints((current) => current.slice(0, -1))}
                        onClear={() => {
                          setPanoMeasurementDraftPoints([]);
                          setPanoMeasurementStatus("");
                        }}
                        onCancel={cancelPanoMeasurement}
                      />
                    )}
                    {selectedSpecsImage && panoCalibrationLoaded ? (
                      <PanoViewer
                        key={`specs-pano-${detail ? detail.id : "segment"}-${selectedSpecsImage.file}-${selectedSpecsImage.candidateId || ""}`}
                        imageUrl={selectedSpecsImage.url}
                        overlays={specsEvidenceOverlays}
                        annotations={viewerSpecsImageAnnotations}
                        activeAnnotationId={activePanoAnnotationId}
                        annotationPlacementMode={specsAnnotationPlacementMode}
                        suppressActiveAnnotationFocus={canUsePanoMeasurementTools && isPanoMeasurementPanelOpen}
                        draftAnnotation={specsActiveDraft}
                        onAnnotationPlacement={placeSpecsPanoAnnotation}
                        onSelectAnnotation={setActivePanoAnnotationId}
                        onDeleteAnnotation={deletePanoAnnotation}
                        onViewChange={setSpecsPanoViewState}
                      />
                    ) : (
                      <div className="pano-empty">{panoCalibrationLoaded ? "Loading panorama..." : "Loading panorama settings..."}</div>
                    )}
                    {selectedSpecsImageIsCandidatePreview && (
                      <div className="pano-candidate-preview-badge">
                        {icon("map-pin-check")}
                        <span>Candidate preview</span>
                      </div>
                    )}
                    {showSpecsImageStrip && detail && selectedSpecsImage && (
                      <ImageStrip
                        detail={detail}
                        currentImage={specsCurrentImage}
                        setCurrentImage={openSpecsPanoImage}
                        previewImage={panoCandidatePreview}
                        previewActive={selectedSpecsImageIsCandidatePreview}
                        onSelectPreview={() => {
                          if (panoCandidatePreview) {
                            setPanoCandidatePreview(panoCandidatePreview);
                          }
                        }}
                      />
                    )}
                  </section>
                </>
              )}
            </div>
          </aside>
        </div>
      </section>
      {fieldBatchSetup && (
        <FieldBatchSetupModal
          setup={fieldBatchSetup}
          busy={fieldPromptBusy}
          onChange={setFieldBatchSetup}
          onCancel={() => setFieldBatchSetup(null)}
          onConfirm={startSpecsFieldPromptSetup}
        />
      )}
    </main>
  );
}

function FieldBatchSetupModal({ setup, busy = false, onChange = null, onCancel = null, onConfirm = null }) {
  if (!setup) {
    return null;
  }
  const isSingleRun = setup.mode === "single";
  const segmentCount = Array.isArray(setup.segmentIds) ? setup.segmentIds.length : 0;
  const tooManySegments = segmentCount > MAX_BATCH_RANGE_SEGMENTS;
  const modelOptions = Array.from(new Set([setup.model, ...AGENT_RUNNER_MODEL_OPTIONS].filter(Boolean)));
  const reasoningOptions = Array.from(new Set([setup.reasoningEffort, ...AGENT_RUNNER_REASONING_OPTIONS].filter(Boolean)));
  const updateSetup = (patch) => {
    if (typeof onChange === "function") {
      onChange({ ...setup, ...patch });
    }
  };

  return (
    <div className="field-batch-modal" role="dialog" aria-modal="true" aria-label={isSingleRun ? "Run field prompt for this segment" : "Run field for filtered grid"}>
      <div className="field-batch-dialog">
        <header>
          <div>
            <span>{isSingleRun ? "Run field prompt" : "Run field for filtered grid"}</span>
            <strong>{setup.fieldLabel}</strong>
          </div>
          <button type="button" className="icon-action" onClick={onCancel} disabled={busy} title="Close">
            {icon("x")}
          </button>
        </header>
        <section className="field-batch-summary">
          <div>
            <span>{isSingleRun ? "Segment scope" : "Grid filter scope"}</span>
            <strong>{segmentCount} segment{segmentCount === 1 ? "" : "s"}</strong>
            <small>{isSingleRun ? `Current segment${segmentCount && setup.segmentIds[0] ? ` ${setup.segmentIds[0]}` : ""}` : "Current filtered segment grid"}</small>
          </div>
          <div>
            <span>Field</span>
            <strong>{setup.fieldCode}</strong>
            <small>{Object.prototype.hasOwnProperty.call(setup, "prompt") ? "Prompt will be saved before run" : "Using saved/current field prompt"}</small>
          </div>
        </section>
        {tooManySegments && (
          <p className="field-batch-warning">
            This filtered grid has {segmentCount} segments. Narrow the grid to {MAX_BATCH_RANGE_SEGMENTS} segments or fewer before starting.
          </p>
        )}
        <div className="field-batch-controls">
          <label>
            <span>Images each</span>
            <select
              value={setup.maxImages}
              onChange={(event) => updateSetup({ maxImages: Number(event.target.value) })}
              disabled={busy}
            >
              {[1, 2, 3, 4, 5, 6, 8, 10].map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Image mode</span>
            <select
              value={setup.imageProcessingMode}
              onChange={(event) => updateSetup({ imageProcessingMode: event.target.value })}
              disabled={busy}
            >
              {IMAGE_PROCESSING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Field image source</span>
            <select
              value={normalizeFieldImageSourceMode(setup.imageSourceMode)}
              onChange={(event) => updateSetup({ imageSourceMode: event.target.value })}
              disabled={busy}
            >
              {FIELD_IMAGE_SOURCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>AI model</span>
            <select
              value={setup.model}
              onChange={(event) => updateSetup({ model: event.target.value })}
              disabled={busy}
            >
              {modelOptions.map((modelName) => (
                <option key={modelName} value={modelName}>{agentRunnerModelLabel(modelName)}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Reasoning effort</span>
            <select
              value={setup.reasoningEffort}
              onChange={(event) => updateSetup({ reasoningEffort: event.target.value })}
              disabled={busy}
            >
              {reasoningOptions.map((effort) => (
                <option key={effort} value={effort}>{effort}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Parallel segments</span>
            <select
              value={isSingleRun ? 1 : setup.segmentConcurrency}
              onChange={(event) => updateSetup({ segmentConcurrency: Number(event.target.value) })}
              disabled={busy || isSingleRun}
            >
              {OPENAI_CONCURRENCY_OPTIONS.map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </label>
          <label>
            <span>AI calls</span>
            <select
              value={setup.openAiCallConcurrency}
              onChange={(event) => updateSetup({ openAiCallConcurrency: Number(event.target.value) })}
              disabled={busy}
            >
              {OPENAI_CONCURRENCY_OPTIONS.map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </label>
        </div>
        <section className="field-batch-run-context" aria-label={isSingleRun ? "Optional agent context for this field prompt run" : "Optional agent context for this field list run"}>
          <strong>Agent prompt control</strong>
          <label className={`field-prompt-agent-inject-toggle ${setup.fieldPromptOnly ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={Boolean(setup.fieldPromptOnly)}
              onChange={(event) => updateSetup({
                fieldPromptOnly: event.target.checked,
                includeAgentInstructions: event.target.checked ? false : setup.includeAgentInstructions,
                includeAgentPromptAddendum: event.target.checked ? false : setup.includeAgentPromptAddendum
              })}
              disabled={busy}
            />
            <span>
              <strong>Field prompt only</strong>
              <small>Ignore the main agent prompt and backend consistency rewrites for this run.</small>
            </span>
          </label>
          <label className={`field-prompt-agent-inject-toggle ${setup.includeAgentInstructions ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={Boolean(setup.includeAgentInstructions)}
              onChange={(event) => updateSetup({ includeAgentInstructions: event.target.checked })}
              disabled={busy || setup.fieldPromptOnly}
            />
            <span>
              <strong>Use Special instructions</strong>
              <small>Include agent Special instructions in this {isSingleRun ? "run" : "list run"}.</small>
            </span>
          </label>
          <label className={`field-prompt-agent-inject-toggle ${setup.includeAgentPromptAddendum ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={Boolean(setup.includeAgentPromptAddendum)}
              onChange={(event) => updateSetup({ includeAgentPromptAddendum: event.target.checked })}
              disabled={busy || setup.fieldPromptOnly}
            />
            <span>
              <strong>Use Prompt addendum</strong>
              <small>Include agent Prompt addendum in this {isSingleRun ? "run" : "list run"}.</small>
            </span>
          </label>
        </section>
        <footer>
          <button type="button" className="secondary-button" onClick={onCancel} disabled={busy}>
            {icon("x")} Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => onConfirm && onConfirm(setup)}
            disabled={busy || !segmentCount || tooManySegments}
          >
            {busy ? icon("loader-circle") : icon("play")} {isSingleRun ? "Run field" : "Start processing"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function formatSpecsSegmentGridValue(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "object") {
    if (value.lat !== undefined && value.lng !== undefined) {
      return `${Number(value.lat).toFixed(6)}, ${Number(value.lng).toFixed(6)}`;
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function getSpecsAiFieldResult(segment, column, analysisResults = {}) {
  if (!segment || !column || column.kind !== "field" || !column.field) {
    return null;
  }
  const analysis = analysisResults[segment.id] || analysisResults[String(segment.id)];
  if (!analysis) {
    return null;
  }
  const fieldCode = column.field.field;
  return analysis.fields && analysis.fields[fieldCode] ? analysis.fields[fieldCode] : null;
}

function getSpecsAiFieldValue(segment, column, analysisResults = {}) {
  if (!segment || !column || column.kind !== "field" || !column.field) {
    return null;
  }
  const analysis = analysisResults[segment.id] || analysisResults[String(segment.id)];
  if (!analysis) {
    return null;
  }
  const fieldCode = column.field.field;
  const saveJson = analysis.saveJson || {};
  if (Object.prototype.hasOwnProperty.call(saveJson, fieldCode) && hasDisplayValue(saveJson[fieldCode])) {
    return saveJson[fieldCode];
  }
  const aiField = getSpecsAiFieldResult(segment, column, analysisResults);
  if (aiField && hasDisplayValue(aiField.value)) {
    return aiField.value;
  }
  return null;
}

function getSpecsAiFieldConfidencePercent(segment, column, analysisResults = {}) {
  const aiField = getSpecsAiFieldResult(segment, column, analysisResults);
  return fieldConfidencePercent(aiField && aiField.confidence);
}

function specsSegmentHasAiAnalysis(segment, analysisResults = {}) {
  if (!segment) {
    return false;
  }
  const analysis = analysisResults[segment.id] || analysisResults[String(segment.id)];
  if (!analysis) {
    return false;
  }
  if (analysis.saveJson && Object.values(analysis.saveJson).some(hasDisplayValue)) {
    return true;
  }
  if (analysis.fields && Object.values(analysis.fields).some((field) => (
    hasDisplayValue(field && field.value) ||
    hasDisplayValue(field && field.suggestion) ||
    hasDisplayValue(field && field.reason)
  ))) {
    return true;
  }
  return true;
}

function specsSegmentIsApproved(segment) {
  return specsSegmentApprovalState(segment) === "approved";
}

function specsSegmentNeedsReview(segment) {
  return specsSegmentApprovalState(segment) === "review";
}

function specsSegmentApprovalState(segment) {
  if (!segment) {
    return "pending";
  }
  const props = (segment.feature && segment.feature.properties) || {};
  return segmentApprovalStateFromProps(props);
}

function specsSegmentApprovalDisplay(segment) {
  const props = (segment && segment.feature && segment.feature.properties) || {};
  return segmentApproveDisplayText(segmentApprovalStateFromProps(props));
}

function specsColumnIsApproval(column) {
  const property = String(column && column.property ? column.property : "").toLowerCase();
  return property === "approve" || property === "approved" || property === "review";
}

function getSpecsColumnFilterType(column) {
  return column.kind === "field" &&
    column.field &&
    Array.isArray(column.field.options) &&
    column.field.options.length
    ? "multi"
    : "text";
}

function normalizeSpecsFilterText(value) {
  return String(value === null || value === undefined ? "" : value)
    .trim()
    .toLowerCase();
}

function isSpecsFilterActive(filter) {
  if (!filter) {
    return false;
  }
  if (filter.type === "multi") {
    return Array.isArray(filter.values) && filter.values.length > 0;
  }
  return Boolean(String(filter.query || "").trim());
}

function getSpecsColumnFilterRawValue(segment, column, analysisResults = {}) {
  if (column.kind === "id") {
    return segment.id;
  }
  if (column.kind === "starRatings") {
    return segmentStarRatingsText(segment);
  }
  if (column.kind === "starRatingUser") {
    const item = segmentStarRatingForUser(segment, column.starUserKey);
    return item ? item.rating : null;
  }
  if (column.kind === "field") {
    return getSpecsAiFieldValue(segment, column, analysisResults);
  }
  const props = (segment.feature && segment.feature.properties) || {};
  if (specsColumnIsApproval(column)) {
    return specsSegmentApprovalDisplay(segment);
  }
  if (column.property && props[column.property] !== undefined) {
    return props[column.property];
  }
  if (column.property === "Road_name") {
    return segment.road;
  }
  if (column.property === "Distance") {
    return segment.distance;
  }
  return null;
}

function specsSegmentMatchesColumnFilter(segment, column, filter, analysisResults = {}) {
  if (!isSpecsFilterActive(filter)) {
    return true;
  }

  const rawValue = getSpecsColumnFilterRawValue(segment, column, analysisResults);
  if (filter.type === "multi") {
    const selectedValues = new Set((filter.values || []).map((value) => String(value)));
    return selectedValues.has(String(rawValue));
  }

  const query = normalizeSpecsFilterText(filter.query);
  if (!query) {
    return true;
  }
  const displayValue = getSpecsSegmentGridValue(segment, column, analysisResults);
  return (
    normalizeSpecsFilterText(displayValue).includes(query) ||
    normalizeSpecsFilterText(rawValue).includes(query)
  );
}

function specsFieldSearchText(column) {
  const field = column && column.field ? column.field : {};
  return [
    specsAdvancedFieldLabel(column),
    column && column.label,
    column && column.title,
    field.field,
    field.caption,
    field.group,
    field.geoProperty
  ].map((value) => String(value || "").toLowerCase()).join(" ");
}

function specsAdvancedFieldLabel(column) {
  if (!column) {
    return "";
  }
  const fieldCode = column.field && column.field.field ? column.field.field : "";
  return `${column.label || fieldCode || ""}${fieldCode ? ` (${fieldCode})` : ""}`;
}

function specsAdvancedFilterValueOptions(column, segments, analysisResults = {}) {
  if (!column) {
    return [];
  }
  if (column.kind === "field" && column.field && Array.isArray(column.field.options) && column.field.options.length) {
    return column.field.options.map((option) => ({
      value: String(option.id),
      label: `${option.id}: ${option.label}`
    }));
  }
  const seen = new Set();
  return (segments || [])
    .map((segment) => getSpecsColumnFilterRawValue(segment, column, analysisResults))
    .filter((value) => hasDisplayValue(value))
    .map((value) => ({
      value: String(value),
      label: formatSpecsSegmentGridValue(value)
    }))
    .filter((option) => {
      if (seen.has(option.value)) {
        return false;
      }
      seen.add(option.value);
      return true;
    })
    .slice(0, 200);
}

function getSpecsOldFieldRawValue(segment, column) {
  if (!segment || !column || column.kind !== "field" || !column.field) {
    return null;
  }
  const props = (segment.feature && segment.feature.properties) || {};
  const propertyName = column.field.geoProperty || column.field.field || "";
  if (propertyName && Object.prototype.hasOwnProperty.call(props, propertyName)) {
    return props[propertyName];
  }
  return null;
}

const SPECS_ADVANCED_COMPARISON_FILTERS = {
  oldMismatch: {
    value: "__old_mismatch__",
    display: "AI value differs from old value",
    short: "Old != AI",
    icon: "git-compare-arrows"
  },
  oldSame: {
    value: "__old_same__",
    display: "AI value same as old value",
    short: "Old = AI",
    icon: "equal"
  },
  aiMissing: {
    value: "__ai_missing__",
    display: "AI value missing",
    short: "AI missing",
    icon: "circle-slash"
  }
};

function specsAdvancedComparisonMeta(mode) {
  return SPECS_ADVANCED_COMPARISON_FILTERS[mode] || null;
}

const SPECS_ADVANCED_CONFIDENCE_FILTERS = {
  confidenceHigh: {
    value: "__confidence_high__",
    display: "Confidence high (>=80%)",
    short: "Conf high",
    icon: "signal-high"
  },
  confidenceMedium: {
    value: "__confidence_medium__",
    display: "Confidence medium (55-79%)",
    short: "Conf med",
    icon: "signal-medium"
  },
  confidenceLow: {
    value: "__confidence_low__",
    display: "Confidence low (<55%)",
    short: "Conf low",
    icon: "signal-low"
  },
  confidenceNone: {
    value: "__confidence_none__",
    display: "No confidence",
    short: "No conf",
    icon: "signal-zero"
  }
};

function specsAdvancedConfidenceMeta(mode) {
  return SPECS_ADVANCED_CONFIDENCE_FILTERS[mode] || null;
}

const SPECS_CONFIDENCE_RANGE_MIN = 1;
const SPECS_CONFIDENCE_RANGE_MAX = 100;
const SPECS_DEFAULT_CONFIDENCE_RANGE = {
  min: SPECS_CONFIDENCE_RANGE_MIN,
  max: SPECS_CONFIDENCE_RANGE_MAX
};

function normalizeAdvancedConfidenceRange(range = {}) {
  const rawMin = Number(range.min);
  const rawMax = Number(range.max);
  const safeMin = Number.isFinite(rawMin) ? rawMin : SPECS_CONFIDENCE_RANGE_MIN;
  const safeMax = Number.isFinite(rawMax) ? rawMax : SPECS_CONFIDENCE_RANGE_MAX;
  const min = Math.max(SPECS_CONFIDENCE_RANGE_MIN, Math.min(SPECS_CONFIDENCE_RANGE_MAX, Math.round(Math.min(safeMin, safeMax))));
  const max = Math.max(SPECS_CONFIDENCE_RANGE_MIN, Math.min(SPECS_CONFIDENCE_RANGE_MAX, Math.round(Math.max(safeMin, safeMax))));
  return { min, max };
}

function advancedConfidenceRangeIsDefault(range) {
  const normalized = normalizeAdvancedConfidenceRange(range);
  return normalized.min === SPECS_CONFIDENCE_RANGE_MIN && normalized.max === SPECS_CONFIDENCE_RANGE_MAX;
}

function advancedConfidenceRangeLabel(range) {
  const normalized = normalizeAdvancedConfidenceRange(range);
  return `Confidence ${normalized.min}-${normalized.max}%`;
}

function specsSegmentFieldHasOldMismatch(segment, column, analysisResults = {}) {
  const currentValue = getSpecsColumnFilterRawValue(segment, column, analysisResults);
  const oldValue = getSpecsOldFieldRawValue(segment, column);
  if (!hasDisplayValue(currentValue) && !hasDisplayValue(oldValue)) {
    return false;
  }
  return normalizeSpecComparableValue(currentValue) !== normalizeSpecComparableValue(oldValue);
}

function specsSegmentFieldHasOldSame(segment, column, analysisResults = {}) {
  const currentValue = getSpecsColumnFilterRawValue(segment, column, analysisResults);
  if (!hasDisplayValue(currentValue)) {
    return false;
  }
  const oldValue = getSpecsOldFieldRawValue(segment, column);
  return normalizeSpecComparableValue(currentValue) === normalizeSpecComparableValue(oldValue);
}

function specsSegmentFieldHasAiMissing(segment, column, analysisResults = {}) {
  if (!specsSegmentHasAiAnalysis(segment, analysisResults)) {
    return false;
  }
  const currentValue = getSpecsColumnFilterRawValue(segment, column, analysisResults);
  return !hasDisplayValue(currentValue);
}

function specsSegmentMatchesAdvancedComparisonMode(segment, column, mode, analysisResults = {}) {
  if (mode === "oldMismatch") {
    return specsSegmentFieldHasOldMismatch(segment, column, analysisResults);
  }
  if (mode === "oldSame") {
    return specsSegmentFieldHasOldSame(segment, column, analysisResults);
  }
  if (mode === "aiMissing") {
    return specsSegmentFieldHasAiMissing(segment, column, analysisResults);
  }
  return true;
}

function specsSegmentMatchesAdvancedConfidenceMode(segment, column, mode, analysisResults = {}) {
  const confidence = getSpecsAiFieldConfidencePercent(segment, column, analysisResults);
  if (mode === "confidenceHigh") {
    return confidence !== null && confidence >= 80;
  }
  if (mode === "confidenceMedium") {
    return confidence !== null && confidence >= 55 && confidence < 80;
  }
  if (mode === "confidenceLow") {
    return confidence !== null && confidence < 55;
  }
  if (mode === "confidenceNone") {
    return confidence === null;
  }
  return true;
}

function specsSegmentMatchesAdvancedConfidenceRange(segment, column, range, analysisResults = {}) {
  const confidence = getSpecsAiFieldConfidencePercent(segment, column, analysisResults);
  if (confidence === null) {
    return false;
  }
  const normalized = normalizeAdvancedConfidenceRange(range);
  return confidence >= normalized.min && confidence <= normalized.max;
}

function specsOldNewFieldDisplay(segment, column, analysisResults = {}) {
  const oldValue = getSpecsOldFieldRawValue(segment, column);
  const aiValue = getSpecsColumnFilterRawValue(segment, column, analysisResults);
  return {
    oldValue,
    aiValue,
    oldDisplay: column && column.kind === "field"
      ? specDisplayValue(column.field, oldValue, fieldOptionLabel(column.field, oldValue))
      : formatSpecsSegmentGridValue(oldValue),
    aiDisplay: column && column.kind === "field"
      ? specDisplayValue(column.field, aiValue, fieldOptionLabel(column.field, aiValue))
      : formatSpecsSegmentGridValue(aiValue),
    different: normalizeSpecComparableValue(oldValue) !== normalizeSpecComparableValue(aiValue)
  };
}

function modelComparisonValueDisplay(field, value) {
  return specDisplayValue(field, value, fieldOptionLabel(field, value));
}

function modelComparisonModelKey(analysis, fieldResult) {
  return String(
    (fieldResult && (fieldResult.model || fieldResult.modelName || fieldResult.aiModel)) ||
    (analysis && (analysis.model || analysis.agentModel || analysis.openAiModel)) ||
    "Unknown model"
  ).trim() || "Unknown model";
}

function modelComparisonModelLabel(modelName) {
  const text = String(modelName || "").trim();
  if (!text || text === "Unknown model") {
    return "Unknown model";
  }
  return agentRunnerModelLabel(text);
}

function modelComparisonSegmentOldValue(segment, field) {
  const props = (segment && segment.feature && segment.feature.properties) || {};
  const propertyName = field && (field.geoProperty || field.field) ? field.geoProperty || field.field : "";
  return propertyName && Object.prototype.hasOwnProperty.call(props, propertyName)
    ? props[propertyName]
    : null;
}

function modelComparisonFieldValue(analysis, fieldCode) {
  if (!analysis || !fieldCode) {
    return { hasFieldResult: false, value: null, fieldResult: null };
  }
  const fieldResult = analysis.fields && analysis.fields[fieldCode] ? analysis.fields[fieldCode] : null;
  const saveJson = analysis.saveJson || {};
  const hasSavedValue = Object.prototype.hasOwnProperty.call(saveJson, fieldCode) && hasDisplayValue(saveJson[fieldCode]);
  if (hasSavedValue) {
    return { hasFieldResult: true, value: saveJson[fieldCode], fieldResult };
  }
  if (fieldResult) {
    return { hasFieldResult: true, value: fieldResult.value, fieldResult };
  }
  return { hasFieldResult: false, value: null, fieldResult: null };
}

function modelComparisonRunAt(analysis, fieldResult) {
  return String(
    (fieldResult && (fieldResult.analyzedAt || fieldResult.createdAt || fieldResult.updatedAt)) ||
    (analysis && (analysis.createdAt || analysis.updatedAt || analysis.analyzedAt)) ||
    ""
  ).trim();
}

function modelComparisonDateKey(value) {
  const text = String(value || "").trim();
  if (!text) {
    return "__no_date__";
  }
  const parsed = new Date(text);
  if (Number.isFinite(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  const match = text.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : text;
}

function modelComparisonDateLabel(value) {
  const key = String(value || "").trim();
  return key === "__no_date__" ? "No date" : key;
}

function modelComparisonDateTimeDisplay(value) {
  const text = String(value || "").trim();
  if (!text) {
    return "No date";
  }
  const parsed = new Date(text);
  return Number.isFinite(parsed.getTime()) ? parsed.toLocaleString() : text;
}

function modelComparisonStatusLabel(status) {
  if (status === "same") {
    return "Same";
  }
  if (status === "changed") {
    return "Changed";
  }
  if (status === "missing") {
    return "Missing";
  }
  return "All";
}

function buildFieldModelComparisonReport(field, segments, analysisResults = {}) {
  const fieldCode = field && field.field;
  const groups = new Map();
  const safeSegments = Array.isArray(segments) ? segments : [];
  const totals = {
    scope: safeSegments.length,
    withAnyAnalysis: 0,
    noAnalysis: 0,
    noFieldResult: 0,
    fieldRecords: 0,
    processed: 0,
    same: 0,
    changed: 0,
    missing: 0
  };

  safeSegments.forEach((segment) => {
    const analysis = dashboardAnalysisForSegment(segment, analysisResults);
    if (!analysis) {
      totals.noAnalysis += 1;
      return;
    }
    totals.withAnyAnalysis += 1;
    const fieldValue = modelComparisonFieldValue(analysis, fieldCode);
    if (!fieldValue.hasFieldResult) {
      totals.noFieldResult += 1;
      return;
    }

    const modelKey = modelComparisonModelKey(analysis, fieldValue.fieldResult);
    if (!groups.has(modelKey)) {
      groups.set(modelKey, {
        model: modelKey,
        label: modelComparisonModelLabel(modelKey),
        total: 0,
        processed: 0,
        same: 0,
        changed: 0,
        missing: 0,
        lastRunAt: "",
        records: []
      });
    }
    const group = groups.get(modelKey);
    const oldValue = modelComparisonSegmentOldValue(segment, field);
    const aiValue = fieldValue.value;
    const executedAt = modelComparisonRunAt(analysis, fieldValue.fieldResult);
    const rowBase = {
      segmentId: segment && segment.id,
      road: segment && segment.road,
      distance: segment && segment.distance,
      model: modelKey,
      modelLabel: group.label,
      executedAt,
      dateKey: modelComparisonDateKey(executedAt),
      dateDisplay: modelComparisonDateTimeDisplay(executedAt),
      oldValue,
      aiValue,
      oldDisplay: modelComparisonValueDisplay(field, oldValue),
      aiDisplay: modelComparisonValueDisplay(field, aiValue)
    };

    totals.fieldRecords += 1;
    group.total += 1;
    if (executedAt && (!group.lastRunAt || String(executedAt) > String(group.lastRunAt))) {
      group.lastRunAt = executedAt;
    }
    if (!hasDisplayValue(aiValue)) {
      totals.missing += 1;
      group.missing += 1;
      group.records.push({ ...rowBase, status: "missing" });
      return;
    }

    totals.processed += 1;
    group.processed += 1;
    if (normalizeSpecComparableValue(aiValue) === normalizeSpecComparableValue(oldValue)) {
      totals.same += 1;
      group.same += 1;
      group.records.push({ ...rowBase, status: "same" });
      return;
    }

    totals.changed += 1;
    group.changed += 1;
    group.records.push({ ...rowBase, status: "changed" });
  });

  const rows = Array.from(groups.values())
    .map((group) => ({
      ...group,
      samePct: dashboardPct(group.same, group.processed),
      changedPct: dashboardPct(group.changed, group.processed),
      missingPct: dashboardPct(group.missing, group.total),
      lastRunDisplay: modelComparisonDateTimeDisplay(group.lastRunAt),
      records: group.records.slice().sort((left, right) => (Number(left.segmentId) - Number(right.segmentId)) || String(left.road || "").localeCompare(String(right.road || "")))
    }))
    .sort((left, right) => (right.processed - left.processed) || left.label.localeCompare(right.label));

  return {
    totals: {
      ...totals,
      samePct: dashboardPct(totals.same, totals.processed),
      changedPct: dashboardPct(totals.changed, totals.processed),
      missingPct: dashboardPct(totals.missing, totals.fieldRecords)
    },
    rows
  };
}

function filterFieldModelComparisonReportByDate(report, dateFilter = "all") {
  if (!report || dateFilter === "all") {
    return report || { totals: {}, rows: [] };
  }
  const rows = (report.rows || [])
    .map((row) => {
      const records = (row.records || []).filter((record) => record.dateKey === dateFilter);
      const same = records.filter((record) => record.status === "same").length;
      const changed = records.filter((record) => record.status === "changed").length;
      const missing = records.filter((record) => record.status === "missing").length;
      const processed = same + changed;
      const total = records.length;
      const lastRunAt = records.reduce((latest, record) => {
        const value = record && record.executedAt ? String(record.executedAt) : "";
        return value && (!latest || value > latest) ? value : latest;
      }, "");
      return {
        ...row,
        records,
        total,
        processed,
        same,
        changed,
        missing,
        samePct: dashboardPct(same, processed),
        changedPct: dashboardPct(changed, processed),
        missingPct: dashboardPct(missing, total),
        lastRunAt,
        lastRunDisplay: modelComparisonDateTimeDisplay(lastRunAt)
      };
    })
    .filter((row) => row.total > 0)
    .sort((left, right) => (right.processed - left.processed) || left.label.localeCompare(right.label));

  const uniqueSegments = new Set();
  const totals = rows.reduce((stats, row) => {
    stats.fieldRecords += row.total;
    stats.processed += row.processed;
    stats.same += row.same;
    stats.changed += row.changed;
    stats.missing += row.missing;
    (row.records || []).forEach((record) => {
      if (record && record.segmentId !== null && record.segmentId !== undefined) {
        uniqueSegments.add(Number(record.segmentId));
      }
    });
    return stats;
  }, {
    scope: 0,
    withAnyAnalysis: 0,
    noAnalysis: 0,
    noFieldResult: 0,
    fieldRecords: 0,
    processed: 0,
    same: 0,
    changed: 0,
    missing: 0
  });
  totals.scope = uniqueSegments.size;
  totals.withAnyAnalysis = uniqueSegments.size;
  return {
    totals: {
      ...totals,
      samePct: dashboardPct(totals.same, totals.processed),
      changedPct: dashboardPct(totals.changed, totals.processed),
      missingPct: dashboardPct(totals.missing, totals.fieldRecords)
    },
    rows
  };
}

function FieldModelComparisonPanel({
  item,
  allSegments = [],
  searchSegments = [],
  gridSegmentIds = [],
  analysisResults = {},
  onClose
}) {
  const [scope, setScope] = useState("grid");
  const [selectedModel, setSelectedModel] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const field = item && item.field ? item.field : null;
  const gridSegments = useMemo(() => {
    const idSet = new Set((gridSegmentIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id)));
    if (!idSet.size) {
      return Array.isArray(searchSegments) ? searchSegments : [];
    }
    return (allSegments || []).filter((segment) => idSet.has(Number(segment && segment.id)));
  }, [allSegments, searchSegments, gridSegmentIds]);
  const scopeOptions = [
    { value: "grid", label: "Filtered grid", segments: gridSegments },
    { value: "search", label: "Search results", segments: Array.isArray(searchSegments) ? searchSegments : [] },
    { value: "all", label: "All segments", segments: Array.isArray(allSegments) ? allSegments : [] }
  ];
  const selectedScope = scopeOptions.find((option) => option.value === scope) || scopeOptions[0];
  const baseReport = useMemo(
    () => buildFieldModelComparisonReport(field, selectedScope.segments, analysisResults),
    [field, selectedScope.segments, analysisResults]
  );
  const dateOptions = useMemo(() => {
    const counts = new Map();
    (baseReport.rows || []).forEach((row) => {
      (row.records || []).forEach((record) => {
        counts.set(record.dateKey, (counts.get(record.dateKey) || 0) + 1);
      });
    });
    return Array.from(counts.entries())
      .sort(([left], [right]) => {
        if (left === "__no_date__") {
          return 1;
        }
        if (right === "__no_date__") {
          return -1;
        }
        return String(right).localeCompare(String(left));
      })
      .map(([value, count]) => ({ value, count, label: modelComparisonDateLabel(value) }));
  }, [baseReport]);
  const dateOptionsKey = dateOptions.map((option) => option.value).join("|");
  useEffect(() => {
    if (dateFilter !== "all" && !dateOptions.some((option) => option.value === dateFilter)) {
      setDateFilter("all");
    }
  }, [dateFilter, dateOptionsKey, dateOptions]);
  const report = useMemo(
    () => filterFieldModelComparisonReportByDate(baseReport, dateFilter),
    [baseReport, dateFilter]
  );
  const rowKey = report.rows.map((row) => row.model).join("|");
  useEffect(() => {
    if (!report.rows.length) {
      if (selectedModel) {
        setSelectedModel("");
      }
      return;
    }
    if (!report.rows.some((row) => row.model === selectedModel)) {
      setSelectedModel(report.rows[0].model);
    }
  }, [rowKey, selectedModel, report.rows]);
  useEffect(() => {
    setStatusFilter("all");
  }, [selectedModel, scope, field && field.field]);
  useEffect(() => {
    setDateFilter("all");
  }, [scope, field && field.field]);
  const selectedModelRow = report.rows.find((row) => row.model === selectedModel) || report.rows[0] || null;
  const detailRows = useMemo(() => {
    const records = selectedModelRow && Array.isArray(selectedModelRow.records) ? selectedModelRow.records : [];
    return records.filter((record) => (
      statusFilter === "all" || record.status === statusFilter
    ));
  }, [selectedModelRow, statusFilter]);
  const activeDateLabel = dateFilter === "all" ? "" : modelComparisonDateLabel(dateFilter);
  if (!field) {
    return null;
  }

  return (
    <aside className="model-compare-side-panel" role="complementary" aria-label="Compare model result">
      <div className="model-compare-dialog">
        <header>
          <div>
            <span>Compare result</span>
            <strong>{field.caption || field.field}</strong>
            <small>{field.field}{field.group ? ` - ${field.group}` : ""}</small>
          </div>
          <button className="icon-action" type="button" title="Close" onClick={onClose}>
            {icon("x")}
          </button>
        </header>

        <div className="model-compare-controls">
          <label>
            <span>Scope</span>
            <select value={scope} onChange={(event) => setScope(event.target.value)}>
              {scopeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} ({option.segments.length})
                </option>
              ))}
            </select>
          </label>
          <div>
            <span>Basis</span>
            <strong>AI value vs old value</strong>
          </div>
        </div>

        <section className="model-compare-summary" aria-label="Comparison summary">
          <div>
            <span>Segments</span>
            <strong>{report.totals.scope}</strong>
            <small>{activeDateLabel ? `${activeDateLabel} run date` : `${report.totals.withAnyAnalysis} with AI analysis`}</small>
          </div>
          <div>
            <span>Same as old</span>
            <strong>{report.totals.samePct}%</strong>
            <small>{report.totals.same}/{report.totals.processed} processed values{activeDateLabel ? " on date" : ""}</small>
          </div>
          <div>
            <span>Changed</span>
            <strong>{report.totals.changedPct}%</strong>
            <small>{report.totals.changed}/{report.totals.processed} processed values{activeDateLabel ? " on date" : ""}</small>
          </div>
          <div>
            <span>No field result</span>
            <strong>{report.totals.noFieldResult + report.totals.noAnalysis}</strong>
            <small>{report.totals.noAnalysis} no AI, {report.totals.noFieldResult} no field</small>
          </div>
        </section>

        <section className="model-compare-table-wrap">
          <table className="model-compare-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Field records</th>
                <th>Processed</th>
                <th>Same</th>
                <th>Changed</th>
                <th>Missing</th>
                <th>Last run</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.length ? report.rows.map((row) => (
                <tr
                  key={row.model}
                  className={selectedModelRow && selectedModelRow.model === row.model ? "is-selected" : ""}
                  tabIndex="0"
                  title="Show this model's segments"
                  onClick={() => setSelectedModel(row.model)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedModel(row.model);
                    }
                  }}
                >
                  <td>
                    <strong title={row.model}>{row.label}</strong>
                    {row.label !== row.model && <small>{row.model}</small>}
                  </td>
                  <td>{row.total}</td>
                  <td>{row.processed}</td>
                  <td><b>{row.samePct}%</b> <small>{row.same}</small></td>
                  <td><b>{row.changedPct}%</b> <small>{row.changed}</small></td>
                  <td><b>{row.missingPct}%</b> <small>{row.missing}</small></td>
                  <td><small>{row.lastRunDisplay}</small></td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7">No saved AI result found for this field in the selected scope.</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="model-compare-details">
          <header>
            <div>
              <strong>{selectedModelRow ? `${selectedModelRow.label} segments` : "Model segments"}</strong>
              <span>{detailRows.length ? `${detailRows.length}/${selectedModelRow ? selectedModelRow.records.length : 0} shown` : "No rows to show"}</span>
            </div>
            <div className="model-compare-detail-filters">
              <label>
                <span>Status</span>
                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="all">All</option>
                  <option value="same">Same</option>
                  <option value="changed">Changed</option>
                  <option value="missing">Missing</option>
                </select>
              </label>
              <label>
                <span>Run date</span>
                <select value={dateFilter} onChange={(event) => setDateFilter(event.target.value)}>
                  <option value="all">All dates</option>
                  {dateOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} ({option.count})
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </header>
          {detailRows.length > 0 ? (
            <div className="model-compare-detail-grid" role="table" aria-label="Model segment records">
              <div className="model-compare-detail-row model-compare-detail-head" role="row">
                <span>Segment</span>
                <span>Road</span>
                <span>Status</span>
                <span>Old</span>
                <span>AI</span>
                <span>Run date</span>
              </div>
              {detailRows.map((row) => (
                <div key={`${row.segmentId}-${row.model}-${row.status}`} className={`model-compare-detail-row is-${row.status}`}>
                  <span>{row.segmentId}</span>
                  <span title={row.road || ""}>{row.road || "-"}</span>
                  <span>{modelComparisonStatusLabel(row.status)}</span>
                  <span title={row.oldDisplay}>{row.oldDisplay}</span>
                  <span title={row.aiDisplay}>{row.aiDisplay}</span>
                  <span title={row.dateDisplay}>{row.dateDisplay}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="model-compare-empty">No segments match the selected model/date/status filters.</div>
          )}
        </section>

        <footer>
          <small>Legacy results without per-field model metadata are grouped by the segment analysis model.</small>
          <button className="primary-button compact" type="button" onClick={onClose}>Close</button>
        </footer>
      </div>
    </aside>
  );
}

function specsSegmentMatchesAdvancedFilters(segment, filters, columns, analysisResults = {}) {
  const activeFilters = Array.isArray(filters) ? filters.filter((filter) => filter && filter.fieldKey) : [];
  if (!activeFilters.length) {
    return true;
  }
  const columnsByKey = new Map((columns || []).map((column) => [column.key, column]));
  const filtersByField = new Map();
  activeFilters.forEach((filter) => {
    const column = columnsByKey.get(filter.fieldKey);
    if (!column) {
      return;
    }
    const current = filtersByField.get(filter.fieldKey) || { column, values: [], comparisonModes: [], confidenceModes: [], confidenceRanges: [] };
    if (specsAdvancedComparisonMeta(filter.mode)) {
      current.comparisonModes.push(filter.mode);
    } else if (filter.mode === "confidenceRange") {
      current.confidenceRanges.push({
        min: filter.minConfidence,
        max: filter.maxConfidence
      });
    } else if (specsAdvancedConfidenceMeta(filter.mode)) {
      current.confidenceModes.push(filter.mode);
    } else {
      current.values.push(filter.value);
    }
    filtersByField.set(filter.fieldKey, current);
  });
  return Array.from(filtersByField.values()).every(({ column, values, comparisonModes, confidenceModes, confidenceRanges }) => {
    const rawValue = getSpecsColumnFilterRawValue(segment, column, analysisResults);
    const comparableRawValue = normalizeSpecComparableValue(rawValue);
    const matchesValues = values.length
      ? values.some((value) => comparableRawValue === normalizeSpecComparableValue(value))
      : true;
    const uniqueComparisonModes = Array.from(new Set(comparisonModes));
    const matchesComparison = uniqueComparisonModes.length
      ? uniqueComparisonModes.some((mode) => specsSegmentMatchesAdvancedComparisonMode(segment, column, mode, analysisResults))
      : true;
    const uniqueConfidenceModes = Array.from(new Set(confidenceModes));
    const confidenceChecks = uniqueConfidenceModes.map((mode) => () => specsSegmentMatchesAdvancedConfidenceMode(segment, column, mode, analysisResults))
      .concat((confidenceRanges || []).map((range) => () => specsSegmentMatchesAdvancedConfidenceRange(segment, column, range, analysisResults)));
    const matchesConfidence = confidenceChecks.length
      ? confidenceChecks.some((check) => check())
      : true;
    return matchesValues && matchesComparison && matchesConfidence;
  });
}

function getSpecsSegmentGridValue(segment, column, analysisResults = {}) {
  if (column.kind === "id") {
    return `#${segment.id}`;
  }
  if (column.kind === "starRatings") {
    return segmentStarRatingsText(segment);
  }
  if (column.kind === "starRatingUser") {
    const item = segmentStarRatingForUser(segment, column.starUserKey);
    return item ? formatStarRatingValue(item.rating) : "-";
  }
  const props = (segment.feature && segment.feature.properties) || {};
  const formatValue = (value) => {
    if (column.kind === "field") {
      return fieldOptionLabel(column.field, value) || formatSpecsSegmentGridValue(value);
    }
    return formatSpecsSegmentGridValue(value);
  };
  if (column.kind === "field") {
    const aiValue = getSpecsAiFieldValue(segment, column, analysisResults);
    return hasDisplayValue(aiValue) ? formatValue(aiValue) : "-";
  }
  if (specsColumnIsApproval(column)) {
    return specsSegmentApprovalDisplay(segment);
  }
  if (column.property && props[column.property] !== undefined) {
    return formatValue(props[column.property]);
  }
  if (column.property === "Road_name") {
    return formatValue(segment.road);
  }
  if (column.property === "Distance") {
    return formatValue(segment.distance);
  }
  return "-";
}

function SpecsColumnHeader({
  column,
  filter,
  isOpen,
  onToggleOpen,
  onChangeFilter,
  onClearFilter,
  sortDirection = "",
  onSort = null,
  onAutoFit = null,
  onResizeStart = null
}) {
  const filterType = getSpecsColumnFilterType(column);
  const active = isSpecsFilterActive(filter);
  const selectedValues = Array.isArray(filter && filter.values) ? filter.values.map(String) : [];
  const selectedSet = new Set(selectedValues);
  const optionCount = selectedValues.length;

  const setTextFilter = (query) => {
    onChangeFilter(column.key, { type: "text", query });
  };

  const toggleOption = (optionId) => {
    const next = new Set(selectedValues);
    const key = String(optionId);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onChangeFilter(column.key, { type: "multi", values: Array.from(next) });
  };

  return (
    <>
      <button
        type="button"
        className={`specs-column-title-button ${sortDirection ? "active" : ""}`}
        title={`Sort by ${column.label}`}
        aria-label={`Sort by ${column.label}`}
        onClick={(event) => {
          event.stopPropagation();
          if (typeof onSort === "function") {
            onSort(column);
          }
        }}
      >
        <span className="specs-column-title">{column.label}</span>
        {sortDirection && (
          <span className="specs-column-sort-icon">
            {icon(sortDirection === "asc" ? "arrow-up" : "arrow-down")}
          </span>
        )}
      </button>
      <button
        type="button"
        className={`specs-column-filter-button ${active ? "active" : ""}`}
        title={`Filter ${column.label}`}
        aria-label={`Filter ${column.label}`}
        onClick={(event) => {
          event.stopPropagation();
          onToggleOpen(isOpen ? null : column.key);
        }}
      >
        {icon("filter")}
        {optionCount > 0 && <small>{optionCount}</small>}
      </button>
      {isOpen && (
        <div
          className="specs-column-filter-popover"
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.stopPropagation()}
        >
          <header>
            <strong>{column.label}</strong>
            <button type="button" onClick={() => onToggleOpen(null)} title="Close filter">
              {icon("x")}
            </button>
          </header>
          {filterType === "multi" ? (
            <div className="specs-filter-options">
              <div className="specs-filter-option-actions">
                <button
                  type="button"
                  onClick={() => onChangeFilter(column.key, {
                    type: "multi",
                    values: (column.field.options || []).map((option) => String(option.id))
                  })}
                >
                  All
                </button>
                <button type="button" onClick={() => onClearFilter(column.key)}>
                  Clear
                </button>
              </div>
              {(column.field.options || []).map((option) => (
                <label key={String(option.id)} className="specs-filter-option">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(String(option.id))}
                    onChange={() => toggleOption(option.id)}
                  />
                  <span>{option.id}: {option.label}</span>
                </label>
              ))}
            </div>
          ) : (
            <label className="specs-filter-text">
              <span>Contains</span>
              <input
                autoFocus
                value={(filter && filter.query) || ""}
                onChange={(event) => setTextFilter(event.target.value)}
                placeholder="Filter text"
              />
            </label>
          )}
          <footer>
            <button type="button" onClick={() => onClearFilter(column.key)} disabled={!active}>
              Clear filter
            </button>
          </footer>
        </div>
      )}
      <button
        type="button"
        className="specs-column-resize-handle"
        title="Drag to resize. Double-click to auto-fit."
        aria-label={`Resize ${column.label}`}
        onPointerDown={(event) => {
          if (typeof onResizeStart === "function") {
            onResizeStart(event, column);
          }
        }}
        onDoubleClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (typeof onAutoFit === "function") {
            onAutoFit(column);
          }
        }}
      />
    </>
  );
}

function SpecsSegmentListPanel({
  filteredSegments,
  selectedId,
  search,
  selectSegment,
  onClose,
  columns,
  gridTemplateColumns,
  analysisResults = {},
  limitMapToList = false,
  setLimitMapToList = null,
  onVisibleSegmentIdsChange = null,
  fieldValueFilterEnabled = false,
  setFieldValueFilterEnabled = null,
  fieldValueFilters = [],
  clearFieldValueFilters = null,
  selectedAgentGroups = [],
  allAgentGroups = [],
  runningSegmentAgentId = null,
  onRunSegmentAgents = null,
  batchJob = null,
  externalAdvancedFilterRequest = null,
  onFocusFieldCodesChange = null,
  canEdit = false,
  canViewStarRanking = false
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [aiAnalysisFilter, setAiAnalysisFilter] = useState("all");
  const [batchExclusionFilter, setBatchExclusionFilter] = useState("active");
  const [approvalFilter, setApprovalFilter] = useState("all");
  const [starRatingFilters, setStarRatingFilters] = useState([]);
  const [starRatingMenuOpen, setStarRatingMenuOpen] = useState(false);
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [advancedFieldSearch, setAdvancedFieldSearch] = useState("");
  const [advancedFieldPickerOpen, setAdvancedFieldPickerOpen] = useState(false);
  const [advancedFieldKey, setAdvancedFieldKey] = useState("");
  const [advancedValues, setAdvancedValues] = useState([]);
  const [advancedComparisonMode, setAdvancedComparisonMode] = useState("");
  const [advancedConfidenceRange, setAdvancedConfidenceRange] = useState(SPECS_DEFAULT_CONFIDENCE_RANGE);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [sortState, setSortState] = useState({ key: "", direction: "" });
  const [columnContextMenu, setColumnContextMenu] = useState(null);
  const [segmentContextMenu, setSegmentContextMenu] = useState(null);
  const columnResizeRef = useRef(null);
  const starRatingFilterRef = useRef(null);
  const previousSelectedIdRef = useRef(selectedId);
  const selectedAgentGroupCount = Array.isArray(selectedAgentGroups) ? selectedAgentGroups.filter(Boolean).length : 0;
  const allAgentGroupCount = Array.isArray(allAgentGroups) ? allAgentGroups.filter(Boolean).length : 0;
  const batchTotal = Number(batchJob && batchJob.total ? batchJob.total : 0);
  const batchCompleted = Number(batchJob && batchJob.completed ? batchJob.completed : 0);
  const batchProgress = batchTotal ? Math.round((batchCompleted / batchTotal) * 100) : 0;
  const showGridBatchProgress = Boolean(batchJob && batchTotal);
  const gridBatchActive = isActiveBatchJob(batchJob);
  const batchFieldCodes = Array.isArray(batchJob && batchJob.options && batchJob.options.fields)
    ? batchJob.options.fields
    : [];
  const batchFieldLabels = batchFieldCodes.map((fieldCode) => {
    const code = String(fieldCode || "").trim();
    const column = columns.find((candidate) => (
      candidate &&
      candidate.kind === "field" &&
      candidate.field &&
      String(candidate.field.field || "").trim() === code
    ));
    const label = String((column && (column.label || column.field.caption)) || code).trim();
    return code && label && !label.includes(code) ? `${label} (${code})` : (label || code);
  }).filter(Boolean);
  const batchScopeLabel = batchFieldLabels.length
    ? batchFieldLabels.join(", ")
    : "AI agents";
  const batchActiveSegmentIdSet = useMemo(
    () => new Set(((batchJob && batchJob.activeSegmentIds) || []).map((id) => Number(id))),
    [batchJob && Array.isArray(batchJob.activeSegmentIds) ? batchJob.activeSegmentIds.join("|") : ""]
  );
  const batchResultBySegmentId = useMemo(() => {
    const map = new Map();
    ((batchJob && batchJob.results) || []).forEach((result) => {
      const id = Number(result && result.segmentId);
      if (Number.isFinite(id)) {
        map.set(id, result);
      }
    });
    return map;
  }, [batchJob && Array.isArray(batchJob.results) ? batchJob.results.map((result) => `${result.segmentId}:${result.success}`).join("|") : ""]);
  const safeFieldValueFilters = Array.isArray(fieldValueFilters) ? fieldValueFilters : [];
  const fieldValueFilterCount = safeFieldValueFilters.length;
  const fieldValueFilterKey = safeFieldValueFilters.map((filter) => filter && filter.key).filter(Boolean).join("|");
  const starRatingFilterKey = starRatingFilters.join("|");
  const columnFilterCount = Object.values(columnFilters).filter(isSpecsFilterActive).length;
  const advancedFilterCount = advancedFilters.length;
  const advancedFilterKey = advancedFilters.map((filter) => filter && filter.key).filter(Boolean).join("|");
  const starRatingFilterActive = canViewStarRanking && starRatingFilters.length > 0;
  const editorFilterCount = canEdit
    ? (batchExclusionFilter !== "active" ? 1 : 0) +
      (aiAnalysisFilter !== "all" ? 1 : 0) +
      (approvalFilter !== "all" ? 1 : 0)
    : 0;
  const activeFilterCount = columnFilterCount + advancedFilterCount + editorFilterCount + (fieldValueFilterEnabled ? fieldValueFilterCount : 0) + (starRatingFilterActive ? 1 : 0);
  const comparisonFieldKeys = useMemo(
    () => Array.from(new Set(
      advancedFilters
        .filter((filter) => filter && specsAdvancedComparisonMeta(filter.mode) && filter.fieldKey)
        .map((filter) => filter.fieldKey)
    )),
    [advancedFilterKey]
  );
  const comparisonFieldKeySet = useMemo(() => new Set(comparisonFieldKeys), [comparisonFieldKeys.join("|")]);
  const columnFilterFocusKeys = useMemo(
    () => Object.entries(columnFilters)
      .filter(([, filter]) => isSpecsFilterActive(filter))
      .map(([key]) => key),
    [columnFilters]
  );
  const fieldValueFocusFieldKeys = useMemo(() => {
    if (!fieldValueFilterEnabled || !safeFieldValueFilters.length) {
      return [];
    }
    const fieldCodes = new Set(
      safeFieldValueFilters
        .map((filter) => String(filter && filter.fieldCode ? filter.fieldCode : "").trim())
        .filter(Boolean)
    );
    if (!fieldCodes.size) {
      return [];
    }
    return columns
      .filter((column) => (
        column &&
        column.kind === "field" &&
        column.field &&
        fieldCodes.has(String(column.field.field || "").trim())
      ))
      .map((column) => column.key);
  }, [columns, fieldValueFilterEnabled, fieldValueFilterKey]);
  const focusFieldKeys = useMemo(
    () => Array.from(new Set([
      advancedFieldKey,
      ...columnFilterFocusKeys,
      ...fieldValueFocusFieldKeys,
      ...advancedFilters.map((filter) => filter && filter.fieldKey)
    ].filter(Boolean))),
    [advancedFieldKey, columnFilterFocusKeys.join("|"), fieldValueFocusFieldKeys.join("|"), advancedFilterKey]
  );
  const focusFieldKeySet = useMemo(() => new Set(focusFieldKeys), [focusFieldKeys.join("|")]);
  const focusFieldCodes = useMemo(() => {
    if (!focusFieldKeys.length) {
      return [];
    }
    const keys = new Set(focusFieldKeys);
    return Array.from(new Set(columns
      .filter((column) => (
        column &&
        column.kind === "field" &&
        column.field &&
        column.field.field &&
        keys.has(column.key)
      ))
      .map((column) => String(column.field.field || "").trim())
      .filter(Boolean)));
  }, [columns, focusFieldKeys.join("|")]);
  const focusFieldCodeKey = focusFieldCodes.join("|");
  const baseGridColumns = useMemo(() => {
    if (!focusFieldKeys.length) {
      return columns;
    }
    const columnsByKey = new Map(columns.map((column) => [column.key, column]));
    const idColumn = columnsByKey.get("__id");
    const focusColumns = focusFieldKeys
      .map((key) => columnsByKey.get(key))
      .filter(Boolean)
      .map((column) => ({
        ...column,
        gridFocusField: true,
        oldMismatchFocus: comparisonFieldKeySet.has(column.key),
        width: Math.max(Number(column.width) || 0, comparisonFieldKeySet.has(column.key) ? 280 : 220)
      }));
    const remainingColumns = columns.filter((column) => (
      column.key !== "__id" && !focusFieldKeySet.has(column.key)
    ));
    return (idColumn ? [idColumn] : []).concat(focusColumns, remainingColumns);
  }, [columns, focusFieldKeys.join("|"), focusFieldKeySet, comparisonFieldKeySet]);
  const gridColumns = useMemo(() => (
    baseGridColumns.map((column) => {
      const customWidth = columnWidths[column.key];
      return {
        ...column,
        width: clampSpecsGridColumnWidth(column, customWidth === undefined ? column.width : customWidth)
      };
    })
  ), [baseGridColumns, columnWidths]);
  const effectiveGridTemplateColumns = useMemo(
    () => gridColumns.map((column) => `${column.width}px`).join(" "),
    [gridColumns]
  );
  const blockedBatchCount = useMemo(
    () => filteredSegments.filter((segment) => segmentBatchExcluded(segment)).length,
    [filteredSegments]
  );
  const activeBatchCount = Math.max(0, filteredSegments.length - blockedBatchCount);
  const batchScopedSegments = useMemo(() => {
    if (!canEdit || batchExclusionFilter === "all") {
      return filteredSegments;
    }
    if (batchExclusionFilter === "blocked") {
      return filteredSegments.filter((segment) => segmentBatchExcluded(segment));
    }
    return filteredSegments.filter((segment) => !segmentBatchExcluded(segment));
  }, [filteredSegments, batchExclusionFilter, canEdit]);
  const aiProcessedCount = useMemo(
    () => batchScopedSegments.filter((segment) => specsSegmentHasAiAnalysis(segment, analysisResults)).length,
    [batchScopedSegments, analysisResults]
  );
  const aiNotProcessedCount = Math.max(0, batchScopedSegments.length - aiProcessedCount);
  const approvedCount = useMemo(
    () => batchScopedSegments.filter((segment) => specsSegmentIsApproved(segment)).length,
    [batchScopedSegments]
  );
  const reviewCount = useMemo(
    () => batchScopedSegments.filter((segment) => specsSegmentNeedsReview(segment)).length,
    [batchScopedSegments]
  );
  const pendingApprovalCount = useMemo(
    () => batchScopedSegments.filter((segment) => specsSegmentApprovalState(segment) === "pending").length,
    [batchScopedSegments]
  );
  const advancedFieldColumns = useMemo(
    () => columns.filter((column) => column.kind === "field" && column.field && column.field.field),
    [columns]
  );
  const filteredAdvancedFieldColumns = useMemo(() => {
    const query = advancedFieldSearch.trim().toLowerCase();
    if (!query) {
      return advancedFieldColumns;
    }
    return advancedFieldColumns.filter((column) => specsFieldSearchText(column).includes(query));
  }, [advancedFieldColumns, advancedFieldSearch]);
  const selectedAdvancedColumn = useMemo(() => {
    if (advancedFieldKey) {
      return advancedFieldColumns.find((column) => column.key === advancedFieldKey) || null;
    }
    if (!advancedFieldSearch.trim() || !filteredAdvancedFieldColumns.length) {
      return null;
    }
    return filteredAdvancedFieldColumns[0] || null;
  }, [advancedFieldColumns, filteredAdvancedFieldColumns, advancedFieldKey, advancedFieldSearch]);
  const advancedValueOptions = useMemo(
    () => specsAdvancedFilterValueOptions(selectedAdvancedColumn, batchScopedSegments, analysisResults),
    [selectedAdvancedColumn, batchScopedSegments, analysisResults]
  );
  const advancedSelectedValueSet = useMemo(
    () => new Set((Array.isArray(advancedValues) ? advancedValues : []).map((value) => String(value))),
    [advancedValues]
  );
  const advancedSelectedValueOptions = useMemo(
    () => advancedValueOptions.filter((option) => advancedSelectedValueSet.has(String(option.value))),
    [advancedValueOptions, advancedSelectedValueSet]
  );
  const normalizedAdvancedConfidenceRange = normalizeAdvancedConfidenceRange(advancedConfidenceRange);
  const advancedConfidenceRangeActive = !advancedConfidenceRangeIsDefault(normalizedAdvancedConfidenceRange);
  const advancedConfidenceKey = `${normalizedAdvancedConfidenceRange.min}-${normalizedAdvancedConfidenceRange.max}`;
  const advancedConfidenceMinPct = ((normalizedAdvancedConfidenceRange.min - SPECS_CONFIDENCE_RANGE_MIN) / (SPECS_CONFIDENCE_RANGE_MAX - SPECS_CONFIDENCE_RANGE_MIN)) * 100;
  const advancedConfidenceMaxPct = ((normalizedAdvancedConfidenceRange.max - SPECS_CONFIDENCE_RANGE_MIN) / (SPECS_CONFIDENCE_RANGE_MAX - SPECS_CONFIDENCE_RANGE_MIN)) * 100;
  const advancedAddCount = advancedSelectedValueOptions.length + (advancedComparisonMode ? 1 : 0) + (advancedConfidenceRangeActive ? 1 : 0);
  const advancedFilterGroups = useMemo(() => {
    const groups = [];
    const groupsByField = new Map();
    advancedFilters.forEach((filter) => {
      if (!filter || !filter.fieldKey) {
        return;
      }
      const fieldKey = filter.fieldKey;
      let group = groupsByField.get(fieldKey);
      if (!group) {
        group = {
          fieldKey,
          fieldLabel: filter.fieldLabel || filter.fieldCode || fieldKey,
          comparisons: [],
          confidences: [],
          values: [],
          keys: []
        };
        groupsByField.set(fieldKey, group);
        groups.push(group);
      }
      const comparisonMeta = specsAdvancedComparisonMeta(filter.mode);
      if (comparisonMeta) {
        group.comparisons.push(comparisonMeta.short);
      } else if (filter.mode === "confidenceRange") {
        group.confidences.push(advancedConfidenceRangeLabel({
          min: filter.minConfidence,
          max: filter.maxConfidence
        }));
      } else if (specsAdvancedConfidenceMeta(filter.mode)) {
        group.confidences.push(specsAdvancedConfidenceMeta(filter.mode).short);
      } else {
        group.values.push(filter.display || formatSpecsSegmentGridValue(filter.value));
      }
      group.keys.push(filter.key);
    });
    return groups.map((group) => ({
      ...group,
      comparisonLabels: Array.from(new Set(group.comparisons)),
      confidenceLabels: Array.from(new Set(group.confidences)),
      count: group.values.length + group.comparisons.length + group.confidences.length,
      title: `${group.fieldLabel}\n${group.comparisons.concat(group.confidences, group.values).join("\n")}`
    }));
  }, [advancedFilters]);
  const aiScopedSegments = useMemo(
    () => {
      if (!canEdit) {
        return batchScopedSegments;
      }
      if (aiAnalysisFilter === "ai") {
        return batchScopedSegments.filter((segment) => specsSegmentHasAiAnalysis(segment, analysisResults));
      }
      if (aiAnalysisFilter === "notProcessed") {
        return batchScopedSegments.filter((segment) => !specsSegmentHasAiAnalysis(segment, analysisResults));
      }
      return batchScopedSegments;
    },
    [batchScopedSegments, analysisResults, aiAnalysisFilter, canEdit]
  );
  const approvalScopedSegments = useMemo(
    () => {
      if (!canEdit) {
        return aiScopedSegments;
      }
      if (approvalFilter === "approved") {
        return aiScopedSegments.filter((segment) => specsSegmentIsApproved(segment));
      }
      if (approvalFilter === "review") {
        return aiScopedSegments.filter((segment) => specsSegmentNeedsReview(segment));
      }
      if (approvalFilter === "pending") {
        return aiScopedSegments.filter((segment) => specsSegmentApprovalState(segment) === "pending");
      }
      return aiScopedSegments;
    },
    [aiScopedSegments, approvalFilter, canEdit]
  );
  const starRatingFilterGroups = useMemo(() => {
    if (!canViewStarRanking) {
      return [];
    }
    const countFor = (value) => approvalScopedSegments.filter((segment) => segmentMatchesStarRatingFilter(segment, value)).length;
    const zeroStarCount = SEGMENT_STAR_RATING_USERS.reduce((count, user) => (
      count + approvalScopedSegments.filter((segment) => segmentMatchesStarRatingFilter(segment, `${user.key}:0`)).length
    ), 0);
    const bandValues = (zeroStarCount > 0 ? [0, 1, 2, 3, 4, 5] : [1, 2, 3, 4, 5]);
    return [
      {
        label: "Whole segment",
        options: [
          { value: "lowest:1", label: "Any 1-star", count: countFor("lowest:1") },
          { value: "lowest:2", label: "Any 1-2 star", count: countFor("lowest:2") },
          { value: "all:3plus", label: "All users 3+", count: countFor("all:3plus") }
        ]
      },
      ...SEGMENT_STAR_RATING_USERS.map((user) => ({
        label: user.label,
        options: bandValues.map((band) => ({
          value: `${user.key}:${band}`,
          label: `${user.short} ${band} star`,
          count: countFor(`${user.key}:${band}`)
        }))
      }))
    ];
  }, [approvalScopedSegments, canViewStarRanking]);
  const starRatingSelectedSet = useMemo(() => new Set(starRatingFilters), [starRatingFilterKey]);
  const selectedStarRatingFilterCount = !starRatingFilterActive
    ? approvalScopedSegments.length
    : approvalScopedSegments.filter((segment) => segmentMatchesStarRatingFilters(segment, starRatingFilters)).length;
  const starScopedSegments = useMemo(
    () => !canViewStarRanking || !starRatingFilterActive
      ? approvalScopedSegments
      : approvalScopedSegments.filter((segment) => segmentMatchesStarRatingFilters(segment, starRatingFilters)),
    [approvalScopedSegments, canViewStarRanking, starRatingFilterActive, starRatingFilterKey]
  );
  const toggleStarRatingFilter = (value) => {
    if (!canViewStarRanking) {
      return;
    }
    const key = String(value || "").trim();
    if (!key || key === "all") {
      setStarRatingFilters([]);
      return;
    }
    setStarRatingFilters((current) => {
      const safeCurrent = Array.isArray(current) ? current : [];
      return safeCurrent.includes(key)
        ? safeCurrent.filter((item) => item !== key)
        : safeCurrent.concat(key);
    });
  };
  const fieldValueScopedSegments = useMemo(() => {
    if (!fieldValueFilterEnabled || !fieldValueFilterCount) {
      return starScopedSegments;
    }
    return starScopedSegments.filter((segment) =>
      specsSegmentMatchesFieldValueFilters(segment, safeFieldValueFilters, columns, analysisResults)
    );
  }, [starScopedSegments, fieldValueFilterEnabled, fieldValueFilterKey, fieldValueFilterCount, columns, analysisResults]);
  const advancedScopedSegments = useMemo(() => {
    if (!advancedFilterCount) {
      return fieldValueScopedSegments;
    }
    return fieldValueScopedSegments.filter((segment) =>
      specsSegmentMatchesAdvancedFilters(segment, advancedFilters, columns, analysisResults)
    );
  }, [fieldValueScopedSegments, advancedFilters, advancedFilterCount, columns, analysisResults]);
  const visibleSegments = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, filter]) => isSpecsFilterActive(filter));
    if (!activeFilters.length) {
      return advancedScopedSegments;
    }
    const columnsByKey = new Map(columns.map((column) => [column.key, column]));
    return advancedScopedSegments.filter((segment) =>
      activeFilters.every(([key, filter]) => {
        const column = columnsByKey.get(key);
        return !column || specsSegmentMatchesColumnFilter(segment, column, filter, analysisResults);
      })
    );
  }, [advancedScopedSegments, columns, columnFilters, analysisResults]);
  const sortColumn = useMemo(
    () => sortState.key ? gridColumns.find((column) => column.key === sortState.key) || null : null,
    [gridColumns, sortState.key]
  );
  const sortedVisibleSegments = useMemo(() => {
    if (!sortColumn || !sortState.direction) {
      return visibleSegments;
    }
    const direction = sortState.direction === "desc" ? -1 : 1;
    const sortValue = (segment) => (
      sortColumn.oldMismatchFocus
        ? specsOldNewFieldDisplay(segment, sortColumn, analysisResults).aiValue
        : getSpecsColumnFilterRawValue(segment, sortColumn, analysisResults)
    );
    return visibleSegments.slice().sort((left, right) => {
      const leftRaw = sortValue(left);
      const rightRaw = sortValue(right);
      const leftHasValue = hasDisplayValue(leftRaw);
      const rightHasValue = hasDisplayValue(rightRaw);
      if (leftHasValue !== rightHasValue) {
        return leftHasValue ? -1 : 1;
      }
      const leftNumber = Number(leftRaw);
      const rightNumber = Number(rightRaw);
      const numeric = leftHasValue && rightHasValue && Number.isFinite(leftNumber) && Number.isFinite(rightNumber)
        ? leftNumber - rightNumber
        : String(leftRaw ?? "").localeCompare(String(rightRaw ?? ""), undefined, { numeric: true, sensitivity: "base" });
      if (numeric !== 0) {
        return numeric * direction;
      }
      return (Number(left.id) - Number(right.id)) || 0;
    });
  }, [visibleSegments, sortColumn, sortState.direction, analysisResults]);
  const totalPages = Math.max(1, Math.ceil(sortedVisibleSegments.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStartIndex = sortedVisibleSegments.length ? (safePage - 1) * pageSize : 0;
  const pageEndIndex = Math.min(pageStartIndex + pageSize, sortedVisibleSegments.length);
  const pagedSegments = useMemo(
    () => sortedVisibleSegments.slice(pageStartIndex, pageEndIndex),
    [sortedVisibleSegments, pageStartIndex, pageEndIndex]
  );
  const visibleSegmentIds = useMemo(
    () => sortedVisibleSegments
      .map((segment) => Number(segment && segment.id))
      .filter((segmentId) => Number.isFinite(segmentId)),
    [sortedVisibleSegments]
  );
  const visibleSegmentIdsKey = visibleSegmentIds.join("|");
  const batchStatusForSegment = (segment) => {
    const segmentId = Number(segment && segment.id);
    if (!Number.isFinite(segmentId)) {
      return null;
    }
    if (batchActiveSegmentIdSet.has(segmentId)) {
      return "running";
    }
    const result = batchResultBySegmentId.get(segmentId);
    if (!result) {
      return null;
    }
    return result.success === false ? "failed" : "complete";
  };
  const renderSegmentCellValue = (segment, column) => {
    if (column.kind === "id") {
      const isBlocked = segmentBatchExcluded(segment);
      return (
        <span className="specs-segment-id-cell">
          <span>{segment.id}</span>
          {isBlocked && (
            <span className="specs-segment-blocked-badge" title="Blocked from batch processing">
              {icon("ban")}
            </span>
          )}
          <span
            className="specs-aerial-overlay-button"
            role="button"
            title={`Open aerial overlay for segment ${segment.id}`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              openSegmentAerialOverlay(segment.id);
            }}
          >
            {icon("map")}
          </span>
        </span>
      );
    }
    if (column.kind === "starRatings") {
      return <SegmentStarRatingMini ratings={segmentStarRatings(segment)} />;
    }
    if (column.kind === "starRatingUser") {
      const item = segmentStarRatingForUser(segment, column.starUserKey);
      return item ? <SegmentStarRatingMini ratings={[item]} /> : "-";
    }
    if (column.gridFocusField && !column.oldMismatchFocus) {
      const confidence = getSpecsAiFieldConfidencePercent(segment, column, analysisResults);
      const confidenceTone = fieldConfidenceTone(confidence);
      const aiField = getSpecsAiFieldResult(segment, column, analysisResults);
      return (
        <span className="specs-grid-field-focus-value">
          <span className="specs-grid-field-focus-value__display">
            {getSpecsSegmentGridValue(segment, column, analysisResults)}
          </span>
          <span className={`specs-grid-confidence confidence-${confidenceTone}`}>
            {confidence === null ? (aiField ? "No conf." : "No AI") : `${confidence}%`}
          </span>
        </span>
      );
    }
    if (!column.oldMismatchFocus) {
      return getSpecsSegmentGridValue(segment, column, analysisResults);
    }
    const comparison = specsOldNewFieldDisplay(segment, column, analysisResults);
    const confidence = getSpecsAiFieldConfidencePercent(segment, column, analysisResults);
    const confidenceTone = fieldConfidenceTone(confidence);
    const aiField = getSpecsAiFieldResult(segment, column, analysisResults);
    return (
      <span className={`specs-old-new-value ${comparison.different ? "is-different" : "is-same"}`}>
        <span className="specs-old-new-value__part is-old">
          <small>Old</small>
          <b>{comparison.oldDisplay}</b>
        </span>
        <span className="specs-old-new-value__part is-ai">
          <small>AI</small>
          <span className="specs-old-new-value__ai-main">
            <b>{comparison.aiDisplay}</b>
            <em className={`specs-grid-confidence confidence-${confidenceTone}`}>
              {confidence === null ? (aiField ? "No conf." : "No AI") : `${confidence}%`}
            </em>
          </span>
        </span>
      </span>
    );
  };
  const segmentCellTitle = (segment, column) => {
    if (column.kind === "starRatings") {
      return segmentStarRatingsTitle(segment);
    }
    if (column.kind === "starRatingUser") {
      const item = segmentStarRatingForUser(segment, column.starUserKey);
      return item ? segmentStarRatingTitle(item) : `No ${column.title} value`;
    }
    if (!column.gridFocusField && !column.oldMismatchFocus) {
      return column.title;
    }
    const confidence = getSpecsAiFieldConfidencePercent(segment, column, analysisResults);
    const aiField = getSpecsAiFieldResult(segment, column, analysisResults);
    const confidenceText = confidence === null ? (aiField ? "No confidence" : "No AI result") : `${confidence}% confidence`;
    if (!column.oldMismatchFocus) {
      return `${column.title}\nValue: ${getSpecsSegmentGridValue(segment, column, analysisResults)}\n${confidenceText}`;
    }
    const comparison = specsOldNewFieldDisplay(segment, column, analysisResults);
    return `${column.title}\nOld: ${comparison.oldDisplay}\nAI: ${comparison.aiDisplay}\n${confidenceText}`;
  };
  const estimateColumnAutoWidth = (column, segmentPool = sortedVisibleSegments) => {
    const sample = (Array.isArray(segmentPool) ? segmentPool : []).slice(0, 300);
    const textLength = (value) => String(value === null || value === undefined ? "" : value)
      .replace(/\s+/g, " ")
      .trim()
      .length;
    if (column.oldMismatchFocus) {
      const lengths = sample.reduce((acc, segment) => {
        const comparison = specsOldNewFieldDisplay(segment, column, analysisResults);
        return {
          old: Math.max(acc.old, textLength(comparison.oldDisplay)),
          ai: Math.max(acc.ai, textLength(comparison.aiDisplay))
        };
      }, {
        old: textLength(column.label),
        ai: textLength(column.label)
      });
      return clampSpecsGridColumnWidth(column, 140 + Math.ceil((lengths.old + lengths.ai) * 6.8));
    }
    const maxLength = sample.reduce((longest, segment) => (
      Math.max(longest, textLength(getSpecsSegmentGridValue(segment, column, analysisResults)))
    ), textLength(column.label));
    return clampSpecsGridColumnWidth(column, (column.gridFocusField ? 118 : 68) + Math.ceil(maxLength * 7.1));
  };
  const autoFitColumn = (column) => {
    if (!column || !column.key) {
      return;
    }
    const width = estimateColumnAutoWidth(column);
    setColumnWidths((current) => ({
      ...current,
      [column.key]: width
    }));
  };
  const autoFitAllColumns = () => {
    const nextWidths = {};
    gridColumns.forEach((column) => {
      nextWidths[column.key] = estimateColumnAutoWidth(column);
    });
    setColumnWidths((current) => ({
      ...current,
      ...nextWidths
    }));
  };
  const toggleColumnSort = (column) => {
    if (!column || !column.key) {
      return;
    }
    setSortState((current) => {
      if (!current || current.key !== column.key) {
        return { key: column.key, direction: "asc" };
      }
      if (current.direction === "asc") {
        return { key: column.key, direction: "desc" };
      }
      return { key: "", direction: "" };
    });
  };
  const startColumnResize = (event, column) => {
    if (!column || !column.key || (event.button !== undefined && event.button !== 0)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (columnResizeRef.current && typeof columnResizeRef.current.cleanup === "function") {
      columnResizeRef.current.cleanup();
    }
    const startX = event.clientX;
    const startWidth = Number(column.width) || specsGridColumnMinWidth(column);
    const handlePointerMove = (moveEvent) => {
      const nextWidth = clampSpecsGridColumnWidth(column, startWidth + moveEvent.clientX - startX);
      setColumnWidths((current) => ({
        ...current,
        [column.key]: nextWidth
      }));
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", cleanup);
      window.removeEventListener("pointercancel", cleanup);
      if (columnResizeRef.current && columnResizeRef.current.cleanup === cleanup) {
        columnResizeRef.current = null;
      }
    };
    columnResizeRef.current = { cleanup };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", cleanup);
    window.addEventListener("pointercancel", cleanup);
  };

  const updateColumnFilter = (key, filter) => {
    setColumnFilters((current) => ({
      ...current,
      [key]: filter
    }));
  };

  const clearColumnFilter = (key) => {
    setColumnFilters((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const clearAllColumnFilters = () => {
    setColumnFilters({});
    setOpenFilterKey(null);
  };
  const toggleAdvancedValue = (value) => {
    const comparableValue = String(value);
    setAdvancedComparisonMode("");
    setAdvancedValues((current) => {
      const safeCurrent = Array.isArray(current) ? current.map((candidate) => String(candidate)) : [];
      return safeCurrent.includes(comparableValue)
        ? safeCurrent.filter((candidate) => candidate !== comparableValue)
        : safeCurrent.concat(comparableValue);
    });
  };
  const updateAdvancedConfidenceRange = (key, value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return;
    }
    setAdvancedConfidenceRange((current) => {
      const normalized = normalizeAdvancedConfidenceRange(current);
      if (key === "min") {
        return normalizeAdvancedConfidenceRange({
          ...normalized,
          min: Math.min(numeric, normalized.max)
        });
      }
      return normalizeAdvancedConfidenceRange({
        ...normalized,
        max: Math.max(numeric, normalized.min)
      });
    });
  };
  const chooseAdvancedField = (column) => {
    if (!column) {
      return;
    }
    setAdvancedFieldKey(column.key);
    setAdvancedFieldSearch(specsAdvancedFieldLabel(column));
    setAdvancedValues([]);
    setAdvancedComparisonMode("");
    setAdvancedConfidenceRange(SPECS_DEFAULT_CONFIDENCE_RANGE);
    setAdvancedFieldPickerOpen(false);
  };
  const addAdvancedFilter = () => {
    const column = selectedAdvancedColumn;
    const selectedOptions = advancedSelectedValueOptions.length ? advancedSelectedValueOptions : [];
    if (!column || (!selectedOptions.length && !advancedComparisonMode && !advancedConfidenceRangeActive)) {
      return;
    }
    const nextFilters = selectedOptions.map((option) => ({
      key: `${column.key}::${normalizeSpecComparableValue(option.value)}`,
      fieldKey: column.key,
      fieldCode: column.field && column.field.field,
      fieldLabel: column.label,
      value: option.value,
      display: option.label
    }));
    const comparisonMeta = specsAdvancedComparisonMeta(advancedComparisonMode);
    if (comparisonMeta) {
      nextFilters.push({
        key: `${column.key}::${comparisonMeta.value}`,
        fieldKey: column.key,
        fieldCode: column.field && column.field.field,
        fieldLabel: column.label,
        mode: advancedComparisonMode,
        value: comparisonMeta.value,
        display: comparisonMeta.display
      });
    }
    if (advancedConfidenceRangeActive) {
      const range = normalizedAdvancedConfidenceRange;
      nextFilters.push({
        key: `${column.key}::__confidence_range__:${range.min}-${range.max}`,
        fieldKey: column.key,
        fieldCode: column.field && column.field.field,
        fieldLabel: column.label,
        mode: "confidenceRange",
        value: "__confidence_range__",
        minConfidence: range.min,
        maxConfidence: range.max,
        display: advancedConfidenceRangeLabel(range)
      });
    }
    setAdvancedFilters((current) => {
      const existingKeys = new Set(current.map((filter) => filter.key));
      const additions = nextFilters.filter((filter) => !existingKeys.has(filter.key));
      return additions.length ? current.concat(additions) : current;
    });
  };
  const removeAdvancedFilterGroup = (fieldKey) => {
    setAdvancedFilters((current) => current.filter((filter) => filter.fieldKey !== fieldKey));
  };
  const clearAllFilters = () => {
    clearAllColumnFilters();
    setAdvancedFilters([]);
    setAdvancedComparisonMode("");
    setAdvancedConfidenceRange(SPECS_DEFAULT_CONFIDENCE_RANGE);
    setStarRatingFilters([]);
    setStarRatingMenuOpen(false);
    setAdvancedFilterOpen(false);
    setBatchExclusionFilter("active");
    setAiAnalysisFilter("all");
    setApprovalFilter("all");
    if (typeof clearFieldValueFilters === "function") {
      clearFieldValueFilters();
    }
  };

  useEffect(() => {
    const request = externalAdvancedFilterRequest;
    if (!request || !request.requestId) {
      return;
    }
    const modeAliases = {
      changed: "oldMismatch",
      different: "oldMismatch",
      same: "oldSame",
      missing: "aiMissing"
    };
    const comparisonMode = modeAliases[request.mode] || request.mode;
    const comparisonMeta = specsAdvancedComparisonMeta(comparisonMode);
    if (!comparisonMeta) {
      return;
    }
    const fieldCode = String(request.fieldCode || "").trim();
    const fieldKey = String(request.fieldKey || "").trim();
    const column = columns.find((candidate) => (
      candidate &&
      candidate.kind === "field" &&
      (
        (fieldCode && candidate.field && String(candidate.field.field || "").trim() === fieldCode) ||
        (fieldKey && candidate.key === fieldKey)
      )
    ));
    if (!column) {
      return;
    }

    const filter = {
      key: `${column.key}::${comparisonMeta.value}`,
      fieldKey: column.key,
      fieldCode: column.field && column.field.field,
      fieldLabel: column.label,
      mode: comparisonMode,
      value: comparisonMeta.value,
      display: comparisonMeta.display
    };

    setAiAnalysisFilter("ai");
    setApprovalFilter("all");
    setStarRatingFilters([]);
    setStarRatingMenuOpen(false);
    setColumnFilters({});
    setOpenFilterKey(null);
    if (typeof clearFieldValueFilters === "function") {
      clearFieldValueFilters();
    }
    setAdvancedFilterOpen(true);
    setAdvancedFieldKey(column.key);
    setAdvancedFieldSearch(specsAdvancedFieldLabel(column));
    setAdvancedValues([]);
    setAdvancedComparisonMode(comparisonMode);
    setAdvancedConfidenceRange(SPECS_DEFAULT_CONFIDENCE_RANGE);
    setAdvancedFilters([filter]);
    setSortState({ key: column.key, direction: "asc" });
  }, [
    externalAdvancedFilterRequest && externalAdvancedFilterRequest.requestId,
    columns
  ]);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, columnFilters, aiAnalysisFilter, batchExclusionFilter, approvalFilter, starRatingFilterKey, fieldValueFilterEnabled, fieldValueFilterKey, advancedFieldKey, advancedConfidenceKey, advancedFilterKey, sortState.key, sortState.direction]);

  useEffect(() => {
    if (typeof onFocusFieldCodesChange === "function") {
      onFocusFieldCodesChange(focusFieldCodes);
    }
  }, [focusFieldCodeKey]);

  useEffect(() => () => {
    if (typeof onFocusFieldCodesChange === "function") {
      onFocusFieldCodesChange([]);
    }
  }, []);

  useEffect(() => {
    if (!advancedFieldKey && selectedAdvancedColumn) {
      setAdvancedFieldKey(selectedAdvancedColumn.key);
    }
  }, [advancedFieldKey, selectedAdvancedColumn]);

  useEffect(() => {
    if (advancedComparisonMode) {
      setAdvancedValues([]);
      return;
    }
    if (!advancedValueOptions.length) {
      setAdvancedValues([]);
      return;
    }
    const validValues = new Set(advancedValueOptions.map((option) => String(option.value)));
    setAdvancedValues((current) => {
      const safeCurrent = (Array.isArray(current) ? current : []).map((value) => String(value));
      const next = safeCurrent.filter((value) => validValues.has(value));
      return next;
    });
  }, [advancedValueOptions, advancedComparisonMode]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    const previousSelectedId = previousSelectedIdRef.current;
    previousSelectedIdRef.current = selectedId;
    if (Number(previousSelectedId) === Number(selectedId)) {
      return;
    }
    const selectedIndex = sortedVisibleSegments.findIndex((segment) => Number(segment.id) === Number(selectedId));
    if (selectedIndex >= 0) {
      setPage(Math.floor(selectedIndex / pageSize) + 1);
    }
  }, [selectedId, sortedVisibleSegments, pageSize]);

  useEffect(() => {
    const validKeys = new Set(columns.map((column) => column.key));
    setColumnFilters((current) => {
      const next = {};
      Object.entries(current).forEach(([key, filter]) => {
        if (validKeys.has(key)) {
          next[key] = filter;
        }
      });
      return next;
    });
    setAdvancedFilters((current) => current.filter((filter) => validKeys.has(filter.fieldKey)));
    setColumnWidths((current) => {
      const next = {};
      Object.entries(current).forEach(([key, width]) => {
        if (validKeys.has(key)) {
          next[key] = width;
        }
      });
      return next;
    });
    setSortState((current) => current && current.key && !validKeys.has(current.key) ? { key: "", direction: "" } : current);
    setColumnContextMenu((current) => (
      current && current.column && validKeys.has(current.column.key) ? current : null
    ));
  }, [columns]);

  useEffect(() => {
    if (typeof onVisibleSegmentIdsChange === "function") {
      onVisibleSegmentIdsChange(visibleSegmentIds);
    }
  }, [visibleSegmentIdsKey, onVisibleSegmentIdsChange]);

  useEffect(() => () => {
    if (columnResizeRef.current && typeof columnResizeRef.current.cleanup === "function") {
      columnResizeRef.current.cleanup();
    }
  }, []);

  useEffect(() => {
    if (!canEdit && segmentContextMenu) {
      setSegmentContextMenu(null);
    }
  }, [canEdit, segmentContextMenu]);

  useEffect(() => {
    if (canEdit) {
      return;
    }
    setBatchExclusionFilter("active");
    setAiAnalysisFilter("all");
    setApprovalFilter("all");
  }, [canEdit]);

  useEffect(() => {
    if (canViewStarRanking) {
      return;
    }
    setStarRatingFilters([]);
    setStarRatingMenuOpen(false);
  }, [canViewStarRanking]);

  useEffect(() => {
    if (!segmentContextMenu) {
      return undefined;
    }
    const closeMenu = () => setSegmentContextMenu(null);
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };
    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [segmentContextMenu]);

  useEffect(() => {
    if (!columnContextMenu) {
      return undefined;
    }
    const closeMenu = () => setColumnContextMenu(null);
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };
    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [columnContextMenu]);

  useEffect(() => {
    if (!starRatingMenuOpen) {
      return undefined;
    }
    const closeMenu = (event) => {
      if (event && starRatingFilterRef.current && starRatingFilterRef.current.contains(event.target)) {
        return;
      }
      setStarRatingMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };
    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [starRatingMenuOpen]);

  const openColumnContextMenu = (event, column) => {
    if (!column || !column.key) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const menuWidth = 224;
    const menuHeight = 112;
    const x = Math.min(event.clientX, Math.max(12, window.innerWidth - menuWidth - 12));
    const y = Math.min(event.clientY, Math.max(12, window.innerHeight - menuHeight - 12));
    setOpenFilterKey(null);
    setSegmentContextMenu(null);
    setColumnContextMenu({
      column,
      x,
      y
    });
  };

  const openSegmentContextMenu = (event, segment) => {
    if (!canEdit || !segment) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setColumnContextMenu(null);
    const menuWidth = 270;
    const menuHeight = 156;
    const x = Math.min(event.clientX, Math.max(12, window.innerWidth - menuWidth - 12));
    const y = Math.min(event.clientY, Math.max(12, window.innerHeight - menuHeight - 12));
    setSegmentContextMenu({
      segment,
      x,
      y
    });
  };

  const runSegmentContextAction = (runAll = false) => {
    if (!segmentContextMenu || typeof onRunSegmentAgents !== "function") {
      return;
    }
    const segment = segmentContextMenu.segment;
    setSegmentContextMenu(null);
    onRunSegmentAgents(segment, { runAll });
  };

  return (
    <section className="specs-segment-list">
      <header>
        <div className="specs-segment-list-summary">
          <span>Segments</span>
          <strong>{visibleSegments.length}</strong>
        </div>
        <div className="specs-segment-list-controls">
          {canEdit && (
            <label
              className={`specs-status-filter-combo specs-batch-filter-combo ${batchExclusionFilter !== "active" ? "active" : ""} is-${batchExclusionFilter}`}
              title="Filter blocked segments"
            >
              {icon(batchExclusionFilter === "blocked" ? "ban" : batchExclusionFilter === "all" ? "list-filter" : "circle-slash")}
              <span>Batch</span>
              <select
                value={batchExclusionFilter}
                onChange={(event) => setBatchExclusionFilter(event.target.value)}
                aria-label="Batch block filter"
              >
                <option value="active">Active ({activeBatchCount})</option>
                <option value="blocked">Blocked ({blockedBatchCount})</option>
                <option value="all">All ({filteredSegments.length})</option>
              </select>
            </label>
          )}
          {canEdit && (
            <label
              className={`specs-status-filter-combo ${approvalFilter !== "all" ? "active" : ""} is-${approvalFilter}`}
              title="Filter by approval status"
            >
              {icon(approvalFilter === "approved" ? "check" : approvalFilter === "review" ? "alert-triangle" : approvalFilter === "pending" ? "clock" : "list-filter")}
              <span>Status</span>
              <select
                value={approvalFilter}
                onChange={(event) => setApprovalFilter(event.target.value)}
                aria-label="Approval status filter"
              >
                <option value="all">All ({aiScopedSegments.length})</option>
                <option value="approved">Approved ({approvedCount})</option>
                <option value="review">Review ({reviewCount})</option>
                <option value="pending">Pending ({pendingApprovalCount})</option>
              </select>
            </label>
          )}
          {canViewStarRanking && (
            <div
              ref={starRatingFilterRef}
              className={`specs-status-filter-combo specs-star-filter-combo ${starRatingFilterActive ? "active" : ""}`}
              title="Filter by iRAP/AusRAP star rating"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="specs-star-filter-trigger"
                aria-label="Star rating filter"
                aria-expanded={starRatingMenuOpen}
                onClick={() => setStarRatingMenuOpen((current) => !current)}
              >
                {icon("star")}
                <span>Stars</span>
                <strong>{starRatingFiltersLabel(starRatingFilters)}</strong>
                {icon(starRatingMenuOpen ? "chevron-up" : "chevron-down")}
              </button>
              {starRatingFilterActive && <small>{selectedStarRatingFilterCount}</small>}
              {starRatingMenuOpen && (
                <div
                  className="specs-star-filter-popover"
                  onClick={(event) => event.stopPropagation()}
                  onWheel={(event) => event.stopPropagation()}
                >
                  <header>
                    <strong>Star Rating</strong>
                    <button
                      type="button"
                      disabled={!starRatingFilterActive}
                      onClick={() => setStarRatingFilters([])}
                    >
                      Clear
                    </button>
                  </header>
                  <label className={`specs-star-filter-option ${!starRatingFilterActive ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={!starRatingFilterActive}
                      onChange={() => setStarRatingFilters([])}
                    />
                    <span>All</span>
                    <small>{approvalScopedSegments.length}</small>
                  </label>
                  {starRatingFilterGroups.map((group) => (
                    <section key={group.label} className="specs-star-filter-group">
                      <strong>{group.label}</strong>
                      {group.options.map((option) => (
                        <label
                          key={option.value}
                          className={`specs-star-filter-option ${starRatingSelectedSet.has(option.value) ? "active" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={starRatingSelectedSet.has(option.value)}
                            onChange={() => toggleStarRatingFilter(option.value)}
                          />
                          <span>{option.label}</span>
                          <small>{option.count}</small>
                        </label>
                      ))}
                    </section>
                  ))}
                </div>
              )}
            </div>
          )}
          {canEdit && (
            <label
              className={`specs-status-filter-combo specs-ai-filter-combo ${aiAnalysisFilter !== "all" ? "active" : ""} is-${aiAnalysisFilter}`}
              title="Filter by AI processing status"
            >
              {icon("bot")}
              <span>AI</span>
              <select
                value={aiAnalysisFilter}
                onChange={(event) => setAiAnalysisFilter(event.target.value)}
                aria-label="AI processing filter"
              >
                <option value="all">All ({batchScopedSegments.length})</option>
                <option value="ai">AI ({aiProcessedCount})</option>
                <option value="notProcessed">Not processed ({aiNotProcessedCount})</option>
              </select>
            </label>
          )}
          <button
            className={`specs-ai-filter-toggle is-advanced ${advancedFilterOpen || advancedFilterCount ? "active" : ""}`}
            type="button"
            onClick={() => setAdvancedFilterOpen((current) => !current)}
            title="Build advanced field filters"
            aria-expanded={advancedFilterOpen}
            aria-pressed={advancedFilterCount > 0}
          >
            {icon("sliders-horizontal")}
            <span>Advanced</span>
            <small>{advancedFilterCount}</small>
          </button>
          <label
            className={`specs-map-list-toggle ${limitMapToList ? "active" : ""}`}
            title="Show only listed segments on the map"
          >
            <input
              type="checkbox"
              checked={Boolean(limitMapToList)}
              onChange={(event) => {
                if (typeof setLimitMapToList === "function") {
                  setLimitMapToList(event.target.checked);
                }
              }}
            />
            {icon("map")}
            <span>Map list</span>
            <small>{visibleSegments.length}</small>
          </label>
          {activeFilterCount > 0 && (
            <button
              className="specs-clear-filters"
              type="button"
              onClick={clearAllFilters}
              title="Clear all filters"
            >
              Clear
            </button>
          )}
        </div>
        <button
          className="icon-action specs-panel-close"
          type="button"
          title="Hide segment list"
          aria-label="Hide segment list"
          onClick={onClose}
        >
          {icon("panel-left-close")}
        </button>
      </header>
      {advancedFilterOpen && (
        <section className="specs-advanced-filter-panel" aria-label="Advanced segment filters">
          <div className="specs-advanced-filter-builder">
            <div className="specs-advanced-field-combo">
              <label htmlFor="specs-advanced-field-combo">Field</label>
              <input
                id="specs-advanced-field-combo"
                value={advancedFieldSearch}
                onFocus={() => setAdvancedFieldPickerOpen(true)}
                onBlur={() => setAdvancedFieldPickerOpen(false)}
                onChange={(event) => {
                  setAdvancedFieldSearch(event.target.value);
                  setAdvancedFieldKey("");
                  setAdvancedValues([]);
                  setAdvancedComparisonMode("");
                  setAdvancedConfidenceRange(SPECS_DEFAULT_CONFIDENCE_RANGE);
                  setAdvancedFieldPickerOpen(true);
                }}
                placeholder="Search/select field, e.g. bicycle"
                autoComplete="off"
                aria-expanded={advancedFieldPickerOpen}
              />
              {advancedFieldPickerOpen && (
                <div className="specs-advanced-field-options" role="listbox" aria-label="Matching fields">
                  {filteredAdvancedFieldColumns.length ? filteredAdvancedFieldColumns.slice(0, 18).map((column) => (
                    <button
                      key={column.key}
                      type="button"
                      className={`specs-advanced-field-option ${selectedAdvancedColumn && selectedAdvancedColumn.key === column.key ? "active" : ""}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => chooseAdvancedField(column)}
                      title={specsAdvancedFieldLabel(column)}
                    >
                      <strong>{column.label}</strong>
                      {column.field && column.field.field && <small>{column.field.field}</small>}
                    </button>
                  )) : (
                    <span>No matching fields</span>
                  )}
                </div>
              )}
            </div>
            <div className="specs-advanced-value-picker">
              <div className="specs-advanced-value-picker__head">
                <span>Values</span>
                <small>{advancedSelectedValueOptions.length} selected</small>
                <button
                  type="button"
                  onClick={() => {
                    setAdvancedComparisonMode("");
                    setAdvancedValues(advancedValueOptions.map((option) => String(option.value)));
                  }}
                  disabled={!advancedValueOptions.length}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setAdvancedValues([])}
                  disabled={!advancedSelectedValueOptions.length}
                >
                  Clear
                </button>
              </div>
              <div className={`specs-advanced-value-options ${advancedValueOptions.length ? "" : "is-empty"}`} role="group" aria-label="Advanced filter values">
                {advancedValueOptions.length ? advancedValueOptions.map((option) => (
                  <label key={option.value} className="specs-advanced-value-option">
                    <input
                      type="checkbox"
                      checked={advancedSelectedValueSet.has(String(option.value))}
                      onChange={() => toggleAdvancedValue(option.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                )) : (
                  <span>No values</span>
                )}
              </div>
              <div className="specs-advanced-comparison-options">
                {Object.entries(SPECS_ADVANCED_COMPARISON_FILTERS).map(([mode, option]) => (
                  <label key={mode} className={`specs-advanced-old-mismatch specs-advanced-comparison-option ${advancedComparisonMode === mode ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={advancedComparisonMode === mode}
                      onChange={(event) => {
                        setAdvancedComparisonMode(event.target.checked ? mode : "");
                        if (event.target.checked) {
                          setAdvancedValues([]);
                        }
                      }}
                    />
                    {icon(option.icon)}
                    <span>{option.display}</span>
                  </label>
                ))}
              </div>
              <div className={`specs-advanced-confidence-range ${advancedConfidenceRangeActive ? "active" : ""}`} aria-label="Confidence range filter">
                <header>
                  <span>Confidence</span>
                  <strong>{normalizedAdvancedConfidenceRange.min}-{normalizedAdvancedConfidenceRange.max}%</strong>
                  <button
                    type="button"
                    onClick={() => setAdvancedConfidenceRange(SPECS_DEFAULT_CONFIDENCE_RANGE)}
                    disabled={!advancedConfidenceRangeActive}
                  >
                    Reset
                  </button>
                </header>
                <div
                  className="specs-confidence-range-slider"
                  style={{
                    "--range-min": `${advancedConfidenceMinPct}%`,
                    "--range-max": `${advancedConfidenceMaxPct}%`
                  }}
                >
                  <input
                    type="range"
                    min={SPECS_CONFIDENCE_RANGE_MIN}
                    max={SPECS_CONFIDENCE_RANGE_MAX}
                    value={normalizedAdvancedConfidenceRange.min}
                    onChange={(event) => updateAdvancedConfidenceRange("min", event.target.value)}
                    aria-label="Minimum confidence"
                  />
                  <input
                    type="range"
                    min={SPECS_CONFIDENCE_RANGE_MIN}
                    max={SPECS_CONFIDENCE_RANGE_MAX}
                    value={normalizedAdvancedConfidenceRange.max}
                    onChange={(event) => updateAdvancedConfidenceRange("max", event.target.value)}
                    aria-label="Maximum confidence"
                  />
                </div>
                <div className="specs-confidence-range-inputs">
                  <label>
                    <span>Min</span>
                    <input
                      type="number"
                      min={SPECS_CONFIDENCE_RANGE_MIN}
                      max={normalizedAdvancedConfidenceRange.max}
                      value={normalizedAdvancedConfidenceRange.min}
                      onChange={(event) => updateAdvancedConfidenceRange("min", event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Max</span>
                    <input
                      type="number"
                      min={normalizedAdvancedConfidenceRange.min}
                      max={SPECS_CONFIDENCE_RANGE_MAX}
                      value={normalizedAdvancedConfidenceRange.max}
                      onChange={(event) => updateAdvancedConfidenceRange("max", event.target.value)}
                    />
                  </label>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="specs-advanced-add"
              onClick={addAdvancedFilter}
              disabled={!selectedAdvancedColumn || !advancedAddCount}
            >
              {icon("plus")} Add{advancedAddCount > 1 ? ` ${advancedAddCount}` : ""}
            </button>
          </div>
          <div className="specs-advanced-filter-chips">
            {advancedFilterGroups.length ? advancedFilterGroups.map((group) => (
              <div
                key={group.fieldKey}
                className="specs-advanced-filter-chip"
                title={group.title}
              >
                <strong>{group.fieldLabel}</strong>
                {group.comparisonLabels.map((label) => <small key={label}>{label}</small>)}
                {group.confidenceLabels.map((label) => <small key={label}>{label}</small>)}
                {group.count > 1 && <small>{group.count}</small>}
                <button
                  type="button"
                  className="specs-advanced-filter-remove"
                  onClick={() => removeAdvancedFilterGroup(group.fieldKey)}
                  title={`Remove ${group.fieldLabel} filter`}
                  aria-label={`Remove ${group.fieldLabel} filter`}
                >
                  {icon("x")}
                </button>
              </div>
            )) : (
              <span>No advanced filters added.</span>
            )}
          </div>
        </section>
      )}
      {showGridBatchProgress && (
        <div className={`specs-grid-batch-progress ${gridBatchActive ? "is-active" : "is-complete"}`}>
          <div>
            <strong>{gridBatchActive ? "Processing filtered grid" : "Grid batch finished"}</strong>
            <span>{batchScopeLabel} - {batchCompleted}/{batchTotal} segments</span>
          </div>
          <div className="specs-grid-batch-meter" aria-label={`Batch progress ${batchProgress}%`}>
            <span style={{ width: `${batchProgress}%` }}></span>
          </div>
          <small>
            {gridBatchActive && batchJob.activeSegmentIds && batchJob.activeSegmentIds.length
              ? `Active: ${batchJob.activeSegmentIds.join(", ")}`
              : `${batchProgress}% - ${batchJob.status}${Number(batchJob.excludedSegmentCount || 0) ? ` - ${batchJob.excludedSegmentCount} blocked skipped` : ""}`}
          </small>
        </div>
      )}
      <div className="specs-segment-scroll">
        <div
          className="specs-segment-grid"
          role="table"
          aria-label="Segments"
          style={{ "--specs-segment-grid-columns": effectiveGridTemplateColumns }}
        >
          <div className="specs-segment-grid-head" role="row">
            {gridColumns.map((column) => (
              <div
                key={column.key}
                className={`specs-segment-grid-cell specs-segment-grid-head-cell ${column.stickyLeft !== undefined ? "is-sticky" : ""} ${isSpecsFilterActive(columnFilters[column.key]) ? "is-filtered" : ""} ${column.gridFocusField ? "is-focus-field" : ""} ${column.oldMismatchFocus ? "is-old-mismatch-field" : ""}`}
                role="columnheader"
                style={column.stickyLeft !== undefined ? { "--sticky-left": `${column.stickyLeft}px` } : undefined}
                title={column.title}
                onContextMenu={(event) => openColumnContextMenu(event, column)}
              >
                <SpecsColumnHeader
                  column={column}
                  filter={columnFilters[column.key]}
                  isOpen={openFilterKey === column.key}
                  onToggleOpen={setOpenFilterKey}
                  onChangeFilter={updateColumnFilter}
                  onClearFilter={clearColumnFilter}
                  sortDirection={sortState.key === column.key ? sortState.direction : ""}
                  onSort={toggleColumnSort}
                  onAutoFit={autoFitColumn}
                  onResizeStart={startColumnResize}
                />
              </div>
            ))}
          </div>
          {pagedSegments.map((segment) => {
            const rowBatchStatus = batchStatusForSegment(segment);
            const isBatchBlocked = segmentBatchExcluded(segment);
            return (
              <button
                key={segment.id}
                className={`specs-segment-grid-row ${Number(segment.id) === Number(selectedId) ? "active" : ""} ${isBatchBlocked ? "is-batch-blocked" : ""} ${Number(runningSegmentAgentId) === Number(segment.id) ? "is-running-agents" : ""} ${rowBatchStatus ? `is-batch-${rowBatchStatus}` : ""}`}
                type="button"
                role="row"
                aria-label={`Segment ${segment.id}, ${segment.road || "Road segment"}, ${segment.distance || "no distance"}`}
                onClick={() => selectSegment(segment.id)}
                onContextMenu={canEdit ? (event) => openSegmentContextMenu(event, segment) : undefined}
              >
                {gridColumns.map((column) => (
                  <span
                    key={column.key}
                    className={`specs-segment-grid-cell ${column.stickyLeft !== undefined ? "is-sticky" : ""} ${column.gridFocusField ? "is-focus-field" : ""} ${column.oldMismatchFocus ? "is-old-mismatch-field" : ""}`}
                    role="cell"
                    style={column.stickyLeft !== undefined ? { "--sticky-left": `${column.stickyLeft}px` } : undefined}
                    title={segmentCellTitle(segment, column)}
                  >
                    {column.key === "__id" && rowBatchStatus && (
                      <span className={`specs-segment-batch-status is-${rowBatchStatus}`} title={`Batch ${rowBatchStatus}`}>
                        {icon(rowBatchStatus === "running" ? "loader-circle" : rowBatchStatus === "failed" ? "x" : "check")}
                      </span>
                    )}
                    {renderSegmentCellValue(segment, column)}
                  </span>
                ))}
              </button>
            );
          })}
        </div>
      </div>
      {columnContextMenu && (
        <div
          className="specs-column-context-menu"
          style={{ left: `${columnContextMenu.x}px`, top: `${columnContextMenu.y}px` }}
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
        >
          <strong>{columnContextMenu.column && columnContextMenu.column.label}</strong>
          <button
            type="button"
            onClick={() => {
              if (columnContextMenu.column) {
                autoFitColumn(columnContextMenu.column);
              }
              setColumnContextMenu(null);
            }}
            title="Fit this column to its visible values"
          >
            {icon("move-horizontal")}
            <span>Fit column</span>
          </button>
          <button
            type="button"
            onClick={() => {
              autoFitAllColumns();
              setColumnContextMenu(null);
            }}
            title="Fit all visible columns"
          >
            {icon("columns-2")}
            <span>Fit all columns</span>
          </button>
        </div>
      )}
      {canEdit && segmentContextMenu && (
        <div
          className="specs-segment-context-menu"
          style={{ left: `${segmentContextMenu.x}px`, top: `${segmentContextMenu.y}px` }}
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
        >
          <strong>Segment #{segmentContextMenu.segment && segmentContextMenu.segment.id}</strong>
          <button
            type="button"
            onClick={() => {
              if (segmentContextMenu.segment) {
                openSegmentAerialOverlay(segmentContextMenu.segment.id);
              }
              setSegmentContextMenu(null);
            }}
            title="Open the server-rendered aerial overlay for this segment"
          >
            {icon("map")}
            <span>Aerial overlay</span>
            <small>PNG</small>
          </button>
          <button
            type="button"
            onClick={() => runSegmentContextAction(false)}
            disabled={!selectedAgentGroupCount || typeof onRunSegmentAgents !== "function" || runningSegmentAgentId !== null}
            title={selectedAgentGroupCount ? `Run ${selectedAgentGroupCount} selected AI agent${selectedAgentGroupCount === 1 ? "" : "s"}` : "No AI agents selected"}
          >
            {icon(Number(runningSegmentAgentId) === Number(segmentContextMenu.segment && segmentContextMenu.segment.id) ? "loader-circle" : "bot")}
            <span>Run selected agents</span>
            <small>{selectedAgentGroupCount || "none"}</small>
          </button>
          <button
            type="button"
            onClick={() => runSegmentContextAction(true)}
            disabled={!allAgentGroupCount || typeof onRunSegmentAgents !== "function" || runningSegmentAgentId !== null}
            title={`Run all ${allAgentGroupCount} AI agent${allAgentGroupCount === 1 ? "" : "s"} for this segment`}
          >
            {icon("play")}
            <span>Run all agents</span>
            <small>{allAgentGroupCount}</small>
          </button>
        </div>
      )}
      <footer className="specs-segment-pagination">
        <span className="specs-segment-pagination__summary">
          {visibleSegments.length ? `${pageStartIndex + 1}-${pageEndIndex}` : "0-0"} of {visibleSegments.length}
          {visibleSegments.length !== filteredSegments.length && ` filtered from ${filteredSegments.length}`}
        </span>
        <div className="specs-segment-pagination__controls">
          <label>
            Rows
            <select
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
              aria-label="Rows per page"
            >
              {[25, 50, 100, 200].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => setPage(1)} disabled={safePage <= 1} title="First page">
            {icon("chevrons-left")}
          </button>
          <button type="button" onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} title="Previous page">
            {icon("chevron-left")}
          </button>
          <span>Page {safePage}/{totalPages}</span>
          <button type="button" onClick={() => setPage(Math.min(totalPages, safePage + 1))} disabled={safePage >= totalPages} title="Next page">
            {icon("chevron-right")}
          </button>
          <button type="button" onClick={() => setPage(totalPages)} disabled={safePage >= totalPages} title="Last page">
            {icon("chevrons-right")}
          </button>
        </div>
      </footer>
    </section>
  );
}

function SpecsSegmentInfoPanel({
  activeGroup,
  setActiveGroup,
  groupNames,
  groupedItems,
  allGroupedItems = null,
  aiInsightItems = null,
  neighborInsight = null,
  iconSize,
  setSpecIconSize,
  iconSizingStyle,
  fieldViewMode = "valued",
  setFieldViewMode = null,
  fieldViewCounts = {},
  showOldValues = false,
  setShowOldValues = null,
  fieldValueFilterEnabled = false,
  fieldValueFilterKeys = null,
  focusedFieldCodes = [],
  onToggleFieldValueFilter = null,
  metadata,
  starRatings = [],
  canViewStarRanking = false,
  evidenceItems,
  gisAssets = [],
  gisSummary = [],
  gisLoading = false,
  gisError = "",
  gisMapVisible = true,
  onToggleGisMapVisible = null,
  gisHiddenLayerKeys = [],
  onToggleGisLayerVisible = null,
  gisLayerSettings = DEFAULT_GIS_LAYER_SETTINGS,
  gisLayerOptions = null,
  selectedSegment,
  allSegments = [],
  searchSegments = [],
  gridSegmentIds = [],
  analysisResults = {},
  segmentImages = [],
  selectedImageFile = "",
  onOpenEvidence,
  onOpenFieldSourceImage = null,
  onClose,
  editMode = false,
  setEditMode = null,
  canEdit = false,
  onSaveField = null,
  onAddFieldEvidence = null,
  onOpenAnnotationEvidence = null,
  fieldPrompts = {},
  fieldPromptBusy = false,
  fieldPromptRunningField = "",
  onFieldPromptsUpdated = null,
  onSaveFieldPrompt = null,
  onRunFieldPrompt = null,
  onRunFieldPromptBatch = null,
  agentRunnerModel = "gpt-5.5",
  agentRunnerReasoningEffort = "low",
  authUser = null,
  logs = [],
  loadLogs = null,
  approvalState = "pending",
  approveBusy = false,
  onSetApprovalState = null,
  batchExcluded = false,
  batchExclusionReason = "",
  batchExcludeBusy = false,
  onSetBatchExcluded = null
}) {
  const [fieldSearch, setFieldSearch] = useState("");
  const [confidenceFilter, setConfidenceFilter] = useState("all");
  const [aiInsightCollapsed, setAiInsightCollapsed] = useState(true);
  const [neighborInsightEnabled, setNeighborInsightEnabled] = useState(false);
  const [editDraftValues, setEditDraftValues] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [fieldPromptMenu, setFieldPromptMenu] = useState(null);
  const [fieldModelComparisonItem, setFieldModelComparisonItem] = useState(null);
  const [fieldPromptEditor, setFieldPromptEditor] = useState(null);
  const [fieldPromptEditorMode, setFieldPromptEditorMode] = useState("discussion");
  const [fieldPromptDraft, setFieldPromptDraft] = useState("");
  const [fieldPromptError, setFieldPromptError] = useState("");
  const [fieldPromptChatMessages, setFieldPromptChatMessages] = useState([]);
  const [fieldPromptChatInput, setFieldPromptChatInput] = useState("");
  const [fieldPromptChatBusy, setFieldPromptChatBusy] = useState(false);
  const [fieldPromptSelectedImages, setFieldPromptSelectedImages] = useState([]);
  const [fieldPromptPastedImages, setFieldPromptPastedImages] = useState([]);
  const [fieldPromptSuggestion, setFieldPromptSuggestion] = useState("");
  const [fieldPromptDiscussionId, setFieldPromptDiscussionId] = useState("");
  const [fieldPromptDiscussionStatus, setFieldPromptDiscussionStatus] = useState("");
  const [fieldPromptDiscussionList, setFieldPromptDiscussionList] = useState([]);
  const [fieldPromptHistoryOpen, setFieldPromptHistoryOpen] = useState(false);
  const [fieldPromptFeedbackBusy, setFieldPromptFeedbackBusy] = useState(false);
  const [fieldPromptManagerStatus, setFieldPromptManagerStatus] = useState("");
  const [fieldPromptInjectToAgentPrompt, setFieldPromptInjectToAgentPrompt] = useState(false);
  const [fieldPromptIncludeAgentInstructions, setFieldPromptIncludeAgentInstructions] = useState(false);
  const [fieldPromptIncludeAgentPromptAddendum, setFieldPromptIncludeAgentPromptAddendum] = useState(false);
  const [fieldPromptImageSourceMode, setFieldPromptImageSourceMode] = useState(DEFAULT_FIELD_IMAGE_SOURCE_MODE);
  const [fieldPromptGisEnabled, setFieldPromptGisEnabled] = useState(false);
  const [fieldPromptGisClassIds, setFieldPromptGisClassIds] = useState([]);
  const [fieldPromptTrainerImages, setFieldPromptTrainerImages] = useState([]);
  const [fieldPromptTrainerBusy, setFieldPromptTrainerBusy] = useState(false);
  const [fieldPromptTrainerResult, setFieldPromptTrainerResult] = useState(null);
  const [starRatingChatOpen, setStarRatingChatOpen] = useState(false);
  const [starRatingChatMessages, setStarRatingChatMessages] = useState([]);
  const [starRatingChatInput, setStarRatingChatInput] = useState("");
  const [starRatingChatBusy, setStarRatingChatBusy] = useState(false);
  const [starRatingChatError, setStarRatingChatError] = useState("");
  const [focusedFieldDismissedKey, setFocusedFieldDismissedKey] = useState("");
  const evidenceCount = Array.isArray(evidenceItems) ? evidenceItems.length : 0;
  const gisAssetCount = Array.isArray(gisAssets) ? gisAssets.length : 0;
  const showEvidence = activeGroup === SPECS_EVIDENCE_TAB;
  const isIrapEditor = userCanEditIrap(authUser);
  const effectiveOpenAiModel = agentRunnerModel || "gpt-5.5";
  const effectiveReasoningEffort = agentRunnerReasoningEffort || "low";
  const openAiSettingsLabel = `${effectiveOpenAiModel} / ${effectiveReasoningEffort}`;
  const normalizedPanelGisSettings = normalizeGisLayerSettings(gisLayerSettings);
  const fieldPromptAiGisLayerOptions = useMemo(
    () => gisLayerOptionsForTarget(normalizedPanelGisSettings, gisLayerOptions, "ai"),
    [
      gisLayerOptions,
      normalizedPanelGisSettings.aiSelectionMode,
      normalizedPanelGisSettings.aiClassIds.join(",")
    ]
  );
  const fieldPromptAiGisClassIdSet = useMemo(
    () => new Set(normalizedPanelGisSettings.aiClassIds.map((classId) => String(classId))),
    [normalizedPanelGisSettings.aiClassIds.join(",")]
  );
  useEffect(() => {
    setAiInsightCollapsed(true);
  }, [selectedSegment && selectedSegment.id]);
  useEffect(() => {
    setStarRatingChatOpen(false);
    setStarRatingChatMessages([]);
    setStarRatingChatInput("");
    setStarRatingChatError("");
    setStarRatingChatBusy(false);
  }, [selectedSegment && selectedSegment.id]);
  useEffect(() => {
    if (canViewStarRanking) {
      return;
    }
    setStarRatingChatOpen(false);
    setStarRatingChatMessages([]);
    setStarRatingChatInput("");
    setStarRatingChatError("");
    setStarRatingChatBusy(false);
  }, [canViewStarRanking]);
  useEffect(() => {
    if (!fieldPromptMenu) {
      return undefined;
    }
    const close = () => setFieldPromptMenu(null);
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        close();
      }
    };
    window.addEventListener("click", close);
    window.addEventListener("contextmenu", close);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("contextmenu", close);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [fieldPromptMenu]);
  const editableItems = useMemo(() => (
    (Array.isArray(allGroupedItems) ? allGroupedItems : groupedItems)
      .flatMap((group) => Array.isArray(group.items) ? group.items : [])
      .filter((item) => item && item.field && item.field.field)
  ), [allGroupedItems, groupedItems]);
  const makeEditDraftValues = () => {
    const nextDrafts = {};
    editableItems.forEach((item) => {
      nextDrafts[item.field.field] = item.value ?? "";
    });
    return nextDrafts;
  };
  const fieldPromptConversationStorageKey = (item) => {
    const segmentId = selectedSegment && selectedSegment.id;
    const fieldCode = item && item.field && item.field.field;
    return segmentId && fieldCode ? `irap.fieldDiscussion.${segmentId}.${fieldCode}` : "";
  };
  const fieldPromptUserPayload = () => ({
    id: authUser && (authUser.id || authUser.userId || authUser.nid || authUser.username || authUser.userName || authUser.email) || "anonymous",
    name: authUser ? userDisplayName(authUser) : "Anonymous user",
    email: authUser && (authUser.email || authUser.mail) || "",
    isEditor: isIrapEditor
  });
  const fieldPromptGisSelection = () => {
    const normalized = normalizeFieldPromptGisSelection({
      enabled: fieldPromptGisEnabled,
      classIds: fieldPromptGisClassIds,
      limit: DEFAULT_GIS_LAYER_SETTINGS.aiLimit
    });
    const classIds = normalized.classIds.filter((classId) => (
      normalizedPanelGisSettings.aiSelectionMode === "selected"
        ? fieldPromptAiGisClassIdSet.has(String(classId))
        : true
    ));
    return {
      ...normalized,
      enabled: Boolean(normalized.enabled && classIds.length),
      classIds
    };
  };
  const applyLoadedFieldDiscussion = (discussion) => {
    if (!discussion) {
      return;
    }
    setFieldPromptDiscussionId(discussion.id || "");
    setFieldPromptDiscussionStatus(discussion.status || "");
    const incomingMessages = Array.isArray(discussion.messages) ? discussion.messages : [];
    setFieldPromptChatMessages((currentMessages) => {
      const previewByKey = new Map();
      (Array.isArray(currentMessages) ? currentMessages : []).forEach((message) => {
        (Array.isArray(message && message.attachments) ? message.attachments : []).forEach((attachment) => {
          const key = attachment && (attachment.id || attachment.name);
          const previewUrl = attachment && (attachment.previewUrl || attachment.dataUrl);
          if (key && previewUrl) {
            previewByKey.set(String(key), previewUrl);
          }
        });
      });
      return incomingMessages.map((message) => ({
        ...message,
        attachments: (Array.isArray(message && message.attachments) ? message.attachments : []).map((attachment) => {
          const key = attachment && (attachment.id || attachment.name);
          return key && previewByKey.has(String(key))
            ? { ...attachment, previewUrl: previewByKey.get(String(key)) }
            : attachment;
        })
      }));
    });
    setFieldPromptSuggestion(discussion.suggestedPrompt || "");
  };
  const loadFieldPromptDiscussions = async (item, options = {}) => {
    if (!item || !item.field || !selectedSegment || !selectedSegment.id) {
      return null;
    }
    const user = fieldPromptUserPayload();
    const params = new URLSearchParams({
      userId: user.id,
      userName: user.name,
      userEmail: user.email || "",
      isEditor: user.isEditor ? "1" : "0",
      all: (options.all || user.isEditor) ? "1" : "0",
      limit: "50"
    });
    const data = await apiFetch(`/api/segments/${Number(selectedSegment.id)}/field-discussions/${encodeURIComponent(item.field.field)}?${params.toString()}`);
    const discussions = Array.isArray(data && data.discussions) ? data.discussions : [];
    setFieldPromptDiscussionList(discussions);
    const current = data && data.current ? data.current : discussions[0];
    if (current) {
      applyLoadedFieldDiscussion(current);
    }
    return current || null;
  };
  const readStoredFieldPromptConversation = (item) => {
    const key = fieldPromptConversationStorageKey(item);
    if (!key) {
      return { messages: [], suggestion: "" };
    }
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || "{}");
      return {
        messages: Array.isArray(parsed.messages) ? parsed.messages : [],
        suggestion: typeof parsed.suggestion === "string" ? parsed.suggestion : ""
      };
    } catch (_error) {
      return { messages: [], suggestion: "" };
    }
  };
  const fieldPromptForItem = (item) => {
    const fieldCode = item && item.field && item.field.field;
    const promptEntry = fieldCode ? fieldPrompts[fieldCode] : null;
    return promptEntry && promptEntry.prompt ? promptEntry.prompt : "";
  };
  const fieldPromptImageSourceModeForItem = (item) => {
    const fieldCode = item && item.field && item.field.field;
    const promptEntry = fieldCode ? fieldPrompts[fieldCode] : null;
    return normalizeFieldImageSourceMode(promptEntry && promptEntry.imageSourceMode);
  };
  const filterGisSelectionToAiLayers = (selection) => {
    const normalized = normalizeFieldPromptGisSelection(selection);
    const classIds = normalized.classIds.filter((classId) => (
      normalizedPanelGisSettings.aiSelectionMode === "selected"
        ? fieldPromptAiGisClassIdSet.has(String(classId))
        : true
    ));
    return {
      ...normalized,
      enabled: Boolean(normalized.enabled && classIds.length),
      classIds
    };
  };
  const fieldPromptGisSelectionForItem = (item) => {
    const fieldCode = item && item.field && item.field.field;
    const promptEntry = fieldCode ? fieldPrompts[fieldCode] : null;
    return filterGisSelectionToAiLayers(promptEntry && promptEntry.gisLayerSelection);
  };
  const fieldPromptIsRunnable = (item) => Boolean(String(fieldPromptForItem(item) || "").trim());
  const openFieldPromptEditor = (item, options = {}) => {
    setFieldPromptMenu(null);
    const mode = options.mode === "prompt" ? "prompt" : "discussion";
    const initialImageSourceMode = fieldPromptImageSourceModeForItem(item);
    const initialGisSelection = fieldPromptGisSelectionForItem(item);
    setFieldPromptEditorMode(mode);
    setFieldPromptEditor(item);
    setFieldPromptDraft(fieldPromptForItem(item));
    setFieldPromptImageSourceMode(initialImageSourceMode);
    setFieldPromptGisEnabled(initialGisSelection.enabled);
    setFieldPromptGisClassIds(initialGisSelection.classIds.map((classId) => String(classId)));
    setFieldPromptError("");
    const storedConversation = readStoredFieldPromptConversation(item);
    setFieldPromptSuggestion(storedConversation.suggestion);
    setFieldPromptDiscussionId("");
    setFieldPromptDiscussionStatus("");
    setFieldPromptDiscussionList([]);
    setFieldPromptHistoryOpen(false);
    setFieldPromptManagerStatus("");
    setFieldPromptInjectToAgentPrompt(false);
    setFieldPromptIncludeAgentInstructions(false);
    setFieldPromptIncludeAgentPromptAddendum(false);
    setFieldPromptTrainerImages([]);
    setFieldPromptTrainerBusy(false);
    setFieldPromptTrainerResult(null);
    setFieldPromptChatInput("");
    setFieldPromptChatMessages(storedConversation.messages);
    setFieldPromptPastedImages([]);
    const sourceImages = fieldSourceImages(item);
    const firstSource = initialImageSourceMode === "aerial"
      ? AERIAL_OVERLAY_IMAGE_FILE
      : sourceImages[0] || (initialImageSourceMode === "both" ? AERIAL_OVERLAY_IMAGE_FILE : "") || selectedImageFile || (segmentImages[0] && segmentImages[0].file) || "";
    setFieldPromptSelectedImages(firstSource ? [firstSource] : []);
    if (mode === "discussion") {
      loadFieldPromptDiscussions(item).catch((error) => {
        setFieldPromptError(error.message || "Could not load field discussion history.");
      });
    }
  };
  const openFieldPromptDiscussion = (item) => openFieldPromptEditor(item, { mode: "discussion" });
  const openFieldPromptManager = (item) => openFieldPromptEditor(item, { mode: "prompt" });
  const openStarRatingChat = () => {
    if (!canViewStarRanking || !selectedSegment || !selectedSegment.id) {
      return;
    }
    if (!starRatingChatMessages.length && !String(starRatingChatInput || "").trim()) {
      setStarRatingChatInput(STAR_RATING_DEFAULT_CHAT_QUESTION);
    }
    setStarRatingChatError("");
    setStarRatingChatOpen(true);
  };
  const sendStarRatingChat = async (messageText = "") => {
    if (!canViewStarRanking || !selectedSegment || !selectedSegment.id || starRatingChatBusy) {
      return;
    }
    const content = String(messageText || starRatingChatInput || "").trim();
    if (!content) {
      return;
    }
    const nextMessages = starRatingChatMessages.concat({
      role: "user",
      content
    });
    setStarRatingChatMessages(nextMessages);
    setStarRatingChatInput("");
    setStarRatingChatBusy(true);
    setStarRatingChatError("");
    try {
      const response = await apiFetch(`/api/segments/${Number(selectedSegment.id)}/star-rating-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: nextMessages,
          model: effectiveOpenAiModel,
          reasoningEffort: effectiveReasoningEffort,
          imageDetail: "auto",
          user: fieldPromptUserPayload()
        })
      });
      const assistantText = starRatingChatResponseText(response) || "I could not produce a star-rating explanation.";
      setStarRatingChatMessages(nextMessages.concat({
        role: "assistant",
        content: assistantText
      }));
      if (typeof loadLogs === "function") {
        loadLogs({ silent: true });
      }
    } catch (error) {
      setStarRatingChatError(error.message || "Could not chat about this star rating.");
      setStarRatingChatMessages(nextMessages.concat({
        role: "assistant",
        content: `Error: ${error.message || "Could not chat about this star rating."}`
      }));
    } finally {
      setStarRatingChatBusy(false);
    }
  };
  useEffect(() => {
    if (!fieldPromptEditor) {
      return;
    }
    const key = fieldPromptConversationStorageKey(fieldPromptEditor);
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify({
        messages: fieldPromptChatMessages,
        suggestion: fieldPromptSuggestion
      }));
    } catch (_error) {
      // Local persistence is helpful but not required for the discussion flow.
    }
  }, [
    selectedSegment && selectedSegment.id,
    fieldPromptEditor && fieldPromptEditor.field && fieldPromptEditor.field.field,
    fieldPromptChatMessages,
    fieldPromptSuggestion
  ]);
  const saveFieldPromptDraft = async (options = {}) => {
    if (!fieldPromptEditor) {
      return;
    }
    setFieldPromptError("");
    const fieldCode = fieldPromptEditor.field && fieldPromptEditor.field.field;
    const applyToAgentPrompt = Boolean(options.applyToAgentPrompt || fieldPromptInjectToAgentPrompt);
    const includeAgentInstructions = Boolean(fieldPromptIncludeAgentInstructions);
    const includeAgentPromptAddendum = Boolean(fieldPromptIncludeAgentPromptAddendum);
    if ((options.runCurrent || options.runBatch) && !String(fieldPromptDraft || "").trim()) {
      setFieldPromptError("Field prompt is empty. Add a field prompt before running this field.");
      setFieldPromptManagerStatus("Run blocked because this field prompt is empty.");
      return;
    }
    const actionText = options.runCurrent
      ? applyToAgentPrompt
        ? "Updating the agent prompt, running this field, and reloading the segment form..."
        : "Running this field and reloading the segment form..."
      : options.runBatch
      ? applyToAgentPrompt
        ? "Updating the agent prompt and queueing this field for the visible list..."
        : "Saving prompt and queueing this field for the visible list..."
      : applyToAgentPrompt
      ? "Saving field prompt and updating the agent prompt..."
      : "Saving field prompt...";
    setFieldPromptManagerStatus(actionText);
    try {
      if (options.runCurrent && typeof onRunFieldPrompt === "function") {
        const analysisResult = await onRunFieldPrompt(fieldPromptEditor, {
          prompt: fieldPromptDraft,
          applyToAgentPrompt,
          includeAgentInstructions,
          includeAgentPromptAddendum,
          imageSourceMode: fieldPromptImageSourceMode,
          gisLayerSelection: fieldPromptGisSelection()
        });
        if (analysisResult && analysisResult.pendingConfirmation) {
          setFieldPromptManagerStatus(`Review run settings for ${fieldCode}.`);
          return;
        }
        const fieldResult = analysisResult && analysisResult.fields && fieldCode ? analysisResult.fields[fieldCode] : null;
        if (fieldResult) {
          setFieldPromptEditor((current) => current && current.field && current.field.field === fieldCode ? {
            ...current,
            value: fieldResult.value,
            decodedValue: fieldResult.decodedValue || fieldResult.value || "",
            confidence: fieldResult.confidence,
            evidence: fieldResult.evidence || fieldResult.reason || current.evidence || "",
            sourceImages: Array.isArray(fieldResult.sourceImages) ? fieldResult.sourceImages : current.sourceImages,
            needsReview: fieldResult.needsReview == null ? current.needsReview : Boolean(fieldResult.needsReview),
            validOption: fieldResult.validOption == null ? current.validOption : fieldResult.validOption
          } : current);
          const valueText = hasDisplayValue(fieldResult.value)
            ? specDisplayValue(fieldPromptEditor.field, fieldResult.value, fieldResult.decodedValue)
            : "Blank";
          setFieldPromptManagerStatus(`Updated ${fieldCode}: ${valueText}. Segment form reloaded.`);
        } else {
          setFieldPromptManagerStatus("Run finished. Segment form reloaded.");
        }
        return;
      }
      if (options.runBatch && typeof onRunFieldPromptBatch === "function") {
        const batchResult = await onRunFieldPromptBatch(fieldPromptEditor, {
          prompt: fieldPromptDraft,
          applyToAgentPrompt,
          includeAgentInstructions,
          includeAgentPromptAddendum,
          imageSourceMode: fieldPromptImageSourceMode,
          gisLayerSelection: fieldPromptGisSelection()
        });
        setFieldPromptManagerStatus(batchResult && batchResult.pendingConfirmation
          ? `Review list run settings for ${fieldCode}.`
          : `Batch queued for ${fieldCode}.`);
        return;
      }
      if (typeof onSaveFieldPrompt !== "function") {
        return;
      }
      await onSaveFieldPrompt(fieldPromptEditor, fieldPromptDraft, {
        ...options,
        applyToAgentPrompt,
        imageSourceMode: fieldPromptImageSourceMode,
        gisLayerSelection: fieldPromptGisSelection()
      });
      if (!options.runCurrent && !options.runBatch) {
        setFieldPromptManagerStatus(applyToAgentPrompt
          ? "Agent prompt updated."
          : "Field prompt saved.");
      }
      if (!options.keepOpen) {
        setFieldPromptEditor(null);
      }
    } catch (error) {
      setFieldPromptError(error.message || "Could not save field prompt.");
      setFieldPromptManagerStatus("");
    }
  };
  const toggleFieldPromptImage = (imageFile) => {
    setFieldPromptSelectedImages((current) => (
      current.includes(imageFile)
        ? current.filter((file) => file !== imageFile)
        : [imageFile].concat(current)
    ));
  };
  const pasteFieldPromptImages = async (event) => {
    try {
      const hasClipboardImage = Array.from((event.clipboardData && event.clipboardData.items) || [])
        .some((item) => item && item.kind === "file" && /^image\//i.test(item.type || ""));
      if (!hasClipboardImage) {
        return;
      }
      event.preventDefault();
      const attachments = await pastedImagesFromClipboardEvent(event, fieldPromptPastedImages.length);
      if (!attachments.length) {
        return;
      }
      setFieldPromptPastedImages((current) => current.concat(attachments).slice(0, FIELD_PROMPT_MAX_PASTED_IMAGES));
      setFieldPromptError("");
    } catch (error) {
      setFieldPromptError(error.message || "Could not read pasted image.");
    }
  };
  const removeFieldPromptPastedImage = (imageId) => {
    setFieldPromptPastedImages((current) => current.filter((image) => image.id !== imageId));
  };
  const pasteFieldPromptTrainerImages = async (event) => {
    try {
      const hasClipboardImage = Array.from((event.clipboardData && event.clipboardData.items) || [])
        .some((item) => item && item.kind === "file" && /^image\//i.test(item.type || ""));
      if (!hasClipboardImage) {
        return;
      }
      event.preventDefault();
      const attachments = await pastedImagesFromClipboardEvent(event, fieldPromptTrainerImages.length, PROMPT_TRAINER_MAX_IMAGES);
      if (!attachments.length) {
        return;
      }
      setFieldPromptTrainerImages((current) => current.concat(attachments).slice(0, PROMPT_TRAINER_MAX_IMAGES));
      setFieldPromptTrainerResult(null);
      setFieldPromptError("");
    } catch (error) {
      setFieldPromptError(error.message || "Could not read pasted image.");
    }
  };
  const addFieldPromptTrainerImageFiles = async (files) => {
    try {
      const attachments = await imageAttachmentsFromFiles(files, fieldPromptTrainerImages.length, PROMPT_TRAINER_MAX_IMAGES);
      if (!attachments.length) {
        return;
      }
      setFieldPromptTrainerImages((current) => current.concat(attachments).slice(0, PROMPT_TRAINER_MAX_IMAGES));
      setFieldPromptTrainerResult(null);
      setFieldPromptError("");
      setFieldPromptManagerStatus(`Attached ${attachments.length} screenshot${attachments.length === 1 ? "" : "s"} for prompt training.`);
    } catch (error) {
      setFieldPromptError(error.message || "Could not read image files.");
    }
  };
  const importFieldPromptTrainerImages = (event) => {
    const files = Array.from((event.target && event.target.files) || []);
    event.target.value = "";
    addFieldPromptTrainerImageFiles(files);
  };
  const dropFieldPromptTrainerImages = (event) => {
    const files = Array.from((event.dataTransfer && event.dataTransfer.files) || []);
    if (!files.some((file) => file && /^image\//i.test(file.type || ""))) {
      return;
    }
    event.preventDefault();
    addFieldPromptTrainerImageFiles(files);
  };
  const removeFieldPromptTrainerImage = (imageId) => {
    setFieldPromptTrainerImages((current) => current.filter((image) => image.id !== imageId));
  };
  const generateFieldPromptFromTrainerImages = async () => {
    if (!fieldPromptEditor || !selectedSegment || fieldPromptTrainerBusy) {
      return;
    }
    const pastedImages = fieldPromptTrainerImages.slice(0, PROMPT_TRAINER_MAX_IMAGES);
    if (!pastedImages.length) {
      setFieldPromptError("Paste at least one screenshot image before generating a prompt.");
      return;
    }
    setFieldPromptTrainerBusy(true);
    setFieldPromptManagerStatus("Reading pasted screenshots and drafting a field prompt...");
    setFieldPromptError("");
    try {
      const response = await apiFetch(`/api/segments/${selectedSegment.id}/field-prompt-train`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fieldName: fieldPromptEditor.field.field,
          currentPrompt: fieldPromptDraft,
          pastedImages: pastedImages.map((image) => ({
            id: image.id,
            name: image.name,
            type: image.type,
            dataUrl: image.dataUrl
          })),
          model: effectiveOpenAiModel,
          reasoningEffort: effectiveReasoningEffort,
          imageDetail: "high"
        })
      });
      setFieldPromptTrainerResult(response || null);
      setFieldPromptManagerStatus("Prompt draft generated from screenshots. Review before applying.");
      if (typeof loadLogs === "function") {
        loadLogs({ silent: true });
      }
    } catch (error) {
      setFieldPromptError(error.message || "Could not generate a prompt from pasted images.");
      setFieldPromptManagerStatus("");
    } finally {
      setFieldPromptTrainerBusy(false);
    }
  };
  const applyFieldPromptTrainerSuggestion = () => {
    const suggestedPrompt = fieldPromptTrainerResult && fieldPromptTrainerResult.suggestedPrompt;
    if (!suggestedPrompt) {
      return;
    }
    setFieldPromptDraft(suggestedPrompt);
    setFieldPromptManagerStatus("Suggested prompt copied into the editor. Save or test it when ready.");
  };
  const testFieldPromptTrainerSuggestion = async () => {
    if (!fieldPromptEditor || !selectedSegment || fieldPromptTrainerBusy) {
      return;
    }
    const prompt = String(fieldPromptTrainerResult && fieldPromptTrainerResult.suggestedPrompt || fieldPromptDraft || "").trim();
    if (!prompt) {
      setFieldPromptError("Generate or write a prompt before testing.");
      return;
    }
    setFieldPromptTrainerBusy(true);
    setFieldPromptManagerStatus("Dry-run testing this prompt on the current segment...");
    setFieldPromptError("");
    try {
      const response = await apiFetch(`/api/segments/${selectedSegment.id}/field-prompt-test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fieldName: fieldPromptEditor.field.field,
          prompt,
          imageFiles: fieldPromptSelectedImages,
          imageSourceMode: fieldPromptImageSourceMode,
          imageProcessingMode: "full360",
          model: effectiveOpenAiModel,
          reasoningEffort: effectiveReasoningEffort,
          imageDetail: "high",
          gisLayerSelection: fieldPromptGisSelection(),
          maxPastedImages: PROMPT_TRAINER_MAX_IMAGES
        })
      });
      setFieldPromptTrainerResult((current) => ({
        ...(current || {}),
        test: response || null
      }));
      setFieldPromptManagerStatus("Dry-run test finished. Nothing was saved.");
      if (typeof loadLogs === "function") {
        loadLogs({ silent: true });
      }
    } catch (error) {
      setFieldPromptError(error.message || "Could not test this prompt.");
      setFieldPromptManagerStatus("");
    } finally {
      setFieldPromptTrainerBusy(false);
    }
  };
  const sendFieldPromptChat = async () => {
    if (!fieldPromptEditor || !selectedSegment || fieldPromptChatBusy) {
      return;
    }
    const content = String(fieldPromptChatInput || "").trim();
    const pastedImages = fieldPromptPastedImages.slice(0, FIELD_PROMPT_MAX_PASTED_IMAGES);
    if (!content && !pastedImages.length) {
      return;
    }
    const attachments = pastedImages.map((image) => ({
      id: image.id,
      name: image.name,
      type: image.type,
      previewUrl: image.dataUrl
    }));
    const messageContent = content || "Please inspect the pasted image for this field.";
    const nextMessages = fieldPromptChatMessages.concat({
      role: "user",
      content: messageContent,
      attachments
    });
    setFieldPromptChatMessages(nextMessages);
    setFieldPromptChatInput("");
    setFieldPromptPastedImages([]);
    setFieldPromptChatBusy(true);
    setFieldPromptError("");
    try {
      const response = await apiFetch(`/api/segments/${selectedSegment.id}/field-prompt-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fieldName: fieldPromptEditor.field.field,
          currentPrompt: fieldPromptDraft,
          messages: nextMessages,
          imageFiles: fieldPromptSelectedImages,
          imageSourceMode: fieldPromptImageSourceMode,
          pastedImages: pastedImages.map((image) => ({
            id: image.id,
            name: image.name,
            type: image.type,
            dataUrl: image.dataUrl
          })),
          imageProcessingMode: "full360",
          model: effectiveOpenAiModel,
          reasoningEffort: effectiveReasoningEffort,
          imageDetail: "high",
          gisLayerSelection: fieldPromptGisSelection(),
          discussionId: fieldPromptDiscussionId || "",
          user: fieldPromptUserPayload()
        })
      });
      const assistantText = [
        response.reply || "",
        response.suggestedValue ? `Suggested value: ${response.suggestedValue}` : "",
        response.reason ? `Reason: ${response.reason}` : ""
      ].filter(Boolean).join("\n");
      if (response.discussion) {
        applyLoadedFieldDiscussion(response.discussion);
      } else {
        setFieldPromptChatMessages(nextMessages.concat({
          role: "assistant",
          content: assistantText || "I could not produce a response."
        }));
      }
      if (response.suggestedPrompt) {
        setFieldPromptSuggestion(response.suggestedPrompt);
      }
      if (typeof loadLogs === "function") {
        loadLogs({ silent: true });
      }
    } catch (error) {
      setFieldPromptError(error.message || "Could not chat about this field.");
      setFieldPromptChatMessages(nextMessages.concat({
        role: "assistant",
        content: `Error: ${error.message || "Could not chat about this field."}`
      }));
    } finally {
      setFieldPromptChatBusy(false);
    }
  };
  const makePromptFromDiscussion = async () => {
    if (!fieldPromptEditor || !selectedSegment || fieldPromptChatBusy) {
      return;
    }
    const finalizeMessage = {
      role: "user",
      content: "Based on this discussion, write the final field-specific prompt for this iRAP field. Make it reusable, concrete, and focused on preventing the mistake we discussed."
    };
    const nextMessages = fieldPromptChatMessages.concat(finalizeMessage);
    setFieldPromptChatMessages(nextMessages);
    setFieldPromptChatBusy(true);
    setFieldPromptError("");
    try {
      const response = await apiFetch(`/api/segments/${selectedSegment.id}/field-prompt-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fieldName: fieldPromptEditor.field.field,
          currentPrompt: fieldPromptDraft,
          messages: nextMessages,
          imageFiles: fieldPromptSelectedImages,
          imageSourceMode: fieldPromptImageSourceMode,
          imageProcessingMode: "full360",
          model: effectiveOpenAiModel,
          reasoningEffort: effectiveReasoningEffort,
          imageDetail: "high",
          gisLayerSelection: fieldPromptGisSelection(),
          finalizePrompt: true
        })
      });
      const assistantText = [
        response.reply || "I created a final field-specific prompt from the discussion.",
        response.reason ? `Reason: ${response.reason}` : ""
      ].filter(Boolean).join("\n");
      setFieldPromptChatMessages(nextMessages.concat({
        role: "assistant",
        content: assistantText
      }));
      if (response.suggestedPrompt) {
        setFieldPromptSuggestion(response.suggestedPrompt);
        setFieldPromptDraft(response.suggestedPrompt);
      }
      if (typeof loadLogs === "function") {
        loadLogs({ silent: true });
      }
    } catch (error) {
      setFieldPromptError(error.message || "Could not make a prompt from this discussion.");
      setFieldPromptChatMessages(nextMessages.concat({
        role: "assistant",
        content: `Error: ${error.message || "Could not make a prompt from this discussion."}`
      }));
    } finally {
      setFieldPromptChatBusy(false);
    }
  };
  const clearFieldPromptDiscussions = async () => {
    if (!fieldPromptEditor || !selectedSegment || fieldPromptFeedbackBusy) {
      return;
    }
    const hasSavedChats = Boolean(fieldPromptDiscussionId || fieldPromptDiscussionList.length);
    const hasLocalChat = fieldPromptChatMessages.length > 0 || fieldPromptSuggestion;
    if (!hasSavedChats && !hasLocalChat) {
      return;
    }
    if (typeof window !== "undefined" && !window.confirm("Delete all chats for this field on this segment?")) {
      return;
    }
    setFieldPromptFeedbackBusy(true);
    setFieldPromptError("");
    try {
      await apiFetch(`/api/segments/${selectedSegment.id}/field-discussions/${encodeURIComponent(fieldPromptEditor.field.field)}?all=${isIrapEditor ? "1" : "0"}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: fieldPromptUserPayload(),
          all: isIrapEditor
        })
      });
      const storageKey = fieldPromptConversationStorageKey(fieldPromptEditor);
      if (storageKey) {
        try {
          localStorage.removeItem(storageKey);
        } catch (_error) {
          // Clearing server-side history is the source of truth.
        }
      }
      setFieldPromptChatMessages([]);
      setFieldPromptChatInput("");
      setFieldPromptPastedImages([]);
      setFieldPromptSuggestion("");
      setFieldPromptDiscussionId("");
      setFieldPromptDiscussionStatus("");
      setFieldPromptDiscussionList([]);
      setFieldPromptHistoryOpen(false);
    } catch (error) {
      setFieldPromptError(error.message || "Could not clear chats.");
    } finally {
      setFieldPromptFeedbackBusy(false);
    }
  };
  const submitFieldPromptFeedback = async () => {
    if (!fieldPromptDiscussionId || fieldPromptFeedbackBusy) {
      return;
    }
    setFieldPromptFeedbackBusy(true);
    setFieldPromptError("");
    try {
      const response = await apiFetch(`/api/field-discussions/${encodeURIComponent(fieldPromptDiscussionId)}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: fieldPromptUserPayload(),
          feedbackText: "Reviewer submitted this discussion as feedback for iRAP editor review."
        })
      });
      if (response.discussion) {
        applyLoadedFieldDiscussion(response.discussion);
      }
    } catch (error) {
      setFieldPromptError(error.message || "Could not submit feedback.");
    } finally {
      setFieldPromptFeedbackBusy(false);
    }
  };
  const openFieldPromptMenu = (item, event) => {
    if (!isIrapEditor || !item || !item.field) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setFieldPromptMenu({
      item,
      x: event.clientX,
      y: event.clientY
    });
  };
  const openFieldModelComparison = (item) => {
    setFieldPromptMenu(null);
    setFieldModelComparisonItem(item);
  };
  const startEditMode = () => {
    setEditError("");
    setEditDraftValues(makeEditDraftValues());
    if (setEditMode) {
      setEditMode(true);
    }
  };
  const cancelEditMode = () => {
    setEditError("");
    setEditDraftValues({});
    if (setEditMode) {
      setEditMode(false);
    }
  };
  const saveEditDrafts = async () => {
    if (!canEdit || typeof onSaveField !== "function" || editSaving) {
      return;
    }
    const edits = editableItems.filter((item) => {
      const fieldCode = item.field.field;
      if (!Object.prototype.hasOwnProperty.call(editDraftValues, fieldCode)) {
        return false;
      }
      return normalizeSpecComparableValue(editDraftValues[fieldCode]) !== normalizeSpecComparableValue(item.value);
    });
    setEditSaving(true);
    setEditError("");
    try {
      for (const item of edits) {
        const fieldCode = item.field.field;
        await onSaveField(fieldCode, editDraftValues[fieldCode], {
          previousValue: item.value,
          previousDecodedValue: item.decodedValue
        });
      }
      setEditDraftValues({});
      if (setEditMode) {
        setEditMode(false);
      }
    } catch (error) {
      setEditError(error.message || "Could not save field changes.");
    } finally {
      setEditSaving(false);
    }
  };
  useEffect(() => {
    if (!editMode) {
      setEditSaving(false);
      setEditError("");
      setEditDraftValues({});
    }
  }, [editMode]);
  const normalizedFieldSearch = fieldSearch.trim().toLowerCase();
  const visibleGroupedItems = useMemo(() => {
    const baseGroups = normalizedFieldSearch && Array.isArray(allGroupedItems)
      ? allGroupedItems
      : groupedItems;
    const matchesConfidenceFilter = (item) => {
      if (confidenceFilter === "all") {
        return true;
      }
      const percent = fieldConfidencePercent(item && item.confidence);
      if (confidenceFilter === "none") {
        return percent === null;
      }
      if (percent === null) {
        return false;
      }
      if (confidenceFilter === "high") {
        return percent >= 80;
      }
      if (confidenceFilter === "medium") {
        return percent >= 55 && percent < 80;
      }
      if (confidenceFilter === "low") {
        return percent < 55;
      }
      return true;
    };
    return baseGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (!matchesConfidenceFilter(item)) {
            return false;
          }
          if (!normalizedFieldSearch) {
            return true;
          }
          const field = item.field || {};
          const oldValue = item.oldValue || {};
          const haystack = [
            field.field,
            field.caption,
            field.group,
            item.value,
            item.decodedValue,
            item.evidence,
            Array.isArray(item.sourceImages) ? item.sourceImages.join(" ") : "",
            oldValue.value,
            oldValue.decodedValue,
            oldValue.display
          ].map((value) => String(value || "").toLowerCase()).join(" ");
          return haystack.includes(normalizedFieldSearch);
        })
      }))
      .filter((group) => group.items.length);
  }, [allGroupedItems, groupedItems, normalizedFieldSearch, confidenceFilter]);
  const visibleFieldSearchCount = visibleGroupedItems.reduce((count, group) => count + group.items.length, 0);
  const focusedFieldCodeKey = (Array.isArray(focusedFieldCodes) ? focusedFieldCodes : [])
    .map((fieldCode) => String(fieldCode || "").trim())
    .filter(Boolean)
    .join("|");
  useEffect(() => {
    setFocusedFieldDismissedKey("");
  }, [focusedFieldCodeKey]);
  const focusedFieldCodeSet = useMemo(
    () => new Set(focusedFieldCodeKey.split("|").filter(Boolean)),
    [focusedFieldCodeKey]
  );
  const focusedFieldItems = useMemo(() => {
    if (!focusedFieldCodeSet.size) {
      return [];
    }
    const sourceGroups = Array.isArray(allGroupedItems) ? allGroupedItems : groupedItems;
    const seen = new Set();
    return (Array.isArray(sourceGroups) ? sourceGroups : [])
      .flatMap((group) => Array.isArray(group && group.items) ? group.items : [])
      .filter((item) => {
        const fieldCode = String(item && item.field && item.field.field || "").trim();
        if (!fieldCode || !focusedFieldCodeSet.has(fieldCode) || seen.has(fieldCode)) {
          return false;
        }
        seen.add(fieldCode);
        return true;
      });
  }, [allGroupedItems, groupedItems, focusedFieldCodeKey]);
  const showFocusedFieldSection = Boolean(focusedFieldItems.length && focusedFieldDismissedKey !== focusedFieldCodeKey);
  const isFocusedFieldItem = (item) => focusedFieldCodeSet.has(String(item && item.field && item.field.field || "").trim());
  const insightItems = Array.isArray(aiInsightItems) ? aiInsightItems : editableItems;
  const selectedFieldViewMode = SPECS_FIELD_VIEW_MODES.find((option) => option.mode === fieldViewMode) || SPECS_FIELD_VIEW_MODES[0];
  const aiInsight = useMemo(
    () => specsAiInsightFromItems(insightItems),
    [insightItems]
  );
  const renderFieldTile = (item, keyPrefix = "field") => (
    <IrapIconTile
      key={`${keyPrefix}-${item.field.field}-${item.index}`}
      item={item}
      selectedSegment={selectedSegment}
      onOpenEvidence={onOpenEvidence}
      onOpenSourceImage={onOpenFieldSourceImage}
      editMode={editMode}
      canEdit={canEdit}
      onSaveField={onSaveField}
      onAddEvidence={onAddFieldEvidence}
      onOpenAnnotationEvidence={onOpenAnnotationEvidence}
      showOldValues={showOldValues}
      editDraftValue={Object.prototype.hasOwnProperty.call(editDraftValues, item.field.field) ? editDraftValues[item.field.field] : item.value ?? ""}
      editSaving={editSaving}
      onEditDraftValueChange={(fieldCode, nextValue) => {
        setEditDraftValues((current) => ({
          ...current,
          [fieldCode]: nextValue
        }));
      }}
      fieldValueFilterEnabled={fieldValueFilterEnabled}
      fieldValueFilterSelected={Boolean(
        fieldValueFilterKeys &&
        typeof fieldValueFilterKeys.has === "function" &&
        fieldValueFilterKeys.has(specsFieldValueFilterKeyFromItem(item))
      )}
      fieldFocusSelected={isFocusedFieldItem(item)}
      onToggleFieldValueFilter={onToggleFieldValueFilter}
      hasFieldPrompt={isIrapEditor && Boolean(fieldPrompts[item.field.field] && fieldPrompts[item.field.field].prompt)}
      isFieldRunning={fieldPromptBusy && fieldPromptRunningField === item.field.field}
      onOpenFieldDiscussion={openFieldPromptDiscussion}
      onOpenFieldPrompt={isIrapEditor ? openFieldPromptManager : null}
      onFieldContextMenu={isIrapEditor ? openFieldPromptMenu : null}
    />
  );

  return (
    <section className="segment-spec-content">
      <button
        className="icon-action specs-panel-close segment-info-close"
        type="button"
        title="Hide segment info"
        aria-label="Hide segment info"
        onClick={onClose}
      >
        {icon("panel-left-close")}
      </button>
      <section className="segment-info-grid" aria-label="Segment metadata">
        {metadata.map((item) => (
          <div
            key={item.label}
            className={String(item.label || "").toLowerCase() === "segment" ? "segment-info-grid__segment" : ""}
          >
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </section>
      {canViewStarRanking && <SegmentStarRatingSummary ratings={starRatings} onOpenChat={openStarRatingChat} />}

      <div className="segment-spec-icon-toolbar">
        <div className="segment-spec-icon-actions">
          {typeof onSetApprovalState === "function" && (
            <div className="segment-status-toggle" role="group" aria-label="Segment status">
              {[
                { state: "pending", label: "Pending", icon: "clock" },
                { state: "review", label: "Review", icon: "alert-triangle" },
                { state: "approved", label: "Approve", icon: "check", inactiveIcon: "circle" }
              ].map((option) => {
                const isActive = approvalState === option.state;
                return (
                  <button
                    key={option.state}
                    type="button"
                    className={isActive ? "active" : ""}
                    disabled={approveBusy}
                    onClick={() => onSetApprovalState(option.state)}
                    title={`Set segment status to ${option.label}`}
                    aria-pressed={isActive}
                  >
                    {icon(isActive ? option.icon : (option.inactiveIcon || option.icon))}
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
          )}
          {typeof onSetBatchExcluded === "function" && (
            <button
              type="button"
              className={`segment-batch-block-toggle ${batchExcluded ? "active" : ""}`}
              disabled={batchExcludeBusy}
              onClick={() => onSetBatchExcluded(!batchExcluded)}
              title={batchExcluded
                ? `Unblock this segment for batch processing${batchExclusionReason ? `: ${batchExclusionReason}` : ""}`
                : "Block this segment from all batch processing"}
              aria-pressed={Boolean(batchExcluded)}
            >
              {icon(batchExcludeBusy ? "loader-circle" : batchExcluded ? "ban" : "circle-slash")}
              <span>{batchExcluded ? "Blocked from batch" : "Block batch"}</span>
            </button>
          )}
          {canEdit && !editMode && (
            <button
              className="secondary-button compact segment-spec-edit-toggle"
              type="button"
              title="Edit iRAP field values"
              onClick={startEditMode}
            >
              {icon("pencil")} Edit
            </button>
          )}
          {canEdit && editMode && (
            <>
              <button
                className="secondary-button compact segment-spec-edit-toggle active"
                type="button"
                title="Save field changes"
                disabled={editSaving}
                onClick={saveEditDrafts}
              >
                {icon(editSaving ? "loader-circle" : "check")} {editSaving ? "Saving" : "Done"}
              </button>
              <button
                className="secondary-button compact segment-spec-edit-cancel"
                type="button"
                title="Cancel edit mode without saving draft changes"
                disabled={editSaving}
                onClick={cancelEditMode}
              >
                {icon("x")} Cancel
              </button>
            </>
          )}
          {editMode && editError && <small className="segment-spec-edit-error">{editError}</small>}
          <label className="segment-icon-size-control">
            <span>{icon("minimize-2")}</span>
            <input
              aria-label="Icon size"
              type="range"
              min={SPECS_ICON_SIZE_MIN}
              max={SPECS_ICON_SIZE_MAX}
              step="0.05"
              value={iconSize}
              onChange={(event) => setSpecIconSize(Number(event.target.value))}
            />
            <span>{icon("maximize-2")}</span>
          </label>
        </div>
        <div className="segment-spec-view-controls" aria-label="Field display filters">
          <label className="segment-field-search">
            {icon("search")}
            <input
              type="search"
              value={fieldSearch}
              placeholder="Search fields or values"
              onChange={(event) => setFieldSearch(event.target.value)}
            />
            {fieldSearch && <small>{visibleFieldSearchCount}</small>}
            {fieldSearch && (
              <button
                type="button"
                title="Clear field search"
                aria-label="Clear field search"
                onClick={() => setFieldSearch("")}
              >
                {icon("x")}
              </button>
            )}
          </label>
          <label className="segment-confidence-filter" title="Filter fields by AI confidence">
            {icon("gauge")}
            <select
              value={confidenceFilter}
              aria-label="Confidence filter"
              onChange={(event) => setConfidenceFilter(event.target.value)}
            >
              <option value="all">Confidence: All</option>
              <option value="high">High 80%+</option>
              <option value="medium">Medium 55-79%</option>
              <option value="low">Low &lt;55%</option>
              <option value="none">No confidence</option>
            </select>
          </label>
          <label className="segment-spec-view-combo" title="Field comparison view">
            {icon(selectedFieldViewMode.icon)}
            <select
              value={fieldViewMode}
              aria-label="Field comparison view"
              onChange={(event) => setFieldViewMode && setFieldViewMode(event.target.value)}
            >
              {SPECS_FIELD_VIEW_MODES.map(({ mode, label }) => (
                <option key={mode} value={mode}>
                  {label} ({fieldViewCounts[mode] || 0})
                </option>
              ))}
            </select>
          </label>
          <button
            className={`secondary-button compact segment-old-value-toggle ${showOldValues ? "active" : ""}`}
            type="button"
            title="Show old/manual values beside AI values"
            aria-pressed={showOldValues}
            onClick={() => setShowOldValues && setShowOldValues(!showOldValues)}
          >
            {icon("columns-2")} Old values
          </button>
          <SegmentAiInsight
            insight={aiInsight}
            collapsed={aiInsightCollapsed}
            onToggleCollapsed={() => setAiInsightCollapsed((current) => !current)}
            onOpenSourceImage={onOpenFieldSourceImage}
            neighborInsight={neighborInsight}
            neighborEnabled={neighborInsightEnabled}
            onToggleNeighbor={setNeighborInsightEnabled}
          />
        </div>
        {fieldPromptMenu && (
          <div
            className="field-prompt-menu"
            style={{ left: fieldPromptMenu.x, top: fieldPromptMenu.y }}
            onClick={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.preventDefault()}
          >
            <strong>{fieldPromptMenu.item.field.caption || fieldPromptMenu.item.field.field}</strong>
            <button type="button" onClick={() => openFieldModelComparison(fieldPromptMenu.item)}>
              {icon("git-compare-arrows")} Compare result
            </button>
            <button type="button" onClick={() => openFieldPromptEditor(fieldPromptMenu.item, { mode: "prompt" })}>
              {icon("message-square-text")} Field prompt
            </button>
            <button
              type="button"
              disabled={fieldPromptBusy || !fieldPromptIsRunnable(fieldPromptMenu.item)}
              title={fieldPromptIsRunnable(fieldPromptMenu.item) ? "Run this field with its saved field prompt" : "Add a field prompt before running"}
              onClick={() => {
                const item = fieldPromptMenu.item;
                const prompt = fieldPromptForItem(item);
                const imageSourceMode = fieldPromptImageSourceModeForItem(item);
                if (!String(prompt || "").trim()) {
                  setFieldPromptError("Field prompt is empty. Add a field prompt before running this field.");
                  setFieldPromptMenu(null);
                  return;
                }
                setFieldPromptMenu(null);
                onRunFieldPrompt && onRunFieldPrompt(item, {
                  prompt,
                  imageSourceMode,
                  gisLayerSelection: fieldPromptGisSelectionForItem(item)
                });
              }}
            >
              {fieldPromptBusy && fieldPromptRunningField === fieldPromptMenu.item.field.field ? icon("loader-circle") : icon("refresh-cw")} Run this field
            </button>
            <button
              type="button"
              disabled={fieldPromptBusy || !fieldPromptIsRunnable(fieldPromptMenu.item)}
              title={fieldPromptIsRunnable(fieldPromptMenu.item) ? "Run this field for the filtered grid with its saved field prompt" : "Add a field prompt before running this list"}
              onClick={() => {
                const item = fieldPromptMenu.item;
                if (!fieldPromptIsRunnable(item)) {
                  setFieldPromptError("Field prompt is empty. Add a field prompt before running this field for the list.");
                  setFieldPromptMenu(null);
                  return;
                }
                setFieldPromptMenu(null);
                onRunFieldPromptBatch && onRunFieldPromptBatch(item, {
                  imageSourceMode: fieldPromptImageSourceModeForItem(item),
                  gisLayerSelection: fieldPromptGisSelectionForItem(item)
                });
              }}
            >
              {icon("list-restart")} Run field for list
            </button>
          </div>
        )}
        {fieldPromptEditor && (
          <div className="field-prompt-modal" role="dialog" aria-modal="true" aria-label="Field-specific AI prompt">
            <div className="field-prompt-dialog">
              <FieldPromptDiscussionHeader
                item={fieldPromptEditor}
                titleLabel={fieldPromptEditorMode === "prompt" ? "Field prompt" : "Discussion topic"}
                onClose={() => setFieldPromptEditor(null)}
              />
              {fieldPromptEditorMode === "prompt" ? (
                <div className="field-prompt-workspace is-manager">
                  <FieldPromptManager
                    item={fieldPromptEditor}
                    prompt={fieldPromptDraft}
                    setPrompt={setFieldPromptDraft}
                    busy={fieldPromptBusy}
                    status={fieldPromptManagerStatus}
                    error={fieldPromptError}
                    trainerImages={fieldPromptTrainerImages}
                    trainerBusy={fieldPromptTrainerBusy}
                    trainerResult={fieldPromptTrainerResult}
                    openAiSettingsLabel={openAiSettingsLabel}
                    imageSourceMode={fieldPromptImageSourceMode}
                    setImageSourceMode={setFieldPromptImageSourceMode}
                    gisLayerOptions={fieldPromptAiGisLayerOptions}
                    gisEnabled={fieldPromptGisEnabled}
                    setGisEnabled={setFieldPromptGisEnabled}
                    gisClassIds={fieldPromptGisClassIds}
                    setGisClassIds={setFieldPromptGisClassIds}
                    injectToAgentPrompt={fieldPromptInjectToAgentPrompt}
                    setInjectToAgentPrompt={setFieldPromptInjectToAgentPrompt}
                    includeAgentInstructionsForRun={fieldPromptIncludeAgentInstructions}
                    setIncludeAgentInstructionsForRun={setFieldPromptIncludeAgentInstructions}
                    includeAgentPromptAddendumForRun={fieldPromptIncludeAgentPromptAddendum}
                    setIncludeAgentPromptAddendumForRun={setFieldPromptIncludeAgentPromptAddendum}
                    onPasteTrainerImages={pasteFieldPromptTrainerImages}
                    onImportTrainerImages={importFieldPromptTrainerImages}
                    onDropTrainerImages={dropFieldPromptTrainerImages}
                    onRemoveTrainerImage={removeFieldPromptTrainerImage}
                    onGenerateFromImages={generateFieldPromptFromTrainerImages}
                    onApplyTrainerPrompt={applyFieldPromptTrainerSuggestion}
                    onTestTrainerPrompt={testFieldPromptTrainerSuggestion}
                    onClose={() => setFieldPromptEditor(null)}
                    onSave={() => saveFieldPromptDraft({ keepOpen: true })}
                    onRunCurrent={() => saveFieldPromptDraft({ runCurrent: true, keepOpen: true })}
                    onRunBatch={() => saveFieldPromptDraft({ runBatch: true, keepOpen: true })}
                    onSaveToAgent={() => saveFieldPromptDraft({ applyToAgentPrompt: true, keepOpen: true })}
                  />
                </div>
              ) : (
                <div className="field-prompt-workspace">
                  <section className="field-prompt-chat-pane">
                    <FieldResultDiscussion
                      item={fieldPromptEditor}
                      messages={fieldPromptChatMessages}
                      chatInput={fieldPromptChatInput}
                      setChatInput={setFieldPromptChatInput}
                      chatBusy={fieldPromptChatBusy}
                      pastedImages={fieldPromptPastedImages}
                      onPasteImages={pasteFieldPromptImages}
                      onRemovePastedImage={removeFieldPromptPastedImage}
                      discussionStatus={fieldPromptDiscussionStatus}
                      openAiSettingsLabel={openAiSettingsLabel}
                      imageSourceMode={fieldPromptImageSourceMode}
                      setImageSourceMode={setFieldPromptImageSourceMode}
                      gisLayerOptions={fieldPromptAiGisLayerOptions}
                      gisEnabled={fieldPromptGisEnabled}
                      setGisEnabled={setFieldPromptGisEnabled}
                      gisClassIds={fieldPromptGisClassIds}
                      setGisClassIds={setFieldPromptGisClassIds}
                      discussionId={fieldPromptDiscussionId}
                      discussions={fieldPromptDiscussionList}
                      historyOpen={fieldPromptHistoryOpen}
                      setHistoryOpen={setFieldPromptHistoryOpen}
                      isEditor={isIrapEditor}
                      actionBusy={fieldPromptFeedbackBusy}
                      onSelectDiscussion={applyLoadedFieldDiscussion}
                      onClearDiscussions={clearFieldPromptDiscussions}
                      onSubmitFeedback={submitFieldPromptFeedback}
                      onSend={sendFieldPromptChat}
                    />
                    {fieldPromptError && <small className="field-prompt-error">{fieldPromptError}</small>}
                  </section>
                  <FieldPromptPanoPane
                    item={fieldPromptEditor}
                    selectedSegment={selectedSegment}
                    segmentImages={segmentImages}
                    selectedImages={fieldPromptSelectedImages}
                    imageSourceMode={fieldPromptImageSourceMode}
                    onToggleImage={toggleFieldPromptImage}
                    logs={logs}
                    onRefreshLogs={loadLogs}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {canViewStarRanking && starRatingChatOpen && (
          <StarRatingChatModal
            selectedSegment={selectedSegment}
            ratings={starRatings}
            messages={starRatingChatMessages}
            chatInput={starRatingChatInput}
            setChatInput={setStarRatingChatInput}
            chatBusy={starRatingChatBusy}
            error={starRatingChatError}
            openAiSettingsLabel={openAiSettingsLabel}
            onSend={sendStarRatingChat}
            onClose={() => setStarRatingChatOpen(false)}
          />
        )}
        {fieldModelComparisonItem && (
          <FieldModelComparisonPanel
            item={fieldModelComparisonItem}
            allSegments={allSegments}
            searchSegments={searchSegments}
            gridSegmentIds={gridSegmentIds}
            analysisResults={analysisResults}
            onClose={() => setFieldModelComparisonItem(null)}
          />
        )}
        <nav className="segment-spec-tabs" aria-label="iRAP icon categories">
          <button
            className={activeGroup === "All" ? "active" : ""}
            type="button"
            onClick={() => setActiveGroup("All")}
          >
            All
          </button>
          {groupNames.map((groupName) => {
            const meta = CATEGORY_META[groupName] || {};
            return (
              <button
                key={groupName}
                className={activeGroup === groupName ? "active" : ""}
                style={{ "--category-color": meta.color || "#64748b" }}
                type="button"
                onClick={() => setActiveGroup(groupName)}
              >
                {meta.short || groupName}
              </button>
            );
          })}
          <button
            className={showEvidence ? "active" : ""}
            style={{ "--category-color": "#0284c7" }}
            type="button"
            onClick={() => setActiveGroup(SPECS_EVIDENCE_TAB)}
          >
            Evidence
            {(evidenceCount + gisAssetCount) > 0 && <small>{evidenceCount + gisAssetCount}</small>}
          </button>
        </nav>
      </div>

      <div className="segment-icon-sections" style={iconSizingStyle}>
        {!showEvidence && showFocusedFieldSection && (
          <section
            className="segment-icon-section segment-focused-field-section"
            style={{ "--category-color": "#2563eb" }}
          >
            <header>
              <div>
                <h3>{focusedFieldItems.length === 1 ? "Focused field" : "Focused fields"}</h3>
                <span>{focusedFieldItems.length} from list filter</span>
              </div>
              <button
                type="button"
                className="icon-action segment-focused-field-close"
                title="Hide focused field"
                aria-label="Hide focused field"
                onClick={() => setFocusedFieldDismissedKey(focusedFieldCodeKey)}
              >
                {icon("x")}
              </button>
            </header>
            <div className="segment-icon-grid segment-focused-field-grid">
              {focusedFieldItems.map((item) => renderFieldTile(item, "focused"))}
            </div>
          </section>
        )}
        {showEvidence ? (
          <SpecsEvidenceSection
            evidenceItems={evidenceItems}
            gisAssets={gisAssets}
            gisSummary={gisSummary}
            gisLoading={gisLoading}
            gisError={gisError}
            gisMapVisible={gisMapVisible}
            onToggleGisMapVisible={onToggleGisMapVisible}
            gisHiddenLayerKeys={gisHiddenLayerKeys}
            onToggleGisLayerVisible={onToggleGisLayerVisible}
            gisLayerSettings={gisLayerSettings}
            selectedSegment={selectedSegment}
            onOpenEvidence={onOpenEvidence}
          />
        ) : visibleGroupedItems.length ? visibleGroupedItems.map((group) => {
          const meta = CATEGORY_META[group.groupName] || {};
          return (
            <section
              key={group.groupName}
              className="segment-icon-section"
              style={{ "--category-color": meta.color || "#64748b" }}
            >
              <header>
                <h3>{meta.short || group.groupName}</h3>
                <span>{group.items.length} fields</span>
              </header>
              <div className="segment-icon-grid">
                {group.items.map((item) => renderFieldTile(item))}
              </div>
            </section>
          );
        }) : (
          <div className="segment-spec-empty">
            {normalizedFieldSearch || confidenceFilter !== "all" ? icon("search-x") : icon("loader-circle")}
            <strong>{normalizedFieldSearch || confidenceFilter !== "all" ? "No matching fields" : "Loading"}</strong>
            {(normalizedFieldSearch || confidenceFilter !== "all") && (
              <span>No fields match the current search and confidence filters.</span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function normalizeEvidenceImageUrl(rawUrl, segment) {
  const text = String(rawUrl || "").trim();
  if (!text) {
    return "";
  }
  if (/^(https?:|data:|blob:)/i.test(text) || text.startsWith("/")) {
    return text;
  }
  if (text.startsWith("api/")) {
    return `/${text}`;
  }
  const fileName = text.split(/[\\/]/).filter(Boolean).pop();
  if (fileName && segment && segment.id) {
    return `/api/evidence-crops/${Number(segment.id)}/${encodeURIComponent(fileName)}`;
  }
  return text;
}

function inferredSegmentationImageFile(item) {
  if (!isPromptSegmentationItem(item) || !item || !item.id) {
    return "";
  }
  const rawId = String(item.id).trim();
  if (!rawId) {
    return "";
  }
  return `${rawId}-segmentation.jpg`;
}

function evidenceMarkedOutputUrl(item, segment) {
  const segmentation = item && item.segmentation ? item.segmentation : null;
  const rawUrl = item && [
    segmentation && segmentation.markedImageUrl,
    segmentation && segmentation.markedImageFile,
    segmentation && segmentation.maskedImageUrl,
    segmentation && segmentation.maskedImageFile,
    inferredSegmentationImageFile(item),
    isPromptSegmentationItem(item) ? item.markedImageUrl : "",
    isPromptSegmentationItem(item) ? item.markedImageFile : "",
    item.markedImageUrl,
    item.markedImageFile,
    item.markedCropUrl,
    item.markedCrop && item.markedCrop.url,
    item.cropUrl,
    item.crop && item.crop.url
  ].find(Boolean);
  return normalizeEvidenceImageUrl(rawUrl, segment);
}

function evidenceMarkedImageUrl(item, segment) {
  return (
    evidenceMarkedOutputUrl(item, segment) ||
    evidenceThumbnailImage(item, segment)
  );
}

function evidenceFieldReferenceLabels(item) {
  const refs = [];
  const collect = (value) => {
    if (!value) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }
    if (typeof value === "object") {
      const fieldCode = value.field || value.fieldCode || value.code || value.id || "";
      const caption = value.caption || value.label || value.name || "";
      if (fieldCode || caption) {
        refs.push([fieldCode, caption].filter(Boolean).join(" "));
      }
      return;
    }
    refs.push(String(value));
  };
  collect(item && item.fieldRefs);
  collect(item && item.agentEvidence && item.agentEvidence.fieldRefs);
  collect(item && item.segmentation && item.segmentation.fieldRefs);
  if (!refs.length) {
    refs.push(...evidenceFieldRefKeys(item));
  }
  return Array.from(new Set(refs.map((ref) => String(ref).trim()).filter(Boolean)));
}

function panoAnnotationFieldCode(annotation) {
  const meta = annotation && annotation.meta && typeof annotation.meta === "object" ? annotation.meta : {};
  return String(meta.fieldCode || meta.field || "").trim();
}

function panoAnnotationMatchesField(annotation, field) {
  const code = panoAnnotationFieldCode(annotation);
  return Boolean(code && field && String(field.field) === code);
}

function panoAnnotationThumbUrl(annotation) {
  if (!annotation || !annotation.segmentId || !annotation.imageFile) {
    return "";
  }
  return `/api/segments/${Number(annotation.segmentId)}/images/${encodeURIComponent(annotation.imageFile)}/thumb`;
}

function fieldConfidencePercent(confidence) {
  const value = Number(confidence);
  if (!Number.isFinite(value)) {
    return null;
  }
  const percent = value <= 1 ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(percent)));
}

function fieldConfidenceTone(percent) {
  if (percent === null || percent === undefined) {
    return "none";
  }
  if (percent >= 80) {
    return "high";
  }
  if (percent >= 55) {
    return "medium";
  }
  return "low";
}

function fieldSourceImages(item) {
  const visualImages = item && item.visualEvidence && Array.isArray(item.visualEvidence.sourceImages)
    ? item.visualEvidence.sourceImages
    : [];
  const images = Array.isArray(item && item.sourceImages) ? item.sourceImages : [];
  const primary = item && item.visualEvidence && item.visualEvidence.sourceImage ? item.visualEvidence.sourceImage : "";
  return Array.from(new Set([primary, ...images, ...visualImages].map((image) => String(image || "").trim()).filter(isValidFieldSourceImageName)));
}

function isValidFieldSourceImageName(fileName) {
  const baseName = String(fileName || "").split(/[\\/]/).filter(Boolean).pop() || "";
  return /^vid_/i.test(baseName);
}

function resolveFieldSourceImageName(imageName, detail) {
  const text = String(imageName || "").trim();
  if (!text || !detail || !Array.isArray(detail.images) || !detail.images.length) {
    return "";
  }
  const baseName = text.split(/[\\/]/).filter(Boolean).pop() || text;
  const exact = detail.images.find((image) => String(image && image.file || "") === baseName);
  if (exact && isValidFieldSourceImageName(exact.file)) {
    return exact.file;
  }
  const lowerBaseName = baseName.toLowerCase();
  const caseMatch = detail.images.find((image) => String(image && image.file || "").toLowerCase() === lowerBaseName);
  if (caseMatch && isValidFieldSourceImageName(caseMatch.file)) {
    return caseMatch.file;
  }
  const aliasMatch = lowerBaseName.match(/^image[_-]?(\d+)\.(?:jpe?g|png|webp)$/i);
  if (aliasMatch) {
    const image = detail.images[Number(aliasMatch[1]) - 1];
    return image && isValidFieldSourceImageName(image.file) ? image.file : "";
  }
  if (/^\d+$/.test(lowerBaseName)) {
    const image = detail.images[Number(lowerBaseName) - 1];
    return image && isValidFieldSourceImageName(image.file) ? image.file : "";
  }
  return "";
}

function normalizeFieldSourceImages(sourceImages, detail) {
  const rawImages = Array.isArray(sourceImages) ? sourceImages : sourceImages ? [sourceImages] : [];
  return Array.from(new Set(rawImages.map((imageName) => resolveFieldSourceImageName(imageName, detail)).filter(Boolean)));
}

function fieldConfidenceTitle(item) {
  const percent = fieldConfidencePercent(item && item.confidence);
  const evidence = String((item && item.evidence) || "").trim();
  const sourceImages = fieldSourceImages(item);
  return [
    percent === null ? "Confidence: not recorded" : `Confidence: ${percent}%`,
    evidence ? `Reason: ${evidence}` : "Reason: not recorded",
    sourceImages.length ? `Source image${sourceImages.length === 1 ? "" : "s"}: ${sourceImages.join(", ")}` : "Source images: none recorded"
  ].join("\n");
}

function specsAiInsightFromItems(items) {
  const safeItems = Array.isArray(items) ? items.filter((item) => item && item.field) : [];
  const analyzedItems = safeItems.filter((item) => (
    hasDisplayValue(item.value) ||
    fieldConfidencePercent(item.confidence) !== null ||
    Boolean(item.evidence) ||
    fieldSourceImages(item).length > 0 ||
    item.needsReview
  ));
  if (!analyzedItems.length) {
    return null;
  }
  const confidenceValues = analyzedItems
    .map((item) => fieldConfidencePercent(item.confidence))
    .filter((value) => value !== null);
  const averageConfidence = confidenceValues.length
    ? Math.round(confidenceValues.reduce((sum, value) => sum + value, 0) / confidenceValues.length)
    : null;
  const sourceImages = Array.from(new Set(analyzedItems.flatMap((item) => fieldSourceImages(item))));
  const countByComparison = (status) => analyzedItems.filter((item) => item.comparison && item.comparison.status === status).length;
  const attentionItems = analyzedItems
    .map((item) => {
      const confidence = fieldConfidencePercent(item.confidence);
      const sourceImageList = fieldSourceImages(item);
      const valueText = hasDisplayValue(item.value)
        ? specDisplayValue(item.field, item.value, item.decodedValue)
        : "No AI value";
      const reasons = [];
      if (item.needsReview || item.validOption === false) {
        reasons.push("Needs review");
      }
      if (confidence !== null && confidence < 55) {
        reasons.push("Low confidence");
      }
      if (item.comparison && item.comparison.status === "different") {
        reasons.push("Changed");
      }
      if (item.comparison && item.comparison.status === "missing") {
        reasons.push("Missing");
      }
      if (hasDisplayValue(item.value) && !sourceImageList.length) {
        reasons.push("No source image");
      }
      return {
        item,
        fieldLabel: item.field.caption || item.field.field,
        valueText,
        confidence,
        sourceImages: sourceImageList,
        reasons,
        priority:
          (item.needsReview || item.validOption === false ? 0 : 10) +
          (confidence !== null && confidence < 55 ? 1 : 10) +
          (item.comparison && item.comparison.status === "different" ? 2 : 10) +
          (hasDisplayValue(item.value) && !sourceImageList.length ? 3 : 10)
      };
    })
    .filter((item) => item.reasons.length)
    .sort((a, b) => a.priority - b.priority || (a.confidence ?? 101) - (b.confidence ?? 101))
    .slice(0, 6);

  return {
    total: safeItems.length,
    analyzed: analyzedItems.length,
    valued: analyzedItems.filter((item) => hasDisplayValue(item.value)).length,
    changed: countByComparison("different"),
    same: countByComparison("same"),
    missing: countByComparison("missing"),
    needsReview: analyzedItems.filter((item) => item.needsReview || item.validOption === false).length,
    lowConfidence: analyzedItems.filter((item) => {
      const confidence = fieldConfidencePercent(item.confidence);
      return confidence !== null && confidence < 55;
    }).length,
    noSource: analyzedItems.filter((item) => hasDisplayValue(item.value) && !fieldSourceImages(item).length).length,
    averageConfidence,
    sourceImages,
    attentionItems,
    expert: specsAiExpertInterpretation(analyzedItems, {
      total: safeItems.length,
      analyzed: analyzedItems.length,
      valued: analyzedItems.filter((item) => hasDisplayValue(item.value)).length,
      changed: countByComparison("different"),
      missing: countByComparison("missing"),
      needsReview: analyzedItems.filter((item) => item.needsReview || item.validOption === false).length,
      lowConfidence: analyzedItems.filter((item) => {
        const confidence = fieldConfidencePercent(item.confidence);
        return confidence !== null && confidence < 55;
      }).length,
      noSource: analyzedItems.filter((item) => hasDisplayValue(item.value) && !fieldSourceImages(item).length).length,
      averageConfidence,
      sourceImages
    })
  };
}

function specsInsightItemSearchText(item) {
  const field = item && item.field ? item.field : {};
  return [
    field.field,
    field.caption,
    field.group,
    field.geoProperty
  ].map((value) => String(value || "").toLowerCase()).join(" ");
}

function specsInsightValueText(item) {
  if (!item || !hasDisplayValue(item.value)) {
    return "";
  }
  return specDisplayValue(item.field, item.value, item.decodedValue);
}

function specsFindInsightItem(items, patterns) {
  const safePatterns = (patterns || []).map((pattern) => String(pattern || "").toLowerCase()).filter(Boolean);
  return (items || []).find((item) => {
    const haystack = specsInsightItemSearchText(item);
    return safePatterns.some((pattern) => haystack.includes(pattern));
  }) || null;
}

function specsAiExpertFact(items, label, patterns) {
  const item = specsFindInsightItem(items, patterns);
  const valueText = specsInsightValueText(item);
  if (!valueText) {
    return null;
  }
  const confidence = fieldConfidencePercent(item.confidence);
  return {
    label,
    value: valueText,
    confidence,
    text: `${label}: ${valueText}${confidence === null ? "" : ` (${confidence}%)`}`
  };
}

function specsAiExpertInterpretation(items, summary) {
  const valuedItems = (items || []).filter((item) => hasDisplayValue(item.value));
  if (!valuedItems.length) {
    return null;
  }
  const factDefs = [
    ["Road profile", ["carriageway label", "carriageway"]],
    ["Median", ["median type"]],
    ["Lanes", ["number of lanes"]],
    ["Lane width", ["lane width"]],
    ["Curvature", ["curvature"]],
    ["Grade", ["grade"]],
    ["Road condition", ["road condition"]],
    ["Delineation", ["delineation"]],
    ["Sight distance", ["sight distance"]],
    ["Street lighting", ["street lighting"]],
    ["Speed", ["speed limit", "operating speed", "speed"]],
    ["Roadside severity", ["roadside severity"]],
    ["Bicycle facility", ["facilities for bicycles", "bicycle"]],
    ["Pedestrian facility", ["pedestrian crossing", "sidewalk", "pedestrian fencing", "pedestrian"]],
    ["Intersection", ["intersection type", "intersection channelisation", "intersection quality"]]
  ];
  const seenFields = new Set();
  const facts = factDefs
    .map(([label, patterns]) => specsAiExpertFact(valuedItems, label, patterns))
    .filter(Boolean)
    .filter((fact) => {
      if (seenFields.has(fact.label)) {
        return false;
      }
      seenFields.add(fact.label);
      return true;
    });
  const profileFacts = facts.slice(0, 5);
  const lowerValue = (item) => specsInsightValueText(item).toLowerCase();
  const vruItems = valuedItems.filter((item) => {
    const haystack = specsInsightItemSearchText(item);
    return /pedestrian|bicycle|cycle|school|sidewalk|vru|motorcycl/.test(haystack);
  });
  const vruWeakItems = vruItems.filter((item) => /none|not present|no facility|not applicable|blank/.test(lowerValue(item)));
  const roadConditionItem = specsFindInsightItem(valuedItems, ["road condition"]);
  const sightDistanceItem = specsFindInsightItem(valuedItems, ["sight distance"]);
  const roadsideItem = specsFindInsightItem(valuedItems, ["roadside severity"]);
  const speedItem = specsFindInsightItem(valuedItems, ["speed limit", "operating speed", "speed"]);
  const focusAreas = [];

  const roadProfileItem = specsFindInsightItem(valuedItems, ["carriageway label", "carriageway"]);
  const medianItem = specsFindInsightItem(valuedItems, ["median type"]);
  const lanesItem = specsFindInsightItem(valuedItems, ["number of lanes"]);
  const laneWidthItem = specsFindInsightItem(valuedItems, ["lane width"]);
  const gradeItem = specsFindInsightItem(valuedItems, ["grade"]);
  const curvatureItem = specsFindInsightItem(valuedItems, ["curvature"]);
  if (roadProfileItem || medianItem || lanesItem || laneWidthItem) {
    focusAreas.push("Cross-section risk is the first review topic: confirm how carriageway separation, lane count, median type, and lane width combine to affect crash likelihood and severity.");
  }
  if (vruWeakItems.length) {
    focusAreas.push("Vulnerable road-user risk appears important because the coded infrastructure indicates absent or limited pedestrian/cyclist protection.");
  }
  if (roadConditionItem && /poor|bad|sealed - poor|unsealed|rough/.test(lowerValue(roadConditionItem))) {
    focusAreas.push("Road surface condition may increase crash likelihood through reduced control, braking, and lane keeping.");
  }
  if (sightDistanceItem && /poor|limited|inadequate|restricted/.test(lowerValue(sightDistanceItem))) {
    focusAreas.push("Sight distance is safety-sensitive because restricted visibility can reduce reaction time for conflicts and roadside hazards.");
  }
  if (roadsideItem && !/not applicable|none|no object/.test(lowerValue(roadsideItem))) {
    focusAreas.push("Roadside severity should be interpreted for run-off-road risk, especially clear zones, barriers, fixed objects, and recovery space.");
  }
  if (speedItem) {
    focusAreas.push("Speed context should be considered with the infrastructure because operating speed drives crash severity in the iRAP interpretation.");
  }
  if (gradeItem || curvatureItem) {
    focusAreas.push("Alignment risk should be read together with grade, curvature, delineation, and sight distance rather than as isolated field values.");
  }
  if (!focusAreas.length) {
    focusAreas.push("The available field values describe the road environment; expert interpretation should focus on likely crash risk and star-rating implications.");
  }

  const roadForm = [
    roadProfileItem ? specsInsightValueText(roadProfileItem) : "",
    medianItem ? specsInsightValueText(medianItem) : "",
    lanesItem ? specsInsightValueText(lanesItem) : "",
    laneWidthItem ? specsInsightValueText(laneWidthItem) : ""
  ].filter(Boolean).join(", ");
  const headline = roadForm
    ? `From an iRAP road-safety view, this segment reads as ${roadForm}; the interpretation should focus on crash risk and likely star-rating effects.`
    : "From an iRAP road-safety view, this segment should be interpreted through the coded road environment and its likely crash-risk implications.";
  const profile = profileFacts.length
    ? `Road context from AI-coded fields: ${profileFacts.map((fact) => `${fact.label.toLowerCase()} ${fact.value}`).join("; ")}.`
    : "Observed road context is limited, so the expert interpretation should be treated as preliminary.";

  return {
    headline,
    profile,
    facts: facts.slice(0, 6),
    focusAreas: focusAreas.slice(0, 5),
    interpretationType: "road-risk"
  };
}

function specsSegmentOrderValue(segment) {
  return Number(segment && segment.id) || 0;
}

function specsNeighborSegments(currentSegment, allSegments) {
  if (!currentSegment || !Array.isArray(allSegments) || !allSegments.length) {
    return { previous: null, next: null };
  }
  const roadName = String(currentSegment.road || "").trim();
  const pool = allSegments
    .filter((segment) => {
      if (!roadName) {
        return true;
      }
      return String(segment && segment.road || "").trim() === roadName;
    })
    .slice()
    .sort((a, b) => (
      specsSegmentOrderValue(a) - specsSegmentOrderValue(b)
    ));
  const index = pool.findIndex((segment) => Number(segment && segment.id) === Number(currentSegment.id));
  if (index < 0) {
    return { previous: null, next: null };
  }
  return {
    previous: pool[index - 1] || null,
    next: pool[index + 1] || null
  };
}

function specsFieldAnalysisValue(analysis, field) {
  if (!analysis || !field || !field.field) {
    return null;
  }
  const fieldCode = field.field;
  const analysisField = analysis.fields && analysis.fields[fieldCode] ? analysis.fields[fieldCode] : null;
  const saveJson = analysis.saveJson || {};
  const value = Object.prototype.hasOwnProperty.call(saveJson, fieldCode) && hasDisplayValue(saveJson[fieldCode])
    ? saveJson[fieldCode]
    : analysisField && hasDisplayValue(analysisField.value)
    ? analysisField.value
    : null;
  if (!hasDisplayValue(value)) {
    return null;
  }
  return {
    value,
    decodedValue: analysisField ? analysisField.decodedValue || "" : "",
    confidence: analysisField ? fieldConfidencePercent(analysisField.confidence) : null
  };
}

function specsNeighborAiInsight({ currentSegment, allSegments, fields, analysisResults, currentItems }) {
  const { previous, next } = specsNeighborSegments(currentSegment, allSegments);
  if (!previous && !next) {
    return null;
  }
  const currentItemsByField = new Map((Array.isArray(currentItems) ? currentItems : [])
    .filter((item) => item && item.field && item.field.field)
    .map((item) => [item.field.field, item]));
  const safeFields = Array.isArray(fields) ? fields.filter((field) => field && field.field) : [];
  const previousAnalysis = previous ? analysisResults[previous.id] || analysisResults[String(previous.id)] : null;
  const nextAnalysis = next ? analysisResults[next.id] || analysisResults[String(next.id)] : null;
  const suggestions = [];

  safeFields.forEach((field) => {
    const previousValue = specsFieldAnalysisValue(previousAnalysis, field);
    const nextValue = specsFieldAnalysisValue(nextAnalysis, field);
    if (!previousValue && !nextValue) {
      return;
    }
    const agreed =
      previousValue &&
      nextValue &&
      normalizeSpecComparableValue(previousValue.value) === normalizeSpecComparableValue(nextValue.value);
    const candidate = agreed ? previousValue : previousValue || nextValue;
    if (!candidate || !hasDisplayValue(candidate.value)) {
      return;
    }
    const currentItem = currentItemsByField.get(field.field);
    const currentValue = currentItem ? currentItem.value : null;
    if (normalizeSpecComparableValue(currentValue) === normalizeSpecComparableValue(candidate.value)) {
      return;
    }
    const neighborConfidences = [previousValue && previousValue.confidence, nextValue && nextValue.confidence]
      .filter((value) => value !== null && value !== undefined);
    const averageNeighborConfidence = neighborConfidences.length
      ? Math.round(neighborConfidences.reduce((sum, value) => sum + value, 0) / neighborConfidences.length)
      : null;
    const confidence = agreed
      ? Math.max(80, Math.min(95, averageNeighborConfidence || 80))
      : Math.max(60, Math.min(74, averageNeighborConfidence || 65));
    const sourceSegments = [
      previousValue ? `previous #${previous.id}` : "",
      nextValue ? `next #${next.id}` : ""
    ].filter(Boolean);
    suggestions.push({
      field,
      fieldLabel: field.caption || field.field,
      fieldCode: field.field,
      suggestedValue: candidate.value,
      suggestedDisplay: specDisplayValue(field, candidate.value, candidate.decodedValue),
      currentDisplay: hasDisplayValue(currentValue)
        ? specDisplayValue(field, currentValue, currentItem && currentItem.decodedValue)
        : "Blank",
      confidence,
      strength: agreed ? "strong" : "weak",
      reason: agreed
        ? `Previous and next segment both indicate ${specDisplayValue(field, candidate.value, candidate.decodedValue)}.`
        : `Only ${sourceSegments.join(" and ")} has this value, so treat as a softer continuity suggestion.`,
      sourceSegments
    });
  });

  return {
    previous,
    next,
    suggestions: suggestions
      .sort((a, b) => b.confidence - a.confidence || a.fieldLabel.localeCompare(b.fieldLabel))
      .slice(0, 8)
  };
}

function SegmentAiInsight({
  insight,
  collapsed,
  onToggleCollapsed,
  onOpenSourceImage = null,
  neighborInsight = null,
  neighborEnabled = false,
  onToggleNeighbor = null
}) {
  const [activeInsightTab, setActiveInsightTab] = useState("expert");
  if (!insight) {
    return null;
  }
  const confidenceLabel = insight.averageConfidence === null ? "No confidence" : `${insight.averageConfidence}% avg`;
  const neighborSuggestionCount = neighborInsight && Array.isArray(neighborInsight.suggestions)
    ? neighborInsight.suggestions.length
    : 0;
  const hasExpert = Boolean(insight.expert);
  const activeTab = activeInsightTab === "expert" && !hasExpert
    ? "attention"
    : activeInsightTab === "neighbor" && !neighborEnabled
    ? "attention"
    : activeInsightTab;
  return (
    <section className={`segment-ai-insight ${collapsed ? "collapsed" : ""}`}>
      <header>
        <div>
          <strong>{icon("sparkles")} AI Insight</strong>
          <span>{insight.valued}/{insight.total} values - {confidenceLabel}</span>
        </div>
        <button
          type="button"
          onClick={onToggleCollapsed}
          title={collapsed ? "Open AI Insight" : "Close AI Insight"}
          aria-label={collapsed ? "Open AI Insight" : "Close AI Insight"}
          aria-expanded={!collapsed}
        >
          {icon(collapsed ? "chevron-down" : "chevron-up")}
        </button>
      </header>
      {!collapsed && (
        <>
          <div className="segment-ai-insight-tabs" role="tablist" aria-label="AI Insight sections">
            {hasExpert && (
              <button
                type="button"
                className={activeTab === "expert" ? "active" : ""}
                onClick={() => setActiveInsightTab("expert")}
              >
                {icon("route")} iRAP analysis
              </button>
            )}
            <button
              type="button"
              className={activeTab === "attention" ? "active" : ""}
              onClick={() => setActiveInsightTab("attention")}
            >
              {icon("clipboard-check")} Data check
              <small>{insight.attentionItems.length}</small>
            </button>
            {neighborEnabled && (
              <button
                type="button"
                className={activeTab === "neighbor" ? "active" : ""}
                onClick={() => setActiveInsightTab("neighbor")}
              >
                {icon("git-compare-arrows")} Neighbor
                {neighborSuggestionCount > 0 && <small>{neighborSuggestionCount}</small>}
              </button>
            )}
          </div>
          {activeTab === "expert" && insight.expert && (
            <div className="segment-ai-expert">
              <strong>{icon("route")} iRAP road-risk view</strong>
              <p>{insight.expert.headline}</p>
              <p>{insight.expert.profile}</p>
              {insight.expert.facts.length > 0 && (
                <div className="segment-ai-expert-facts">
                  {insight.expert.facts.map((fact) => (
                    <span key={fact.label} title={fact.text}>
                      <b>{fact.label}</b>
                      {fact.value}
                    </span>
                  ))}
                </div>
              )}
              <div className="segment-ai-expert-focus">
                <b>Road-safety interpretation</b>
                {insight.expert.focusAreas.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
          )}
          {activeTab === "attention" && (
            <div className="segment-ai-insight-panel">
              <div className="segment-ai-insight-data-actions">
                <label className="segment-ai-insight-neighbor-toggle" title="Use previous and next segment AI values as continuity suggestions">
                  <input
                    type="checkbox"
                    checked={neighborEnabled}
                    onChange={(event) => onToggleNeighbor && onToggleNeighbor(event.target.checked)}
                  />
                  <span>Neighbor</span>
                  {neighborSuggestionCount > 0 && <small>{neighborSuggestionCount}</small>}
                </label>
              </div>
              <div className="segment-ai-insight-kpis">
                <span><b>{insight.changed}</b> changed</span>
                <span><b>{insight.same}</b> same</span>
                <span><b>{insight.missing}</b> missing</span>
                <span><b>{insight.lowConfidence}</b> low confidence</span>
                <span><b>{insight.sourceImages.length}</b> source images</span>
              </div>
              <div className="segment-ai-insight-body">
                <div className="segment-ai-insight-list">
                  <strong>Data validation checks</strong>
                  {insight.attentionItems.length ? insight.attentionItems.map((entry) => {
                    const canOpen = entry.sourceImages.length > 0 && typeof onOpenSourceImage === "function";
                    const Element = canOpen ? "button" : "div";
                    return (
                      <Element
                        key={entry.item.field.field}
                        type={canOpen ? "button" : undefined}
                        className="segment-ai-insight-item"
                        onClick={canOpen ? () => onOpenSourceImage(entry.item) : undefined}
                        title={[
                          entry.valueText,
                          entry.reasons.join(", "),
                          entry.sourceImages.length ? entry.sourceImages.join(", ") : "No source image"
                        ].join("\n")}
                      >
                        <span>{entry.fieldLabel}</span>
                        <em>{entry.confidence === null ? "No AI" : `${entry.confidence}%`}</em>
                        <small>{entry.reasons.join(", ")}</small>
                      </Element>
                    );
                  }) : (
                    <span>No high priority items.</span>
                  )}
                </div>
                <div className="segment-ai-insight-sources">
                  <strong>Sources</strong>
                  {insight.sourceImages.length ? insight.sourceImages.slice(0, 5).map((imageFile) => (
                    <span key={imageFile} title={imageFile}>{imageFile}</span>
                  )) : (
                    <span>No source images recorded.</span>
                  )}
                  {insight.sourceImages.length > 5 && <em>+{insight.sourceImages.length - 5}</em>}
                </div>
              </div>
            </div>
          )}
          {activeTab === "neighbor" && neighborEnabled && (
            <div className="segment-ai-neighbor">
              <strong>
                Segment continuity
                <span>
                  {neighborInsight && neighborInsight.previous ? `Prev #${neighborInsight.previous.id}` : "No prev"}
                  {" / "}
                  {neighborInsight && neighborInsight.next ? `Next #${neighborInsight.next.id}` : "No next"}
                </span>
              </strong>
              {neighborSuggestionCount ? (
                <div className="segment-ai-neighbor-list">
                  {neighborInsight.suggestions.map((suggestion) => (
                    <div
                      key={suggestion.fieldCode}
                      className={`segment-ai-neighbor-item ${suggestion.strength}`}
                      title={`${suggestion.reason}\nCurrent: ${suggestion.currentDisplay}\nSuggested: ${suggestion.suggestedDisplay}`}
                    >
                      <span>{suggestion.fieldLabel}</span>
                      <em>{suggestion.confidence}%</em>
                      <b>{suggestion.suggestedDisplay}</b>
                      <small>{suggestion.sourceSegments.join(", ")}</small>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No continuity differences found from adjacent segments.</p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function analysisFieldSourceImages(analysis, detail) {
  if (!analysis) {
    return [];
  }
  const visualEvidence = analysis.visualEvidence && typeof analysis.visualEvidence === "object"
    ? analysis.visualEvidence
    : {};
  const rawImages = [
    analysis.sourceImage,
    ...(Array.isArray(analysis.sourceImages) ? analysis.sourceImages : []),
    visualEvidence.sourceImage,
    ...(Array.isArray(visualEvidence.sourceImages) ? visualEvidence.sourceImages : [])
  ];
  return normalizeFieldSourceImages(rawImages, detail);
}

function segmentImageUrl(segment, imageFile) {
  if (!segment || !segment.id || !imageFile) {
    return "";
  }
  return `/api/segments/${Number(segment.id)}/images/${encodeURIComponent(imageFile)}`;
}

function SpecsEvidenceSection({
  evidenceItems,
  gisAssets = [],
  gisSummary = [],
  gisLoading = false,
  gisError = "",
  gisMapVisible = true,
  onToggleGisMapVisible = null,
  gisHiddenLayerKeys = [],
  onToggleGisLayerVisible = null,
  gisLayerSettings = DEFAULT_GIS_LAYER_SETTINGS,
  selectedSegment,
  onOpenEvidence
}) {
  const safeItems = Array.isArray(evidenceItems) ? evidenceItems : [];
  const safeGisAssets = Array.isArray(gisAssets) ? gisAssets : [];
  const hiddenGisLayerSet = new Set((Array.isArray(gisHiddenLayerKeys) ? gisHiddenLayerKeys : []).map((layerKey) => String(layerKey)));
  const safeGisSummary = Array.isArray(gisSummary) && gisSummary.length
    ? gisSummary
    : gisLayerClassOptionsFromAssets(safeGisAssets).map((option) => ({
        classId: option.classId,
        className: option.className,
        count: option.count
      }));
  const visibleGisAssets = safeGisAssets.filter((asset) => !hiddenGisLayerSet.has(gisLayerClassKey(asset)));
  const hiddenGisLayerCount = safeGisSummary.filter((item) => hiddenGisLayerSet.has(gisLayerClassKey(item))).length;
  const normalizedGisSettings = normalizeGisLayerSettings(gisLayerSettings);
  const gisStatusText = !normalizedGisSettings.evidenceEnabled
    ? "GIS layer loading is off"
    : gisLayerSelectionIsEmpty(normalizedGisSettings, "evidence")
    ? "No GIS layers selected"
    : gisLoading
    ? "Loading AssetAI layers"
    : gisError
    ? gisError
    : hiddenGisLayerCount
    ? `${visibleGisAssets.length} of ${safeGisAssets.length} approved AssetAI assets visible`
    : `${safeGisAssets.length} approved AssetAI asset${safeGisAssets.length === 1 ? "" : "s"}`;

  return (
    <section className="segment-evidence-section">
      <header>
        <div>
          <h3>Evidence</h3>
          <span>{safeItems.length} saved output{safeItems.length === 1 ? "" : "s"}</span>
        </div>
      </header>
      {safeItems.length ? (
        <div className="segment-evidence-grid">
          {safeItems.map((item, index) => {
            const label = evidenceItemLabel(item);
            const imageUrl = evidenceMarkedImageUrl(item, selectedSegment);
            const refs = evidenceFieldReferenceLabels(item);
            return (
              <button
                key={item.id || item.markedCropUrl || item.cropUrl || `${label}-${index}`}
                type="button"
                className="segment-evidence-card"
                onClick={() => onOpenEvidence && onOpenEvidence(item)}
                title={`Open evidence: ${label}`}
              >
                <span className="segment-evidence-card__image">
                  {imageUrl ? (
                    <img src={imageUrl} alt="" loading="lazy" decoding="async" />
                  ) : (
                    <span>{icon("scan-search")}</span>
                  )}
                </span>
                <span className="segment-evidence-card__body">
                  <strong>{label}</strong>
                  <small>{evidenceCategoryText(item)} - {evidenceConfidenceText(item)}</small>
                  {refs.length > 0 && (
                    <span className="segment-evidence-card__refs">
                      {refs.slice(0, 3).map((ref) => <em key={ref}>{ref}</em>)}
                      {refs.length > 3 && <em>+{refs.length - 3}</em>}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <section className="segment-gis-evidence">
        <header>
          <div>
            <h3>GIS Layers</h3>
            <span>{gisStatusText}</span>
          </div>
          <button
            type="button"
            className={`secondary-button compact ${gisMapVisible ? "active" : ""}`}
            disabled={gisLoading || !safeGisAssets.length || typeof onToggleGisMapVisible !== "function"}
            onClick={() => onToggleGisMapVisible && onToggleGisMapVisible((current) => !current)}
          >
            {icon(gisMapVisible ? "eye" : "eye-off")} Map
          </button>
        </header>
        {safeGisSummary.length > 0 && (
          <div className="segment-gis-summary">
            {safeGisSummary.map((item) => {
              const layerKey = gisLayerClassKey(item);
              const layerVisible = !hiddenGisLayerSet.has(layerKey);
              const layerName = item.className || `Class ${item.classId}`;
              return (
                <button
                  key={`${item.classId || item.className}`}
                  type="button"
                  className={`segment-gis-layer-toggle ${layerVisible ? "active" : ""}`}
                  disabled={gisLoading || typeof onToggleGisLayerVisible !== "function"}
                  aria-pressed={layerVisible}
                  title={`${layerVisible ? "Hide" : "Show"} ${layerName}`}
                  onClick={() => onToggleGisLayerVisible && onToggleGisLayerVisible(layerKey)}
                >
                  {icon(layerVisible ? "eye" : "eye-off")}
                  <strong>{layerName}</strong>
                  <em>{item.count}</em>
                </button>
              );
            })}
          </div>
        )}
        <div className="segment-gis-list">
          {visibleGisAssets.slice(0, 80).map((asset) => (
            <article key={gisAssetId(asset) || `${asset.classId}-${asset.assetName}`}>
              <span
                className="segment-gis-list__marker"
                style={{ background: gisLayerColor(asset.classId || asset.className || asset.assetType) }}
              ></span>
              <div>
                <strong>{gisAssetLabel(asset)}</strong>
                <small>{gisAssetMetaLabel(asset) || asset.geometryType || "GIS layer"}</small>
                <em>{asset.assetId ? `Asset ${asset.assetId}` : ""}{asset.geometryType ? ` - ${asset.geometryType}` : ""}</em>
              </div>
            </article>
          ))}
          {!gisLoading && !gisError && safeGisAssets.length > 0 && !visibleGisAssets.length && (
            <div className="segment-spec-empty compact">
              {icon("eye-off")}
              <strong>No visible GIS layers</strong>
              <span>Turn a layer back on above to show its assets.</span>
            </div>
          )}
          {!gisLoading && !gisError && !safeGisAssets.length && (
            <div className="segment-spec-empty compact">
              {icon("database")}
              <strong>No GIS assets found</strong>
              <span>AssetAI returned no approved GIS assets intersecting this segment.</span>
            </div>
          )}
        </div>
      </section>
    </section>
  );
}

function FieldPromptDiscussionHeader({ item, onClose, titleLabel = "Discussion topic" }) {
  if (!item || !item.field) {
    return null;
  }
  const field = item.field;
  const displayValue = hasDisplayValue(item.value)
    ? specDisplayValue(field, item.value, item.decodedValue)
    : "Blank";
  const confidence = fieldConfidencePercent(item.confidence);
  const confidenceTone = fieldConfidenceTone(confidence);
  const reason = String(item.evidence || "").trim();

  return (
    <header className="field-prompt-discussion-header">
      <div className="field-prompt-discussion-title">
        <span>{titleLabel}</span>
        <strong>{field.caption || field.field}</strong>
        <small>{field.field} - {field.group}</small>
      </div>
      <div className="field-prompt-discussion-value">
        <span>AI value</span>
        <strong>{displayValue}</strong>
        <em className={`field-result-confidence confidence-${confidenceTone}`}>
          {confidence === null ? "No confidence" : `${confidence}%`}
        </em>
      </div>
      <p className="field-prompt-discussion-reason">
        {reason || "No AI reason recorded for this field."}
      </p>
      <button type="button" className="icon-action" onClick={onClose} title="Close">
        {icon("x")}
      </button>
    </header>
  );
}

function StarRatingChatModal({
  selectedSegment,
  messages = [],
  chatInput = "",
  setChatInput = null,
  chatBusy = false,
  error = "",
  openAiSettingsLabel = "gpt-5.5 / low",
  onSend = null,
  onClose = null
}) {
  const chatLogRef = useRef(null);
  const segmentId = selectedSegment && selectedSegment.id;
  const safeMessages = Array.isArray(messages) ? messages : [];

  useEffect(() => {
    if (chatLogRef.current) {
      chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
    }
  }, [safeMessages.length, chatBusy]);

  const sendCurrent = () => {
    if (typeof onSend === "function") {
      onSend();
    }
  };
  const sendDefault = () => {
    if (typeof onSend === "function") {
      onSend(STAR_RATING_DEFAULT_CHAT_QUESTION);
    }
  };

  return (
    <div className="field-prompt-modal" role="dialog" aria-modal="true" aria-label="Star rating chat">
      <div className="field-prompt-dialog segment-star-chat-dialog">
        <header className="field-prompt-discussion-header segment-star-chat-header">
          <div className="field-prompt-discussion-title">
            <span>Star Rating Chat</span>
            <strong>Segment {segmentId || "-"}</strong>
            <small>Road-attribute calculation context</small>
          </div>
          <p className="field-prompt-discussion-reason">
            The chat calculation context uses only coded road attributes and AI evidence for those attributes. Old score/result fields are excluded from formula inputs.
          </p>
          <button type="button" className="icon-action" onClick={onClose} title="Close">
            {icon("x")}
          </button>
        </header>
        <div className="segment-star-chat-workspace">
          <section className="field-prompt-chat-pane segment-star-chat-pane">
            <section className="field-result-discussion">
              <div className="field-prompt-chat-heading">
                <div>
                  <strong>{icon("message-circle-question")} Star rating explanation</strong>
                </div>
                <div>
                  <button
                    type="button"
                    className="secondary-button compact"
                    disabled={chatBusy}
                    onClick={sendDefault}
                  >
                    {icon("sparkles")} Explain rating
                  </button>
                </div>
              </div>
              <div className="field-prompt-chat-log" ref={chatLogRef}>
                {safeMessages.map((message, index) => {
                  const isAssistant = String(message && message.role || "").toLowerCase() === "assistant";
                  return (
                    <div key={`${message.role || "user"}-${index}`} className={`field-prompt-chat-message ${isAssistant ? "assistant" : "user"}`}>
                      <b>{isAssistant ? "AiRAP" : "You"}</b>
                      <span>{message.content}</span>
                    </div>
                  );
                })}
                {!safeMessages.length && (
                  <div className="field-prompt-chat-empty">
                    Ask which road attributes are driving the risk or which attribute changes would likely improve the rating.
                  </div>
                )}
                {chatBusy && (
                  <div className="field-prompt-chat-message assistant is-thinking">
                    <b>AiRAP</b>
                    <span>Reading the segment coded road attributes...</span>
                  </div>
                )}
              </div>
              <div className="field-prompt-chat-input">
                <textarea
                  value={chatInput}
                  onChange={(event) => setChatInput && setChatInput(event.target.value)}
                  onKeyDown={(event) => {
                    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                      event.preventDefault();
                      sendCurrent();
                    }
                  }}
                  placeholder="Ask about the road-attribute calculation inputs..."
                  disabled={chatBusy}
                />
                <button
                  type="button"
                  className="primary-button"
                  disabled={chatBusy || !String(chatInput || "").trim()}
                  onClick={sendCurrent}
                >
                  {icon(chatBusy ? "loader-circle" : "send")} Send
                </button>
              </div>
              {error && <small className="field-prompt-error">{error}</small>}
            </section>
          </section>
          <aside className="segment-star-chat-context" aria-label="Star rating data summary">
            <section>
              <strong>{icon("calculator")} iRAP Formula</strong>
              <div className="segment-star-formula">
                <div>
                  <span>1. Start from coded road attributes</span>
                  <p>
                    The model reads the selected segment's road attributes: lane width, median,
                    roadside hazards, intersections, pedestrian and bicycle facilities, speeds,
                    traffic flow, and user flows. Internal F fields are only storage codes; the
                    explanation should use the attribute titles and option labels.
                  </p>
                </div>
                <div>
                  <span>2. Calculate crash scores</span>
                  <p>
                    For each road user, iRAP calculates risk for the crash types that apply to that
                    user, then adds those crash scores together.
                  </p>
                  <code>SRS(user) = sum of Crash Scores for that user</code>
                </div>
                <div>
                  <span>3. Crash score equation</span>
                  <p>
                    Each crash score combines exposure, the chance a crash starts, how severe it is,
                    speed effects, and flow modifiers.
                  </p>
                  <code>Crash Score = Likelihood x Severity x Speed Factor x Flow Factor x Median Factor</code>
                </div>
                <div>
                  <span>4. Road user crash types</span>
                  <ul>
                    <li><b>VO</b>: run-off road, head-on, and intersection crashes.</li>
                    <li><b>MC</b>: run-off road, head-on, and intersection crashes.</li>
                    <li><b>Ped</b>: walking along the road and crossing the road.</li>
                    <li><b>Bike</b>: riding along the road and intersection crashes; v3.10 also adds bicyclist crossing.</li>
                  </ul>
                </div>
                <div>
                  <span>5. Convert SRS to stars</span>
                  <p>
                    The raw SRS may be smoothed with nearby segments. The smoothed SRS is then placed
                    into the official iRAP star bands for that road user.
                  </p>
                  <code>Star(user) = Band(Smoothed SRS(user))</code>
                </div>
                <p className="segment-star-formula-note">
                  Important: old stored result fields such as SRS totals or Star Rating outputs must
                  not be used as calculation inputs. The real coefficients and star-band thresholds
                  must come from the official iRAP model tables for the selected model version.
                </p>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}

function GisLayerSettingsPanel({
  options = null,
  loading = false,
  error = "",
  settings = DEFAULT_GIS_LAYER_SETTINGS,
  setSettings = null,
  disabled = false,
  title = "GIS layer settings",
  compact = false
}) {
  const [searchText, setSearchText] = useState("");
  const normalized = normalizeGisLayerSettings(settings);
  const layerOptions = normalizeGisLayerOptions(options);
  const classOptions = layerOptions.classes;
  const classById = new Map(classOptions.map((item) => [String(item.classId), item]));
  const evidenceSet = new Set(normalized.evidenceClassIds.map((classId) => String(classId)));
  const aiSet = new Set(normalized.aiClassIds.map((classId) => String(classId)));
  const evidenceCount = gisLayerSelectedCount(normalized, layerOptions, "evidence");
  const aiCount = gisLayerSelectedCount(normalized, layerOptions, "ai");
  const normalizedSearch = searchText.trim().toLowerCase();
  const filteredClasses = classOptions.filter((item) => {
    if (normalizedSearch) {
      const haystack = `${item.classId} ${item.className} ${(item.aliases || []).join(" ")}`.toLowerCase();
      if (!haystack.includes(normalizedSearch)) {
        return false;
      }
    }
    return true;
  });
  const selectedItemsForTarget = (target) => {
    const ids = target === "ai" ? normalized.aiClassIds : normalized.evidenceClassIds;
    return ids.map((classId) => {
      const existing = classById.get(String(classId));
      return existing || {
        classId,
        className: `Class ${classId}`,
        count: 0,
        aliases: []
      };
    });
  };
  const evidenceItems = selectedItemsForTarget("evidence");
  const aiItems = selectedItemsForTarget("ai");
  const status = loading
    ? "Loading layer list"
    : error
    ? error
    : !classOptions.length
    ? "No approved AssetAI layers found"
    : `${filteredClasses.length} shown of ${classOptions.length} layers - ${evidenceCount} Evidence - ${aiCount} AI`;

  const updateSettings = (patcher) => {
    if (typeof setSettings !== "function") {
      return;
    }
    setSettings((current) => {
      const currentSettings = normalizeGisLayerSettings(current);
      const next = typeof patcher === "function" ? patcher(currentSettings) : { ...currentSettings, ...patcher };
      return normalizeGisLayerSettings(next);
    });
  };

  const changeClassTarget = (target, classId, shouldAdd) => {
    const id = Number(classId);
    if (!Number.isInteger(id) || id <= 0) {
      return;
    }
    const prefix = target === "ai" ? "ai" : "evidence";
    updateSettings((currentSettings) => {
      const current = normalizeGisLayerSettings(currentSettings);
      const currentSet = new Set(current[`${prefix}ClassIds`]);
      if (shouldAdd) {
        currentSet.add(id);
      } else {
        currentSet.delete(id);
      }
      const nextIds = Array.from(currentSet).sort((a, b) => a - b);
      return {
        ...current,
        [`${prefix}Enabled`]: true,
        [`${prefix}SelectionMode`]: "selected",
        [`${prefix}ClassIds`]: nextIds
      };
    });
  };
  const addClassToTarget = (target, classId) => changeClassTarget(target, classId, true);
  const removeClassFromTarget = (target, classId) => changeClassTarget(target, classId, false);
  const renderSelectedList = (target, label, items) => (
    <section className={`gis-layer-selected-list ${target}`}>
      <header>
        <strong>{label}</strong>
        <span>{items.length} layers</span>
      </header>
      <div>
        {items.length ? items.map((item) => (
          <article key={`${target}-${item.classId}`}>
            <span>
              <strong>{item.className || `Class ${item.classId}`}</strong>
              <small>Class {item.classId}{item.count ? ` - ${item.count} assets` : ""}</small>
            </span>
            <button
              type="button"
              className="icon-action"
              title={`Remove ${item.className || `Class ${item.classId}`}`}
              aria-label={`Remove ${item.className || `Class ${item.classId}`} from ${label}`}
              disabled={disabled || loading}
              onClick={() => removeClassFromTarget(target, item.classId)}
            >
              {icon("x")}
            </button>
          </article>
        )) : (
          <p>No layers added.</p>
        )}
      </div>
    </section>
  );

  return (
    <section className={`gis-layer-settings ${compact ? "compact" : ""}`}>
      <header>
        <div>
          <strong>{icon("layers")} {title}</strong>
          <span>{status}</span>
        </div>
      </header>
      <div className="gis-layer-settings__workspace">
        <section className="gis-layer-available">
          <div className="gis-layer-settings__search">
            <label>
              {icon("search")}
              <input
                type="search"
                value={searchText}
                placeholder="Search layer name or class ID"
                onChange={(event) => setSearchText(event.target.value)}
              />
            </label>
          </div>
          <div className="gis-layer-settings__table-wrap">
            <table className="gis-layer-settings__table">
              <thead>
                <tr>
                  <th>Layer</th>
                  <th>Assets</th>
                  <th>Add to</th>
                </tr>
              </thead>
              <tbody>
                {filteredClasses.length ? filteredClasses.map((item) => {
                  const evidenceAdded = evidenceSet.has(String(item.classId));
                  const aiAdded = aiSet.has(String(item.classId));
                  return (
                    <tr key={item.classId}>
                      <td>
                        <strong>{item.className || `Class ${item.classId}`}</strong>
                        <small>
                          Class {item.classId}
                          {item.aliases && item.aliases.length ? ` - also ${item.aliases.slice(0, 3).join(", ")}` : ""}
                        </small>
                      </td>
                      <td>{item.count}</td>
                      <td>
                        <div className="gis-layer-row-actions">
                          <button
                            type="button"
                            className={evidenceAdded ? "active" : ""}
                            disabled={disabled || loading || evidenceAdded}
                            onClick={() => addClassToTarget("evidence", item.classId)}
                          >
                            {icon(evidenceAdded ? "check" : "plus")} Evidence
                          </button>
                          <button
                            type="button"
                            className={aiAdded ? "active" : ""}
                            disabled={disabled || loading || aiAdded}
                            onClick={() => addClassToTarget("ai", item.classId)}
                          >
                            {icon(aiAdded ? "check" : "plus")} AI
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="3" className="gis-layer-settings__empty">{loading ? "Loading layers..." : "No layers match this search."}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        <aside className="gis-layer-selected-stack">
          {renderSelectedList("evidence", "Evidence layers", evidenceItems)}
          {renderSelectedList("ai", "AI layers", aiItems)}
        </aside>
      </div>
    </section>
  );
}

function GisLayerContextPicker({
  layerOptions = [],
  enabled = false,
  setEnabled = null,
  selectedClassIds = [],
  setSelectedClassIds = null,
  disabled = false
}) {
  const [search, setSearch] = useState("");
  const classOptions = useMemo(() => (
    (Array.isArray(layerOptions) ? layerOptions : [])
      .map((option) => ({
        classId: Number(option && option.classId),
        className: String(option && option.className || "").trim(),
        count: Math.max(0, Number(option && option.count) || 0),
        aliases: Array.isArray(option && option.aliases) ? option.aliases : []
      }))
      .filter((option) => Number.isInteger(option.classId) && option.classId > 0)
      .sort((a, b) => a.className.localeCompare(b.className) || a.classId - b.classId)
  ), [layerOptions]);
  const selectedSet = new Set(parsePositiveIdArray(selectedClassIds).map((classId) => String(classId)));
  const normalizedSearch = String(search || "").trim().toLowerCase();
  const visibleOptions = normalizedSearch
    ? classOptions.filter((option) => (
        `${option.className} ${option.classId} ${(option.aliases || []).join(" ")}`
          .toLowerCase()
          .includes(normalizedSearch)
      ))
    : classOptions;
  const selectedCount = classOptions.filter((option) => selectedSet.has(String(option.classId))).length;
  const hasLayers = classOptions.length > 0;
  const updateSelection = (nextKeys) => {
    const ids = parsePositiveIdArray(nextKeys);
    if (setSelectedClassIds) {
      setSelectedClassIds(ids.map((classId) => String(classId)));
    }
    if (setEnabled) {
      setEnabled(ids.length > 0);
    }
  };
  const toggleClass = (option) => {
    if (!option || !setSelectedClassIds) {
      return;
    }
    const key = String(option.classId);
    const nextKeys = new Set(selectedSet);
    if (nextKeys.has(key)) {
      nextKeys.delete(key);
    } else {
      nextKeys.add(key);
    }
    updateSelection(Array.from(nextKeys));
  };

  return (
    <section className={`field-prompt-gis-context ${selectedCount ? "active" : ""}`}>
      <div className="field-prompt-gis-heading">
        <span>
          <strong>{icon("database")} GIS data for this field</strong>
          <small>
            {hasLayers
              ? `${selectedCount} of ${classOptions.length} AI layer${classOptions.length === 1 ? "" : "s"} selected`
              : "Add AI layers in Settings before selecting GIS data for this field."}
          </small>
        </span>
        {selectedCount > 0 && (
          <button
            type="button"
            className="secondary-button compact"
            disabled={disabled}
            onClick={() => updateSelection([])}
          >
            {icon("x")} Clear
          </button>
        )}
      </div>
      {hasLayers && (
        <div className="field-prompt-gis-options">
          <label className="field-prompt-gis-search">
            {icon("search")}
            <input
              type="search"
              value={search}
              disabled={disabled}
              placeholder="Search AI layer"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <div className="field-prompt-gis-class-list">
            {visibleOptions.length ? visibleOptions.map((option) => {
              const key = String(option.classId);
              const checked = selectedSet.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={checked ? "active" : ""}
                  disabled={disabled}
                  onClick={() => toggleClass(option)}
                >
                  <span>{option.className}</span>
                  <em>{option.count}</em>
                </button>
              );
            }) : (
              <p>No AI layers match this search.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function FieldPromptManager({
  item,
  prompt = "",
  setPrompt = null,
  busy = false,
  status = "",
  error = "",
  trainerImages = [],
  trainerBusy = false,
  trainerResult = null,
  openAiSettingsLabel = "gpt-5.5 / low",
  imageSourceMode = DEFAULT_FIELD_IMAGE_SOURCE_MODE,
  setImageSourceMode = null,
  gisLayerOptions = [],
  gisEnabled = false,
  setGisEnabled = null,
  gisClassIds = [],
  setGisClassIds = null,
  injectToAgentPrompt = false,
  setInjectToAgentPrompt = null,
  includeAgentInstructionsForRun = false,
  setIncludeAgentInstructionsForRun = null,
  includeAgentPromptAddendumForRun = false,
  setIncludeAgentPromptAddendumForRun = null,
  onPasteTrainerImages = null,
  onImportTrainerImages = null,
  onDropTrainerImages = null,
  onRemoveTrainerImage = null,
  onGenerateFromImages = null,
  onApplyTrainerPrompt = null,
  onTestTrainerPrompt = null,
  onClose = null,
  onSave = null,
  onRunCurrent = null,
  onRunBatch = null,
  onSaveToAgent = null
}) {
  if (!item || !item.field) {
    return null;
  }
  const field = item.field;
  const currentPrompt = String(prompt || "");
  const canRunWithPrompt = Boolean(currentPrompt.trim());
  const activeImageSourceMode = normalizeFieldImageSourceMode(imageSourceMode);
  const runAction = (event, handler) => {
    event.preventDefault();
    event.stopPropagation();
    if ((busy || trainerBusy) || typeof handler !== "function") {
      return;
    }
    handler();
  };

  return (
    <section className="field-prompt-manager" aria-busy={busy || trainerBusy} onPaste={onPasteTrainerImages}>
      <header className="field-prompt-manager-header">
        <div>
          <span>{field.field} - {field.group}</span>
          <h3>{field.caption || field.field}</h3>
        </div>
        <em className={(busy || trainerBusy) ? "is-busy" : ""}>
          {(busy || trainerBusy) && icon("loader-circle")}
          {busy || trainerBusy ? "Updating" : currentPrompt.trim() ? "Field prompt saved" : "No field prompt yet"}
        </em>
      </header>
      {status && (
        <div className={`field-prompt-manager-status ${(busy || trainerBusy) ? "is-busy" : "is-done"}`}>
          {(busy || trainerBusy) ? icon("loader-circle") : icon("check-circle")}
          <span>{status}</span>
        </div>
      )}
      <section className="field-prompt-source-mode" aria-label="Image source for this field">
        <div>
          <strong>Image source for this field</strong>
          <span>{activeImageSourceMode === "panorama" ? "OpenAI will use only panorama frames." : activeImageSourceMode === "aerial" ? "OpenAI will use only the aerial segment overlay." : "OpenAI will use the aerial overlay and panorama frames together."}</span>
        </div>
        <div className="field-prompt-source-options" role="group" aria-label="Image source mode">
          {FIELD_IMAGE_SOURCE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={activeImageSourceMode === option.value ? "active" : ""}
              disabled={busy || trainerBusy}
              onClick={() => setImageSourceMode && setImageSourceMode(option.value)}
            >
              {icon(option.icon)}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </section>
      <GisLayerContextPicker
        layerOptions={gisLayerOptions}
        enabled={gisEnabled}
        setEnabled={setGisEnabled}
        selectedClassIds={gisClassIds}
        setSelectedClassIds={setGisClassIds}
        disabled={busy || trainerBusy}
      />
      <label className="field-prompt-manager-editor">
        <span>Special prompt for this field</span>
        <textarea
          value={currentPrompt}
          onChange={(event) => setPrompt && setPrompt(event.target.value)}
          placeholder="Write the field-specific instruction here."
          spellCheck="false"
        />
      </label>
      <label className={`field-prompt-agent-inject-toggle ${injectToAgentPrompt ? "active" : ""}`}>
        <input
          type="checkbox"
          checked={Boolean(injectToAgentPrompt)}
          onChange={(event) => setInjectToAgentPrompt && setInjectToAgentPrompt(event.target.checked)}
          disabled={busy || trainerBusy}
        />
        <span>
          <strong>Also inject this field prompt into the agent prompt on save/run</strong>
          <small>Use this when the rule should become part of the main agent prompt without pressing Update agent prompt.</small>
        </span>
      </label>
      <section className="field-prompt-run-context" aria-label="Optional agent context for field prompt runs">
        <div className="field-prompt-run-context-header">
          <strong>Optional agent context for run</strong>
          <span>Default skips these two agent sections.</span>
        </div>
        <div className="field-prompt-run-context-options">
          <label className={`field-prompt-agent-inject-toggle ${includeAgentInstructionsForRun ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={Boolean(includeAgentInstructionsForRun)}
              onChange={(event) => setIncludeAgentInstructionsForRun && setIncludeAgentInstructionsForRun(event.target.checked)}
              disabled={busy || trainerBusy}
            />
            <span>
              <strong>Use Special instructions</strong>
              <small>Include the agent Special instructions in this field run.</small>
            </span>
          </label>
          <label className={`field-prompt-agent-inject-toggle ${includeAgentPromptAddendumForRun ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={Boolean(includeAgentPromptAddendumForRun)}
              onChange={(event) => setIncludeAgentPromptAddendumForRun && setIncludeAgentPromptAddendumForRun(event.target.checked)}
              disabled={busy || trainerBusy}
            />
            <span>
              <strong>Use Prompt addendum</strong>
              <small>Include the agent Prompt addendum in this field run.</small>
            </span>
          </label>
        </div>
      </section>
      <section className="field-prompt-trainer">
        <header>
          <div>
            <strong>{icon("scan-text")} Prompt trainer</strong>
            <span>Paste, drop, or upload screenshots from a PDF, manual, table, or correction note.</span>
          </div>
        </header>
        <div
          className={`field-prompt-trainer-dropzone ${Array.isArray(trainerImages) && trainerImages.length ? "has-images" : ""}`}
          tabIndex={0}
          role="button"
          aria-label="Paste screenshots here"
          onPaste={onPasteTrainerImages}
          onDrop={onDropTrainerImages}
          onDragOver={(event) => event.preventDefault()}
        >
          {Array.isArray(trainerImages) && trainerImages.length ? (
            <div className="field-prompt-trainer-images">
              {trainerImages.map((image) => (
                <figure key={image.id}>
                  <img src={image.dataUrl} alt="" />
                  <figcaption>{image.name || "Pasted image"}</figcaption>
                  <button
                    type="button"
                    className="icon-action"
                    title="Remove image"
                    aria-label="Remove image"
                    onClick={() => onRemoveTrainerImage && onRemoveTrainerImage(image.id)}
                  >
                    {icon("x")}
                  </button>
                </figure>
              ))}
            </div>
          ) : (
            <span>{icon("clipboard")} Click here and press Ctrl+V, drop images, or upload screenshots.</span>
          )}
        </div>
        <div className="field-prompt-trainer-actions">
          <label className="secondary-button file-import-button">
            {icon("upload")} Upload images
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={onImportTrainerImages}
              disabled={busy || trainerBusy}
            />
          </label>
          <button
            type="button"
            className="secondary-button"
            disabled={busy || trainerBusy || !(Array.isArray(trainerImages) && trainerImages.length)}
            onClick={(event) => runAction(event, onGenerateFromImages)}
          >
            {trainerBusy ? icon("loader-circle") : icon("wand-sparkles")} Generate prompt from images
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={busy || trainerBusy || !(trainerResult && trainerResult.suggestedPrompt)}
            onClick={(event) => runAction(event, onApplyTrainerPrompt)}
          >
            {icon("file-pen-line")} Apply to editor
          </button>
          <button
            type="button"
            className="secondary-button"
            disabled={busy || trainerBusy || !(currentPrompt.trim() || (trainerResult && trainerResult.suggestedPrompt))}
            onClick={(event) => runAction(event, onTestTrainerPrompt)}
          >
            {trainerBusy ? icon("loader-circle") : icon("flask-conical")} Test this segment
          </button>
        </div>
        {trainerResult && (
          <div className="field-prompt-trainer-result">
            {trainerResult.summary && (
              <section>
                <strong>Summary</strong>
                <p>{trainerResult.summary}</p>
              </section>
            )}
            {Array.isArray(trainerResult.extractedRules) && trainerResult.extractedRules.length > 0 && (
              <section>
                <strong>Extracted rules</strong>
                <ul>
                  {trainerResult.extractedRules.map((rule, index) => <li key={`${rule}-${index}`}>{rule}</li>)}
                </ul>
              </section>
            )}
            {trainerResult.suggestedPrompt && (
              <section>
                <strong>Suggested prompt</strong>
                <pre>{trainerResult.suggestedPrompt}</pre>
              </section>
            )}
            {Array.isArray(trainerResult.warnings) && trainerResult.warnings.length > 0 && (
              <section>
                <strong>Warnings</strong>
                <ul>
                  {trainerResult.warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}
                </ul>
              </section>
            )}
            {trainerResult.test && (
              <section className="field-prompt-trainer-test">
                <strong>Dry-run test</strong>
                <div>
                  <span>Value</span>
                  <b>{trainerResult.test.value === null || trainerResult.test.value === undefined || trainerResult.test.value === "" ? "Blank" : `${trainerResult.test.value}${trainerResult.test.decodedValue ? `: ${trainerResult.test.decodedValue}` : ""}`}</b>
                </div>
                {trainerResult.test.confidence !== null && trainerResult.test.confidence !== undefined && (
                  <div>
                    <span>Confidence</span>
                    <b>{Math.round(Number(trainerResult.test.confidence) * 100)}%</b>
                  </div>
                )}
                {trainerResult.test.evidence && <p>{trainerResult.test.evidence}</p>}
                {Array.isArray(trainerResult.test.warnings) && trainerResult.test.warnings.length > 0 && (
                  <ul>
                    {trainerResult.test.warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}
                  </ul>
                )}
              </section>
            )}
          </div>
        )}
      </section>
      {error && <small className="field-prompt-error">{error}</small>}
      <footer className="field-prompt-manager-actions">
        <button type="button" className="secondary-button" onClick={(event) => runAction(event, onClose)} disabled={busy || trainerBusy}>
          {icon("x")} Close
        </button>
        <div>
          <button type="button" className="secondary-button" onClick={(event) => runAction(event, onSave)} disabled={busy || trainerBusy}>
            {icon("save")} Save
          </button>
          <button type="button" className="secondary-button" onClick={(event) => runAction(event, onRunCurrent)} disabled={busy || trainerBusy || !canRunWithPrompt} title={canRunWithPrompt ? "Run this segment" : "Add a field prompt before running"}>
            {busy ? icon("loader-circle") : icon("refresh-cw")} Run this segment
          </button>
          <button type="button" className="secondary-button" onClick={(event) => runAction(event, onRunBatch)} disabled={busy || trainerBusy || !canRunWithPrompt} title={canRunWithPrompt ? "Run field for the filtered grid" : "Add a field prompt before running"}>
            {icon("list-restart")} Run field for list
          </button>
          <button type="button" className="primary-button" onClick={(event) => runAction(event, onSaveToAgent)} disabled={busy || trainerBusy}>
            {icon("bot")} Update agent prompt
          </button>
        </div>
      </footer>
    </section>
  );
}

function FieldResultDiscussion({
  item,
  messages = [],
  chatInput = "",
  setChatInput = null,
  chatBusy = false,
  pastedImages = [],
  onPasteImages = null,
  onRemovePastedImage = null,
  discussionStatus = "",
  openAiSettingsLabel = "gpt-5.5 / low",
  imageSourceMode = DEFAULT_FIELD_IMAGE_SOURCE_MODE,
  setImageSourceMode = null,
  gisLayerOptions = [],
  gisEnabled = false,
  setGisEnabled = null,
  gisClassIds = [],
  setGisClassIds = null,
  discussionId = "",
  discussions = [],
  historyOpen = false,
  setHistoryOpen = null,
  isEditor = false,
  actionBusy = false,
  onSelectDiscussion = null,
  onClearDiscussions = null,
  onSubmitFeedback = null,
  onSend = null
}) {
  const chatLogRef = useRef(null);

  useEffect(() => {
    const node = chatLogRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [messages.length, chatBusy]);

  if (!item || !item.field) {
    return null;
  }
  const activeImageSourceMode = normalizeFieldImageSourceMode(imageSourceMode);

  return (
    <section className="field-result-discussion">
      <div className="field-prompt-chat-heading">
        <div>
          <strong>AiRAP Agent</strong>
          {discussionStatus && <span>{discussionStatus}</span>}
        </div>
        <div>
          {isEditor && discussions.length > 0 && (
            <button type="button" className="secondary-button compact" onClick={() => setHistoryOpen && setHistoryOpen(!historyOpen)}>
              {icon("messages-square")} Chats {discussions.length}
            </button>
          )}
          {(messages.length > 0 || discussions.length > 0 || discussionId) && (
            <button type="button" className="secondary-button compact danger" disabled={actionBusy || chatBusy} onClick={onClearDiscussions}>
              {icon("trash-2")} Clear chats
            </button>
          )}
          {!isEditor && discussionId && messages.length > 0 && (
            <button type="button" className="secondary-button compact" disabled={actionBusy || discussionStatus === "feedback"} onClick={onSubmitFeedback}>
              {icon("send")} {discussionStatus === "feedback" ? "Feedback sent" : "Send feedback"}
            </button>
          )}
        </div>
      </div>

      <div className="field-prompt-discussion-source">
        <span>Image source</span>
        <div className="field-prompt-source-options" role="group" aria-label="Image source mode">
          {FIELD_IMAGE_SOURCE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={activeImageSourceMode === option.value ? "active" : ""}
              disabled={chatBusy || actionBusy}
              onClick={() => setImageSourceMode && setImageSourceMode(option.value)}
            >
              {icon(option.icon)}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </div>
      <GisLayerContextPicker
        layerOptions={gisLayerOptions}
        enabled={gisEnabled}
        setEnabled={setGisEnabled}
        selectedClassIds={gisClassIds}
        setSelectedClassIds={setGisClassIds}
        disabled={chatBusy || actionBusy}
      />

      {isEditor && historyOpen && discussions.length > 0 && (
        <div className="field-prompt-history-list">
          {discussions.map((discussion) => (
            <button
              key={discussion.id}
              type="button"
              className={discussion.id === discussionId ? "active" : ""}
              onClick={() => onSelectDiscussion && onSelectDiscussion(discussion)}
            >
              <span>{discussion.user && discussion.user.name ? discussion.user.name : "Reviewer"}</span>
              <em>{discussion.status || "open"}</em>
              <small>{discussion.updatedAt ? new Date(discussion.updatedAt).toLocaleString() : ""}</small>
            </button>
          ))}
        </div>
      )}

      <div className="field-prompt-chat-log" ref={chatLogRef}>
        {messages.length ? messages.map((message, index) => (
          <div key={`${message.role}-${index}`} className={`field-prompt-chat-message ${message.role === "assistant" ? "assistant" : "user"}`}>
            <b>{message.role === "assistant" ? "AiRAP Agent" : "You"}</b>
            <span>{message.content}</span>
            {Array.isArray(message.attachments) && message.attachments.length > 0 && (
              <div className="field-prompt-message-attachments">
                {message.attachments.map((attachment, attachmentIndex) => (
                  <figure key={attachment.id || `${attachment.name}-${attachmentIndex}`}>
                    {attachment.previewUrl ? <img src={attachment.previewUrl} alt="" /> : icon("image")}
                    <figcaption>{attachment.name || "Pasted image"}</figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        )) : (
          <div className="field-prompt-chat-empty">
            Start a focused discussion with the AiRAP agent about this field result. Ask why it chose the value, point out missed visual evidence, or describe the rule it should apply in similar cases.
          </div>
        )}
        {chatBusy && (
          <div className="field-prompt-chat-message assistant is-thinking">
            <b>AiRAP Agent</b>
            <span>Thinking...</span>
          </div>
        )}
      </div>

      <div className="field-prompt-chat-input">
        {Array.isArray(pastedImages) && pastedImages.length > 0 && (
          <div className="field-prompt-pasted-images">
            {pastedImages.map((image) => (
              <figure key={image.id}>
                <img src={image.dataUrl} alt="" />
                <figcaption>{image.name || "Pasted image"}</figcaption>
                <button
                  type="button"
                  className="icon-action"
                  title="Remove pasted image"
                  aria-label="Remove pasted image"
                  onClick={() => onRemovePastedImage && onRemovePastedImage(image.id)}
                >
                  {icon("x")}
                </button>
              </figure>
            ))}
          </div>
        )}
        <textarea
          value={chatInput}
          onChange={(event) => setChatInput && setChatInput(event.target.value)}
          onPaste={onPasteImages}
          placeholder="Chat with the AiRAP agent about this result..."
          onKeyDown={(event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
              event.preventDefault();
              onSend && onSend();
            }
          }}
        />
        <button type="button" className="primary-button" disabled={chatBusy || (!String(chatInput || "").trim() && !(Array.isArray(pastedImages) && pastedImages.length))} onClick={onSend}>
          {icon("send")} Chat
        </button>
      </div>
    </section>
  );
}

function FieldPromptPanoPane({
  item,
  selectedSegment,
  segmentImages = [],
  selectedImages = [],
  imageSourceMode = DEFAULT_FIELD_IMAGE_SOURCE_MODE,
  onToggleImage = null,
  logs = [],
  onRefreshLogs = null
}) {
  const [promptLogOpen, setPromptLogOpen] = useState(false);

  if (!item || !item.field) {
    return null;
  }
  const safeImages = Array.isArray(segmentImages) ? segmentImages : [];
  const selectedList = (selectedImages || []).filter(Boolean);
  const selectedSet = new Set(selectedList);
  const sourceImages = fieldSourceImages(item);
  const normalizedImageSourceMode = normalizeFieldImageSourceMode(imageSourceMode);
  const shouldShowAerialSource = normalizedImageSourceMode === "aerial" ||
    normalizedImageSourceMode === "both" ||
    selectedSet.has(AERIAL_OVERLAY_IMAGE_FILE) ||
    sourceImages.includes(AERIAL_OVERLAY_IMAGE_FILE);
  const sourceSet = new Set(sourceImages);
  if (shouldShowAerialSource) {
    sourceSet.add(AERIAL_OVERLAY_IMAGE_FILE);
  }
  const activeSelectedFile = selectedList[0] || "";
  const activeIsAerial = shouldShowAerialSource && (
    activeSelectedFile === AERIAL_OVERLAY_IMAGE_FILE ||
    (!activeSelectedFile && normalizedImageSourceMode === "aerial")
  );
  const activeImage = activeIsAerial
    ? null
    : safeImages.find((image) => activeSelectedFile && (image && (image.file || image.fileName)) === activeSelectedFile) ||
      safeImages.find((image) => sourceSet.has(image && (image.file || image.fileName))) ||
      safeImages[0] ||
      null;
  const imageFile = activeImage && (activeImage.file || activeImage.fileName);
  const activeDisplayFile = activeIsAerial ? AERIAL_OVERLAY_IMAGE_FILE : imageFile;
  const imageUrl = activeImage && (activeImage.url || (
    selectedSegment && selectedSegment.id && imageFile
      ? `/api/segments/${Number(selectedSegment.id)}/images/${encodeURIComponent(imageFile)}`
      : ""
  ));
  const previewImageUrl = selectedSegment && selectedSegment.id && imageFile
    ? `/api/segments/${Number(selectedSegment.id)}/images/${encodeURIComponent(imageFile)}/preview?width=4096&q=84`
    : imageUrl;
  const aerialImageUrl = selectedSegment && selectedSegment.id
    ? `/api/segments/${Number(selectedSegment.id)}/aerial-overlay?zoom=19`
    : "";
  const fieldCode = String(item.field.field || "");
  const segmentPromptLogs = (Array.isArray(logs) ? logs : [])
    .filter((log) => selectedSegment && Number(log && log.segmentId) === Number(selectedSegment.id));
  const fieldPromptLogs = segmentPromptLogs.filter((log) => {
    const promptText = String(log && log.prompt || "");
    return !fieldCode || promptText.includes(` ${fieldCode} `) || promptText.includes(`${fieldCode} `);
  });
  const visiblePromptLogs = fieldPromptLogs.length ? fieldPromptLogs : segmentPromptLogs;
  const latestPromptLog = visiblePromptLogs[0] || null;
  const sentLogImages = latestPromptLog
    ? (Array.isArray(latestPromptLog.sentImages) && latestPromptLog.sentImages.length
      ? latestPromptLog.sentImages
      : (Array.isArray(latestPromptLog.images) ? latestPromptLog.images : []).map((file) => ({ file, pasted: false, dataUrl: "" })))
    : [];
  const promptLogDetail = latestPromptLog ? JSON.stringify({
    images: latestPromptLog.images || [],
    sentImages: sentLogImages.map((image) => ({
      file: image.file || "",
      pasted: Boolean(image.pasted)
    })),
    fieldAnalysis: latestPromptLog.fieldAnalysis || null,
    parsedResult: latestPromptLog.parsedResult || null,
    response: latestPromptLog.response || null,
    error: latestPromptLog.error || null
  }, null, 2) : "";

  return (
    <aside className="field-prompt-pano-pane">
      <div className="field-prompt-pano-stage">
        {activeIsAerial && aerialImageUrl ? (
          <div className="field-prompt-aerial-preview">
            <img src={aerialImageUrl} alt="Aerial segment overlay" />
          </div>
        ) : imageUrl ? (
          <PanoViewer
            key={`field-prompt-pano-${selectedSegment ? selectedSegment.id : "segment"}-${imageFile}`}
            imageUrl={previewImageUrl}
          />
        ) : (
          <div className="pano-empty">No source image available.</div>
        )}
        <div className="field-prompt-pano-image-strip">
          <header>
            <div>
              <strong>{icon("image")} Source images</strong>
              <span>{activeDisplayFile || "No image selected"}</span>
            </div>
            <small>{selectedSet.size || 0} selected / {sourceSet.size || 0} source</small>
          </header>
          <div>
            {shouldShowAerialSource && (
              <button
                type="button"
                className={`${selectedSet.has(AERIAL_OVERLAY_IMAGE_FILE) || activeIsAerial ? "selected" : ""} ${sourceSet.has(AERIAL_OVERLAY_IMAGE_FILE) ? "source" : ""} aerial-source`}
                onClick={() => onToggleImage && onToggleImage(AERIAL_OVERLAY_IMAGE_FILE)}
                title="AI source image - aerial segment overlay"
                aria-pressed={selectedSet.has(AERIAL_OVERLAY_IMAGE_FILE) || activeIsAerial}
              >
                {aerialImageUrl ? <img src={aerialImageUrl} alt="" loading="lazy" decoding="async" /> : <span>{icon("map")}</span>}
                <small>Air</small>
                {sourceSet.has(AERIAL_OVERLAY_IMAGE_FILE) && <em>source</em>}
              </button>
            )}
            {safeImages.length ? safeImages.map((image, index) => {
              const file = image && (image.file || image.fileName);
              if (!file) {
                return null;
              }
              const isSelected = selectedSet.has(file);
              const isSource = sourceSet.has(file);
              const thumbUrl = selectedSegment && selectedSegment.id
                ? `/api/segments/${Number(selectedSegment.id)}/images/${encodeURIComponent(file)}/thumb`
                : "";
              return (
                <button
                  key={file}
                  type="button"
                  className={`${isSelected ? "selected" : ""} ${isSource ? "source" : ""}`}
                  onClick={() => onToggleImage && onToggleImage(file)}
                  title={`${isSource ? "AI source image - " : ""}${file}`}
                  aria-pressed={isSelected}
                >
                  {thumbUrl ? <img src={thumbUrl} alt="" loading="lazy" decoding="async" /> : <span>{index + 1}</span>}
                  <small>{index + 1}</small>
                  {isSource && <em>source</em>}
                </button>
              );
            }) : (
              <span>No segment images found.</span>
            )}
          </div>
        </div>
        {promptLogOpen && (
          <div className="field-prompt-log-panel">
            <header>
              <div>
                <strong>{icon("scroll-text")} Prompt log</strong>
                <span>{latestPromptLog ? `Segment ${latestPromptLog.segmentId}${latestPromptLog.fieldName ? ` - ${latestPromptLog.fieldName}` : ""}` : "No prompt log yet"}</span>
              </div>
              <div className="field-prompt-log-actions">
                <button type="button" className="icon-action" title="Refresh prompt log" onClick={() => onRefreshLogs && onRefreshLogs({ silent: true })}>
                  {icon("refresh-cw")}
                </button>
                <button type="button" className="icon-action" title="Close prompt log" onClick={() => setPromptLogOpen(false)}>
                  {icon("x")}
                </button>
              </div>
            </header>
            {latestPromptLog ? (
              <>
                <div className="field-prompt-log-meta">
                  <span>{latestPromptLog.model || "model"}</span>
                  <span>{latestPromptLog.status || "pending"}</span>
                  <span>{latestPromptLog.kind || "analysis"}</span>
                  <span>{sentLogImages.length} image{sentLogImages.length === 1 ? "" : "s"}</span>
                  <span>{latestPromptLog.timestamp ? new Date(latestPromptLog.timestamp).toLocaleString() : ""}</span>
                </div>
                <section>
                  <strong>Sent images</strong>
                  <div className="field-prompt-log-images">
                    {sentLogImages.length ? sentLogImages.map((image, index) => {
                      const file = String(image && image.file || "");
                      const imageSrc = image && image.dataUrl
                        ? image.dataUrl
                        : selectedSegment && selectedSegment.id && file === AERIAL_OVERLAY_IMAGE_FILE
                          ? `/api/segments/${Number(selectedSegment.id)}/aerial-overlay?zoom=19`
                          : selectedSegment && selectedSegment.id && file
                          ? `/api/segments/${Number(selectedSegment.id)}/images/${encodeURIComponent(file)}/thumb`
                          : "";
                      return (
                        <figure key={`${file || "image"}-${index}`}>
                          {imageSrc ? <img src={imageSrc} alt="" loading="lazy" decoding="async" /> : <span>{icon("image")}</span>}
                          <figcaption>
                            <strong>{index + 1}</strong>
                            <small title={file}>{file || "Unnamed image"}</small>
                            {image && image.pasted && <em>Pasted</em>}
                          </figcaption>
                        </figure>
                      );
                    }) : (
                      <div className="field-prompt-log-empty inline">No sent images recorded for this log.</div>
                    )}
                  </div>
                </section>
                <section>
                  <strong>Prompt</strong>
                  <pre>{latestPromptLog.prompt || "No prompt text recorded."}</pre>
                </section>
                <section>
                  <strong>Details</strong>
                  <pre>{promptLogDetail || "No response details recorded."}</pre>
                </section>
              </>
            ) : (
              <div className="field-prompt-log-empty">No prompt log for this field yet. Send a chat message or refresh logs after an AI request.</div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function IrapIconTile({
  item,
  selectedSegment,
  onOpenEvidence,
  onOpenSourceImage = null,
  editMode = false,
  canEdit = false,
  onSaveField = null,
  onAddEvidence = null,
  onOpenAnnotationEvidence = null,
  editDraftValue = "",
  editSaving = false,
  onEditDraftValueChange = null,
  showOldValues = false,
  fieldValueFilterEnabled = false,
  fieldValueFilterSelected = false,
  fieldFocusSelected = false,
  onToggleFieldValueFilter = null,
  hasFieldPrompt = false,
  isFieldRunning = false,
  onOpenFieldPrompt = null,
  onOpenFieldDiscussion = null,
  onFieldContextMenu = null
}) {
  const [missingIcon, setMissingIcon] = useState(false);
  const [iconStyle, setIconStyle] = useState(null);
  const [draftValue, setDraftValue] = useState(item.value ?? "");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const { field, value, decodedValue, iconUrl } = item;
  const oldValue = item.oldValue || specOldFieldValue(null, field);
  const comparison = item.comparison || { status: "", label: "" };
  const linkedEvidence = Array.isArray(item.evidenceItems) ? item.evidenceItems : [];
  const fieldAnnotationEvidence = Array.isArray(item.annotationEvidenceItems) ? item.annotationEvidenceItems : [];
  const sourceImages = fieldSourceImages(item);
  const hiddenEvidenceCount =
    Math.max(0, linkedEvidence.length - 2) +
    Math.max(0, fieldAnnotationEvidence.length - 2);
  const displayValue = hasDisplayValue(value)
    ? specDisplayValue(field, value, decodedValue)
    : "Blank";
  const fieldOptions = Array.isArray(field.options) ? field.options : [];
  const usesExternalDraft = editMode && typeof onEditDraftValueChange === "function";
  const effectiveDraftValue = usesExternalDraft ? editDraftValue : draftValue;
  const effectiveSaving = usesExternalDraft ? editSaving : saving;
  const draftValueText = effectiveDraftValue === null || effectiveDraftValue === undefined ? "" : String(effectiveDraftValue);
  const confidencePercent = fieldConfidencePercent(item.confidence);
  const confidenceTone = fieldConfidenceTone(confidencePercent);
  const hasEvidenceRow = editMode || linkedEvidence.length > 0 || fieldAnnotationEvidence.length > 0;
  const canOpenSourceImage = sourceImages.length > 0 && typeof onOpenSourceImage === "function";
  const canOpenDiscussion = typeof onOpenFieldDiscussion === "function";
  const canOpenFieldPrompt = typeof onOpenFieldPrompt === "function";

  useEffect(() => {
    setMissingIcon(false);
    setIconStyle(null);
  }, [iconUrl]);

  useEffect(() => {
    if (!saving && !usesExternalDraft) {
      setDraftValue(value ?? "");
    }
  }, [value, saving, usesExternalDraft]);

  const commitFieldValue = async (nextValue) => {
    const previousValue = draftValue;
    setDraftValue(nextValue);
    if (!canEdit || typeof onSaveField !== "function") {
      return;
    }
    setSaving(true);
    setEditError("");
    try {
      await onSaveField(field.field, nextValue, {
        previousValue: value,
        previousDecodedValue: decodedValue
      });
    } catch (error) {
      setDraftValue(previousValue);
      setEditError(error.message || "Could not save value.");
    } finally {
      setSaving(false);
    }
  };
  const updateDraftFieldValue = (nextValue) => {
    if (usesExternalDraft) {
      onEditDraftValueChange(field.field, nextValue);
      return;
    }
    setDraftValue(nextValue);
  };

  const normalizeIconPosition = (event) => {
    const image = event.currentTarget;
    if (!image.naturalWidth || !image.naturalHeight) {
      return;
    }

    try {
      const sampleSize = 160;
      const canvas = document.createElement("canvas");
      canvas.width = sampleSize;
      canvas.height = sampleSize;
      const context = canvas.getContext("2d");
      context.drawImage(image, 0, 0, sampleSize, sampleSize);
      const pixels = context.getImageData(0, 0, sampleSize, sampleSize).data;
      let minX = sampleSize;
      let minY = sampleSize;
      let maxX = -1;
      let maxY = -1;
      let count = 0;

      for (let y = 0; y < sampleSize; y += 1) {
        for (let x = 0; x < sampleSize; x += 1) {
          const alpha = pixels[(y * sampleSize + x) * 4 + 3];
          if (alpha > 12) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            count += 1;
          }
        }
      }

      if (!count) {
        return;
      }

      const centerX = (minX + maxX + 1) / 2 / sampleSize;
      const centerY = (minY + maxY + 1) / 2 / sampleSize;
      const bboxWidth = (maxX - minX + 1) / sampleSize;
      const bboxHeight = (maxY - minY + 1) / sampleSize;
      const shiftX = (0.5 - centerX) * 100;
      const shiftY = (0.5 - centerY) * 100;
      const coverage = Math.max(bboxWidth, bboxHeight, 0.01);
      const scale = Math.min(1.18, Math.max(1, 0.82 / coverage));

      setIconStyle({
        "--icon-shift-x": `${shiftX.toFixed(2)}%`,
        "--icon-shift-y": `${shiftY.toFixed(2)}%`,
        "--icon-scale": scale.toFixed(3)
      });
    } catch (_error) {
      setIconStyle(null);
    }
  };

  return (
    <article
      className={`segment-icon-card comparison-${comparison.status || "none"} ${missingIcon ? "missing" : ""} ${canOpenSourceImage ? "has-source-image" : ""} ${fieldValueFilterEnabled ? "is-value-filterable" : ""} ${fieldValueFilterSelected ? "is-value-filter-selected" : ""} ${fieldFocusSelected ? "is-focused-field" : ""} ${hasFieldPrompt ? "has-field-prompt" : ""} ${canOpenDiscussion ? "has-discuss" : ""} ${isFieldRunning ? "is-field-running" : ""}`}
      title={canOpenSourceImage ? `${fieldConfidenceTitle(item)}\nClick card to open source image in pano viewer.${onFieldContextMenu ? "\nRight-click for field AI prompt tools." : ""}` : (onFieldContextMenu ? "Right-click for field AI prompt tools." : undefined)}
      tabIndex={canOpenSourceImage ? 0 : undefined}
      onContextMenu={onFieldContextMenu ? (event) => onFieldContextMenu(item, event) : undefined}
      onClick={(event) => {
        if (!canOpenSourceImage) {
          return;
        }
        if (event.target.closest("button, input, select, textarea, label, a")) {
          return;
        }
        onOpenSourceImage(item);
      }}
      onKeyDown={(event) => {
        if (!canOpenSourceImage || !["Enter", " "].includes(event.key)) {
          return;
        }
        event.preventDefault();
        onOpenSourceImage(item);
      }}
    >
      {canOpenDiscussion && (
        <button
          type="button"
          className="segment-icon-discuss"
          title={`Discuss AI result for ${field.caption || field.field}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpenFieldDiscussion(item);
          }}
        >
          {icon("messages-square")}
        </button>
      )}
      {confidencePercent !== null && (
        <span
          className={`segment-icon-confidence confidence-${confidenceTone}`}
          title={fieldConfidenceTitle(item)}
        >
          {confidencePercent}%
        </span>
      )}
      {isFieldRunning && (
        <span className="segment-icon-running" title="Updating this field with AI">
          {icon("loader-circle")}
          Updating
        </span>
      )}
      {hasFieldPrompt && canOpenFieldPrompt ? (
        <button
          type="button"
          className="segment-icon-field-prompt"
          title="Open this field prompt"
          onClick={(event) => {
            event.stopPropagation();
            onOpenFieldPrompt(item);
          }}
        >
          {icon("message-square-text")}
        </button>
      ) : hasFieldPrompt && (
        <span className="segment-icon-field-prompt" title="This field has a special AI prompt">
          {icon("message-square-text")}
        </span>
      )}
      {fieldValueFilterEnabled && (
        <label
          className="segment-icon-value-filter-check"
          title={`Filter segments by ${field.caption || field.field}: ${displayValue}`}
        >
          <input
            type="checkbox"
            checked={fieldValueFilterSelected}
            onChange={() => onToggleFieldValueFilter && onToggleFieldValueFilter(item)}
          />
          <span>{icon("filter")}</span>
        </label>
      )}
      <div className="segment-icon-frame">
        {iconUrl && !missingIcon ? (
          <img
            alt={field.caption || field.field}
            src={iconUrl}
            style={iconStyle || undefined}
            onLoad={normalizeIconPosition}
            onError={() => setMissingIcon(true)}
          />
        ) : (
          <span>{normalizeIconFileValue(value) || "?"}</span>
        )}
      </div>
      {hasEvidenceRow && (
        <div className="segment-icon-evidence-actions" aria-label={`${field.caption || field.field} evidence`}>
          {editMode && (
            <button
              className="segment-icon-evidence-add"
              type="button"
              disabled={!canEdit}
              onClick={() => onAddEvidence && onAddEvidence(item)}
              title={`Add panorama evidence for ${field.caption || "this field"}`}
              aria-label={`Add panorama evidence for ${field.caption || "this field"}`}
            >
              {icon("plus")}
            </button>
          )}
          {linkedEvidence.slice(0, 2).map((evidenceItem, index) => (
            <button
              key={evidenceItem.id || evidenceItem.markedCropUrl || evidenceItem.cropUrl || index}
              type="button"
              className="segment-icon-evidence-thumb"
              onClick={() => onOpenEvidence && onOpenEvidence(evidenceItem)}
              title={`Open marked evidence: ${evidenceItemLabel(evidenceItem)}`}
            >
              {evidenceMarkedImageUrl(evidenceItem, selectedSegment) ? (
                <img src={evidenceMarkedImageUrl(evidenceItem, selectedSegment)} alt="" loading="lazy" decoding="async" />
              ) : (
                icon("scan-search")
              )}
              <span>{index + 1}</span>
            </button>
          ))}
          {fieldAnnotationEvidence.slice(0, 2).map((annotation, index) => (
            <button
              key={annotation.id}
              type="button"
              className="segment-icon-evidence-thumb is-pano-annotation"
              onClick={() => onOpenAnnotationEvidence && onOpenAnnotationEvidence(annotation)}
              title={`Open field evidence: ${annotation.label}`}
            >
              {panoAnnotationThumbUrl(annotation) ? (
                <img src={panoAnnotationThumbUrl(annotation)} alt="" loading="lazy" decoding="async" />
              ) : (
                icon("map-pin")
              )}
              <span>{panoAnnotationShapeLabel(panoAnnotationShape(annotation).kind).slice(0, 1)}</span>
            </button>
          ))}
          {hiddenEvidenceCount > 0 && <small>+{hiddenEvidenceCount}</small>}
        </div>
      )}
      <div>
        <strong>{field.caption || field.field}</strong>
        {editMode ? (
          <label className="segment-icon-edit-control">
            <span className="sr-only">{`Edit ${field.caption || field.field}`}</span>
            {fieldOptions.length ? (
              <select
                value={draftValueText}
                disabled={!canEdit || effectiveSaving}
                onChange={(event) => {
                  if (usesExternalDraft) {
                    updateDraftFieldValue(event.target.value);
                  } else {
                    commitFieldValue(event.target.value);
                  }
                }}
              >
                <option value="">Blank</option>
                {fieldOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.id}: {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={draftValueText}
                disabled={!canEdit || effectiveSaving}
                onChange={(event) => updateDraftFieldValue(event.target.value)}
                onBlur={() => {
                  if (!usesExternalDraft) {
                    commitFieldValue(draftValue);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (!usesExternalDraft) {
                      commitFieldValue(draftValue);
                    }
                  }
                }}
              />
            )}
            {(effectiveSaving || editError) && <small>{effectiveSaving ? "Saving..." : editError}</small>}
          </label>
        ) : (
          <div className="segment-icon-value-stack">
            <span className="segment-icon-value-line">
              <b>{displayValue}</b>
            </span>
            {showOldValues && (
              <span className="segment-icon-value-line is-old-value" title={oldValue.propertyName || "Old value"}>
                <em>Old</em>
                <b>{oldValue.display}</b>
              </span>
            )}
          </div>
        )}
        {editMode && showOldValues && (
          <span className="segment-icon-edit-old-value" title={oldValue.propertyName || "Old value"}>
            Old: {oldValue.display}
          </span>
        )}
      </div>
    </article>
  );
}

function dashboardHasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function dashboardNormalizeValue(value) {
  if (!dashboardHasValue(value)) {
    return "";
  }
  return typeof value === "object" ? JSON.stringify(value) : String(value).trim();
}

function dashboardPct(value, total) {
  const pct = total ? (Number(value || 0) / total) * 100 : 0;
  return Number.isFinite(pct) ? pct.toFixed(1) : "0.0";
}

function dashboardAnalysisForSegment(segment, analysisResults = {}) {
  return segment ? analysisResults[segment.id] || analysisResults[String(segment.id)] || null : null;
}

const DASHBOARD_EXTERNAL_DATA_FIELDS = new Set(["F40", "F41", "F42", "F43", "F44", "F45", "F62"]);

function dashboardFieldRequiresExternalData(fieldCode) {
  return DASHBOARD_EXTERNAL_DATA_FIELDS.has(String(fieldCode || "").trim().toUpperCase());
}

function dashboardEmptyStats(total = 0) {
  return {
    total,
    same: 0,
    changed: 0,
    missing: 0,
    external: 0,
    review: 0
  };
}

function dashboardStatsWithRates(stats) {
  const total = Number(stats.total || 0);
  const comparableTotal = Math.max(0, total - Number(stats.external || 0));
  return {
    ...stats,
    comparableTotal,
    samePct: dashboardPct(stats.same, comparableTotal),
    sameOverallPct: dashboardPct(stats.same, total),
    changedPct: dashboardPct(stats.changed, comparableTotal),
    changedOverallPct: dashboardPct(stats.changed, total),
    missingPct: dashboardPct(stats.missing, comparableTotal),
    missingOverallPct: dashboardPct(stats.missing, total),
    externalPct: dashboardPct(stats.external, total),
    reviewPct: dashboardPct(stats.review, comparableTotal)
  };
}

function addDashboardStats(target, source) {
  target.total += Number(source.total || 0);
  target.same += Number(source.same || 0);
  target.changed += Number(source.changed || 0);
  target.missing += Number(source.missing || 0);
  target.external += Number(source.external || 0);
  target.review += Number(source.review || 0);
}

const DASHBOARD_SORT_OPTIONS = [
  { metric: "same", label: "Same" },
  { metric: "changed", label: "Changed" },
  { metric: "missing", label: "Missing" }
];

function sortDashboardFields(rows, sortMetric, sortDirection) {
  const metric = ["same", "changed", "missing"].includes(sortMetric) ? sortMetric : "changed";
  const direction = sortDirection === "asc" ? 1 : -1;
  const pctKey = `${metric}Pct`;
  return rows.slice().sort((a, b) => {
    const pctDiff = Number(a.stats[pctKey] || 0) - Number(b.stats[pctKey] || 0);
    if (pctDiff) {
      return pctDiff * direction;
    }
    const countDiff = Number(a.stats[metric] || 0) - Number(b.stats[metric] || 0);
    if (countDiff) {
      return countDiff * direction;
    }
    return a.order - b.order;
  });
}

function dashboardSegmentProps(segment) {
  return segment && segment.properties
    ? segment.properties
    : (segment && segment.feature && segment.feature.properties) || {};
}

function dashboardFieldReviewFocus(categories) {
  return categories
    .flatMap((category) => category.fields.map((field) => ({
      ...field,
      categoryLabel: category.label,
      categoryColor: category.color
    })))
    .filter((field) => Number(field.stats && field.stats.changed || 0) > 0)
    .sort((left, right) => {
      const pctDiff = Number(right.stats.changedPct || 0) - Number(left.stats.changedPct || 0);
      if (pctDiff) {
        return pctDiff;
      }
      return Number(right.stats.changed || 0) - Number(left.stats.changed || 0);
    })
    .slice(0, 5);
}

function dashboardInsightLines(totals, rows) {
  const total = Number(totals && totals.total || 0);
  if (!total) {
    return ["No AI comparison data is available for the current dashboard scope."];
  }

  const samePct = totals.samePct || "0.0";
  const changedPct = totals.changedPct || "0.0";
  const missingPct = totals.missingPct || "0.0";
  const externalPct = totals.externalPct || "0.0";
  const missingPctValue = Number(missingPct || 0);
  const externalPctValue = Number(externalPct || 0);
  const changedCount = Number(totals.changed || 0);
  const lines = [
    `${samePct}% of comparable field values match previous values, while ${changedPct}% need review across ${changedCount} changed field values.`
  ];

  const top = rows && rows[0];
  if (top) {
    lines.push(`${top.caption} is the strongest review driver with ${top.stats.changed} changed values (${top.stats.changedPct}%).`);
  }

  const nextRows = (rows || []).slice(1, 4);
  if (nextRows.length) {
    lines.push(`Next priorities are ${nextRows.map((row) => `${row.caption} (${row.stats.changedPct}%)`).join(", ")}.`);
  }

  if (missingPctValue > 0) {
    lines.push(`${missingPct}% of comparable values are missing from AI output, so those fields should be checked before approval.`);
  }

  if (externalPctValue > 0) {
    lines.push(`${externalPct}% of all field records require external evidence and are separated from AI-missing values.`);
  }

  return lines;
}

function formatAuditDate(value) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
}

function auditEventLabel(value) {
  const eventType = String(value || "");
  const labels = {
    "auth.login": "Login",
    "auth.logout": "Logout",
    "auth.login_failed": "Login failed",
    segment_click: "Segment click",
    segment_view: "Segment view",
    system_chat: "System chat",
    system_chat_failed: "Chat failed",
    panorama_view: "Panorama view"
  };
  return labels[eventType] || eventType.replace(/[_.-]+/g, " ");
}

function auditUserLabel(row) {
  return row.UserName || row.userLabel || row.Email || row.UserId || "Unknown user";
}

function auditUserFilterKey(row) {
  return String(auditUserLabel(row) || "").trim().toLowerCase();
}

function normalizeAuditFilterText(value) {
  return String(value || "").trim().toLowerCase();
}

function auditIncludesQuery(values, query) {
  const normalizedQuery = normalizeAuditFilterText(query);
  if (!normalizedQuery) {
    return true;
  }
  return values.some((value) => normalizeAuditFilterText(value).includes(normalizedQuery));
}

function auditUserActivityCount(row, activityType) {
  if (activityType === "logins") {
    return Number(row.logins || row.Logins || 0);
  }
  if (activityType === "clicks") {
    return Number(row.segmentClicks || row.SegmentClicks || 0);
  }
  if (activityType === "views") {
    return Number(row.segmentViews || row.SegmentViews || 0);
  }
  if (activityType === "chats") {
    return Number(row.systemChats || row.SystemChats || 0);
  }
  if (activityType === "panos") {
    return Number(row.panoramaViews || row.PanoramaViews || 0);
  }
  return Number(row.events || row.Events || 0);
}

function auditClientLabel(row) {
  const browser = [row.BrowserName, row.BrowserVersion].filter(Boolean).join(" ");
  const os = [row.OsName, row.OsVersion].filter(Boolean).join(" ");
  return [browser, os, row.DeviceType].filter(Boolean).join(" / ") || "-";
}

function auditDetails(row) {
  const source = row && row.DetailsJson;
  if (!source) {
    return {};
  }
  if (typeof source === "object") {
    return source;
  }
  try {
    return JSON.parse(source);
  } catch (_error) {
    return {};
  }
}

function auditShortText(value, maxLength = 130) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function auditChatSummary(row) {
  const details = auditDetails(row);
  const eventType = String(row && row.EventType || "");
  if (!eventType.includes("chat") && !details.latestUserMessage && !details.assistantReply) {
    return "-";
  }
  const messages = Array.isArray(details.messages) ? details.messages : [];
  const latestFromMessages = [...messages].reverse().find((message) => message && message.role === "user" && message.content);
  const userText = details.latestUserMessage || (latestFromMessages && latestFromMessages.content) || "";
  const assistantText = details.assistantReply || details.error || "";
  const parts = [];
  if (details.chatType) {
    parts.push(String(details.chatType).replace(/[_.-]+/g, " "));
  }
  if (userText) {
    parts.push(`User: ${auditShortText(userText, 100)}`);
  }
  if (assistantText) {
    parts.push(`${eventType === "system_chat_failed" ? "Error" : "AI"}: ${auditShortText(assistantText, 100)}`);
  }
  return parts.join(" | ") || "-";
}

function UserActivityDashboardView() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [days, setDays] = useState("14");
  const [selectedUserKey, setSelectedUserKey] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userActivityFilter, setUserActivityFilter] = useState("all");
  const [sessionQuery, setSessionQuery] = useState("");
  const [sessionBrowserFilter, setSessionBrowserFilter] = useState("all");
  const [eventQuery, setEventQuery] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");

  const loadDashboard = (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) {
      setLoading(true);
    }
    setError("");
    apiFetch(`/api/audit/dashboard?days=${encodeURIComponent(days)}&limit=250`)
      .then((data) => setDashboard(data))
      .catch((loadError) => setError(loadError.message || "Could not load user activity from SQL."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboard();
  }, [days]);

  const totals = dashboard && dashboard.totals ? dashboard.totals : {};
  const events = Array.isArray(dashboard && dashboard.events) ? dashboard.events : [];
  const users = Array.isArray(dashboard && dashboard.users) ? dashboard.users : [];
  const sessions = Array.isArray(dashboard && dashboard.sessions) ? dashboard.sessions : [];
  const selectedUser = selectedUserKey ? users.find((row) => auditUserFilterKey(row) === selectedUserKey) : null;
  const selectedUserLabel = selectedUser ? auditUserLabel(selectedUser) : "";
  const hasSessionFilters = Boolean(sessionQuery || sessionBrowserFilter !== "all");
  const hasEventFilters = Boolean(eventQuery || eventTypeFilter !== "all");
  const browserOptions = useMemo(() => {
    return Array.from(new Set(
      sessions
        .map((row) => String(row.BrowserName || "").trim())
        .filter(Boolean)
    )).sort((a, b) => a.localeCompare(b));
  }, [sessions]);
  const eventTypeOptions = useMemo(() => {
    return Array.from(new Set(events.map((row) => String(row.EventType || "").trim()).filter(Boolean)))
      .sort((a, b) => auditEventLabel(a).localeCompare(auditEventLabel(b)));
  }, [events]);
  const filteredUsers = useMemo(() => {
    return users.filter((row) => {
      const matchesActivity = userActivityFilter === "all" || auditUserActivityCount(row, userActivityFilter) > 0;
      return matchesActivity && auditIncludesQuery([
        auditUserLabel(row),
        row.Email || row.email,
        row.UserId || row.userId
      ], userQuery);
    });
  }, [users, userQuery, userActivityFilter]);
  const filteredSessions = useMemo(() => {
    return sessions.filter((row) => {
      if (selectedUserKey && auditUserFilterKey(row) !== selectedUserKey) {
        return false;
      }
      if (sessionBrowserFilter !== "all" && String(row.BrowserName || "") !== sessionBrowserFilter) {
        return false;
      }
      return auditIncludesQuery([
        auditUserLabel(row),
        row.Email,
        row.UserId,
        row.ClientSessionId,
        row.IpAddress,
        row.BrowserName,
        row.BrowserVersion,
        row.OsName,
        row.OsVersion,
        row.DeviceType,
        auditClientLabel(row),
        formatAuditDate(row.LastSeenAt)
      ], sessionQuery);
    });
  }, [sessions, selectedUserKey, sessionQuery, sessionBrowserFilter]);
  const filteredEvents = useMemo(() => {
    return events.filter((row) => {
      if (selectedUserKey && auditUserFilterKey(row) !== selectedUserKey) {
        return false;
      }
      if (eventTypeFilter !== "all" && String(row.EventType || "") !== eventTypeFilter) {
        return false;
      }
      return auditIncludesQuery([
        formatAuditDate(row.CreatedAt),
        auditUserLabel(row),
        row.Email,
        row.UserId,
        row.EventType,
        auditEventLabel(row.EventType),
        row.SegmentId,
        row.RoadName,
        row.Distance,
        row.ImageFile,
        row.IpAddress,
        row.ClientSessionId,
        auditClientLabel(row),
        auditChatSummary(row)
      ], eventQuery);
    });
  }, [events, selectedUserKey, eventQuery, eventTypeFilter]);

  useEffect(() => {
    if (dashboard && selectedUserKey && !users.some((row) => auditUserFilterKey(row) === selectedUserKey)) {
      setSelectedUserKey("");
    }
  }, [dashboard, selectedUserKey, users]);

  return (
    <main className="workspace-dashboard activity-dashboard">
      <section className="dashboard-hero activity-dashboard-hero">
        <div>
          <h2>{icon("activity")} User Logs</h2>
          <p>SQL audit dashboard for login, logout, segment views, AI chats, panorama views, IP address, browser, operating system, and session activity.</p>
        </div>
        <div className="activity-dashboard-actions">
          <label>
            <span>Range</span>
            <select value={days} onChange={(event) => setDays(event.target.value)}>
              <option value="1">1 day</option>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
            </select>
          </label>
          <button type="button" className="secondary-button compact" onClick={() => loadDashboard()} disabled={loading}>
            {icon(loading ? "loader-circle" : "refresh-cw")}
            <span>{loading ? "Loading" : "Refresh"}</span>
          </button>
        </div>
      </section>

      {error && <div className="dashboard-empty activity-dashboard-error">{error}</div>}

      <section className="dashboard-kpis">
        <DashboardKpi iconName="database" label="SQL events" value={Number(totals.totalEvents || 0)} detail={`${dashboard && dashboard.storage || "sql"} storage`} tone="active" />
        <DashboardKpi iconName="users" label="Users" value={Number(totals.uniqueUsers || 0)} detail="Unique users" />
        <DashboardKpi iconName="log-in" label="Logins" value={Number(totals.logins || 0)} detail={`${Number(totals.logouts || 0)} logouts`} tone="complete" />
        <DashboardKpi iconName="mouse-pointer-click" label="Segment clicks" value={Number(totals.segmentClicks || 0)} detail="Selected segments" tone="changed" />
        <DashboardKpi iconName="eye" label="Segment views" value={Number(totals.segmentViews || 0)} detail="Loaded segment detail" tone="active" />
        <DashboardKpi iconName="messages-square" label="AI chats" value={Number(totals.systemChats || 0)} detail={`${Number(totals.failedChats || 0)} failed`} tone="external" />
        <DashboardKpi iconName="image" label="Panorama views" value={Number(totals.panoramaViews || 0)} detail="Viewed pano images" tone="waiting" />
      </section>

      <section className="activity-dashboard-grid">
        <section className="dashboard-panel activity-dashboard-panel">
          <header>
            <div>
              <h3>Recent users</h3>
              <span>{filteredUsers.length} of {users.length} users in range</span>
            </div>
            {selectedUserLabel && (
              <button type="button" className="activity-filter-clear" onClick={() => setSelectedUserKey("")} title="Clear user filter">
                {icon("x")}
                <span>Clear</span>
              </button>
            )}
          </header>
          <ActivityFilterBar
            hasActive={Boolean(userQuery || userActivityFilter !== "all")}
            onClear={() => {
              setUserQuery("");
              setUserActivityFilter("all");
            }}
          >
            <label className="activity-filter-control is-search">
              {icon("search")}
              <input
                type="search"
                value={userQuery}
                onChange={(event) => setUserQuery(event.target.value)}
                placeholder="Filter users"
              />
            </label>
            <label className="activity-filter-control">
              <span>Activity</span>
              <select value={userActivityFilter} onChange={(event) => setUserActivityFilter(event.target.value)}>
                <option value="all">All activity</option>
                <option value="logins">Logins</option>
                <option value="clicks">Segment clicks</option>
                <option value="views">Segment views</option>
                <option value="chats">AI chats</option>
                <option value="panos">Panorama views</option>
              </select>
            </label>
          </ActivityFilterBar>
          <ActivityMiniTable
            emptyText="No users match the current filters."
            columns={["User", "Events", "Views", "Chats", "Panos", "Last seen"]}
            rows={filteredUsers.map((row, index) => {
              const userKey = auditUserFilterKey(row);
              const userLabel = auditUserLabel(row);
              return {
                key: `${userKey || "user"}-${index}`,
                cells: [
                  userLabel,
                  Number(row.events || row.Events || 0),
                  Number(row.segmentViews || row.SegmentViews || 0),
                  Number(row.systemChats || row.SystemChats || 0),
                  Number(row.panoramaViews || row.PanoramaViews || 0),
                  formatAuditDate(row.lastSeenAt || row.LastSeenAt)
                ],
                isSelected: userKey && userKey === selectedUserKey,
                onClick: () => setSelectedUserKey((current) => current === userKey ? "" : userKey),
                title: `Filter activity for ${userLabel}`
              };
            })}
          />
        </section>

        <section className="dashboard-panel activity-dashboard-panel">
          <header>
            <div>
              <h3>Recent sessions</h3>
              <span>
                {selectedUserLabel
                  ? `${filteredSessions.length} of ${sessions.length} sessions for ${selectedUserLabel}`
                  : hasSessionFilters
                  ? `${filteredSessions.length} of ${sessions.length} sessions`
                  : `${sessions.length} sessions in range`}
              </span>
            </div>
          </header>
          <ActivityFilterBar
            hasActive={Boolean(sessionQuery || sessionBrowserFilter !== "all")}
            onClear={() => {
              setSessionQuery("");
              setSessionBrowserFilter("all");
            }}
          >
            {selectedUserLabel && (
              <button type="button" className="activity-filter-chip" onClick={() => setSelectedUserKey("")} title="Clear selected user">
                <span>User: {selectedUserLabel}</span>
                {icon("x")}
              </button>
            )}
            <label className="activity-filter-control is-search">
              {icon("search")}
              <input
                type="search"
                value={sessionQuery}
                onChange={(event) => setSessionQuery(event.target.value)}
                placeholder="Filter sessions"
              />
            </label>
            <label className="activity-filter-control">
              <span>Browser</span>
              <select value={sessionBrowserFilter} onChange={(event) => setSessionBrowserFilter(event.target.value)}>
                <option value="all">All browsers</option>
                {browserOptions.map((browserName) => <option key={browserName} value={browserName}>{browserName}</option>)}
              </select>
            </label>
          </ActivityFilterBar>
          <ActivityMiniTable
            emptyText="No sessions match the current filters."
            columns={["User", "IP", "Client", "Last seen"]}
            rows={filteredSessions.map((row) => [
              auditUserLabel(row),
              row.IpAddress || "-",
              auditClientLabel(row),
              formatAuditDate(row.LastSeenAt)
            ])}
          />
        </section>
      </section>

      <section className="dashboard-panel activity-dashboard-panel activity-dashboard-events">
        <header>
          <div>
            <h3>Event timeline</h3>
            <span>
              {selectedUserLabel
                ? `${filteredEvents.length} of ${events.length} recent SQL events for ${selectedUserLabel}`
                : hasEventFilters
                ? `${filteredEvents.length} of ${events.length} recent SQL events`
                : `${events.length} recent SQL events`}
            </span>
          </div>
        </header>
        <ActivityFilterBar
          hasActive={Boolean(eventQuery || eventTypeFilter !== "all")}
          onClear={() => {
            setEventQuery("");
            setEventTypeFilter("all");
          }}
        >
          {selectedUserLabel && (
            <button type="button" className="activity-filter-chip" onClick={() => setSelectedUserKey("")} title="Clear selected user">
              <span>User: {selectedUserLabel}</span>
              {icon("x")}
            </button>
          )}
          <label className="activity-filter-control">
            <span>Event</span>
            <select value={eventTypeFilter} onChange={(event) => setEventTypeFilter(event.target.value)}>
              <option value="all">All events</option>
              {eventTypeOptions.map((eventType) => <option key={eventType} value={eventType}>{auditEventLabel(eventType)}</option>)}
            </select>
          </label>
          <label className="activity-filter-control is-search">
            {icon("search")}
            <input
              type="search"
              value={eventQuery}
              onChange={(event) => setEventQuery(event.target.value)}
              placeholder="Filter timeline"
            />
          </label>
        </ActivityFilterBar>
        <div className="activity-table-wrap">
          <table className="activity-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Event</th>
                <th>Chat</th>
                <th>Segment</th>
                <th>Panorama</th>
                <th>IP</th>
                <th>Client</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.length ? filteredEvents.map((row, index) => (
                <tr key={row.EventId || `${row.CreatedAt}-${index}`}>
                  <td>{formatAuditDate(row.CreatedAt)}</td>
                  <td>{auditUserLabel(row)}</td>
                  <td>{auditEventLabel(row.EventType)}</td>
                  <td className="activity-chat-cell" title={auditChatSummary(row)}>{auditChatSummary(row)}</td>
                  <td>{row.SegmentId ? `${row.SegmentId}${row.RoadName ? ` - ${row.RoadName}` : ""}` : "-"}</td>
                  <td>{row.ImageFile || "-"}</td>
                  <td>{row.IpAddress || "-"}</td>
                  <td>{auditClientLabel(row)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8">{selectedUserLabel ? `No SQL activity events found for ${selectedUserLabel}.` : "No SQL activity events found."}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function ActivityFilterBar({ children, hasActive = false, onClear = null }) {
  return (
    <div className="activity-filter-bar">
      {children}
      {hasActive && (
        <button type="button" className="activity-filter-reset" onClick={onClear} title="Clear section filters">
          {icon("x")}
          <span>Clear filters</span>
        </button>
      )}
    </div>
  );
}

function ActivityMiniTable({ columns = [], rows = [], emptyText = "No rows." }) {
  const normalizedRows = rows.map((row) => Array.isArray(row) ? { cells: row } : {
    ...row,
    cells: Array.isArray(row && row.cells) ? row.cells : []
  });

  return (
    <div className="activity-table-wrap compact">
      <table className="activity-table compact">
        <thead>
          <tr>
            {columns.map((column) => <th key={column}>{column}</th>)}
          </tr>
        </thead>
        <tbody>
          {normalizedRows.length ? normalizedRows.map((row, rowIndex) => (
            <tr
              key={row.key || rowIndex}
              className={[row.onClick ? "is-clickable" : "", row.isSelected ? "is-selected" : ""].filter(Boolean).join(" ")}
              onClick={row.onClick || undefined}
              onKeyDown={row.onClick ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  row.onClick(event);
                }
              } : undefined}
              role={row.onClick ? "button" : undefined}
              tabIndex={row.onClick ? 0 : undefined}
              title={row.title || undefined}
            >
              {row.cells.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
            </tr>
          )) : (
            <tr>
              <td colSpan={Math.max(1, columns.length)}>{emptyText}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SettingsView({
  gisLayerOptions = null,
  gisLayerOptionsLoading = false,
  gisLayerOptionsError = "",
  gisLayerSettings = DEFAULT_GIS_LAYER_SETTINGS,
  setGisLayerSettings = null
}) {
  const normalizedOptions = normalizeGisLayerOptions(gisLayerOptions);
  const normalizedSettings = normalizeGisLayerSettings(gisLayerSettings);
  return (
    <main className="workspace-settings">
      <section className="settings-panel">
        <header>
          <div>
            <span>{icon("settings")} Settings</span>
            <h2>GIS Layers</h2>
          </div>
          <em>{normalizedOptions.database || "AssetAI"}</em>
        </header>
        <GisLayerSettingsPanel
          options={normalizedOptions}
          loading={gisLayerOptionsLoading}
          error={gisLayerOptionsError}
          settings={normalizedSettings}
          setSettings={setGisLayerSettings}
          title="GIS layers"
        />
      </section>
    </main>
  );
}

function DashboardView({ health, segments, fields, analysisResults, search, onApplySegmentFilter = null }) {
  const [dashboardSort, setDashboardSort] = useState({ metric: "changed", direction: "desc" });
  const [collapsedCategories, setCollapsedCategories] = useState(() => new Set());
  const searchMatchedSegments = useMemo(() => {
    return segments.filter((segment) => segmentMatchesSearch(segment, search));
  }, [segments, search]);
  const activeSegments = useMemo(() => {
    return searchMatchedSegments.filter((segment) => !segmentBatchExcluded(segment));
  }, [searchMatchedSegments]);
  const filteredSegments = activeSegments;
  const blockedFilteredSegmentCount = Math.max(0, searchMatchedSegments.length - activeSegments.length);

  const report = useMemo(() => {
    const analyzedSegments = filteredSegments.filter((segment) => dashboardAnalysisForSegment(segment, analysisResults));
    const groupedFields = new Map();

    fields.forEach((field, index) => {
      const groupName = field.group || "Other";
      if (!groupedFields.has(groupName)) {
        groupedFields.set(groupName, []);
      }
      groupedFields.get(groupName).push({ ...field, __order: index });
    });

    const categoryNames = CATEGORY_ORDER.filter((name) => groupedFields.has(name)).concat(
      Array.from(groupedFields.keys()).filter((name) => !CATEGORY_ORDER.includes(name))
    );

    const categories = categoryNames.map((groupName) => {
      const meta = CATEGORY_META[groupName] || {};
      const categoryStats = dashboardEmptyStats(0);
      const fieldRows = groupedFields.get(groupName).map((field) => {
        const stats = dashboardEmptyStats(analyzedSegments.length);
        const propertyName = field.geoProperty || field.field;
        const externalDataRequired = dashboardFieldRequiresExternalData(field.field);
        const distinctValues = new Set();

        analyzedSegments.forEach((segment) => {
          const props = dashboardSegmentProps(segment);
          if (dashboardHasValue(props[propertyName])) {
            distinctValues.add(dashboardNormalizeValue(props[propertyName]));
          }
          if (externalDataRequired) {
            stats.external += 1;
            return;
          }

          const analysis = dashboardAnalysisForSegment(segment, analysisResults);
          const aiField = analysis && analysis.fields ? analysis.fields[field.field] : null;
          if (!aiField || !dashboardHasValue(aiField.value)) {
            stats.missing += 1;
            return;
          }

          if (aiField.needsReview) {
            stats.review += 1;
          }

          const oldValue = props[propertyName];
          if (dashboardHasValue(aiField.value)) {
            distinctValues.add(dashboardNormalizeValue(aiField.value));
          }
          if (dashboardNormalizeValue(aiField.value) === dashboardNormalizeValue(oldValue)) {
            stats.same += 1;
          } else {
            stats.changed += 1;
          }
        });

        const ratedStats = dashboardStatsWithRates(stats);
        addDashboardStats(categoryStats, ratedStats);
        return {
          field: field.field,
          caption: field.caption || field.field,
          group: groupName,
          order: field.__order,
          propertyName,
          externalDataRequired,
          valueCount: distinctValues.size,
          stats: ratedStats
        };
      });

      return {
        groupName,
        label: meta.short || groupName,
        color: meta.color || "#64748b",
        fields: fieldRows.sort((a, b) => a.order - b.order),
        stats: dashboardStatsWithRates(categoryStats)
      };
    });

    const totals = dashboardEmptyStats(0);
    categories.forEach((category) => addDashboardStats(totals, category.stats));

    return {
      filteredSegments,
      analyzedSegments,
      categories,
      externalFieldCount: fields.filter((field) => dashboardFieldRequiresExternalData(field.field)).length,
      totals: dashboardStatsWithRates(totals)
    };
  }, [filteredSegments, fields, analysisResults]);

  const categoryKey = report.categories.map((category) => category.groupName).join("|");
  useEffect(() => {
    setCollapsedCategories((current) => {
      const validNames = new Set(report.categories.map((category) => category.groupName));
      const next = new Set(Array.from(current).filter((name) => validNames.has(name)));
      return next.size === current.size ? current : next;
    });
  }, [categoryKey]);

  const sortedCategories = useMemo(() => {
    return report.categories.map((category) => ({
      ...category,
      fields: sortDashboardFields(category.fields, dashboardSort.metric, dashboardSort.direction)
    }));
  }, [report.categories, dashboardSort.metric, dashboardSort.direction]);
  const reviewFocus = useMemo(() => dashboardFieldReviewFocus(report.categories), [report.categories]);

  const setSortMetric = (metric) => {
    setDashboardSort((current) => (
      current.metric === metric
        ? { ...current, direction: current.direction === "desc" ? "asc" : "desc" }
        : { metric, direction: "desc" }
    ));
  };

  const toggleSortDirection = () => {
    setDashboardSort((current) => ({
      ...current,
      direction: current.direction === "desc" ? "asc" : "desc"
    }));
  };

  const toggleCategory = (groupName) => {
    setCollapsedCategories((current) => {
      const next = new Set(current);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const collapseAllCategories = () => {
    setCollapsedCategories(new Set(report.categories.map((category) => category.groupName)));
  };

  const expandAllCategories = () => {
    setCollapsedCategories(new Set());
  };

  const applyFieldChartFilter = (row, chartPart) => {
    if (!row || !row.field || typeof onApplySegmentFilter !== "function") {
      return;
    }
    const modeMap = {
      same: "oldSame",
      changed: "oldMismatch",
      missing: "aiMissing"
    };
    const labelMap = {
      same: "AI value same as old value",
      changed: "AI value differs from old value",
      missing: "AI value missing"
    };
    const mode = modeMap[chartPart];
    if (!mode) {
      return;
    }
    onApplySegmentFilter({
      fieldCode: row.field,
      fieldKey: row.field,
      fieldCaption: row.caption,
      mode,
      label: labelMap[chartPart] || chartPart
    });
  };

  const analyzedPct = dashboardPct(report.analyzedSegments.length, filteredSegments.length);
  const fieldCount = health && health.data && health.data.fields ? health.data.fields : fields.length;
  const hasDashboardSearchFilter = String(search || "").trim().length > 0;
  const segmentScopeLabel = hasDashboardSearchFilter ? "Filtered Segments" : "All Segments";
  const segmentScopeDetail = blockedFilteredSegmentCount
    ? `${blockedFilteredSegmentCount} blocked excluded`
    : "No blocked segments";
  const analyzedScopeDetail = `${analyzedPct}% of ${hasDashboardSearchFilter ? "filtered" : "all"}`;

  return (
    <main className="workspace-dashboard">
      <section className="dashboard-hero">
        <div>
          <h2>{icon("layout-dashboard")} Dashboard</h2>
          <p>Field comparison tree for AI-processed segments in the current global search filter.</p>
        </div>
        <div className="dashboard-scope">
          <span>Click any metric to open matching segments {icon("info")}</span>
        </div>
      </section>

      <section className="dashboard-kpis">
        <DashboardKpi iconName="filter" label={segmentScopeLabel} value={filteredSegments.length} detail={segmentScopeDetail} tone="active" />
        <DashboardKpi iconName="sparkles" label="AI Processed" value={report.analyzedSegments.length} detail={analyzedScopeDetail} />
        <DashboardKpi iconName="circle-check" label="Unchanged from Previous Value" value={`${report.totals.samePct}%`} secondaryValue={`${report.totals.same} field values`} detail={`${report.totals.sameOverallPct}% incl. external`} tone="complete" />
        <DashboardKpi iconName="refresh-cw" label="Changed from Previous Value" value={`${report.totals.changedPct}%`} secondaryValue={`${report.totals.changed} field values`} detail={`${report.totals.changedOverallPct}% incl. external`} tone="changed" />
        <DashboardKpi iconName="circle-help" label="AI Value Missing" value={`${report.totals.missingPct}%`} secondaryValue={`${report.totals.missing} field values`} detail={`${report.totals.missingOverallPct}% incl. external`} tone={report.totals.missing ? "waiting" : "complete"} />
      </section>

      <section className="dashboard-content-grid">
        <section className="dashboard-panel control-dashboard-panel">
          <header>
            <div>
              <h3>Category and Field Tree</h3>
              <span>{fieldCount} iRAP fields - {report.externalFieldCount} require external data and are counted separately from missing</span>
            </div>
            <div className="control-dashboard-toolbar">
              <div className="control-dashboard-sort" aria-label="Sort fields">
                <span>Sort</span>
                {DASHBOARD_SORT_OPTIONS.map((option) => (
                  <button
                    key={option.metric}
                    type="button"
                    className={dashboardSort.metric === option.metric ? "active" : ""}
                    onClick={() => setSortMetric(option.metric)}
                  >
                    {option.label}
                  </button>
                ))}
                <button
                  type="button"
                  className="control-dashboard-sort-direction"
                  onClick={toggleSortDirection}
                  title={dashboardSort.direction === "desc" ? "Descending" : "Ascending"}
                >
                  {icon(dashboardSort.direction === "desc" ? "arrow-down-wide-narrow" : "arrow-up-narrow-wide")}
                </button>
              </div>
              <div className="control-dashboard-collapse-actions">
                <button type="button" onClick={expandAllCategories}>Expand all</button>
                <button type="button" onClick={collapseAllCategories}>Collapse all</button>
              </div>
              <ControlDashboardLegend />
            </div>
          </header>
          {report.analyzedSegments.length ? (
            <ControlDashboardTree
              categories={sortedCategories}
              collapsedCategories={collapsedCategories}
              onToggleCategory={toggleCategory}
              onApplyFieldFilter={applyFieldChartFilter}
            />
          ) : (
            <div className="dashboard-empty">
              No AI-processed segments match the current search. Run AI analysis or change the global search filter.
            </div>
          )}
        </section>
        <DashboardReviewFocus
          rows={reviewFocus}
          totals={report.totals}
          onApplyFieldFilter={applyFieldChartFilter}
        />
      </section>
    </main>
  );
}

function ControlDashboardLegend() {
  return (
    <div className="control-dashboard-legend" aria-label="Stacked bar legend">
      <span><i className="same"></i> Same</span>
      <span><i className="changed"></i> Changed</span>
      <span><i className="missing"></i> Missing</span>
      <span><i className="external"></i> External</span>
    </div>
  );
}

function ControlDashboardTree({ categories, collapsedCategories, onToggleCategory, onApplyFieldFilter = null }) {
  return (
    <div className="control-tree">
      <div className="control-tree-header" aria-hidden="true">
        <span>Category / Field</span>
        <span>Status Distribution</span>
      </div>
      {categories.map((category) => {
        const collapsed = collapsedCategories && collapsedCategories.has(category.groupName);
        return (
          <section
            key={category.groupName}
            className={`control-tree-category ${collapsed ? "collapsed" : ""}`}
            style={{ "--category-color": category.color }}
          >
            <div className="control-tree-category-head">
              <button
                type="button"
                className="control-tree-toggle"
                onClick={() => onToggleCategory && onToggleCategory(category.groupName)}
                aria-expanded={!collapsed}
                title={collapsed ? "Expand category" : "Collapse category"}
              >
                {icon(collapsed ? "chevron-right" : "chevron-down")}
              </button>
              <div className="control-tree-node-label">
                <span className="control-tree-dot"></span>
                <div>
                  <strong>{category.label}</strong>
                  <small>{category.fields.length} fields</small>
                </div>
              </div>
              <ControlDashboardStackedBar stats={category.stats} compact />
            </div>
            {!collapsed && (
              <div className="control-tree-fields">
                {category.fields.map((row) => (
                  <article key={row.field} className="control-tree-field-row">
                    <div className="control-tree-node-label">
                      <span className="control-tree-field-code">{row.field}</span>
                      <div>
                        <strong>{row.caption}</strong>
                        <small>{row.propertyName}</small>
                      </div>
                    </div>
                    <ControlDashboardStackedBar
                      stats={row.stats}
                      onSelectPart={(part) => onApplyFieldFilter && onApplyFieldFilter(row, part)}
                    />
                  </article>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function ControlDashboardStackedBar({ stats, compact = false, onSelectPart = null }) {
  const total = Number(stats.total || 0);
  const parts = [
    { key: "same", label: "Same", count: stats.same, pct: stats.samePct, overallPct: stats.sameOverallPct },
    { key: "changed", label: "Changed", count: stats.changed, pct: stats.changedPct, overallPct: stats.changedOverallPct },
    { key: "missing", label: "Missing", count: stats.missing, pct: stats.missingPct, overallPct: stats.missingOverallPct },
    { key: "external", label: "External", count: stats.external, pct: stats.externalPct }
  ];
  const title = total
    ? parts.map((part) => (
        part.overallPct === undefined
          ? `${part.label} ${part.pct}%`
          : `${part.label} ${part.pct}% (${part.overallPct}% incl. external)`
      )).join(", ")
    : "No AI-processed segments";
  const clickable = typeof onSelectPart === "function";
  const selectablePart = (partKey) => clickable && partKey !== "external";
  const activatePart = (partKey) => {
    if (selectablePart(partKey)) {
      onSelectPart(partKey);
    }
  };
  const partKeyDown = (event, partKey) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      activatePart(partKey);
    }
  };

  return (
    <div className={`control-stack ${compact ? "compact" : ""} ${clickable ? "is-clickable" : ""}`} title={title}>
      <div className="control-stack-bar" aria-label={title}>
        {total ? parts.map((part) => (
          <span
            key={part.key}
            className={`control-stack-part ${part.key} ${selectablePart(part.key) ? "selectable" : ""}`}
            style={{ width: `${(Number(part.count || 0) / total) * 100}%` }}
            role={selectablePart(part.key) ? "button" : undefined}
            tabIndex={selectablePart(part.key) ? 0 : undefined}
            title={selectablePart(part.key) ? `Show ${part.label.toLowerCase()} segments for this field` : undefined}
            onClick={selectablePart(part.key) ? () => activatePart(part.key) : undefined}
            onKeyDown={selectablePart(part.key) ? (event) => partKeyDown(event, part.key) : undefined}
          >
            <b>{part.pct}% <em>({part.count})</em></b>
          </span>
        )) : <span className="control-stack-part empty"></span>}
      </div>
      {total && !compact ? (
        <div className={`control-stack-stats ${compact ? "compact" : ""}`}>
          {parts.map((part) => (
            <span
              key={part.key}
              className={`${part.key} ${selectablePart(part.key) ? "selectable" : ""}`}
              role={selectablePart(part.key) ? "button" : undefined}
              tabIndex={selectablePart(part.key) ? 0 : undefined}
              title={selectablePart(part.key) ? `Show ${part.label.toLowerCase()} segments for this field` : undefined}
              onClick={selectablePart(part.key) ? () => activatePart(part.key) : undefined}
              onKeyDown={selectablePart(part.key) ? (event) => partKeyDown(event, part.key) : undefined}
            >
              {part.label} <b>{part.pct}%{part.overallPct === undefined ? null : <small>/{part.overallPct}%</small>}</b> <em>{part.count}</em>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DashboardReviewFocus({ rows, totals, onApplyFieldFilter = null }) {
  const topRows = Array.isArray(rows) ? rows : [];
  const insightLines = dashboardInsightLines(totals || {}, topRows);
  return (
    <aside className="dashboard-panel dashboard-review-panel">
      <header>
        <div>
          <h3>Review Focus</h3>
          <span>Fields needing the most attention</span>
        </div>
      </header>
      <div className="dashboard-review-list">
        {topRows.length ? topRows.map((row, index) => (
          <button
            key={row.field}
            type="button"
            className="dashboard-review-item"
            onClick={() => onApplyFieldFilter && onApplyFieldFilter(row, "changed")}
          >
            <span className="dashboard-review-rank">{index + 1}</span>
            <span className="dashboard-review-text">
              <strong>{row.caption}</strong>
              <small>{row.stats.changed} changed fields</small>
            </span>
            <em>{row.stats.changedPct}% changed</em>
          </button>
        )) : (
          <div className="dashboard-empty">No changed fields in this filtered view.</div>
        )}
      </div>
      <div className="dashboard-quick-insight">
        <h4>{icon("sparkles")} AI Insight</h4>
        <ul>
          {insightLines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      </div>
      {topRows[0] && (
        <button
          type="button"
          className="dashboard-view-all-button"
          onClick={() => onApplyFieldFilter && onApplyFieldFilter(topRows[0], "changed")}
        >
          View priority segments {icon("external-link")}
        </button>
      )}
    </aside>
  );
}

function DashboardKpi({ iconName = "", label, value, detail, tone = "", secondaryValue = "" }) {
  return (
    <div className={`dashboard-kpi ${tone}`}>
      {iconName && <span className="dashboard-kpi-icon">{icon(iconName)}</span>}
      <div>
        <span>{label}</span>
        {secondaryValue ? (
          <strong className="dashboard-kpi-percent-pair">
            <span>{value}</span>
            <em>{secondaryValue}</em>
          </strong>
        ) : (
          <strong>{value}</strong>
        )}
        <small>{detail}</small>
      </div>
    </div>
  );
}

function ProcessingProgress({ progress, label, active }) {
  const safeProgress = Math.max(0, Math.min(100, Number(progress || 0)));
  return (
    <section className={`processing-progress ${active ? "active" : ""}`}>
      <div>
        <strong>{label || "Processing"}</strong>
        <span>{Math.round(safeProgress)}%</span>
      </div>
      <div className="processing-progress__bar">
        <span style={{ width: `${safeProgress}%` }}></span>
      </div>
    </section>
  );
}

function evidenceItemLabel(item) {
  return item && (item.value || item.text || item.description)
    ? String(item.value || item.text || item.description)
    : "iRAP evidence";
}

function evidenceCategoryText(item) {
  return String((item && item.category) || "evidence").replace(/_/g, " ");
}

function evidenceTypeText(item) {
  return String((item && item.type) || "item").replace(/_/g, " ");
}

function evidenceConfidenceText(item) {
  const confidence = Math.round(Number((item && item.confidence) || 0) * 100);
  return confidence > 0 ? `${confidence}%` : "-";
}

function evidenceSourceImages(item) {
  const images = Array.isArray(item && item.sourceImages) ? item.sourceImages.filter(Boolean) : [];
  const primary = sourceImageForEvidence(item);
  return Array.from(new Set([primary, ...images].filter(Boolean)));
}

function evidencePreviewImage(item, segment) {
  const sourceImage = sourceImageForEvidence(item);
  const sourceUrl = sourceImage && segment
    ? `/api/segments/${segment.id}/images/${encodeURIComponent(sourceImage)}/thumb`
    : "";
  return sourceUrl;
}

function evidenceThumbnailImage(item, segment) {
  return evidencePreviewImage(item, segment);
}

function normalizeEvidenceFieldKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function collectEvidenceFieldRefKeys(value, keys) {
  if (!value) {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => collectEvidenceFieldRefKeys(entry, keys));
    return;
  }
  if (typeof value === "object") {
    [
      value.field,
      value.fieldCode,
      value.code,
      value.fCode,
      value.id
    ].forEach((entry) => {
      const key = normalizeEvidenceFieldKey(entry);
      if (key) {
        keys.add(key);
      }
    });
    return;
  }
  const key = normalizeEvidenceFieldKey(value);
  if (key) {
    keys.add(key);
  }
}

function evidenceFieldRefKeys(item) {
  const keys = new Set();
  collectEvidenceFieldRefKeys(item && item.fieldRefs, keys);
  collectEvidenceFieldRefKeys(item && item.agentEvidence && item.agentEvidence.fieldRefs, keys);
  collectEvidenceFieldRefKeys(item && item.segmentation && item.segmentation.fieldRefs, keys);
  const relevanceCodes = String((item && item.irapRelevance) || "").match(/\bF\d{1,3}\b/gi) || [];
  relevanceCodes.forEach((code) => {
    const key = normalizeEvidenceFieldKey(code);
    if (key) {
      keys.add(key);
    }
  });
  return Array.from(keys);
}

function evidenceMatchesGridField(item, fieldName, fieldCode) {
  const refs = evidenceFieldRefKeys(item);
  if (!refs.length) {
    return false;
  }
  const wanted = [fieldName, fieldCode].map(normalizeEvidenceFieldKey).filter(Boolean);
  return refs.some((ref) => wanted.includes(ref));
}

function evidenceForGridField(signResults, segmentId, fieldName, fieldCode) {
  const result = signResults && (signResults[segmentId] || signResults[String(segmentId)]);
  const items = evidenceItemsFromResult(result)
    .filter((item) => !isPromptSegmentationItem(item))
    .filter((item) => evidenceMatchesGridField(item, fieldName, fieldCode));
  return bestUniqueSegmentationItems(items);
}

function markedEvidenceItemsFromResult(result) {
  const items = evidenceItemsFromResult(result).filter((item) => {
    if (!item) {
      return false;
    }
    if (isOpenAiVisualEvidenceItem(item)) {
      return false;
    }
    return Boolean(
      isPromptSegmentationItem(item) ||
        item.markedImageUrl ||
        item.markedImageFile ||
        item.markedCropUrl ||
        (item.markedCrop && item.markedCrop.url) ||
        item.cropUrl ||
      bboxForEvidence(item)
    );
  });
  return items.sort((a, b) =>
    evidenceCategoryText(a).localeCompare(evidenceCategoryText(b)) ||
    evidenceItemLabel(a).localeCompare(evidenceItemLabel(b))
  );
}

function markedEvidenceForField(signResults, segmentId, fieldName, fieldCode) {
  const result = signResults && (signResults[segmentId] || signResults[String(segmentId)]);
  return markedEvidenceItemsFromResult(result)
    .filter((item) => evidenceMatchesGridField(item, fieldName, fieldCode));
}

function EvidencePreview({ item, segment, onOpenInPano, onClose }) {
  const images = evidenceSourceImages(item);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeImage = images[Math.min(activeIndex, Math.max(0, images.length - 1))];
  const imageUrl = activeImage
    ? `/api/segments/${segment.id}/images/${encodeURIComponent(activeImage)}`
    : "";
  const markedImageUrl = evidenceMarkedOutputUrl(item, segment);
  const markedSupportsPano = Boolean(markedImageUrl && isPromptSegmentationItem(item));
  const [markedViewMode, setMarkedViewMode] = useState(markedSupportsPano ? "pano" : "image");
  useEffect(() => {
    setMarkedViewMode(markedSupportsPano ? "pano" : "image");
  }, [markedImageUrl, markedSupportsPano]);
  const showMarkedPano = markedSupportsPano && markedViewMode === "pano";
  const previewOverlays = sourceImageForEvidence(item) === activeImage && bboxForEvidence(item)
    ? [{
        item,
        bbox: bboxForEvidence(item),
        label: evidenceItemLabel(item),
        active: true
      }]
    : [];
  const details = [
    ["Category", evidenceCategoryText(item)],
    ["Type", evidenceTypeText(item)],
    ["Value", item.value || item.text],
    ["Side", item.side],
    ["Location", item.location],
    ["Confidence", evidenceConfidenceText(item)],
    ["Source image", activeImage],
    ["iRAP relevance", item.irapRelevance]
  ].filter(([, value]) => value !== null && value !== undefined && value !== "");

  return (
    <div className="evidence-preview-backdrop" role="dialog" aria-modal="true">
      <section className="evidence-preview evidence-preview--clean">
        <header>
          <div>
            <span>Segment {segment.id}</span>
            <h3>{evidenceItemLabel(item)}</h3>
            <small>{segment.road} {segment.distance ? `- ${segment.distance}` : ""}</small>
          </div>
          <div className="evidence-preview__actions">
            {markedImageUrl && markedSupportsPano && (
              <div className="evidence-preview__mode" role="group" aria-label="Evidence view mode">
                <button
                  type="button"
                  className={markedViewMode === "image" ? "active" : ""}
                  onClick={() => setMarkedViewMode("image")}
                >
                  Image
                </button>
                <button
                  type="button"
                  className={markedViewMode === "pano" ? "active" : ""}
                  onClick={() => setMarkedViewMode("pano")}
                  disabled={!markedSupportsPano}
                >
                  360
                </button>
              </div>
            )}
            <button
              type="button"
              className="secondary-button"
              onClick={() => onOpenInPano && onOpenInPano(item, segment)}
              disabled={!activeImage}
            >
              {icon("eye")} Open in Pano
            </button>
            <button type="button" className="icon-action" onClick={onClose} title="Close">
              {icon("x")}
            </button>
          </div>
        </header>
        <div className="evidence-preview__body">
          <div className="evidence-preview__media">
            {showMarkedPano ? (
              <PanoViewer
                key={`evidence-preview-marked-pano-${segment.id}-${markedImageUrl}`}
                imageUrl={markedImageUrl}
                overlays={[]}
              />
            ) : markedImageUrl ? (
              <figure className="evidence-preview__marked-main">
                <img src={markedImageUrl} alt={`${evidenceItemLabel(item)} evidence output`} />
              </figure>
            ) : imageUrl ? (
              <PanoViewer
                key={`evidence-preview-pano-${segment.id}-${activeImage}`}
                imageUrl={imageUrl}
                overlays={previewOverlays}
              />
            ) : (
              <div className="evidence-preview__empty">No source image recorded.</div>
            )}
          </div>
          <aside className="evidence-preview__side">
            <div className="evidence-preview__details">
              <p>{item.description || "Visible iRAP evidence item."}</p>
              <dl>
                {details.map(([label, value]) => (
                  <React.Fragment key={label}>
                    <dt>{label}</dt>
                    <dd>{String(value).replace(/_/g, " ")}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          </aside>
        </div>
        {!markedImageUrl && images.length > 1 && (
          <div className="evidence-preview__thumbs">
            {images.map((file, index) => (
              <button
                key={file}
                type="button"
                className={index === activeIndex ? "active" : ""}
                onClick={() => setActiveIndex(index)}
                title={file}
              >
                <img src={`/api/segments/${segment.id}/images/${encodeURIComponent(file)}/thumb`} alt={`Source ${index + 1}`} />
                <span>{index + 1}</span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function evidenceOverlayStyle(bbox, renderRect) {
  if (renderRect) {
    return {
      left: `${renderRect.left + bbox.x * renderRect.width}px`,
      top: `${renderRect.top + bbox.y * renderRect.height}px`,
      width: `${bbox.width * renderRect.width}px`,
      height: `${bbox.height * renderRect.height}px`
    };
  }
  return {
    left: `${bbox.x * 100}%`,
    top: `${bbox.y * 100}%`,
    width: `${bbox.width * 100}%`,
    height: `${bbox.height * 100}%`
  };
}

function EvidenceOverlayLayer({ overlays, renderRect = null }) {
  const visibleOverlays = (overlays || []).filter((overlay) => overlay && overlay.bbox);
  if (!visibleOverlays.length) {
    return null;
  }
  return (
    <div className="evidence-box-layer" aria-hidden="true">
      {visibleOverlays.map((overlay, index) => (
        <div
          key={`${overlay.item && overlay.item.id ? overlay.item.id : index}-${index}`}
          className={`evidence-box ${overlay.active ? "active" : ""}`}
          style={evidenceOverlayStyle(overlay.bbox, renderRect)}
        >
          <span>{overlay.label || "Evidence"}</span>
        </div>
      ))}
    </div>
  );
}

function blankWorkflow() {
  return {
    nodes: [],
    edges: [],
    notes: ""
  };
}

function blankDocument() {
  return {
    id: `doc-${Date.now().toString(36)}`,
    name: "New document",
    type: "text/plain",
    enabled: true,
    maxChars: 8000,
    content: ""
  };
}

function safeWorkflow(workflow) {
  return {
    nodes: Array.isArray(workflow && workflow.nodes) ? workflow.nodes : [],
    edges: Array.isArray(workflow && workflow.edges) ? workflow.edges : [],
    notes: workflow && workflow.notes ? String(workflow.notes) : ""
  };
}

function AgentManagerView({ health = null, message, setMessage, error, setError }) {
  const [configState, setConfigState] = useState(null);
  const [activeGroup, setActiveGroup] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workflowJson, setWorkflowJson] = useState("");
  const [agentTrainerImages, setAgentTrainerImages] = useState([]);
  const [agentTrainerBusy, setAgentTrainerBusy] = useState(false);
  const [agentTrainerResult, setAgentTrainerResult] = useState(null);

  const agents = (configState && configState.agents) || [];
  const activeAgent = agents.find((agent) => agent.groupName === activeGroup) || agents[0] || null;
  const workflow = safeWorkflow(activeAgent && activeAgent.workflow);
  const effectiveOpenAiModel = (health && (health.model || health.agentRunnerModel)) || "gpt-5.5";
  const effectiveReasoningEffort = (health && (health.reasoningEffort || health.agentRunnerReasoningEffort)) || "low";
  const openAiSettingsLabel = `${effectiveOpenAiModel} / ${effectiveReasoningEffort}`;

  const loadAgentConfigs = () => {
    setLoading(true);
    setError("");
    apiFetch("/api/agent-configs")
      .then((data) => {
        setConfigState(data);
        const nextActive = activeGroup && data.agents.some((agent) => agent.groupName === activeGroup)
          ? activeGroup
          : data.agents[0] && data.agents[0].groupName;
        setActiveGroup(nextActive || "");
        const nextAgent = data.agents.find((agent) => agent.groupName === nextActive) || data.agents[0];
        setWorkflowJson(JSON.stringify((nextAgent && nextAgent.workflow) || blankWorkflow(), null, 2));
        setMessage(`Loaded ${data.agents.length} editable agent configuration${data.agents.length === 1 ? "" : "s"}.`);
      })
      .catch((loadError) => setError(loadError.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAgentConfigs();
  }, []);

  useEffect(() => {
    if (!activeAgent) {
      return;
    }
    setWorkflowJson(JSON.stringify(activeAgent.workflow || blankWorkflow(), null, 2));
    setAgentTrainerImages([]);
    setAgentTrainerResult(null);
  }, [activeGroup]);

  const updateActiveAgent = (patch) => {
    if (!activeAgent) {
      return;
    }
    setConfigState((current) => ({
      ...current,
      agents: current.agents.map((agent) => (
        agent.groupName === activeAgent.groupName ? { ...agent, ...patch } : agent
      ))
    }));
  };

  const syncWorkflow = (nextWorkflow) => {
    updateActiveAgent({ workflow: nextWorkflow });
    setWorkflowJson(JSON.stringify(nextWorkflow, null, 2));
  };

  const updateWorkflowNode = (index, patch) => {
    const nodes = workflow.nodes.map((node, nodeIndex) => (
      nodeIndex === index ? { ...node, ...patch } : node
    ));
    syncWorkflow({ ...workflow, nodes });
  };

  const addWorkflowStep = () => {
    const node = {
      id: `step-${Date.now().toString(36)}`,
      label: "New workflow step",
      detail: "Describe what this agent should do in this step."
    };
    const edges = workflow.nodes.length
      ? [...workflow.edges, { from: workflow.nodes[workflow.nodes.length - 1].id, to: node.id, label: "next" }]
      : workflow.edges;
    syncWorkflow({ ...workflow, nodes: [...workflow.nodes, node], edges });
  };

  const removeWorkflowStep = (index) => {
    const removed = workflow.nodes[index];
    const nodes = workflow.nodes.filter((_, nodeIndex) => nodeIndex !== index);
    const edges = workflow.edges.filter((edge) => edge.from !== removed.id && edge.to !== removed.id);
    syncWorkflow({ ...workflow, nodes, edges });
  };

  const addWorkflowEdge = () => {
    if (workflow.nodes.length < 2) {
      return;
    }
    syncWorkflow({
      ...workflow,
      edges: [
        ...workflow.edges,
        {
          from: workflow.nodes[0].id,
          to: workflow.nodes[1].id,
          label: "next"
        }
      ]
    });
  };

  const updateWorkflowEdge = (index, patch) => {
    const edges = workflow.edges.map((edge, edgeIndex) => (
      edgeIndex === index ? { ...edge, ...patch } : edge
    ));
    syncWorkflow({ ...workflow, edges });
  };

  const removeWorkflowEdge = (index) => {
    syncWorkflow({
      ...workflow,
      edges: workflow.edges.filter((_, edgeIndex) => edgeIndex !== index)
    });
  };

  const applyWorkflowJson = () => {
    try {
      const parsed = JSON.parse(workflowJson);
      syncWorkflow(safeWorkflow(parsed));
      setMessage("Workflow JSON applied to the active agent.");
      setError("");
    } catch (parseError) {
      setError(`Workflow JSON is invalid: ${parseError.message}`);
    }
  };

  const updateDocument = (index, patch) => {
    const documents = (activeAgent.documents || []).map((document, documentIndex) => (
      documentIndex === index ? { ...document, ...patch } : document
    ));
    updateActiveAgent({ documents });
  };

  const removeDocument = (index) => {
    updateActiveAgent({
      documents: (activeAgent.documents || []).filter((_, documentIndex) => documentIndex !== index)
    });
  };

  const addDocument = () => {
    updateActiveAgent({ documents: [...(activeAgent.documents || []), blankDocument()] });
  };

  const importDocumentFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || !activeAgent) {
      return;
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const document = {
          id: `doc-${Date.now().toString(36)}-${file.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`,
          name: file.name,
          type: file.type || "text/plain",
          enabled: true,
          maxChars: 8000,
          content: String(reader.result || "")
        };
        setConfigState((current) => ({
          ...current,
          agents: current.agents.map((agent) => (
            agent.groupName === activeAgent.groupName
              ? { ...agent, documents: [...(agent.documents || []), document] }
              : agent
          ))
        }));
        setMessage(`Imported ${file.name} for ${activeAgent.name}.`);
      };
      reader.onerror = () => setError(`Could not read ${file.name}. Use text, markdown, CSV, JSON, GeoJSON, or WKT files.`);
      reader.readAsText(file);
    });
  };

  const pasteAgentTrainerImages = async (event) => {
    const attachments = await pastedImagesFromClipboardEvent(event, agentTrainerImages.length, PROMPT_TRAINER_MAX_IMAGES);
    if (!attachments.length) {
      return;
    }
    event.preventDefault();
    setAgentTrainerImages((current) => current.concat(attachments).slice(0, PROMPT_TRAINER_MAX_IMAGES));
    setAgentTrainerResult(null);
    setError("");
    setMessage(`Attached ${attachments.length} screenshot${attachments.length === 1 ? "" : "s"} for ${activeAgent ? activeAgent.name : "the agent"}.`);
  };

  const addAgentTrainerImageFiles = async (files) => {
    try {
      const attachments = await imageAttachmentsFromFiles(files, agentTrainerImages.length, PROMPT_TRAINER_MAX_IMAGES);
      if (!attachments.length) {
        return;
      }
      setAgentTrainerImages((current) => current.concat(attachments).slice(0, PROMPT_TRAINER_MAX_IMAGES));
      setAgentTrainerResult(null);
      setError("");
      setMessage(`Attached ${attachments.length} screenshot${attachments.length === 1 ? "" : "s"} for ${activeAgent ? activeAgent.name : "the agent"}.`);
    } catch (imageError) {
      setError(imageError.message || "Could not read image files.");
    }
  };

  const importAgentTrainerImages = (event) => {
    const files = Array.from((event.target && event.target.files) || []);
    event.target.value = "";
    addAgentTrainerImageFiles(files);
  };

  const dropAgentTrainerImages = (event) => {
    const files = Array.from((event.dataTransfer && event.dataTransfer.files) || []);
    if (!files.some((file) => file && /^image\//i.test(file.type || ""))) {
      return;
    }
    event.preventDefault();
    addAgentTrainerImageFiles(files);
  };

  const removeAgentTrainerImage = (imageId) => {
    setAgentTrainerImages((current) => current.filter((image) => image.id !== imageId));
  };

  const generateAgentTrainingPrompt = async () => {
    if (!activeAgent || agentTrainerBusy || !agentTrainerImages.length) {
      return;
    }
    setAgentTrainerBusy(true);
    setAgentTrainerResult(null);
    setError("");
    setMessage(`Reading pasted screenshots with ${openAiSettingsLabel}...`);
    try {
      const result = await apiFetch("/api/agent-configs/train", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agentId: activeAgent.id,
          groupName: activeAgent.groupName,
          focus: activeAgent.focus || "",
          instructions: activeAgent.instructions || "",
          currentPrompt: activeAgent.prompt || "",
          pastedImages: agentTrainerImages.map((image) => ({
            id: image.id,
            name: image.name,
            type: image.type,
            dataUrl: image.dataUrl
          })),
          model: effectiveOpenAiModel,
          reasoningEffort: effectiveReasoningEffort,
          imageDetail: "high",
          maxPastedImages: PROMPT_TRAINER_MAX_IMAGES
        })
      });
      setAgentTrainerResult(result || null);
      setMessage("Agent training suggestion generated. Review it, then apply it to the prompt addendum.");
    } catch (trainError) {
      setError(trainError.message || "Could not train the agent from the pasted screenshots.");
    } finally {
      setAgentTrainerBusy(false);
    }
  };

  const applyAgentTrainerSuggestion = () => {
    if (!activeAgent || !agentTrainerResult) {
      return;
    }
    const promptAddendum = String(agentTrainerResult.promptAddendum || "").trim();
    if (!promptAddendum) {
      setError("No agent prompt addendum was generated.");
      return;
    }
    const header = `Agent-level learning from pasted rules (${new Date().toLocaleString()}):`;
    const block = `${header}\n${promptAddendum}`;
    updateActiveAgent({
      prompt: [String(activeAgent.prompt || "").trim(), block].filter(Boolean).join("\n\n")
    });
    setError("");
    setMessage("Applied to Prompt addendum. Use Save agents to persist it for future runs.");
  };

  const saveConfigs = async () => {
    if (!configState) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await apiFetch("/api/agent-configs", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(configState)
      });
      setConfigState(saved);
      setMessage("Agent prompts, documents, and workflows saved. New analyses will use them.");
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  const resetConfigs = async () => {
    if (!confirm("Reset all agent prompts, documents, and workflows to defaults?")) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const reset = await apiFetch("/api/agent-configs/reset", { method: "POST" });
      setConfigState(reset);
      setActiveGroup(reset.agents[0] ? reset.agents[0].groupName : "");
      setMessage("Agent configuration reset to defaults.");
    } catch (resetError) {
      setError(resetError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="workspace-agents">
      <section className="agents-toolbar">
        <div>
          <h2>{icon("bot")} Agent Manager</h2>
          <span>Edit prompts, attach text documents, and draw the workflow sent to each category or evidence agent.</span>
        </div>
        <div className="agents-toolbar__actions">
          <button className="secondary-button" type="button" onClick={loadAgentConfigs} disabled={loading || saving}>
            {icon("refresh-cw")} Refresh
          </button>
          <button className="secondary-button" type="button" onClick={resetConfigs} disabled={loading || saving}>
            {icon("rotate-ccw")} Reset
          </button>
          <button className="primary-button" type="button" onClick={saveConfigs} disabled={loading || saving || !configState}>
            {icon("save")} {saving ? "Saving" : "Save agents"}
          </button>
        </div>
      </section>

      <section className="agent-manager">
        <aside className="agent-list-panel">
          <div className="agent-list-title">
            <strong>Agents</strong>
            <span>{agents.length}</span>
          </div>
          {agents.map((agent) => {
            const meta = CATEGORY_META[agent.groupName] || {};
            return (
              <button
                key={agent.groupName}
                className={activeAgent && activeAgent.groupName === agent.groupName ? "active" : ""}
                type="button"
                onClick={() => setActiveGroup(agent.groupName)}
                style={{ borderLeftColor: meta.color || "var(--teal)" }}
              >
                <span>{meta.short || agent.groupName}</span>
                <strong>{agent.name}</strong>
                <small>{(agent.documents || []).filter((document) => document.enabled !== false && document.content).length} docs</small>
              </button>
            );
          })}
        </aside>

        <section className="agent-editor-panel">
          {loading && <div className="agent-empty">{icon("loader-2")} Loading agent configuration...</div>}
          {!loading && !activeAgent && <div className="agent-empty">No agents are available.</div>}
          {!loading && activeAgent && (
            <>
              <div className="agent-editor-header">
                <div>
                  <span>{activeAgent.groupName}</span>
                  <h3>{activeAgent.name}</h3>
                </div>
                <em>{(activeAgent.documents || []).length} document{(activeAgent.documents || []).length === 1 ? "" : "s"}</em>
              </div>

              <div className="agent-form-grid">
                <label>
                  <span>Agent name</span>
                  <input
                    value={activeAgent.name || ""}
                    onChange={(event) => updateActiveAgent({ name: event.target.value })}
                  />
                </label>
                <label>
                  <span>Focus</span>
                  <textarea
                    rows="3"
                    value={activeAgent.focus || ""}
                    onChange={(event) => updateActiveAgent({ focus: event.target.value })}
                  />
                </label>
                <label>
                  <span>Special instructions</span>
                  <textarea
                    rows="6"
                    value={activeAgent.instructions || ""}
                    onChange={(event) => updateActiveAgent({ instructions: event.target.value })}
                  />
                </label>
                <label>
                  <span>Prompt addendum</span>
                  <textarea
                    rows="6"
                    value={activeAgent.prompt || ""}
                    placeholder="Extra instructions for this agent. These are added to the core iRAP prompt."
                    onChange={(event) => updateActiveAgent({ prompt: event.target.value })}
                  />
                </label>
              </div>

              <section className="agent-card-section agent-trainer-section">
                <header>
                  <div>
                    <h4>{icon("sparkles")} Train this agent from screenshot rules</h4>
                    <span>Paste a screenshot from a PDF, manual, table, or correction note. The result becomes a reusable prompt addendum for this agent.</span>
                  </div>
                </header>
                <div className="agent-trainer-body">
                  <div
                    className="agent-trainer-dropzone"
                    tabIndex="0"
                    onPaste={pasteAgentTrainerImages}
                    onDrop={dropAgentTrainerImages}
                    onDragOver={(event) => event.preventDefault()}
                  >
                    {agentTrainerImages.length ? (
                      <div className="agent-trainer-images">
                        {agentTrainerImages.map((image) => (
                          <figure key={image.id}>
                            <img src={image.dataUrl} alt="" />
                            <figcaption>{image.name || "Pasted image"}</figcaption>
                            <button
                              type="button"
                              className="icon-action"
                              title="Remove pasted image"
                              aria-label="Remove pasted image"
                              onClick={() => removeAgentTrainerImage(image.id)}
                            >
                              {icon("x")}
                            </button>
                          </figure>
                        ))}
                      </div>
                    ) : (
                      <span>{icon("image-plus")} Click here and paste with Ctrl+V, drop images, or upload screenshots</span>
                    )}
                  </div>
                  <div className="agent-trainer-actions">
                    <label className="secondary-button file-import-button">
                      {icon("upload")} Upload images
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={importAgentTrainerImages}
                        disabled={agentTrainerBusy}
                      />
                    </label>
                    <button
                      className="primary-button"
                      type="button"
                      disabled={agentTrainerBusy || !agentTrainerImages.length}
                      onClick={generateAgentTrainingPrompt}
                    >
                      {icon(agentTrainerBusy ? "loader-circle" : "wand-sparkles")} Train agent
                    </button>
                    <button
                      className="secondary-button"
                      type="button"
                      disabled={agentTrainerBusy || !agentTrainerResult || !agentTrainerResult.promptAddendum}
                      onClick={applyAgentTrainerSuggestion}
                    >
                      {icon("check")} Apply to prompt
                    </button>
                  </div>
                  {agentTrainerResult && (
                    <div className="agent-trainer-result">
                      {agentTrainerResult.summary && (
                        <section>
                          <strong>Summary</strong>
                          <p>{agentTrainerResult.summary}</p>
                        </section>
                      )}
                      {agentTrainerResult.extractedText && (
                        <section>
                          <strong>Visible text read</strong>
                          <pre>{agentTrainerResult.extractedText}</pre>
                        </section>
                      )}
                      {Array.isArray(agentTrainerResult.extractedRules) && agentTrainerResult.extractedRules.length > 0 && (
                        <section>
                          <strong>Extracted rules</strong>
                          <ul>
                            {agentTrainerResult.extractedRules.map((rule, index) => <li key={`${rule}-${index}`}>{rule}</li>)}
                          </ul>
                        </section>
                      )}
                      {agentTrainerResult.promptAddendum && (
                        <section>
                          <strong>Suggested agent prompt addendum</strong>
                          <pre>{agentTrainerResult.promptAddendum}</pre>
                        </section>
                      )}
                      {Array.isArray(agentTrainerResult.warnings) && agentTrainerResult.warnings.length > 0 && (
                        <section>
                          <strong>Warnings</strong>
                          <ul>
                            {agentTrainerResult.warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}
                          </ul>
                        </section>
                      )}
                    </div>
                  )}
                </div>
              </section>

              <section className="agent-card-section">
                <header>
                  <div>
                    <h4>{icon("file-text")} Documents sent with this agent</h4>
                    <span>Use text, markdown, CSV, JSON, GeoJSON, or WKT context. Enabled documents are injected into the agent prompt.</span>
                  </div>
                  <div className="agent-section-actions">
                    <label className="secondary-button file-import-button">
                      {icon("upload")} Import file
                      <input
                        type="file"
                        multiple
                        accept=".txt,.md,.csv,.json,.geojson,.wkt,.log"
                        onChange={importDocumentFiles}
                      />
                    </label>
                    <button className="secondary-button" type="button" onClick={addDocument}>
                      {icon("plus")} Add document
                    </button>
                  </div>
                </header>
                <div className="agent-doc-list">
                  {(activeAgent.documents || []).length ? (
                    activeAgent.documents.map((document, index) => (
                      <div className="agent-doc-card" key={document.id || index}>
                        <div className="agent-doc-card__meta">
                          <label className="agent-doc-toggle">
                            <input
                              type="checkbox"
                              checked={document.enabled !== false}
                              onChange={(event) => updateDocument(index, { enabled: event.target.checked })}
                            />
                            Enabled
                          </label>
                          <input
                            value={document.name || ""}
                            onChange={(event) => updateDocument(index, { name: event.target.value })}
                            aria-label="Document name"
                          />
                          <input
                            type="number"
                            min="500"
                            max="40000"
                            value={document.maxChars || 8000}
                            onChange={(event) => updateDocument(index, { maxChars: Number(event.target.value) })}
                            aria-label="Maximum characters"
                          />
                          <button className="icon-action" type="button" title="Remove document" onClick={() => removeDocument(index)}>
                            {icon("trash-2")}
                          </button>
                        </div>
                        <textarea
                          rows="5"
                          value={document.content || ""}
                          onChange={(event) => updateDocument(index, { content: event.target.value })}
                          placeholder="Paste document text here."
                        />
                      </div>
                    ))
                  ) : (
                    <div className="agent-empty small">No documents attached to this agent.</div>
                  )}
                </div>
              </section>

              <section className="agent-card-section">
                <header>
                  <div>
                    <h4>{icon("workflow")} Workflow</h4>
                    <span>Draw the agent process. This workflow is also summarized inside the prompt.</span>
                  </div>
                  <div className="agent-section-actions">
                    <button className="secondary-button" type="button" onClick={addWorkflowStep}>
                      {icon("plus")} Add step
                    </button>
                    <button className="secondary-button" type="button" onClick={addWorkflowEdge} disabled={workflow.nodes.length < 2}>
                      {icon("git-branch")} Add link
                    </button>
                  </div>
                </header>

                <WorkflowDiagram workflow={workflow} />

                <div className="workflow-editor">
                  <div className="workflow-step-list">
                    {workflow.nodes.map((node, index) => (
                      <div className="workflow-step-editor" key={node.id || index}>
                        <strong>{index + 1}</strong>
                        <input
                          value={node.label || ""}
                          onChange={(event) => updateWorkflowNode(index, { label: event.target.value })}
                          aria-label="Workflow step label"
                        />
                        <textarea
                          rows="2"
                          value={node.detail || ""}
                          onChange={(event) => updateWorkflowNode(index, { detail: event.target.value })}
                          aria-label="Workflow step detail"
                        />
                        <button className="icon-action" type="button" title="Remove step" onClick={() => removeWorkflowStep(index)}>
                          {icon("trash-2")}
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="workflow-edge-editor">
                    <h5>Links</h5>
                    {workflow.edges.map((edge, index) => (
                      <div className="workflow-edge-row" key={`${edge.from}-${edge.to}-${index}`}>
                        <select value={edge.from || ""} onChange={(event) => updateWorkflowEdge(index, { from: event.target.value })}>
                          {workflow.nodes.map((node) => <option key={node.id} value={node.id}>{node.label || node.id}</option>)}
                        </select>
                        <span>{icon("arrow-right")}</span>
                        <select value={edge.to || ""} onChange={(event) => updateWorkflowEdge(index, { to: event.target.value })}>
                          {workflow.nodes.map((node) => <option key={node.id} value={node.id}>{node.label || node.id}</option>)}
                        </select>
                        <input
                          value={edge.label || ""}
                          placeholder="label"
                          onChange={(event) => updateWorkflowEdge(index, { label: event.target.value })}
                        />
                        <button className="icon-action" type="button" title="Remove link" onClick={() => removeWorkflowEdge(index)}>
                          {icon("x")}
                        </button>
                      </div>
                    ))}
                    {!workflow.edges.length && <div className="agent-empty small">No workflow links yet.</div>}
                  </div>

                  <label className="workflow-notes">
                    <span>Workflow notes</span>
                    <textarea
                      rows="2"
                      value={workflow.notes || ""}
                      onChange={(event) => syncWorkflow({ ...workflow, notes: event.target.value })}
                    />
                  </label>

                  <div className="workflow-json-editor">
                    <div>
                      <strong>Workflow JSON</strong>
                      <button className="secondary-button" type="button" onClick={applyWorkflowJson}>
                        {icon("braces")} Apply JSON
                      </button>
                    </div>
                    <textarea
                      rows="8"
                      value={workflowJson}
                      onChange={(event) => setWorkflowJson(event.target.value)}
                      spellCheck="false"
                    />
                  </div>
                </div>
              </section>
            </>
          )}
        </section>
      </section>
      <div className={`message-line agents-message ${error ? "error" : ""}`}>{error || message}</div>
    </main>
  );
}

function WorkflowDiagram({ workflow }) {
  const safe = safeWorkflow(workflow);
  if (!safe.nodes.length) {
    return <div className="workflow-diagram empty">No workflow steps yet.</div>;
  }
  const edgeLabels = new Map(safe.edges.map((edge) => [`${edge.from}:${edge.to}`, edge.label || "next"]));

  return (
    <div className="workflow-diagram">
      {safe.nodes.map((node, index) => {
        const nextNode = safe.nodes[index + 1];
        const edgeLabel = nextNode ? edgeLabels.get(`${node.id}:${nextNode.id}`) : "";
        return (
          <React.Fragment key={node.id || index}>
            <div className="workflow-node-card">
              <span>{index + 1}</span>
              <strong>{node.label || node.id}</strong>
              {node.detail && <small>{node.detail}</small>}
            </div>
            {nextNode && (
              <div className="workflow-connector">
                {icon("arrow-right")}
                <small>{edgeLabel}</small>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function SegmentBrowser({
  health,
  segments,
  selectedId,
  selectSegment,
  search,
  setSearch,
  runSearch,
  detail
}) {
  const filtered = segments
    .filter((segment) => segmentMatchesSearch(segment, search))
    .slice(0, 80);

  const props = (detail && detail.properties) || {};

  return (
    <aside className="segment-browser">
      <section className="summary-band">
        <h2>Dataset</h2>
        <div className="metric-grid">
          <Metric label="Segments" value={health && health.data ? health.data.segments : "-"} />
          <Metric label="Fields" value={health && health.data ? health.data.fields : "-"} />
          <Metric label="Pano folders" value={health && health.data ? health.data.panoSegmentFolders : "-"} />
          <Metric label="Images" value={health && health.data ? health.data.panoImages : "-"} />
        </div>
      </section>

      <section className="browser-search">
        <form onSubmit={runSearch}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter segments"
          />
          <button className="icon-action" title="Search">{icon("search")}</button>
        </form>
      </section>

      <section className="selected-summary">
        <h2>Selected Segment Attributes</h2>
        {detail ? (
          <div className="attributes-grid">
            <div className="attribute-row header">
              <span>Field</span>
              <span>Value</span>
            </div>
            <div className="attribute-row">
              <span>ID</span>
              <strong>{detail.id}</strong>
            </div>
            <div className="attribute-row">
              <span>Road</span>
              <strong>{props.Road_name || "-"}</strong>
            </div>
            <div className="attribute-row">
              <span>Distance</span>
              <strong>{safeText(props.Distance) || "-"}</strong>
            </div>
            <div className="attribute-row">
              <span>Survey</span>
              <strong>{dateOnly(props.Road_survey_date) || "-"}</strong>
            </div>
            <div className="attribute-row">
              <span>Panos</span>
              <strong>{detail.images.length}</strong>
            </div>
            {Object.entries(props)
              .filter(([key]) => !["SegmentId", "ID", "Road_name", "Distance", "Road_survey_date"].includes(key))
              .map(([key, value]) => (
                <div className="attribute-row" key={key}>
                  <span>{key.replace(/_/g, " ")}</span>
                  <strong>{safeText(value) || "-"}</strong>
                </div>
              ))}
          </div>
        ) : (
          <div className="no-selection">No segment selected</div>
        )}
      </section>

      <section className="segment-list">
        <h2>Road Segments</h2>
        <div className="segment-items">
          {filtered.map((segment) => (
            <button
              key={segment.id}
              className={`segment-item ${Number(selectedId) === segment.id ? "active" : ""}`}
              onClick={() => selectSegment(segment.id)}
            >
              <span className="segment-id">{segment.id}</span>
              <span className="segment-road">{segment.road || "Unnamed road"}</span>
              <span className="segment-distance">{safeText(segment.distance)}</span>
            </button>
          ))}
        </div>
      </section>
    </aside>
  );
}

function Metric({ label, value }) {
  return (
    <div className="metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function VisualWorkspace({
  segments,
  selectedId,
  selectSegment,
  detail,
  allSpeedRecords,
  viewMode,
  setViewMode,
  currentImage,
  setCurrentImage,
  imageProcessingMode,
  signResults,
  activeEvidenceTarget,
  panoCalibrationState = null,
  panoCalibrationLoaded = true,
  panoAnnotations = [],
  activePanoAnnotationId = null,
  setActivePanoAnnotationId = null,
  deletePanoAnnotation = null
}) {
  const selectedImage = detail && detail.images[currentImage];
  const evidenceOverlays = evidenceOverlaysForImage(detail, signResults, currentImage, activeEvidenceTarget);
  const imageAnnotations = selectedImage && detail
    ? panoAnnotations.filter(
        (annotation) =>
          Number(annotation.segmentId) === Number(detail.id) &&
          annotation.imageFile === selectedImage.file
      )
    : [];
  const selectedPanoViewCalibration = useMemo(
    () => panoViewCalibrationForImage(panoCalibrationState, selectedImage, detail),
    [panoCalibrationState, selectedImage && selectedImage.file, detail && detail.id]
  );
  const [visualPanoViewState, setVisualPanoViewState] = useState(null);
  useEffect(() => {
    setVisualPanoViewState(null);
  }, [detail && detail.id, selectedImage && selectedImage.file]);
  return (
    <section className="visual-workspace">
      <div className="view-toolbar">
        <div className="segmented-control">
          <button className={viewMode === "map" ? "active" : ""} onClick={() => setViewMode("map")}>
            {icon("map")} Map
          </button>
          <button
            className={viewMode === "pano" ? "active" : ""}
            onClick={() => setViewMode("pano")}
            disabled={!detail || !detail.images.length}
          >
            {icon("image")} Pano
          </button>
        </div>
        <div className="view-context">
          <span>{selectedId ? `Segment ${selectedId}` : "No segment selected"}</span>
          <strong>{detail && detail.properties ? detail.properties.Road_name : ""}</strong>
        </div>
      </div>

      <div className="evidence-stage">
        {viewMode === "pano" && selectedImage && panoCalibrationLoaded ? (
          <div className="pano-stage-layout">
            <div className="pano-stage-main">
              <PanoViewer
                key={`pano-${detail.id}-${selectedImage.file}`}
                imageUrl={selectedImage.url}
                overlays={evidenceOverlays}
                annotations={imageAnnotations}
                activeAnnotationId={activePanoAnnotationId}
                onSelectAnnotation={setActivePanoAnnotationId}
                onDeleteAnnotation={deletePanoAnnotation}
                onViewChange={setVisualPanoViewState}
              />
            </div>
          </div>
        ) : viewMode === "pano" && selectedImage ? (
          <div className="pano-empty">Loading panorama settings...</div>
        ) : (
          <MapPanel
            elementId="segmentMap"
            segments={segments}
            selectedId={selectedId}
            selectSegment={selectSegment}
            detail={detail}
            allSpeedRecords={allSpeedRecords}
            currentImage={currentImage}
            setCurrentImage={setCurrentImage}
            panoMapAnnotations={imageAnnotations}
            activePanoAnnotationId={activePanoAnnotationId}
            panoViewState={visualPanoViewState}
            panoViewCalibration={selectedPanoViewCalibration}
          />
        )}
      </div>

      <ImageStrip
        detail={detail}
        currentImage={currentImage}
        setCurrentImage={setCurrentImage}
      />
    </section>
  );
}

function PanoMeasurementPanel({
  calibrations = [],
  selectedCalibration = null,
  selectedCalibrationId = "",
  onSelectCalibration = null,
  activeMode = null,
  title = "",
  setTitle = null,
  videoId = "",
  assignedCalibrationId = "",
  defaultCalibrationId = "",
  draftPoints = [],
  status = "",
  measurementCount = 0,
  temporaryMeasurements = [],
  savingTemporaryMeasurements = false,
  isListOpen = false,
  onToggleList = null,
  onNewMeasurement = null,
  onSaveTemporaryMeasurements = null,
  onSelectTemporaryMeasurement = null,
  onClose = null,
  onUndo = null,
  onClear = null,
  onCancel = null
}) {
  const hasCalibration = Boolean(selectedCalibration);
  const selectedScale = panoCalibrationScale(selectedCalibration);
  const isMeasuring = activeMode === "measurement";
  const selectedCalibrationMeta = panoCalibrationMeta(selectedCalibration);
  const calibrationSource = selectedCalibration
    ? String(selectedCalibration.id) === String(assignedCalibrationId)
      ? `Video: ${panoVideoLabel(videoId)}`
      : String(selectedCalibration.id) === String(defaultCalibrationId)
      ? "Default"
      : "Selected"
    : "No calibration";
  const measurementInfo = [
    measurementCount ? `${measurementCount} saved earlier` : "Temporary only",
    `${temporaryMeasurements.length} temporary`,
    calibrationSource,
    selectedCalibration ? `${formatPanoMeters(selectedCalibrationMeta.knownMeters)} ref` : ""
  ].filter(Boolean).join(" | ");
  const hintText = isMeasuring
    ? "Click two points"
    : status
    ? `Done: ${status}`
    : hasCalibration
    ? "New measurement"
    : "Calibration required";

  return (
    <div className={`pano-measurement-panel ${activeMode ? "active" : ""}`}>
      <header>
        <div>
          <span>{icon("ruler")} Measurement</span>
          <strong>{selectedScale ? formatPanoCalibrationScale(selectedScale) : "No calibration"}</strong>
        </div>
        <button
          className="icon-action pano-measurement-info"
          type="button"
          title={measurementInfo}
        >
          {icon("info")}
        </button>
        <button
          className="icon-action"
          type="button"
          title="New measurement"
          disabled={!hasCalibration || isMeasuring}
          onClick={onNewMeasurement}
        >
          {icon("plus")}
        </button>
        <button
          className="icon-action"
          type="button"
          title="Save temporary list as evidence"
          disabled={!temporaryMeasurements.length || savingTemporaryMeasurements}
          onClick={onSaveTemporaryMeasurements}
        >
          {icon(savingTemporaryMeasurements ? "loader-circle" : "save")}
        </button>
        <button
          className={`icon-action ${isListOpen ? "active" : ""}`}
          type="button"
          title="Temporary measurements"
          disabled={!temporaryMeasurements.length}
          onClick={onToggleList}
        >
          {icon("list")}
        </button>
        <button
          className="icon-action"
          type="button"
          title="Close measurement"
          onClick={onClose || onCancel}
        >
          {icon("x")}
        </button>
      </header>
      {calibrations.length ? (
        <label>
          <select
            title="Active calibration"
            value={selectedCalibrationId || ""}
            onChange={(event) => onSelectCalibration && onSelectCalibration(event.target.value)}
          >
            {calibrations.map((calibration) => (
              <option key={calibration.id} value={calibration.id}>
                {calibration.name ? panoCalibrationDisplayLabel(calibration) : panoMeasurementAnnotationLabel(calibration)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="pano-measurement-title">
        <input
          type="text"
          value={title}
          maxLength="40"
          placeholder="Title"
          title="Measurement label title"
          onChange={(event) => setTitle && setTitle(event.target.value)}
        />
      </label>
      <div className="pano-measurement-hint">
        {hintText}
      </div>
      {activeMode && (
        <div className="pano-measurement-draft">
          <span title={status || ""}>{draftPoints.length}/2{status ? ` | ${status}` : ""}</span>
          <button
            className="icon-action"
            type="button"
            title="Undo point"
            disabled={!draftPoints.length}
            onClick={onUndo}
          >
            {icon("undo-2")}
          </button>
          <button
            className="icon-action"
            type="button"
            title="Clear points"
            disabled={!draftPoints.length}
            onClick={onClear}
          >
            {icon("eraser")}
          </button>
        </div>
      )}
      {isListOpen && temporaryMeasurements.length ? (
        <div className="pano-measurement-list">
          {temporaryMeasurements.map((measurement, index) => (
            <button
              key={measurement.id || index}
              type="button"
              title={panoMeasurementAnnotationLabel(measurement)}
              onClick={() => onSelectTemporaryMeasurement && onSelectTemporaryMeasurement(measurement)}
            >
              <span>{index + 1}</span>
              <strong>{formatPanoMeters(panoAnnotationMeasurementMeta(measurement).distanceMeters)}</strong>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PanoCalibrationPanel({
  profiles = [],
  selectedProfile = null,
  selectedProfileId = "",
  defaultProfileId = "",
  assignedProfileId = "",
  videoId = "",
  activeMode = null,
  profileName = "",
  setProfileName = null,
  knownMeters = "1",
  setKnownMeters = null,
  viewHeadingOffsetDeg = String(DEFAULT_PANO_HEADING_OFFSET_DEG),
  setViewHeadingOffsetDeg = null,
  viewFovDeg = "",
  setViewFovDeg = null,
  viewRangeMeters = "",
  setViewRangeMeters = null,
  assignToVideo = true,
  setAssignToVideo = null,
  makeDefault = false,
  setMakeDefault = null,
  draftPoints = [],
  status = "",
  onSelectProfile = null,
  onStartCalibration = null,
  onAssignToVideo = null,
  onClearVideo = null,
  onSetDefault = null,
  onDeleteProfile = null,
  onSaveViewCalibration = null,
  onResetViewCalibration = null,
  onUndo = null,
  onClear = null,
  onCancel = null
}) {
  const isCalibrating = activeMode === "calibration";
  const selectedScale = panoCalibrationScale(selectedProfile);
  const currentAssigned = selectedProfile && String(selectedProfile.id) === String(assignedProfileId);
  const currentDefault = selectedProfile && String(selectedProfile.id) === String(defaultProfileId);
  const hasVideoAssignment = Boolean(assignedProfileId);

  return (
    <div className={`pano-calibration-panel ${isCalibrating ? "active" : ""}`}>
      <header>
        <div>
          <span>{icon("settings-2")} Calibration</span>
          <strong>{panoVideoLabel(videoId)}</strong>
        </div>
        {isCalibrating && (
          <button
            className="icon-action"
            type="button"
            title="Stop calibration capture"
            onClick={onCancel}
          >
            {icon("x")}
          </button>
        )}
      </header>

      <div className="pano-calibration-create">
        <label>
          <span>Profile name</span>
          <input
            value={profileName}
            placeholder={`Calibration ${profiles.length + 1}`}
            onChange={(event) => setProfileName && setProfileName(event.target.value)}
          />
        </label>
        <label>
          <span>Known length</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={knownMeters}
            disabled={isCalibrating && draftPoints.length > 0}
            onChange={(event) => setKnownMeters && setKnownMeters(event.target.value)}
          />
        </label>
        <div className="pano-calibration-options">
          <label>
            <input
              type="checkbox"
              checked={assignToVideo}
              onChange={(event) => setAssignToVideo && setAssignToVideo(event.target.checked)}
            />
            <span>Use for this video</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={makeDefault}
              onChange={(event) => setMakeDefault && setMakeDefault(event.target.checked)}
            />
            <span>Make default</span>
          </label>
        </div>
        <div className="pano-measurement-actions">
          <button
            className={`primary-button compact ${isCalibrating ? "active" : ""}`}
            type="button"
            onClick={onStartCalibration}
          >
            {icon("crosshair")} Capture
          </button>
        </div>
      </div>

      {isCalibrating && (
        <div className="pano-measurement-draft">
          <span>{draftPoints.length}/2 points{status ? ` | ${status}` : ""}</span>
          <button
            className="secondary-button compact"
            type="button"
            disabled={!draftPoints.length}
            onClick={onUndo}
          >
            {icon("undo-2")} Undo
          </button>
          <button
            className="secondary-button compact"
            type="button"
            disabled={!draftPoints.length}
            onClick={onClear}
          >
            {icon("eraser")} Clear
          </button>
        </div>
      )}

      <div className="pano-calibration-saved">
        {profiles.length ? (
          <label>
            <span>Saved profiles</span>
            <select
              value={selectedProfileId || ""}
              onChange={(event) => onSelectProfile && onSelectProfile(event.target.value)}
            >
              {profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {panoCalibrationDisplayLabel(profile)}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p>No saved calibration profiles.</p>
        )}
        <div className="pano-fov-calibration">
          <div className="pano-fov-calibration__title">
            <span>{icon("scan")} Field of view</span>
            <small>Global map cone calibration</small>
          </div>
          <div className="pano-fov-calibration__grid">
            <label>
              <span>Heading offset</span>
              <input
                type="number"
                min="-180"
                max="180"
                step="0.1"
                value={viewHeadingOffsetDeg}
                title="Rotate the map view cone for every panorama image"
                onChange={(event) => setViewHeadingOffsetDeg && setViewHeadingOffsetDeg(event.target.value)}
              />
            </label>
            <label>
              <span>FOV width</span>
              <input
                type="number"
                min="18"
                max="140"
                step="0.5"
                value={viewFovDeg}
                placeholder="Auto"
                title="Optional horizontal field of view override in degrees"
                onChange={(event) => setViewFovDeg && setViewFovDeg(event.target.value)}
              />
            </label>
            <label>
              <span>Map range</span>
              <input
                type="number"
                min="10"
                max="300"
                step="1"
                value={viewRangeMeters}
                placeholder="Auto"
                title="Optional display length for the FOV cone on the map"
                onChange={(event) => setViewRangeMeters && setViewRangeMeters(event.target.value)}
              />
            </label>
          </div>
          <div className="pano-fov-calibration__actions">
            <button
              className="secondary-button compact"
              type="button"
              title="Save FOV calibration for all panorama images"
              onClick={onSaveViewCalibration}
            >
              {icon("save")} Save FOV
            </button>
            <button
              className="secondary-button compact"
              type="button"
              title="Reset global FOV calibration"
              onClick={onResetViewCalibration}
            >
              {icon("rotate-ccw")} Reset
            </button>
          </div>
        </div>
        {selectedProfile && (
          <>
            <div className="pano-calibration-summary">
              <span>{selectedScale ? formatPanoCalibrationScale(selectedScale) : "No calibration"}</span>
              <span>{currentAssigned ? "Assigned to this video" : currentDefault ? "Default profile" : "Profile"}</span>
            </div>
            <div className="pano-calibration-actions">
              <button
                className="secondary-button compact"
                type="button"
                disabled={currentAssigned}
                onClick={onAssignToVideo}
              >
                {icon("link")} Use for video
              </button>
              <button
                className="secondary-button compact"
                type="button"
                disabled={currentDefault}
                onClick={onSetDefault}
              >
                {icon("star")} Default
              </button>
              <button
                className="secondary-button compact"
                type="button"
                disabled={!hasVideoAssignment}
                onClick={onClearVideo}
              >
                {icon("unlink")} Clear video
              </button>
              <button
                className="secondary-button compact danger"
                type="button"
                onClick={onDeleteProfile}
              >
                {icon("trash-2")} Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PanoAnnotationPanel({
  annotations = [],
  segmentAnnotationCount = 0,
  contextLabel = null,
  activeAnnotationId = null,
  onSelectAnnotation = null,
  onUpdateAnnotation = null,
  onDeleteAnnotation = null,
  annotationMode,
  setAnnotationMode,
  draft,
  setDraft,
  samBusy = false,
  draftPoints = [],
  onUndoDraftPoint = null,
  onClearDraft = null,
  onSaveDraft = null
}) {
  const updateDraft = (patch) => setDraft({ ...draft, ...patch });
  const minPoints = minimumPointsForPanoShape(draft.shapeKind);
  const isManualMultiPoint = draft.shapeKind !== "point" && draft.shapeKind !== "sam";
  const canSaveDrawing = isManualMultiPoint && draftPoints.length >= minPoints;

  return (
    <aside className="pano-annotation-panel">
      <header>
        <div>
          <span>{icon("pen-line")} Manual Evidence</span>
          <strong>{contextLabel || `${annotations.length} on this image`}</strong>
        </div>
        <button
          className={`secondary-button compact ${annotationMode ? "active" : ""}`}
          onClick={() => {
            if (annotationMode && onClearDraft) {
              onClearDraft();
            }
            setAnnotationMode(!annotationMode);
          }}
          title="Draw manual evidence on panorama"
        >
          {icon(annotationMode ? "check" : "plus")} {annotationMode ? "Drawing" : "Add"}
        </button>
      </header>

      <div className={`pano-annotation-create ${annotationMode ? "active" : ""}`}>
        <label>
          <span>Label</span>
          <input
            value={draft.label}
            placeholder={panoAnnotationTypeLabel(draft.type)}
            onChange={(event) => updateDraft({ label: event.target.value })}
          />
        </label>
        <label>
          <span>Type</span>
          <select
            value={draft.type}
            onChange={(event) => updateDraft({ type: event.target.value })}
          >
            {PANO_ANNOTATION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Draw</span>
          <select
            value={draft.shapeKind}
            disabled={samBusy}
            onChange={(event) => {
              updateDraft({ shapeKind: event.target.value });
              if (onClearDraft) {
                onClearDraft();
              }
            }}
          >
            {MANUAL_PANO_ANNOTATION_SHAPES.map((shape) => (
              <option key={shape.value} value={shape.value}>{shape.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Comment</span>
          <textarea
            value={draft.note}
            disabled={samBusy}
            placeholder="Optional comment"
            onChange={(event) => updateDraft({ note: event.target.value })}
          />
        </label>
        <p>
          {annotationMode
            ? draft.shapeKind === "point"
              ? "Click the panorama to save a point annotation with this comment."
              : draft.shapeKind === "sam"
              ? samBusy
                ? "Segment Anything is selecting the object. This can take a moment on the first run."
                : "Click once on the object. Segment Anything will make a mask and save it."
              : `Click ${panoAnnotationShapeLabel(draft.shapeKind).toLowerCase()} vertices on the panorama, then save the drawing.`
            : "Turn on Add to draw and save manual evidence for this segment."}
        </p>
        {isManualMultiPoint && (
          <div className="pano-annotation-draft-actions">
            <span>{draftPoints.length}/{minPoints}+ points</span>
            <button
              className="secondary-button compact"
              onClick={onUndoDraftPoint}
              disabled={!draftPoints.length}
            >
              {icon("undo-2")} Undo
            </button>
            <button
              className="secondary-button compact"
              onClick={onClearDraft}
              disabled={!draftPoints.length}
            >
              {icon("eraser")} Clear
            </button>
            <button
              className="primary-button compact"
              onClick={onSaveDraft}
              disabled={!canSaveDrawing}
            >
              {icon("save")} Save
            </button>
          </div>
        )}
      </div>

      <div className="pano-annotation-list">
        {annotations.length ? annotations.map((annotation) => (
          <div
            key={annotation.id}
            className={`pano-annotation-item ${String(activeAnnotationId) === String(annotation.id) ? "active" : ""}`}
          >
            <button
              className="pano-annotation-item__main"
              onClick={() => onSelectAnnotation && onSelectAnnotation(annotation.id)}
              title="Focus annotation"
            >
              {icon(panoAnnotationTypeIcon(annotation.type))}
              <span>
                <strong>{panoMeasurementAnnotationLabel(annotation)}</strong>
                <small>
                  {panoAnnotationTypeLabel(annotation.type)} | {panoAnnotationShapeLabel(panoAnnotationShape(annotation).kind)} | {panoAnnotationShape(annotation).points.length} pt
                </small>
              </span>
            </button>
            <div className="pano-annotation-item__actions">
              <button
                className="icon-action"
                title="Rename annotation"
                onClick={() => {
                  const label = prompt("Annotation label", annotation.label);
                  if (label !== null && onUpdateAnnotation) {
                    onUpdateAnnotation(annotation.id, { label });
                  }
                }}
              >
                {icon("pencil")}
              </button>
              <button
                className="icon-action"
                title="Delete annotation"
                onClick={() => onDeleteAnnotation && onDeleteAnnotation(annotation.id)}
              >
                {icon("trash-2")}
              </button>
            </div>
            {annotation.note ? <p>{annotation.note}</p> : null}
          </div>
        )) : (
          <div className="pano-annotation-empty">
            {icon("map-pin-off")}
            <span>No manual evidence on this image yet.</span>
          </div>
        )}
      </div>

      <footer>{segmentAnnotationCount} manual evidence item{segmentAnnotationCount === 1 ? "" : "s"} in this segment</footer>
    </aside>
  );
}

const COMBINED_DEFAULT_WIDTHS = { grid: 44, map: 27, pano: 29 };
const COMBINED_PANEL_MIN_WIDTHS = { grid: 360, map: 260, pano: 300 };
const COMBINED_HANDLE_TOTAL_WIDTH = 20;

function clampResizeDelta(delta, minDelta, maxDelta) {
  return Math.max(minDelta, Math.min(maxDelta, delta));
}

function resizeCombinedWidths(startWidths, divider, deltaPct, availableWidth) {
  const safeAvailableWidth = Math.max(1, availableWidth || 1);
  const minGrid = (COMBINED_PANEL_MIN_WIDTHS.grid / safeAvailableWidth) * 100;
  const minMap = (COMBINED_PANEL_MIN_WIDTHS.map / safeAvailableWidth) * 100;
  const minPano = (COMBINED_PANEL_MIN_WIDTHS.pano / safeAvailableWidth) * 100;

  if (divider === "grid-map") {
    const delta = clampResizeDelta(deltaPct, minGrid - startWidths.grid, startWidths.map - minMap);
    return {
      grid: startWidths.grid + delta,
      map: startWidths.map - delta,
      pano: startWidths.pano
    };
  }

  const delta = clampResizeDelta(deltaPct, minMap - startWidths.map, startWidths.pano - minPano);
  return {
    grid: startWidths.grid,
    map: startWidths.map + delta,
    pano: startWidths.pano - delta
  };
}

function combinedGridTemplate(widths) {
  return [
    `minmax(${COMBINED_PANEL_MIN_WIDTHS.grid}px, calc(${widths.grid}% - ${(COMBINED_HANDLE_TOTAL_WIDTH * widths.grid) / 100}px))`,
    "10px",
    `minmax(${COMBINED_PANEL_MIN_WIDTHS.map}px, calc(${widths.map}% - ${(COMBINED_HANDLE_TOTAL_WIDTH * widths.map) / 100}px))`,
    "10px",
    `minmax(${COMBINED_PANEL_MIN_WIDTHS.pano}px, calc(${widths.pano}% - ${(COMBINED_HANDLE_TOTAL_WIDTH * widths.pano) / 100}px))`
  ].join(" ");
}

function CombinedResizeHandle({ divider, onPointerDown, onKeyDown, onDoubleClick, label }) {
  return (
    <div
      className="combined-resize-handle"
      role="separator"
      aria-label={label}
      aria-orientation="vertical"
      tabIndex="0"
      title="Drag to resize panels"
      onPointerDown={(event) => onPointerDown(divider, event)}
      onKeyDown={(event) => onKeyDown(divider, event)}
      onDoubleClick={onDoubleClick}
    >
      <span></span>
    </div>
  );
}

function CombinedWorkspace({
  health,
  segments,
  segmentIndex,
  fields,
  analysisResults,
  search,
  selectedId,
  selectSegment,
  detail,
  allSpeedRecords,
  currentImage,
  setCurrentImage,
  imageProcessingMode,
  updateGridCell,
  signResults,
  activeEvidenceTarget,
  panoCalibrationState = null,
  panoCalibrationLoaded = true,
  panoAnnotations = [],
  activePanoAnnotationId = null,
  setActivePanoAnnotationId = null,
  deletePanoAnnotation = null,
  openEvidencePreview,
  openEvidenceInPano
}) {
  const selectedImage = detail && detail.images[currentImage];
  const evidenceOverlays = evidenceOverlaysForImage(detail, signResults, currentImage, activeEvidenceTarget);
  const imageAnnotations = selectedImage && detail
    ? panoAnnotations.filter(
        (annotation) =>
          Number(annotation.segmentId) === Number(detail.id) &&
          annotation.imageFile === selectedImage.file
      )
    : [];
  const selectedPanoViewCalibration = useMemo(
    () => panoViewCalibrationForImage(panoCalibrationState, selectedImage, detail),
    [panoCalibrationState, selectedImage && selectedImage.file, detail && detail.id]
  );
  const combinedRef = useRef(null);
  const resizeStateRef = useRef(null);
  const [panelWidths, setPanelWidths] = useState(COMBINED_DEFAULT_WIDTHS);
  const [resizingDivider, setResizingDivider] = useState(null);
  const [combinedPanoViewState, setCombinedPanoViewState] = useState(null);

  useEffect(() => {
    setCombinedPanoViewState(null);
  }, [detail && detail.id, selectedImage && selectedImage.file]);

  const getCombinedAvailableWidth = () => {
    return Math.max(1, (combinedRef.current?.clientWidth || 1) - COMBINED_HANDLE_TOTAL_WIDTH);
  };

  const startCombinedResize = (divider, event) => {
    event.preventDefault();
    resizeStateRef.current = {
      divider,
      startX: event.clientX,
      widths: panelWidths,
      availableWidth: getCombinedAvailableWidth()
    };
    setResizingDivider(divider);
    document.body.classList.add("is-resizing-combined");
  };

  const nudgeCombinedResize = (divider, event) => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 2 : -2;
    const availableWidth = getCombinedAvailableWidth();
    setPanelWidths((current) => resizeCombinedWidths(current, divider, delta, availableWidth));
    window.dispatchEvent(new Event("resize"));
  };

  const resetCombinedResize = () => {
    setPanelWidths(COMBINED_DEFAULT_WIDTHS);
    window.dispatchEvent(new Event("resize"));
  };

  useEffect(() => {
    const handlePointerMove = (event) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) {
        return;
      }
      const deltaPct = ((event.clientX - resizeState.startX) / resizeState.availableWidth) * 100;
      setPanelWidths(resizeCombinedWidths(resizeState.widths, resizeState.divider, deltaPct, resizeState.availableWidth));
      window.dispatchEvent(new Event("resize"));
    };

    const handlePointerUp = () => {
      if (!resizeStateRef.current) {
        return;
      }
      resizeStateRef.current = null;
      setResizingDivider(null);
      document.body.classList.remove("is-resizing-combined");
      window.dispatchEvent(new Event("resize"));
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      document.body.classList.remove("is-resizing-combined");
    };
  }, []);

  return (
    <main
      ref={combinedRef}
      className={`workspace-combined ${resizingDivider ? "is-resizing" : ""}`}
      style={{ gridTemplateColumns: combinedGridTemplate(panelWidths) }}
    >
      <section className="combined-panel combined-grid-panel">
        <SegmentsGridView
          health={health}
          segments={segmentIndex}
          fields={fields}
          analysisResults={analysisResults}
          search={search}
          selectedId={selectedId}
          selectSegment={selectSegment}
          updateGridCell={updateGridCell}
          signResults={signResults}
          openEvidencePreview={openEvidencePreview}
          openEvidenceInPano={openEvidenceInPano}
          compact
        />
      </section>

      <CombinedResizeHandle
        divider="grid-map"
        label="Resize grid and map panels"
        onPointerDown={startCombinedResize}
        onKeyDown={nudgeCombinedResize}
        onDoubleClick={resetCombinedResize}
      />

      <section className="combined-panel combined-map-panel">
        <div className="combined-stage">
          <MapPanel
            elementId="combinedSegmentMap"
            segments={segments}
            selectedId={selectedId}
            selectSegment={selectSegment}
            detail={detail}
            allSpeedRecords={allSpeedRecords}
            currentImage={currentImage}
            setCurrentImage={setCurrentImage}
            panoMapAnnotations={imageAnnotations}
            activePanoAnnotationId={activePanoAnnotationId}
            panoViewState={selectedImage ? combinedPanoViewState : null}
            panoViewCalibration={selectedPanoViewCalibration}
          />
        </div>
      </section>

      <CombinedResizeHandle
        divider="map-pano"
        label="Resize map and pano panels"
        onPointerDown={startCombinedResize}
        onKeyDown={nudgeCombinedResize}
        onDoubleClick={resetCombinedResize}
      />

      <section className="combined-panel combined-pano-panel">
        <header className="combined-panel-header">
          <div>
            <span>Pano</span>
            <strong>{selectedImage ? selectedImage.file : "No image"}</strong>
          </div>
          <small>{detail ? `${detail.images.length} images` : ""}</small>
        </header>
        <div className="combined-stage">
          {selectedImage && panoCalibrationLoaded ? (
            <PanoViewer
              key={`pano-${detail.id}-${selectedImage.file}`}
              imageUrl={selectedImage.url}
              overlays={evidenceOverlays}
              annotations={imageAnnotations}
              activeAnnotationId={activePanoAnnotationId}
              onSelectAnnotation={setActivePanoAnnotationId}
              onDeleteAnnotation={deletePanoAnnotation}
              onViewChange={setCombinedPanoViewState}
            />
          ) : selectedImage ? (
            <div className="pano-empty">Loading panorama settings...</div>
          ) : (
            <div className="pano-empty">No pano available</div>
          )}
        </div>
        <ImageStrip
          detail={detail}
          currentImage={currentImage}
          setCurrentImage={setCurrentImage}
        />
      </section>
    </main>
  );
}

const RUNNER_PANEL_ORDER = ["visual", "setup", "output", "logs"];
const RUNNER_PANEL_META = {
  visual: { label: "Map/Pano", icon: "image", minWidth: 360 },
  setup: { label: "Setup", icon: "sliders-horizontal", minWidth: 300 },
  output: { label: "Output", icon: "panel-right", minWidth: 420 },
  logs: { label: "Logs", icon: "scroll-text", minWidth: 280 }
};
const RUNNER_DEFAULT_VISIBLE_PANELS = {
  visual: false,
  setup: true,
  output: true,
  logs: true
};
const RUNNER_DEFAULT_PANEL_WIDTHS = {
  visual: 30,
  setup: 25,
  output: 35,
  logs: 40
};
const RUNNER_HANDLE_WIDTH = 8;

function resizeRunnerPanelWidths(startWidths, leftKey, rightKey, deltaPct, availableWidth) {
  const safeAvailableWidth = Math.max(1, availableWidth || 1);
  const leftMin = ((RUNNER_PANEL_META[leftKey]?.minWidth || 260) / safeAvailableWidth) * 100;
  const rightMin = ((RUNNER_PANEL_META[rightKey]?.minWidth || 260) / safeAvailableWidth) * 100;
  const total = (Number(startWidths[leftKey]) || 0) + (Number(startWidths[rightKey]) || 0);
  const nextLeft = Math.max(leftMin, Math.min(total - rightMin, (Number(startWidths[leftKey]) || 0) + deltaPct));
  return {
    ...startWidths,
    [leftKey]: nextLeft,
    [rightKey]: Math.max(rightMin, total - nextLeft)
  };
}

function RunnerResizeHandle({ leftKey, rightKey, onPointerDown, onKeyDown, onDoubleClick }) {
  const leftLabel = RUNNER_PANEL_META[leftKey]?.label || leftKey;
  const rightLabel = RUNNER_PANEL_META[rightKey]?.label || rightKey;
  return (
    <div
      className="runner-resize-handle"
      role="separator"
      aria-label={`Resize ${leftLabel} and ${rightLabel} panels`}
      aria-orientation="vertical"
      tabIndex="0"
      title="Drag to resize panels"
      onPointerDown={(event) => onPointerDown(leftKey, rightKey, event)}
      onKeyDown={(event) => onKeyDown(leftKey, rightKey, event)}
      onDoubleClick={onDoubleClick}
    >
      <span></span>
    </div>
  );
}

function RunnerToolbar({ visiblePanels, togglePanel, resetPanels }) {
  return (
    <div className="runner-toolbar" aria-label="Runner panels">
      <div className="runner-toolbar__title">
        {icon("layout-panel-top")}
        <span>Runner panels</span>
      </div>
      <div className="runner-toolbar__buttons">
        {RUNNER_PANEL_ORDER.map((panelKey) => {
          const meta = RUNNER_PANEL_META[panelKey];
          const visible = Boolean(visiblePanels[panelKey]);
          const visibleCount = RUNNER_PANEL_ORDER.filter((key) => visiblePanels[key]).length;
          return (
            <button
              key={panelKey}
              type="button"
              className={visible ? "active" : ""}
              aria-pressed={visible}
              disabled={visible && visibleCount === 1}
              onClick={() => togglePanel(panelKey)}
              title={`${visible ? "Hide" : "Show"} ${meta.label}`}
            >
              {icon(meta.icon)}
              <span>{meta.label}</span>
            </button>
          );
        })}
        <button type="button" onClick={resetPanels} title="Reset runner layout">
          {icon("refresh-cw")}
          <span>Reset</span>
        </button>
      </div>
    </div>
  );
}

function CodingWorkbench({
  canEditIrap = false,
  segments,
  detail,
  fields,
  selectedId,
  selectSegment,
  allSpeedRecords,
  viewMode,
  setViewMode,
  currentImage,
  setCurrentImage,
  signResults,
  activeEvidenceTarget,
  panoAnnotations = [],
  activePanoAnnotationId = null,
  setActivePanoAnnotationId = null,
  deletePanoAnnotation = null,
  activeCategory,
  setActiveCategory,
  agentGroupNames,
  selectedAgentGroups,
  setSelectedAgentGroups,
  draftValues,
  updateDraftValue,
  analyzeSelected,
  applyAiSuggestions,
  busy,
  batchBusy,
  message,
  error,
  saveJson,
  analysisImageCount,
  setAnalysisImageCount,
  imageProcessingMode,
  setImageProcessingMode,
  agentRunnerModel,
  setAgentRunnerModel,
  agentRunnerReasoningEffort,
  setAgentRunnerReasoningEffort,
  includeFieldPromptsInAgentPrompt,
  setIncludeFieldPromptsInAgentPrompt,
  agentConcurrency,
  setAgentConcurrency,
  openAiCallConcurrency,
  setOpenAiCallConcurrency,
  batchRange,
  setBatchRange,
  batchSegmentConcurrency,
  setBatchSegmentConcurrency,
  batchJob,
  startBatchAnalyze,
  cancelBatchAnalyze,
  logs = [],
  loadLogs,
  clearLogs
}) {
  const categoryNames = useMemo(() => {
    return agentGroupNames;
  }, [agentGroupNames]);
  const fieldCategoryNames = useMemo(() => {
    const source = detail ? detail.fields : fields;
    const names = Array.from(new Set((source || []).map((field) => field.group).filter(Boolean)));
    return CATEGORY_ORDER.filter((name) => names.includes(name)).concat(
      names.filter((name) => !CATEGORY_ORDER.includes(name))
    );
  }, [detail, fields]);

  const groupedFields = useMemo(() => {
    const source = detail ? detail.fields : fields;
    return fieldCategoryNames.reduce((groups, categoryName) => {
      groups[categoryName] = (source || []).filter((field) => field.group === categoryName);
      return groups;
    }, {});
  }, [fieldCategoryNames, detail, fields]);

  const groupsToRender =
    activeCategory === "All" || !fieldCategoryNames.includes(activeCategory)
      ? fieldCategoryNames
      : fieldCategoryNames.filter((name) => name === activeCategory);

  const [collapsedSections, setCollapsedSections] = useState({});
  const [visibleRunnerPanels, setVisibleRunnerPanels] = useState(RUNNER_DEFAULT_VISIBLE_PANELS);
  const [runnerPanelWidths, setRunnerPanelWidths] = useState(RUNNER_DEFAULT_PANEL_WIDTHS);
  const [resizingRunner, setResizingRunner] = useState(false);
  const runnerGridRef = useRef(null);
  const runnerResizeStateRef = useRef(null);
  const isSectionCollapsed = (sectionKey) => Boolean(collapsedSections[sectionKey]);
  const toggleSection = (sectionKey) => {
    setCollapsedSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey]
    }));
  };

  useEffect(() => {
    if (activeCategory !== "All" && !fieldCategoryNames.includes(activeCategory)) {
      setActiveCategory("All");
    }
  }, [activeCategory, fieldCategoryNames.join("|")]);

  const visiblePanelKeys = RUNNER_PANEL_ORDER.filter((panelKey) => visibleRunnerPanels[panelKey]);
  const runnerGridTemplate = visiblePanelKeys
    .flatMap((panelKey, index) => {
      const meta = RUNNER_PANEL_META[panelKey] || {};
      const column = `minmax(${meta.minWidth || 260}px, ${runnerPanelWidths[panelKey] || 1}fr)`;
      return index < visiblePanelKeys.length - 1 ? [column, `${RUNNER_HANDLE_WIDTH}px`] : [column];
    })
    .join(" ");
  const fieldTitleMap = useMemo(() => makeFieldTitleMap(detail ? detail.fields : fields), [detail && detail.id, fields]);
  const agentRunnerReasoningOptions = useMemo(
    () => Array.from(new Set([agentRunnerReasoningEffort, ...AGENT_RUNNER_REASONING_OPTIONS].filter(Boolean))),
    [agentRunnerReasoningEffort]
  );
  const agentRunnerModelOptions = useMemo(
    () => Array.from(new Set([agentRunnerModel, ...AGENT_RUNNER_MODEL_OPTIONS].filter(Boolean))),
    [agentRunnerModel]
  );

  const resetRunnerPanels = () => {
    setVisibleRunnerPanels(RUNNER_DEFAULT_VISIBLE_PANELS);
    setRunnerPanelWidths(RUNNER_DEFAULT_PANEL_WIDTHS);
    setTimeout(() => window.dispatchEvent(new Event("resize")), 0);
  };

  const toggleRunnerPanel = (panelKey) => {
    setVisibleRunnerPanels((current) => {
      const visibleCount = RUNNER_PANEL_ORDER.filter((key) => current[key]).length;
      if (current[panelKey] && visibleCount === 1) {
        return current;
      }
      return { ...current, [panelKey]: !current[panelKey] };
    });
    setTimeout(() => window.dispatchEvent(new Event("resize")), 0);
  };

  const getRunnerAvailableWidth = () => {
    return Math.max(1, (runnerGridRef.current?.clientWidth || 1) - RUNNER_HANDLE_WIDTH * Math.max(0, visiblePanelKeys.length - 1));
  };

  const startRunnerResize = (leftKey, rightKey, event) => {
    event.preventDefault();
    runnerResizeStateRef.current = {
      leftKey,
      rightKey,
      startX: event.clientX,
      widths: runnerPanelWidths,
      availableWidth: getRunnerAvailableWidth()
    };
    setResizingRunner(true);
    document.body.classList.add("is-resizing-runner");
  };

  const nudgeRunnerResize = (leftKey, rightKey, event) => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 2 : -2;
    const availableWidth = getRunnerAvailableWidth();
    setRunnerPanelWidths((current) => resizeRunnerPanelWidths(current, leftKey, rightKey, delta, availableWidth));
    window.dispatchEvent(new Event("resize"));
  };

  useEffect(() => {
    const handlePointerMove = (event) => {
      const resizeState = runnerResizeStateRef.current;
      if (!resizeState) {
        return;
      }
      const deltaPct = ((event.clientX - resizeState.startX) / resizeState.availableWidth) * 100;
      setRunnerPanelWidths(resizeRunnerPanelWidths(
        resizeState.widths,
        resizeState.leftKey,
        resizeState.rightKey,
        deltaPct,
        resizeState.availableWidth
      ));
      window.dispatchEvent(new Event("resize"));
    };

    const handlePointerUp = () => {
      if (!runnerResizeStateRef.current) {
        return;
      }
      runnerResizeStateRef.current = null;
      setResizingRunner(false);
      document.body.classList.remove("is-resizing-runner");
      window.dispatchEvent(new Event("resize"));
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      document.body.classList.remove("is-resizing-runner");
    };
  }, []);

  const analysisAgents = detail && detail.analysis && Array.isArray(detail.analysis.agents)
    ? detail.analysis.agents
    : [];
  const canApplyAi = Boolean(detail && detail.analysis && detail.analysis.saveJson);
  const selectedRoadName = detail && detail.properties ? detail.properties.Road_name : "";
  const selectedTargetId = detail ? detail.id : selectedId;
  const useSelectedSegmentRange = () => {
    if (!selectedTargetId || batchBusy) {
      return;
    }
    setBatchRange(singleSegmentRange(selectedTargetId));
  };
  return (
    <aside className="coding-workbench agent-cockpit">
      <div className="coding-header">
        <div>
          <h2>AI Agent Runner</h2>
          <p>Configure runs, compare AI output with the selected panorama, and inspect logs.</p>
        </div>
        <span className="draft-count">{Object.keys(saveJson).length} AI fields</span>
      </div>

      <RunnerToolbar
        visiblePanels={visibleRunnerPanels}
        togglePanel={toggleRunnerPanel}
        resetPanels={resetRunnerPanels}
      />

      <div
        ref={runnerGridRef}
        className={`agent-runner-grid ${resizingRunner ? "is-resizing" : ""}`}
        style={{ gridTemplateColumns: runnerGridTemplate }}
      >
        {visiblePanelKeys.map((panelKey, panelIndex) => (
          <React.Fragment key={panelKey}>
            {panelKey === "visual" && (
              <section className="agent-runner-panel agent-runner-panel--visual">
                <PanelHeader
                  iconName="image"
                  title="Map / Panorama"
                  meta={detail ? `Segment ${detail.id}` : "No segment"}
                />
                <VisualWorkspace
                  segments={segments}
                  selectedId={selectedId}
                  selectSegment={selectSegment}
                  detail={detail}
                  allSpeedRecords={allSpeedRecords}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                  currentImage={currentImage}
                  setCurrentImage={setCurrentImage}
                  imageProcessingMode={imageProcessingMode}
                  signResults={signResults}
                  activeEvidenceTarget={activeEvidenceTarget}
                  panoCalibrationState={panoCalibrationState}
                  panoCalibrationLoaded={panoCalibrationLoaded}
                  panoAnnotations={panoAnnotations}
                  activePanoAnnotationId={activePanoAnnotationId}
                  setActivePanoAnnotationId={setActivePanoAnnotationId}
                  deletePanoAnnotation={deletePanoAnnotation}
                />
              </section>
            )}

            {panelKey === "setup" && (
              <section className="agent-runner-panel agent-runner-panel--setup">
                <PanelHeader iconName="sliders-horizontal" title="Run Setup" meta={batchBusy ? "Running" : "Ready"} />

                <div className="selected-run-target">
                  <span>Processing target</span>
                  <strong>{selectedTargetId ? `Segment ${selectedTargetId}` : "No segment selected"}</strong>
                  {selectedRoadName ? <small>{selectedRoadName}</small> : null}
                </div>

                <div className="single-run-actions">
                  <button className="primary-button" type="button" onClick={analyzeSelected} disabled={busy || batchBusy || !detail || !selectedAgentGroups.length}>
                    {icon("play")} {busy ? "Running Segment" : "Run Current Segment"}
                  </button>
                </div>

                <div className="agent-param-grid">
                  {canEditIrap && (
                    <label className="agent-runner-model-field">
                      <span>AI model</span>
                      <select
                        value={agentRunnerModel}
                        onChange={(event) => setAgentRunnerModel(event.target.value)}
                        disabled={busy || batchBusy}
                      >
                        {agentRunnerModelOptions.map((modelName) => (
                          <option key={modelName} value={modelName}>{agentRunnerModelLabel(modelName)}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label>
                    <span>Agent parallelism</span>
                    <select
                      value={agentConcurrency}
                      onChange={(event) => setAgentConcurrency(Number(event.target.value))}
                      disabled={busy || batchBusy}
                    >
                      {OPENAI_CONCURRENCY_OPTIONS.map((count) => (
                        <option key={count} value={count}>{count}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>AI calls</span>
                    <select
                      value={openAiCallConcurrency}
                      onChange={(event) => setOpenAiCallConcurrency(Number(event.target.value))}
                      disabled={busy || batchBusy}
                    >
                      {OPENAI_CONCURRENCY_OPTIONS.map((count) => (
                        <option key={count} value={count}>{count}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Reasoning effort</span>
                    <select
                      value={agentRunnerReasoningEffort}
                      onChange={(event) => setAgentRunnerReasoningEffort(event.target.value)}
                      disabled={busy || batchBusy}
                    >
                      {agentRunnerReasoningOptions.map((effort) => (
                        <option key={effort} value={effort}>{effort}</option>
                      ))}
                    </select>
                  </label>
                  <label className={`agent-runner-field-prompt-toggle ${includeFieldPromptsInAgentPrompt ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(includeFieldPromptsInAgentPrompt)}
                      onChange={(event) => setIncludeFieldPromptsInAgentPrompt(event.target.checked)}
                      disabled={busy || batchBusy}
                    />
                    <span>Inject field prompts</span>
                  </label>
                </div>

                <AgentPicker
                  categoryNames={categoryNames}
                  selectedAgentGroups={selectedAgentGroups}
                  setSelectedAgentGroups={setSelectedAgentGroups}
                  disabled={busy || batchBusy}
                  collapsed={isSectionCollapsed("agents")}
                  onToggle={() => toggleSection("agents")}
                />

                <BatchAnalyzePanel
                  batchRange={batchRange}
                  setBatchRange={setBatchRange}
                  batchSegmentConcurrency={batchSegmentConcurrency}
                  setBatchSegmentConcurrency={setBatchSegmentConcurrency}
                  selectedAgentGroups={selectedAgentGroups}
                  selectedSegmentId={selectedTargetId}
                  useSelectedSegmentRange={useSelectedSegmentRange}
                  batchJob={batchJob}
                  batchBusy={batchBusy}
                  startBatchAnalyze={startBatchAnalyze}
                  cancelBatchAnalyze={cancelBatchAnalyze}
                  analysisImageCount={analysisImageCount}
                  setAnalysisImageCount={setAnalysisImageCount}
                  imageProcessingMode={imageProcessingMode}
                  setImageProcessingMode={setImageProcessingMode}
                  agentRunnerModel={agentRunnerModel}
                  agentRunnerReasoningEffort={agentRunnerReasoningEffort}
                  includeFieldPromptsInAgentPrompt={includeFieldPromptsInAgentPrompt}
                  openAiCallConcurrency={openAiCallConcurrency}
                  setOpenAiCallConcurrency={setOpenAiCallConcurrency}
                  collapsed={isSectionCollapsed("batch")}
                  onToggle={() => toggleSection("batch")}
                />

                <div className={`message-line agent-message-line ${error ? "error" : ""}`}>
                  {error || message}
                </div>
              </section>
            )}

            {panelKey === "output" && (
              <section className="agent-runner-panel agent-runner-panel--output">
                <PanelHeader
                  iconName="panel-right"
                  title="AI Output"
                  meta={detail ? `Segment ${detail.id}` : "No segment"}
                />

                <AgentSummary
                  agents={analysisAgents}
                  collapsed={isSectionCollapsed("agent-summary")}
                  onToggle={() => toggleSection("agent-summary")}
                />

                <div className="category-nav">
                  <button
                    className={activeCategory === "All" ? "active" : ""}
                    type="button"
                    onClick={() => setActiveCategory("All")}
                  >
                    <span className="category-dot" style={{ background: "#475569" }}></span>
                    All
                  </button>
                  {fieldCategoryNames.map((categoryName) => {
                    const meta = CATEGORY_META[categoryName] || {};
                    return (
                      <button
                        key={categoryName}
                        className={activeCategory === categoryName ? "active" : ""}
                        type="button"
                        onClick={() => setActiveCategory(categoryName)}
                      >
                        <span className="category-dot" style={{ background: meta.color || "#64748b" }}></span>
                        {meta.short || categoryName}
                      </button>
                    );
                  })}
                </div>

                <div className="field-groups">
                  {!detail ? (
                    <div className="blank-workbench">
                      {icon("mouse-pointer-click")}
                      <strong>Select a segment</strong>
                      <span>The category form will load with empty iRAP values.</span>
                    </div>
                  ) : (
                    groupsToRender.map((groupName) => (
                      <FieldCategory
                        key={groupName}
                        groupName={groupName}
                        fields={groupedFields[groupName] || []}
                        analysis={detail.analysis}
                        segment={detail}
                        collapsed={isSectionCollapsed(`category:${groupName}`)}
                        onToggle={() => toggleSection(`category:${groupName}`)}
                      />
                    ))
                  )}
                </div>
              </section>
            )}

            {panelKey === "logs" && (
              <RunLogPanel
                logs={logs}
                fieldTitleMap={fieldTitleMap}
                loadLogs={() => loadLogs && loadLogs()}
                clearLogs={clearLogs}
              />
            )}

            {panelIndex < visiblePanelKeys.length - 1 && (
              <RunnerResizeHandle
                leftKey={panelKey}
                rightKey={visiblePanelKeys[panelIndex + 1]}
                onPointerDown={startRunnerResize}
                onKeyDown={nudgeRunnerResize}
                onDoubleClick={resetRunnerPanels}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </aside>
  );
}

function PanelHeader({ iconName, title, meta }) {
  return (
    <header className="agent-panel-header">
      <span>{icon(iconName)} {title}</span>
      {meta ? <em>{meta}</em> : null}
    </header>
  );
}

function RunLogPanel({ logs = [], fieldTitleMap, loadLogs, clearLogs }) {
  const [expandedLog, setExpandedLog] = useState(null);
  const safeLogs = Array.isArray(logs) ? logs : [];

  const formatTime = (timestamp) => {
    if (!timestamp) {
      return "";
    }
    return new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  };

  return (
    <section className="agent-runner-panel agent-runner-panel--logs">
      <header className="agent-panel-header">
        <span>{icon("scroll-text")} Logs</span>
        <div className="run-log-actions">
          <button className="icon-action" type="button" title="Refresh logs" onClick={loadLogs}>
            {icon("refresh-cw")}
          </button>
          <button className="icon-action" type="button" title="Clear logs" onClick={clearLogs}>
            {icon("trash-2")}
          </button>
        </div>
      </header>

      <div className="run-log-list">
        {!safeLogs.length ? (
          <div className="run-log-empty">No run logs yet.</div>
        ) : (
          safeLogs.map((log) => {
            const key = log.timestamp || `${log.segmentId}-${log.model}`;
            const expanded = expandedLog === key;
            const fieldsText = log.status === "success" && log.savedCount !== undefined
              ? `${log.savedCount}/${log.totalCount} fields`
              : log.status || "pending";
            return (
              <article key={key} className={`run-log-entry ${log.status || "pending"}`}>
                <button type="button" className="run-log-summary" onClick={() => setExpandedLog(expanded ? null : key)}>
                  <span className="run-log-status">
                    {log.status === "success" ? icon("check-circle") : log.status === "error" ? icon("x-circle") : icon("clock")}
                  </span>
                  <strong>Segment {log.segmentId}</strong>
                  <small>{formatTime(log.timestamp)}</small>
                  <em>{fieldsText}</em>
                  {expanded ? icon("chevron-up") : icon("chevron-down")}
                </button>
                {expanded && (
                  <div className="run-log-detail">
                    <div>
                      <span>Model</span>
                      <strong>{log.model || "-"}</strong>
                    </div>
                    <div>
                      <span>Images</span>
                      <strong>{log.images ? log.images.length : 0}</strong>
                    </div>
                    {log.error ? <p>{log.error}</p> : null}
                    {Array.isArray(log.fieldAnalysis) && log.fieldAnalysis.length ? (
                      <ul>
                        {log.fieldAnalysis.map((field, index) => (
                          <li key={`${field.fieldName}-${index}`}>
                            <strong>{displayFieldTitle(field.fieldName, fieldTitleMap)}</strong>
                            <span>{field.decodedValue || field.value || "No value"}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}

function CollapseButton({ collapsed, onToggle, label }) {
  return (
    <button
      type="button"
      className="section-toggle-button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-label={`${collapsed ? "Expand" : "Collapse"} ${label}`}
      title={collapsed ? "Expand" : "Collapse"}
    >
      {icon(collapsed ? "chevron-down" : "chevron-up")}
    </button>
  );
}

function AgentPicker({ categoryNames, selectedAgentGroups, setSelectedAgentGroups, disabled, collapsed, onToggle }) {
  const selectedSet = new Set(selectedAgentGroups);
  const allSelected = categoryNames.length > 0 && selectedAgentGroups.length === categoryNames.length;

  function toggleAgent(groupName) {
    setSelectedAgentGroups((current) => {
      if (current.includes(groupName)) {
        return current.filter((name) => name !== groupName);
      }
      return categoryNames.filter((name) => current.concat(groupName).includes(name));
    });
  }

  return (
    <div className={`agent-picker ${collapsed ? "collapsed" : ""}`}>
      <div className="agent-picker__header">
        <span>{icon("bot")} AI agents to run</span>
        <div className="section-header-actions">
          <button type="button" onClick={() => setSelectedAgentGroups(categoryNames)} disabled={disabled || allSelected}>
            All
          </button>
          <button type="button" onClick={() => setSelectedAgentGroups([])} disabled={disabled || !selectedAgentGroups.length}>
            None
          </button>
          <CollapseButton collapsed={collapsed} onToggle={onToggle} label="agents to run" />
        </div>
      </div>
      {!collapsed && (
        <>
          <div className="agent-options">
            {categoryNames.map((groupName) => {
              const meta = CATEGORY_META[groupName] || {};
              const selected = selectedSet.has(groupName);
              const runsLast = Boolean(meta.runsLast);
              return (
                <button
                  key={groupName}
                  type="button"
                  className={`agent-toggle ${selected ? "selected" : ""} ${runsLast ? "runs-last" : ""}`}
                  onClick={() => toggleAgent(groupName)}
                  disabled={disabled}
                  title={runsLast ? "Runs after the selected iRAP field agents." : undefined}
                  style={selected ? { borderColor: meta.color || "#64748b" } : undefined}
                >
                  <span className="category-dot" style={{ background: meta.color || "#64748b" }}></span>
                  {meta.short || groupName}
                  {runsLast && <small>last</small>}
                </button>
              );
            })}
          </div>
          {!selectedAgentGroups.length && (
            <div className="agent-picker__warning">Select at least one AI agent.</div>
          )}
        </>
      )}
    </div>
  );
}

function BatchAnalyzePanel({
  batchRange,
  setBatchRange,
  batchSegmentConcurrency,
  setBatchSegmentConcurrency,
  selectedAgentGroups,
  selectedSegmentId,
  useSelectedSegmentRange,
  batchJob,
  batchBusy,
  startBatchAnalyze,
  cancelBatchAnalyze,
  analysisImageCount,
  setAnalysisImageCount,
  imageProcessingMode,
  setImageProcessingMode,
  agentRunnerModel = "gpt-5.5",
  agentRunnerReasoningEffort = "low",
  includeFieldPromptsInAgentPrompt = true,
  openAiCallConcurrency,
  setOpenAiCallConcurrency,
  collapsed,
  onToggle
}) {
  const completed = Number(batchJob && batchJob.completed ? batchJob.completed : 0);
  const total = Number(batchJob && batchJob.total ? batchJob.total : 0);
  const progress = total ? Math.round((completed / total) * 100) : 0;
  const statusLabel = batchJob ? batchJob.status : "ready";
  const activeSegments =
    batchJob && batchJob.activeSegmentIds && batchJob.activeSegmentIds.length
      ? `Active: ${batchJob.activeSegmentIds.join(", ")}`
      : "Waiting for next segment";
  const agentSummary = `${selectedAgentGroups.length} agent${selectedAgentGroups.length === 1 ? "" : "s"}`;
  const imageModeText = imageProcessingLabel(imageProcessingMode);
  const imageSummary = `${analysisImageCount} ${imageModeText}${analysisImageCount === 1 ? "" : "s"}`;

  return (
    <div className={`batch-panel ${collapsed ? "collapsed" : ""}`}>
      <div className="batch-panel__title">
        <span>{icon("list-start")} Segment range processing</span>
        <div className="section-header-actions">
          <em>{agentSummary}, {imageSummary}, {includeFieldPromptsInAgentPrompt ? "field prompts on" : "field prompts off"}, {batchSegmentConcurrency} parallel segments, {openAiCallConcurrency} AI calls</em>
          <CollapseButton collapsed={collapsed} onToggle={onToggle} label="AI agent range processing" />
        </div>
      </div>
      {!collapsed && (
        <>
          <div className="batch-controls">
            <label className="batch-range-field">
              <span>Segment ranges</span>
              <div className="batch-range-input-row">
                <input
                  type="text"
                  placeholder="1-10, 100-110, 200-210"
                  value={batchRange.ranges}
                  onChange={(event) => setBatchRange({ ...batchRange, ranges: event.target.value })}
                  disabled={batchBusy}
                />
                <button
                  className="icon-action"
                  type="button"
                  title={selectedSegmentId ? `Use selected segment ${selectedSegmentId}` : "Select a segment first"}
                  onClick={useSelectedSegmentRange}
                  disabled={batchBusy || !selectedSegmentId}
                >
                  {icon("map-pin")}
                </button>
              </div>
            </label>
            <label>
              <span>Images each</span>
              <select
                value={analysisImageCount}
                onChange={(event) => setAnalysisImageCount(Number(event.target.value))}
                disabled={batchBusy}
              >
                {[1, 2, 3, 4, 5, 6, 8, 10].map((count) => (
                  <option key={count} value={count}>{count}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Image mode</span>
              <select
                value={imageProcessingMode}
                onChange={(event) => setImageProcessingMode(event.target.value)}
                disabled={batchBusy}
              >
                {IMAGE_PROCESSING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Parallel segments</span>
              <select
                value={batchSegmentConcurrency}
                onChange={(event) => setBatchSegmentConcurrency(Number(event.target.value))}
                disabled={batchBusy}
              >
                {OPENAI_CONCURRENCY_OPTIONS.map((count) => (
                  <option key={count} value={count}>{count}</option>
                ))}
              </select>
            </label>
            <label>
              <span>AI calls</span>
              <select
                value={openAiCallConcurrency}
                onChange={(event) => setOpenAiCallConcurrency(Number(event.target.value))}
                disabled={batchBusy}
              >
                {OPENAI_CONCURRENCY_OPTIONS.map((count) => (
                  <option key={count} value={count}>{count}</option>
                ))}
              </select>
            </label>
            <button className="primary-button" onClick={startBatchAnalyze} disabled={batchBusy || !selectedAgentGroups.length}>
              {icon("play")} {batchBusy ? "Running" : "Run AI Agents"}
            </button>
            {batchBusy && (
              <button className="secondary-button" onClick={cancelBatchAnalyze}>
                {icon("square")} Cancel
              </button>
            )}
          </div>

          {batchJob && (
            <div className="batch-status">
              <div className="batch-status__line">
                <strong>{statusLabel}</strong>
                <span>{batchProgressText(batchJob)}</span>
              </div>
              <div className="batch-meter">
                <span style={{ width: `${progress}%` }}></span>
              </div>
              <div className="batch-status__meta">
                <span>{progress}%</span>
                <span>{activeSegments}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AgentSummary({ agents, collapsed, onToggle }) {
  const hasAgents = agents && agents.length;

  return (
    <div className={`agent-summary-panel ${collapsed ? "collapsed" : ""}`}>
      <div className="agent-summary-header">
        <span>{icon("activity")} Agent results</span>
        <div className="section-header-actions">
          <em>{hasAgents ? `${agents.length} run` : "No runs"}</em>
          <CollapseButton collapsed={collapsed} onToggle={onToggle} label="agent results" />
        </div>
      </div>
      {!collapsed && <AgentSummaryBody agents={agents} />}
    </div>
  );
}

function AgentSummaryBody({ agents }) {
  if (!agents || !agents.length) {
    return (
      <div className="agent-summary empty">
        <span>Category agents have not run yet.</span>
      </div>
    );
  }

  return (
    <div className="agent-summary">
      {agents.map((agent) => {
        const meta = CATEGORY_META[agent.group] || {};
        return (
          <div className="agent-pill" key={agent.id}>
            <span className="category-dot" style={{ background: meta.color || "#64748b" }}></span>
            <strong>{agent.name}</strong>
            <em>
              {agent.status === "skipped"
                ? `${agent.fieldsSkippedNotVisible || 0} skipped`
                : `${agent.fieldsSaved}/${agent.fieldsAnalyzed} saved${agent.fieldsSkippedNotVisible ? `, ${agent.fieldsSkippedNotVisible} skipped` : ""}`}
            </em>
          </div>
        );
      })}
    </div>
  );
}

function FieldCategory({ groupName, fields, analysis, segment, collapsed, onToggle }) {
  const meta = CATEGORY_META[groupName] || {
    color: "#64748b",
    icon: "list",
    note: ""
  };

  return (
    <section className={`field-category ${collapsed ? "collapsed" : ""}`} style={{ "--category-color": meta.color }}>
      <header>
        <div className="category-title">
          {icon(meta.icon || "list")}
          <div>
            <h3>{groupName}</h3>
            <span>{meta.note}</span>
          </div>
        </div>
        <div className="field-category-actions">
          <strong>{fields.length}</strong>
          <CollapseButton collapsed={collapsed} onToggle={onToggle} label={groupName} />
        </div>
      </header>
      {!collapsed && (
        <div className="category-fields">
          {fields.map((field) => (
            <FieldRow
              key={field.field}
              field={field}
              analysis={analysis && analysis.fields && analysis.fields[field.field]}
              segment={segment}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function FieldRow({ field, analysis, segment }) {
  const confidence = analysis && Number.isFinite(Number(analysis.confidence))
    ? Math.round(Number(analysis.confidence) * 100)
    : null;
  const state = !analysis ? "empty" : analysis.needsReview ? "review" : "ready";
  const suggestion =
    analysis && analysis.value !== null && analysis.value !== undefined
      ? `${analysis.value}${analysis.decodedValue ? `: ${analysis.decodedValue}` : ""}`
      : "No suggestion";
  const reason = analysis && analysis.evidence ? analysis.evidence : "No AI reason yet";
  const sourceImages = analysisFieldSourceImages(analysis, segment);

  return (
    <div className="field-row" title={analysis && analysis.evidence ? analysis.evidence : ""}>
      <div className="field-label">
        <strong>{field.caption || field.field}</strong>
        <em>{field.imageSuitability === "not_visible" ? "not image-visible" : field.imageSuitability}</em>
      </div>
      <div className={`ai-evidence ${state}`}>
        <div className="ai-evidence__top">
          <span>{confidence === null ? "No AI" : `${confidence}%`}</span>
          <strong>{suggestion}</strong>
        </div>
        <p><b>Reason:</b> {reason}</p>
        <div className="ai-evidence__sources">
          <b>Image:</b>
          {sourceImages.length ? sourceImages.map((imageFile, index) => (
            <a
              key={imageFile}
              href={segmentImageUrl(segment, imageFile)}
              target="_blank"
              rel="noreferrer"
              title={imageFile}
            >
              {sourceImages.length === 1 ? imageFile : `Image ${index + 1}`}
            </a>
          )) : (
            <span>No source image</span>
          )}
        </div>
      </div>
    </div>
  );
}

function pointSegmentDistance(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) {
    return Math.hypot(point.x - start.x, point.y - start.y);
  }
  const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(point.x - (start.x + t * dx), point.y - (start.y + t * dy));
}

function geometryCoordinateLines(geometry) {
  if (!geometry) {
    return [];
  }
  const { type, coordinates, geometries } = geometry;
  if (type === "LineString") {
    return [coordinates || []];
  }
  if (type === "MultiLineString" || type === "Polygon") {
    return coordinates || [];
  }
  if (type === "MultiPolygon") {
    return (coordinates || []).flat();
  }
  if (type === "Point") {
    return [[coordinates || []]];
  }
  if (type === "MultiPoint") {
    return (coordinates || []).map((coordinate) => [coordinate]);
  }
  if (type === "GeometryCollection") {
    return (geometries || []).flatMap((candidate) => geometryCoordinateLines(candidate));
  }
  return [];
}

function nearestSegmentFeatureForMapClick(map, segments, latlng) {
  if (!map || !segments || !Array.isArray(segments.features) || !latlng) {
    return null;
  }
  const clickPoint = map.latLngToLayerPoint(latlng);
  const zoom = typeof map.getZoom === "function" ? Number(map.getZoom()) : 14;
  const threshold = zoom >= 17 ? 34 : zoom >= 15 ? 28 : 22;
  let nearest = null;

  segments.features.forEach((feature) => {
    const id = Number(feature && feature.properties && (feature.properties.SegmentId || feature.properties.ID));
    if (!Number.isFinite(id)) {
      return;
    }
    const lines = geometryCoordinateLines(feature.geometry);
    let bestDistance = Infinity;
    lines.forEach((line) => {
      const points = (line || [])
        .filter((coordinate) => Array.isArray(coordinate) && coordinate.length >= 2)
        .map((coordinate) => map.latLngToLayerPoint([Number(coordinate[1]), Number(coordinate[0])]))
        .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
      if (points.length === 1) {
        bestDistance = Math.min(bestDistance, Math.hypot(clickPoint.x - points[0].x, clickPoint.y - points[0].y));
        return;
      }
      for (let index = 1; index < points.length; index += 1) {
        bestDistance = Math.min(bestDistance, pointSegmentDistance(clickPoint, points[index - 1], points[index]));
      }
    });
    if (bestDistance <= threshold && (!nearest || bestDistance < nearest.distance)) {
      nearest = { id, distance: bestDistance };
    }
  });

  return nearest;
}

function formatMapMeasureDistance(meters) {
  const value = Number(meters);
  if (!Number.isFinite(value)) {
    return "";
  }
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(Math.abs(value) >= 10000 ? 1 : 2)} km`;
  }
  if (Math.abs(value) >= 10) {
    return `${value.toFixed(1)} m`;
  }
  return `${value.toFixed(2)} m`;
}

function mapArcLatLngToLocalPoint(point, originLat) {
  const earthRadiusMeters = 6371008.8;
  const lat = degreesToRadians(point.lat);
  const lng = degreesToRadians(point.lng);
  return {
    x: earthRadiusMeters * Math.cos(degreesToRadians(originLat)) * lng,
    y: earthRadiusMeters * lat
  };
}

function mapArcLocalPointToLatLng(point, originLat) {
  const earthRadiusMeters = 6371008.8;
  return {
    lat: radiansToDegrees(point.y / earthRadiusMeters),
    lng: radiansToDegrees(point.x / (earthRadiusMeters * Math.cos(degreesToRadians(originLat))))
  };
}

function normalizeRadians(radians) {
  const value = Number(radians);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return ((value % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
}

function positiveAngleDeltaRadians(start, end) {
  return normalizeRadians(end - start);
}

function mapArcAngleContains(start, middle, end) {
  return positiveAngleDeltaRadians(start, middle) <= positiveAngleDeltaRadians(start, end);
}

function computeMapArcMeasurement(points) {
  if (!Array.isArray(points) || points.length !== 3) {
    return null;
  }
  const cleanPoints = points.map((point) => ({
    lat: Number(point && point.lat),
    lng: Number(point && point.lng)
  }));
  if (cleanPoints.some((point) => !Number.isFinite(point.lat) || !Number.isFinite(point.lng))) {
    return null;
  }

  const originLat = cleanPoints.reduce((sum, point) => sum + point.lat, 0) / cleanPoints.length;
  const [p1, p2, p3] = cleanPoints.map((point) => mapArcLatLngToLocalPoint(point, originLat));
  const determinant = 2 * (
    p1.x * (p2.y - p3.y) +
    p2.x * (p3.y - p1.y) +
    p3.x * (p1.y - p2.y)
  );
  if (!Number.isFinite(determinant) || Math.abs(determinant) < 0.000001) {
    return {
      error: "The three points are too close to a straight line."
    };
  }

  const p1Size = p1.x * p1.x + p1.y * p1.y;
  const p2Size = p2.x * p2.x + p2.y * p2.y;
  const p3Size = p3.x * p3.x + p3.y * p3.y;
  const center = {
    x: (
      p1Size * (p2.y - p3.y) +
      p2Size * (p3.y - p1.y) +
      p3Size * (p1.y - p2.y)
    ) / determinant,
    y: (
      p1Size * (p3.x - p2.x) +
      p2Size * (p1.x - p3.x) +
      p3Size * (p2.x - p1.x)
    ) / determinant
  };
  const radiusMeters = Math.hypot(p1.x - center.x, p1.y - center.y);
  if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) {
    return {
      error: "Could not calculate an arc radius."
    };
  }

  const angles = [p1, p2, p3].map((point) => normalizeRadians(Math.atan2(point.y - center.y, point.x - center.x)));
  let startAngle = angles[0];
  let endAngle = angles[2];
  let direction = 1;
  if (!mapArcAngleContains(startAngle, angles[1], endAngle)) {
    direction = -1;
  }
  const span = direction === 1
    ? positiveAngleDeltaRadians(startAngle, endAngle)
    : positiveAngleDeltaRadians(endAngle, startAngle);
  const stepCount = Math.max(18, Math.min(160, Math.ceil((span * 180) / Math.PI / 3)));
  const arcLatLngs = [];
  for (let index = 0; index <= stepCount; index += 1) {
    const ratio = index / stepCount;
    const angle = direction === 1
      ? startAngle + span * ratio
      : startAngle - span * ratio;
    const point = {
      x: center.x + Math.cos(angle) * radiusMeters,
      y: center.y + Math.sin(angle) * radiusMeters
    };
    arcLatLngs.push(mapArcLocalPointToLatLng(point, originLat));
  }
  const centerLatLng = mapArcLocalPointToLatLng(center, originLat);
  return {
    center: centerLatLng,
    radiusMeters,
    radiusPoint: cleanPoints[1],
    arcLatLngs
  };
}

function createLeafletBaseLayers(L) {
  return MAP_BASE_LAYER_DEFS.reduce((layers, definition) => {
    layers[definition.id] = L.tileLayer(definition.url, definition.options || {});
    return layers;
  }, {});
}

function leafletBaseLayerControlItems(baseLayers) {
  return MAP_BASE_LAYER_DEFS.reduce((items, definition) => {
    if (baseLayers[definition.id]) {
      items[definition.label] = baseLayers[definition.id];
    }
    return items;
  }, {});
}

function normalizedMapBaseLayerId(baseLayerId) {
  return MAP_BASE_LAYER_DEFS.some((definition) => definition.id === baseLayerId)
    ? baseLayerId
    : MAP_BASE_LAYER_DEFS[0].id;
}

function numericPanoValue(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const text = String(value ?? "").trim();
  if (!text) {
    return null;
  }
  const direct = Number(text);
  if (Number.isFinite(direct)) {
    return direct;
  }
  const match = text.match(/[+-]?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
}

function degreesToRadians(degrees) {
  return (Number(degrees) * Math.PI) / 180;
}

function radiansToDegrees(radians) {
  return (Number(radians) * 180) / Math.PI;
}

function normalizeDegrees(degrees) {
  const value = Number(degrees);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return ((value % 360) + 360) % 360;
}

function normalizePanoFovDegrees(value, fallback = 72) {
  let degrees = numericPanoValue(value);
  if (!Number.isFinite(degrees) || degrees <= 0) {
    degrees = fallback;
  } else if (degrees <= Math.PI * 2) {
    degrees = radiansToDegrees(degrees);
  }
  return Math.max(18, Math.min(140, degrees));
}

function bearingDegreesBetween(start, end) {
  if (!start || !end) {
    return 0;
  }
  const lat1 = degreesToRadians(start.lat);
  const lat2 = degreesToRadians(end.lat);
  const lngDelta = degreesToRadians(end.lng - start.lng);
  const y = Math.sin(lngDelta) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(lngDelta);
  return normalizeDegrees(radiansToDegrees(Math.atan2(y, x)));
}

function destinationLatLng(origin, bearingDegrees, distanceMeters) {
  const earthRadiusMeters = 6371008.8;
  const angularDistance = Number(distanceMeters) / earthRadiusMeters;
  const bearing = degreesToRadians(bearingDegrees);
  const lat1 = degreesToRadians(origin.lat);
  const lng1 = degreesToRadians(origin.lng);
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearing)
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat1),
    Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
  );
  return {
    lat: radiansToDegrees(lat2),
    lng: normalizeDegrees(radiansToDegrees(lng2) + 180) - 180
  };
}

function panoFeatureLatLng(feature) {
  const geometry = feature && feature.geometry;
  const coordinates = geometry && geometry.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return null;
  }
  const lng = Number(coordinates[0]);
  const lat = Number(coordinates[1]);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

function panoFeatureVideoId(feature) {
  const props = (feature && feature.properties) || {};
  return String(props.VideoName || props.VideoNameRaw || props.videoName || props.video || "")
    .replace(/\.[^.]+$/, "");
}

function panoFeatureSequence(feature) {
  const props = (feature && feature.properties) || {};
  return numericPanoValue(
    props.FID ?? props._target_fid ?? props.TargetFID ?? props.FeatureIndex ?? props.SortOrder
  );
}

function panoFeatureHeading(feature, features = []) {
  const props = (feature && feature.properties) || {};
  const headingFields = [
    "Heading",
    "heading",
    "Bearing",
    "bearing",
    "Azimuth",
    "azimuth",
    "Yaw",
    "yaw",
    "CameraYaw",
    "cameraYaw",
    "Direction",
    "direction"
  ];
  for (const field of headingFields) {
    const value = numericPanoValue(props[field]);
    if (Number.isFinite(value)) {
      return normalizeDegrees(value);
    }
  }

  const origin = panoFeatureLatLng(feature);
  if (!origin) {
    return 0;
  }
  const featureKey = panoKeyFromFeature(feature);
  const videoId = panoFeatureVideoId(feature);
  const siblings = (features || [])
    .map((candidate, index) => ({
      feature: candidate,
      index,
      point: panoFeatureLatLng(candidate),
      sequence: panoFeatureSequence(candidate),
      videoId: panoFeatureVideoId(candidate),
      key: panoKeyFromFeature(candidate)
    }))
    .filter((item) => item.point && (!videoId || item.videoId === videoId))
    .sort((a, b) => {
      const aSeq = Number.isFinite(a.sequence) ? a.sequence : a.index;
      const bSeq = Number.isFinite(b.sequence) ? b.sequence : b.index;
      return aSeq - bSeq || a.index - b.index;
    });
  const currentIndex = siblings.findIndex((item) =>
    (featureKey && item.key === featureKey) || item.feature === feature
  );
  if (currentIndex >= 0 && siblings[currentIndex + 1]) {
    return bearingDegreesBetween(origin, siblings[currentIndex + 1].point);
  }
  if (currentIndex > 0 && siblings[currentIndex - 1]) {
    return bearingDegreesBetween(siblings[currentIndex - 1].point, origin);
  }
  return 0;
}

function clampMapValue(value, min, max) {
  return Math.max(min, Math.min(max, Number(value)));
}

function angleDeltaDegrees(a, b) {
  return Math.abs(((Number(a) - Number(b) + 540) % 360) - 180);
}

function panoMapAnnotationStyle(annotation, active = false) {
  const shape = panoAnnotationShape(annotation);
  const kind = shape.kind || "point";
  const role = panoAnnotationMeasurementRole(annotation);
  const meta = panoAnnotationMeasurementMeta(annotation);
  const source = String(meta.source || "").toLowerCase();
  if (active) {
    return { color: "#22d3ee", fillColor: "#22d3ee", weight: 3, fillOpacity: kind === "line" ? 0 : 0.2 };
  }
  if (source.includes("draft")) {
    return { color: "#10b981", fillColor: "#10b981", weight: 2, fillOpacity: kind === "line" ? 0 : 0.18, dashArray: "5 5" };
  }
  if (role === "calibration") {
    return { color: "#eab308", fillColor: "#eab308", weight: 2, fillOpacity: kind === "line" ? 0 : 0.14 };
  }
  if (role === "measurement" || meta.showMeasurement) {
    return { color: "#0ea5e9", fillColor: "#0ea5e9", weight: 2, fillOpacity: kind === "line" ? 0 : 0.14 };
  }
  if (kind === "sam") {
    return { color: "#7c3aed", fillColor: "#7c3aed", weight: 2, fillOpacity: 0.16 };
  }
  return { color: "#f97316", fillColor: "#f97316", weight: 2, fillOpacity: kind === "line" ? 0 : 0.14 };
}

function panoAnnotationMapProjection(annotation, { detail, panoViewCalibration, selectedCalibration } = {}) {
  if (!annotation || !detail || !Array.isArray(detail.panoFeatures)) {
    return null;
  }
  const shape = panoAnnotationShape(annotation);
  const points = Array.isArray(shape.points)
    ? shape.points.map(panoAnnotationSphericalPoint).filter(Boolean)
    : [];
  if (!points.length) {
    return null;
  }
  const selectedPanoKey = panoKeyFromImageFile(annotation.imageFile);
  const activeFeature = detail.panoFeatures.find((feature) => selectedPanoKey && selectedPanoKey === panoKeyFromFeature(feature));
  const origin = panoFeatureLatLng(activeFeature);
  if (!origin) {
    return null;
  }

  const viewCalibration = sanitizePanoViewCalibration(panoViewCalibration);
  const baseHeading = normalizeDegrees(panoFeatureHeading(activeFeature, detail.panoFeatures) + viewCalibration.headingOffsetDeg);
  const meta = panoAnnotationMeasurementMeta(annotation);
  const scale = Number(meta.metersPerRadian) || panoCalibrationScale(selectedCalibration);
  const defaultRange = Number.isFinite(viewCalibration.rangeMeters)
    ? clampMapValue(viewCalibration.rangeMeters * 0.5, 8, 160)
    : 24;
  const bearings = points.map((point) => normalizeDegrees(baseHeading + radiansToDegrees(point.yaw)));

  let rangeMeters = defaultRange;
  const distanceMeters = Number(meta.distanceMeters);
  if (points.length >= 2 && Number.isFinite(distanceMeters) && distanceMeters > 0) {
    const spread = angleDeltaDegrees(bearings[0], bearings[1]);
    if (spread > 0.5) {
      rangeMeters = distanceMeters / (2 * Math.sin(degreesToRadians(spread) / 2));
    } else {
      rangeMeters = Math.max(defaultRange, distanceMeters);
    }
  } else if (points.length >= 2 && Number.isFinite(scale) && scale > 0) {
    const angularSpan = Math.max(...points.slice(1).map((point) => panoAngularDistance(points[0], point)).filter(Number.isFinite), 0);
    const bearingSpread = Math.max(...bearings.slice(1).map((bearing) => angleDeltaDegrees(bearings[0], bearing)), 0);
    if (angularSpan > 0 && bearingSpread > 0.5) {
      rangeMeters = (angularSpan * scale) / (2 * Math.sin(degreesToRadians(bearingSpread) / 2));
    }
  }
  rangeMeters = clampMapValue(rangeMeters, 4, 180);

  const latLngs = bearings.map((bearing) => destinationLatLng(origin, bearing, rangeMeters));
  return {
    origin,
    kind: shape.kind || "point",
    latLngs
  };
}

function MapPanel({
  elementId,
  segments,
  selectedId,
  selectSegment,
  detail = null,
  allSpeedRecords = [],
  currentImage = 0,
  setCurrentImage = null,
  segmentFilterIds = null,
  showSegmentTooltips = true,
  panoMapAnnotations = [],
  activePanoAnnotationId = null,
  panoMapCalibration = null,
  panoViewState = null,
  panoViewCalibration = null,
  baseLayerId = "googleRoad",
  onBaseLayerChange = null,
  showBaseControl = true,
  mapMeasureActive: controlledMapMeasureActive = null,
  onMapMeasureActiveChange = null,
  showMapMeasureControl = true,
  mapArcMeasureActive: controlledMapArcMeasureActive = null,
  onMapArcMeasureActiveChange = null,
  mapArcMeasureClearToken = 0,
  showMapArcMeasureControl = true,
  panoCandidatePoints = [],
  selectedPanoCandidateIds = [],
  onTogglePanoCandidate = null,
  gisAssets = []
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const panoLayerRef = useRef(null);
  const panoCandidateLayerRef = useRef(null);
  const gisLayerRef = useRef(null);
  const speedLayerRef = useRef(null);
  const baseControlRef = useRef(null);
  const baseLayersRef = useRef({});
  const selectedBaseLayerIdRef = useRef(normalizedMapBaseLayerId(baseLayerId));
  const mapMeasureLayerRef = useRef(null);
  const mapArcMeasureLayerRef = useRef(null);
  const panoFovLayerRef = useRef(null);
  const panoAnnotationMapLayerRef = useRef(null);
  const onTogglePanoCandidateRef = useRef(onTogglePanoCandidate);
  const mapMeasureActiveRef = useRef(false);
  const mapArcMeasureActiveRef = useRef(false);
  const mapMeasurePointsRef = useRef([]);
  const mapArcMeasurePointsRef = useRef([]);
  const [internalMapMeasureActive, setInternalMapMeasureActive] = useState(false);
  const [internalMapArcMeasureActive, setInternalMapArcMeasureActive] = useState(false);
  const [mapMeasurePoints, setMapMeasurePoints] = useState([]);
  const [mapMeasureDistance, setMapMeasureDistance] = useState(null);
  const [mapArcMeasurePoints, setMapArcMeasurePoints] = useState([]);
  const [mapArcMeasureResult, setMapArcMeasureResult] = useState(null);
  const [mapArcMeasureError, setMapArcMeasureError] = useState("");
  const isMapMeasureControlled = controlledMapMeasureActive !== null && controlledMapMeasureActive !== undefined;
  const mapMeasureActive = isMapMeasureControlled ? Boolean(controlledMapMeasureActive) : internalMapMeasureActive;
  const isMapArcMeasureControlled = controlledMapArcMeasureActive !== null && controlledMapArcMeasureActive !== undefined;
  const mapArcMeasureActive = isMapArcMeasureControlled ? Boolean(controlledMapArcMeasureActive) : internalMapArcMeasureActive;
  const setMapMeasureActiveValue = (nextValue) => {
    const next = typeof nextValue === "function" ? Boolean(nextValue(mapMeasureActive)) : Boolean(nextValue);
    if (isMapMeasureControlled) {
      if (typeof onMapMeasureActiveChange === "function") {
        onMapMeasureActiveChange(next);
      }
      return;
    }
    setInternalMapMeasureActive(next);
  };
  const setMapArcMeasureActiveValue = (nextValue) => {
    const next = typeof nextValue === "function" ? Boolean(nextValue(mapArcMeasureActive)) : Boolean(nextValue);
    if (next) {
      setMapMeasureActiveValue(false);
    }
    if (isMapArcMeasureControlled) {
      if (typeof onMapArcMeasureActiveChange === "function") {
        onMapArcMeasureActiveChange(next);
      }
      return;
    }
    setInternalMapArcMeasureActive(next);
  };

  const selectMapSegment = (segmentId) => {
    if (typeof selectSegment === "function") {
      selectSegment(segmentId, { preserveViewMode: true, syncBatchRange: true, source: "map" });
    }
  };

  const clearMapMeasurement = () => {
    setMapMeasurePoints([]);
    setMapMeasureDistance(null);
  };

  const clearMapArcMeasurement = () => {
    setMapArcMeasurePoints([]);
    setMapArcMeasureResult(null);
    setMapArcMeasureError("");
  };

  const clearAllMapMeasurements = () => {
    clearMapMeasurement();
    clearMapArcMeasurement();
  };

  const placeMapMeasurePoint = (latlng) => {
    if (!mapMeasureActiveRef.current || !latlng || !window.L) {
      return false;
    }
    const point = {
      lat: Number(latlng.lat),
      lng: Number(latlng.lng)
    };
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
      return false;
    }

    const currentPoints = mapMeasurePointsRef.current || [];
    const nextPoints = currentPoints.length >= 2 ? [point] : currentPoints.concat(point);
    const nextDistance = nextPoints.length === 2
      ? L.latLng(nextPoints[0].lat, nextPoints[0].lng).distanceTo(L.latLng(nextPoints[1].lat, nextPoints[1].lng))
      : null;
    setMapMeasurePoints(nextPoints);
    setMapMeasureDistance(nextDistance);
    return true;
  };

  const placeMapArcMeasurePoint = (latlng) => {
    if (!mapArcMeasureActiveRef.current || !latlng) {
      return false;
    }
    const point = {
      lat: Number(latlng.lat),
      lng: Number(latlng.lng)
    };
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
      return false;
    }

    const currentPoints = mapArcMeasurePointsRef.current || [];
    const nextPoints = currentPoints.length >= 3 ? [point] : currentPoints.concat(point);
    setMapArcMeasurePoints(nextPoints);
    setMapArcMeasureError("");
    if (nextPoints.length === 3) {
      const nextResult = computeMapArcMeasurement(nextPoints);
      if (nextResult && nextResult.error) {
        setMapArcMeasureResult(null);
        setMapArcMeasureError(nextResult.error);
      } else {
        setMapArcMeasureResult(nextResult);
      }
    } else {
      setMapArcMeasureResult(null);
    }
    return true;
  };

  const copyMapMeasureDistance = () => {
    if (!Number.isFinite(Number(mapMeasureDistance)) || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(Number(mapMeasureDistance).toFixed(2)).catch(() => {});
  };

  const handleMapMeasureEvent = (event) => {
    const handled = placeMapArcMeasurePoint(event && event.latlng) || placeMapMeasurePoint(event && event.latlng);
    if (handled && event && event.originalEvent && window.L && L.DomEvent) {
      L.DomEvent.stop(event.originalEvent);
    }
    return handled;
  };

  const mapMeasureStatus = Number.isFinite(Number(mapMeasureDistance))
    ? formatMapMeasureDistance(mapMeasureDistance)
    : mapMeasurePoints.length
    ? "1/2 points"
    : "Click two points";
  const mapArcMeasureStatus = mapArcMeasureError
    ? "Invalid arc"
    : mapArcMeasureResult && Number.isFinite(Number(mapArcMeasureResult.radiusMeters))
    ? `R ${formatMapMeasureDistance(mapArcMeasureResult.radiusMeters)}`
    : mapArcMeasurePoints.length
    ? `${mapArcMeasurePoints.length}/3 points`
    : "Click three points";
  const segmentFilterKey = Array.isArray(segmentFilterIds)
    ? segmentFilterIds
        .map((segmentId) => Number(segmentId))
        .filter((segmentId) => Number.isFinite(segmentId))
        .join("|")
    : "";
  const panoCandidateSelectionKey = Array.isArray(selectedPanoCandidateIds)
    ? selectedPanoCandidateIds.map((candidateId) => String(candidateId)).join("|")
    : "";
  const gisAssetsKey = Array.isArray(gisAssets)
    ? gisAssets.map((asset) => `${gisAssetId(asset)}:${asset.classId || ""}:${asset.assetSubTypeId || ""}`).join("|")
    : "";
  const segmentFilterSet = useMemo(() => {
    if (!Array.isArray(segmentFilterIds)) {
      return null;
    }
    const ids = segmentFilterIds
      .map((segmentId) => Number(segmentId))
      .filter((segmentId) => Number.isFinite(segmentId));
    return new Set(ids);
  }, [segmentFilterKey]);
  const mapSegments = useMemo(() => {
    if (!segments || !Array.isArray(segments.features) || !segmentFilterSet) {
      return segments;
    }
    return {
      ...segments,
      features: segments.features.filter((feature) => {
        const props = (feature && feature.properties) || {};
        const segmentId = Number(props.SegmentId ?? props.ID);
        return segmentFilterSet.has(segmentId);
      })
    };
  }, [segments, segmentFilterSet]);

  useEffect(() => {
    mapMeasureActiveRef.current = mapMeasureActive;
    const container = mapRef.current && mapRef.current.getContainer ? mapRef.current.getContainer() : null;
    if (container) {
      container.classList.toggle("is-map-measuring", mapMeasureActive || mapArcMeasureActive);
      container.classList.toggle("is-map-arc-measuring", mapArcMeasureActive);
    }
  }, [mapMeasureActive, mapArcMeasureActive]);

  useEffect(() => {
    mapArcMeasureActiveRef.current = mapArcMeasureActive;
  }, [mapArcMeasureActive]);

  useEffect(() => {
    mapMeasurePointsRef.current = mapMeasurePoints;
  }, [mapMeasurePoints]);

  useEffect(() => {
    mapArcMeasurePointsRef.current = mapArcMeasurePoints;
  }, [mapArcMeasurePoints]);

  useEffect(() => {
    clearMapArcMeasurement();
  }, [mapArcMeasureClearToken]);

  useEffect(() => {
    onTogglePanoCandidateRef.current = onTogglePanoCandidate;
  }, [onTogglePanoCandidate]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.__irapHandleMapMeasurementClick = handleMapMeasureEvent;
    }
  });

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.L) {
      return;
    }

    if (mapMeasureLayerRef.current) {
      mapMeasureLayerRef.current.remove();
      mapMeasureLayerRef.current = null;
    }
    if (!mapMeasurePoints.length) {
      return;
    }

    const layerGroup = L.layerGroup();
    mapMeasurePoints.forEach((point, index) => {
      L.circleMarker([point.lat, point.lng], {
        className: "map-measure-point",
        color: "#0f766e",
        fillColor: "#ffffff",
        fillOpacity: 1,
        opacity: 1,
        radius: 5,
        weight: 2
      })
        .bindTooltip(index === 0 ? "Start" : "End", { direction: "top", opacity: 0.92 })
        .addTo(layerGroup);
    });

    if (mapMeasurePoints.length === 2) {
      const [start, end] = mapMeasurePoints;
      const label = formatMapMeasureDistance(mapMeasureDistance);
      const midpoint = {
        lat: (start.lat + end.lat) / 2,
        lng: (start.lng + end.lng) / 2
      };
      L.polyline([[start.lat, start.lng], [end.lat, end.lng]], {
        className: "map-measure-line",
        color: "#0f766e",
        opacity: 0.95,
        weight: 4
      }).addTo(layerGroup);
      L.marker([midpoint.lat, midpoint.lng], {
        interactive: false,
        icon: L.divIcon({
          className: "map-measure-label",
          html: `<span>${escapeHtml(label)}</span>`
        })
      }).addTo(layerGroup);
    }

    mapMeasureLayerRef.current = layerGroup.addTo(map);
  }, [mapMeasurePoints, mapMeasureDistance]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.L) {
      return;
    }

    if (mapArcMeasureLayerRef.current) {
      mapArcMeasureLayerRef.current.remove();
      mapArcMeasureLayerRef.current = null;
    }
    if (!mapArcMeasurePoints.length) {
      return;
    }

    const layerGroup = L.layerGroup();
    mapArcMeasurePoints.forEach((point, index) => {
      L.circleMarker([point.lat, point.lng], {
        className: "map-arc-measure-point",
        color: "#7c3aed",
        fillColor: "#ffffff",
        fillOpacity: 1,
        opacity: 1,
        radius: 6,
        weight: 2
      })
        .bindTooltip(`P${index + 1}`, { direction: "top", opacity: 0.92 })
        .addTo(layerGroup);
    });

    if (mapArcMeasureResult && mapArcMeasureResult.center) {
      const center = mapArcMeasureResult.center;
      const radiusPoint = mapArcMeasureResult.radiusPoint || mapArcMeasurePoints[1];
      const label = `R = ${formatMapMeasureDistance(mapArcMeasureResult.radiusMeters)}`;
      L.circle([center.lat, center.lng], {
        className: "map-arc-circle",
        color: "#7c3aed",
        dashArray: "8 6",
        fill: false,
        interactive: false,
        opacity: 0.55,
        radius: mapArcMeasureResult.radiusMeters,
        weight: 2
      }).addTo(layerGroup);
      L.polyline(mapArcMeasureResult.arcLatLngs.map((point) => [point.lat, point.lng]), {
        className: "map-arc-line",
        color: "#db6b20",
        interactive: false,
        opacity: 0.98,
        weight: 5
      }).addTo(layerGroup);
      L.polyline([[center.lat, center.lng], [radiusPoint.lat, radiusPoint.lng]], {
        className: "map-arc-radius-line",
        color: "#1d4ed8",
        dashArray: "5 5",
        interactive: false,
        opacity: 0.92,
        weight: 3
      }).addTo(layerGroup);
      L.circleMarker([center.lat, center.lng], {
        className: "map-arc-center-point",
        color: "#1d4ed8",
        fillColor: "#ffffff",
        fillOpacity: 1,
        interactive: false,
        opacity: 1,
        radius: 5,
        weight: 2
      }).addTo(layerGroup);
      L.marker([(center.lat + radiusPoint.lat) / 2, (center.lng + radiusPoint.lng) / 2], {
        interactive: false,
        icon: L.divIcon({
          className: "map-arc-radius-label",
          html: `<span>${escapeHtml(label)}</span>`
        })
      }).addTo(layerGroup);
    } else if (mapArcMeasureError && mapArcMeasurePoints[2]) {
      L.marker([mapArcMeasurePoints[2].lat, mapArcMeasurePoints[2].lng], {
        interactive: false,
        icon: L.divIcon({
          className: "map-arc-radius-label is-error",
          html: `<span>${escapeHtml(mapArcMeasureError)}</span>`
        })
      }).addTo(layerGroup);
    }

    mapArcMeasureLayerRef.current = layerGroup.addTo(map);
    if (typeof layerGroup.bringToFront === "function") {
      layerGroup.bringToFront();
    }
  }, [mapArcMeasurePoints, mapArcMeasureResult, mapArcMeasureError]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !window.ResizeObserver) {
      return;
    }

    let frameId = null;
    const observer = new ResizeObserver(() => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        if (mapRef.current) {
          mapRef.current.invalidateSize();
        }
      });
    });

    observer.observe(container);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!mapSegments || !window.L) {
      return;
    }

    if (window.L.DomEvent && typeof window.L.DomEvent.fakeStop !== "function") {
      window.L.DomEvent.fakeStop = (event) => {
        if (window.L.DomEvent && typeof window.L.DomEvent.stop === "function") {
          window.L.DomEvent.stop(event);
        }
      };
    }

    if (!mapRef.current) {
      mapRef.current = L.map(elementId, {
        zoomControl: true,
        attributionControl: false,
        maxZoom: SPECS_MAP_MAX_ZOOM
      }).setView([-37.9233, 145.0251], 14);

      baseLayersRef.current = createLeafletBaseLayers(L);
      const initialBaseLayerId = normalizedMapBaseLayerId(baseLayerId);
      selectedBaseLayerIdRef.current = initialBaseLayerId;
      (baseLayersRef.current[initialBaseLayerId] || baseLayersRef.current.googleRoad).addTo(mapRef.current);
    }

    if (showBaseControl && !baseControlRef.current && Object.keys(baseLayersRef.current).length) {
      baseControlRef.current = L.control.layers(
        leafletBaseLayerControlItems(baseLayersRef.current),
        {},
        {
          collapsed: false,
          position: "topright"
        }
      ).addTo(mapRef.current);
    } else if (!showBaseControl && baseControlRef.current) {
      baseControlRef.current.remove();
      baseControlRef.current = null;
    }

    if (baseControlRef.current && baseControlRef.current.getContainer()) {
      baseControlRef.current.getContainer().setAttribute("aria-label", "Map base layers");
    }

    if (layerRef.current) {
      layerRef.current.remove();
    }
    if (panoLayerRef.current) {
      panoLayerRef.current.remove();
      panoLayerRef.current = null;
    }
    if (speedLayerRef.current) {
      speedLayerRef.current.remove();
      speedLayerRef.current = null;
    }

    const segmentStyle = (props = {}) => {
      const id = Number(props.SegmentId || props.ID);
      const active = id === Number(selectedId);
      return {
        className: `segment-map-shape ${active ? "active" : ""}`,
        color: active ? "#ff2d55" : "#7c3aed",
        fillColor: active ? "#ff2d55" : "#a855f7",
        fillOpacity: active ? 0.28 : 0.12,
        lineCap: "round",
        lineJoin: "round",
        opacity: 1,
        fill: true,
        weight: active ? 7 : 4
      };
    };

    const useVectorGrid = false;
    if (useVectorGrid && L.vectorGrid && typeof L.vectorGrid.protobuf === "function") {
      layerRef.current = L.vectorGrid.protobuf("/api/segments/tiles/{z}/{x}/{y}.pbf", {
        rendererFactory: L.canvas.tile,
        interactive: true,
        getFeatureId: (feature) => Number(feature.properties && (feature.properties.SegmentId || feature.properties.ID)),
        vectorTileLayerStyles: {
          segments: (properties) => segmentStyle(properties)
        }
      }).addTo(mapRef.current);

      layerRef.current.on("click", (event) => {
        if (mapRef.current.__irapHandleMapMeasurementClick && mapRef.current.__irapHandleMapMeasurementClick(event)) {
          return;
        }
        const props = (event.layer && event.layer.properties) || event.properties || {};
        const id = Number(props.SegmentId || props.ID);
        if (Number.isFinite(id)) {
          selectMapSegment(id);
        }
      });
      layerRef.current.on("mouseover", (event) => {
        const props = (event.layer && event.layer.properties) || event.properties || {};
        const id = Number(props.SegmentId || props.ID);
        if (Number.isFinite(id)) {
          mapRef.current.getContainer().title = showSegmentTooltips ? `Segment ${id}: ${props.Road_name || ""}` : "";
          mapRef.current.getContainer().style.cursor = "pointer";
        }
      });
      layerRef.current.on("mouseout", () => {
        mapRef.current.getContainer().title = "";
        mapRef.current.getContainer().style.cursor = "";
      });
    } else {
      layerRef.current = L.geoJSON(mapSegments, {
        style: (feature) => segmentStyle(feature.properties),
        onEachFeature: (feature, layer) => {
          const id = Number(feature.properties.SegmentId);
          layer.on("click", (event) => {
            if (mapRef.current.__irapHandleMapMeasurementClick && mapRef.current.__irapHandleMapMeasurementClick(event)) {
              return;
            }
            selectMapSegment(id);
          });
          layer.on("mouseover", () => {
            mapRef.current.getContainer().style.cursor = "pointer";
          });
          layer.on("mouseout", () => {
            mapRef.current.getContainer().style.cursor = "";
          });
          if (showSegmentTooltips) {
            layer.bindTooltip(`Segment ${id}: ${feature.properties.Road_name || ""}`, {
              sticky: true
            });
          }
        }
      }).addTo(mapRef.current);

      layerRef.current.eachLayer((layer) => {
        const props = (layer && layer.feature && layer.feature.properties) || {};
        const id = Number(props.SegmentId || props.ID);
        if (id === Number(selectedId) && typeof layer.bringToFront === "function") {
          layer.bringToFront();
        }
      });
    }

    if (mapRef.current.__irapMapFallbackClick) {
      mapRef.current.off("click", mapRef.current.__irapMapFallbackClick);
    }
    mapRef.current.__irapMapFallbackClick = (event) => {
      if (mapRef.current.__irapHandleMapMeasurementClick && mapRef.current.__irapHandleMapMeasurementClick(event)) {
        return;
      }
      const hit = nearestSegmentFeatureForMapClick(mapRef.current, mapSegments, event.latlng);
      if (hit && Number.isFinite(hit.id)) {
        selectMapSegment(hit.id);
      }
    };
    mapRef.current.on("click", mapRef.current.__irapMapFallbackClick);

    const panoFeatures = detail && Number(detail.id) === Number(selectedId)
      ? detail.panoFeatures || []
      : [];
    const selectedSpeedRecords = detail && Number(detail.id) === Number(selectedId)
      ? detail.speedRecords || []
      : [];
    const rawVisibleSpeedRecords = allSpeedRecords.length ? allSpeedRecords : selectedSpeedRecords;
    const visibleSpeedRecords = segmentFilterSet
      ? rawVisibleSpeedRecords.filter((record) => segmentFilterSet.has(Number(record.segmentId)))
      : rawVisibleSpeedRecords;
    const selectedImage = detail && Array.isArray(detail.images) ? detail.images[currentImage] : null;
    const selectedPanoKey = selectedImage ? panoKeyFromImageFile(selectedImage.file) : null;
    let panoBounds = null;
    let selectedSpeedBounds = null;

    if (panoFeatures.length) {
      const panoCollection = {
        type: "FeatureCollection",
        features: panoFeatures
      };

      panoLayerRef.current = L.geoJSON(panoCollection, {
        pointToLayer: (feature, latlng) => {
          const isActive = selectedPanoKey && selectedPanoKey === panoKeyFromFeature(feature);
          return L.circleMarker(latlng, {
            className: `pano-point-marker ${isActive ? "active" : ""}`,
            color: isActive ? "#0b7f47" : "#f97316",
            fillColor: isActive ? "#13a060" : "#fb923c",
            fillOpacity: isActive ? 0.95 : 0.8,
            opacity: 1,
            radius: isActive ? 7 : 5,
            weight: isActive ? 3 : 2
          });
        },
        onEachFeature: (feature, layer) => {
          const props = feature.properties || {};
          const imageIndex = findImageIndexForPanoFeature(detail, feature);
          const title = `Pano point${props.FID ? ` FID ${props.FID}` : ""}`;
          if (showSegmentTooltips) {
            layer.bindTooltip(title, {
              className: "pano-point-tooltip",
              sticky: true
            });
          }
          layer.bindPopup(
            `<strong>${escapeHtml(title)}</strong><br>${escapeHtml(props.VideoName || "")}${imageIndex >= 0 ? `<br>Image ${imageIndex + 1}` : ""}`
          );
          layer.on("click", (event) => {
            if (mapRef.current.__irapHandleMapMeasurementClick && mapRef.current.__irapHandleMapMeasurementClick(event)) {
              return;
            }
            if (imageIndex >= 0 && typeof setCurrentImage === "function") {
              setCurrentImage(imageIndex);
            }
          });
        }
      }).addTo(mapRef.current);

      panoBounds = panoLayerRef.current.getBounds();
    }

    const speedMarkers = visibleSpeedRecords
      .filter((record) => Number.isFinite(Number(record.lat)) && Number.isFinite(Number(record.lng)))
      .map((record) => {
        const isSelectedSpeed = Number(record.segmentId) === Number(selectedId);
        const label = record.label || (record.speedKmh ? `${record.speedKmh} km/h Speed Limit` : "Speed point");
        const marker = L.circleMarker([Number(record.lat), Number(record.lng)], {
          className: `speed-point-marker ${isSelectedSpeed ? "selected" : ""}`,
          color: isSelectedSpeed ? "#7f1d1d" : "#c2410c",
          fillColor: isSelectedSpeed ? "#ef4444" : "#fb923c",
          fillOpacity: isSelectedSpeed ? 1 : 0.78,
          opacity: 1,
          radius: isSelectedSpeed ? 8 : 5,
          weight: isSelectedSpeed ? 3 : 2
        });
        if (showSegmentTooltips) {
          marker.bindTooltip(label, {
            className: "speed-point-tooltip",
            sticky: true
          });
        }
        marker.bindPopup(
          `<strong>${escapeHtml(label)}</strong><br>Speed.csv point${record.sourceCode ? `<br>Source code: ${escapeHtml(record.sourceCode)}` : ""}${record.segmentId ? `<br>Segment ${escapeHtml(record.segmentId)}` : ""}`
        );
        marker.on("click", (event) => {
          if (mapRef.current.__irapHandleMapMeasurementClick && mapRef.current.__irapHandleMapMeasurementClick(event)) {
            return;
          }
        });
        return marker;
      });

    if (speedMarkers.length) {
      speedLayerRef.current = L.featureGroup(speedMarkers).addTo(mapRef.current);
      speedLayerRef.current.bringToFront();
    }

    const selectedSpeedRecordsForBounds = visibleSpeedRecords.filter(
      (record) => Number(record.segmentId) === Number(selectedId)
    );
    const selectedSpeedLatLngs = (selectedSpeedRecordsForBounds.length ? selectedSpeedRecordsForBounds : selectedSpeedRecords)
      .filter((record) => Number.isFinite(Number(record.lat)) && Number.isFinite(Number(record.lng)))
      .map((record) => [Number(record.lat), Number(record.lng)]);
    if (selectedSpeedLatLngs.length) {
      selectedSpeedBounds = L.latLngBounds(selectedSpeedLatLngs);
    }

    if (!selectedId) {
      const bounds = L.geoJSON(mapSegments).getBounds();
      if (bounds.isValid()) {
        mapRef.current.fitBounds(bounds, { padding: [16, 16] });
      }
    } else {
      const selected = (mapSegments.features || []).find(
        (feature) => Number(feature.properties.SegmentId) === Number(selectedId)
      );
      const selectedBounds = selected ? L.geoJSON(selected).getBounds() : null;
      if (selectedBounds && selectedBounds.isValid()) {
        if (panoBounds && panoBounds.isValid()) {
          selectedBounds.extend(panoBounds);
        }
        if (selectedSpeedBounds && selectedSpeedBounds.isValid()) {
          selectedBounds.extend(selectedSpeedBounds);
        }
        mapRef.current.fitBounds(selectedBounds, { maxZoom: 17, padding: [24, 24] });
      } else if (selected && selected.properties.__centroid) {
        mapRef.current.setView([selected.properties.__centroid.lat, selected.properties.__centroid.lng], 15);
      }
    }

    setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 60);
  }, [mapSegments, selectedId, elementId, detail, allSpeedRecords, currentImage, showBaseControl, segmentFilterSet, showSegmentTooltips]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.L) {
      return undefined;
    }

    if (panoCandidateLayerRef.current) {
      panoCandidateLayerRef.current.remove();
      panoCandidateLayerRef.current = null;
    }

    const candidates = Array.isArray(panoCandidatePoints) ? panoCandidatePoints.filter(Boolean) : [];
    if (!candidates.length) {
      return undefined;
    }

    const selectedSet = new Set(
      (Array.isArray(selectedPanoCandidateIds) ? selectedPanoCandidateIds : []).map((candidateId) => String(candidateId))
    );
    const candidateById = new Map();
    const features = candidates
      .map((candidate) => {
        const id = panoCandidateId(candidate);
        if (id) {
          candidateById.set(id, candidate);
        }
        return panoCandidateFeature(candidate);
      })
      .filter((feature) => feature && feature.geometry && Array.isArray(feature.geometry.coordinates));

    if (!features.length) {
      return undefined;
    }

    const layerGroup = L.geoJSON(
      {
        type: "FeatureCollection",
        features
      },
      {
        pointToLayer: (feature, latlng) => {
          const props = feature.properties || {};
          const candidate = candidateById.get(String(props.__candidateId || ""));
          const selected = panoCandidateIsSelected(candidate, selectedSet);
          return L.circleMarker(latlng, {
            className: `pano-candidate-marker ${selected ? "selected" : ""}`,
            color: selected ? "#0369a1" : "#2563eb",
            fillColor: selected ? "#38bdf8" : "#93c5fd",
            fillOpacity: selected ? 0.96 : 0.72,
            opacity: 1,
            radius: selected ? 7 : 5,
            weight: selected ? 3 : 2
          });
        },
        onEachFeature: (feature, layer) => {
          const props = feature.properties || {};
          const candidateId = String(props.__candidateId || "");
          const candidate = candidateById.get(candidateId);
          const selected = panoCandidateIsSelected(candidate, selectedSet);
          const fid = props.FID ?? props._target_fid ?? (candidate && candidate.fid);
          const title = `AssetAi pano${fid ? ` FID ${fid}` : ""}`;
          const fileLabel = panoCandidateFileLabel(candidate) || props.FileName || props.VideoName || "";
          if (showSegmentTooltips) {
            layer.bindTooltip(`${title}${selected ? " - selected" : ""}`, {
              className: "pano-candidate-tooltip",
              sticky: true
            });
          }
          layer.bindPopup(
            `<strong>${escapeHtml(title)}</strong><br>${escapeHtml(fileLabel)}${selected ? "<br>Selected" : ""}`
          );
          layer.on("click", (event) => {
            if (mapRef.current.__irapHandleMapMeasurementClick && mapRef.current.__irapHandleMapMeasurementClick(event)) {
              return;
            }
            if (event && event.originalEvent && window.L && L.DomEvent) {
              L.DomEvent.stop(event.originalEvent);
            }
            const handler = onTogglePanoCandidateRef.current;
            if (candidate && typeof handler === "function") {
              handler(candidate);
            }
          });
          layer.on("mouseover", () => {
            map.getContainer().style.cursor = "pointer";
          });
          layer.on("mouseout", () => {
            map.getContainer().style.cursor = "";
          });
        }
      }
    ).addTo(map);

    panoCandidateLayerRef.current = layerGroup;
    if (typeof layerGroup.bringToFront === "function") {
      layerGroup.bringToFront();
    }

    return () => {
      if (panoCandidateLayerRef.current === layerGroup) {
        layerGroup.remove();
        panoCandidateLayerRef.current = null;
      }
    };
  }, [mapSegments, panoCandidatePoints, panoCandidateSelectionKey, showSegmentTooltips]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.L) {
      return undefined;
    }
    if (gisLayerRef.current) {
      gisLayerRef.current.remove();
      gisLayerRef.current = null;
    }
    const features = (Array.isArray(gisAssets) ? gisAssets : [])
      .map(gisFeatureFromAsset)
      .filter((feature) => feature && feature.geometry);
    if (!features.length) {
      return undefined;
    }

    const layerGroup = L.geoJSON(
      {
        type: "FeatureCollection",
        features
      },
      {
        style: (feature) => {
          const props = (feature && feature.properties) || {};
          const geometryType = String((feature && feature.geometry && feature.geometry.type) || props.geometryType || "").toLowerCase();
          const isPolygon = geometryType.includes("polygon");
          const color = String(props.lineColor || "").trim() || gisLayerColor(props.classId || props.className || props.assetType);
          const fillColor = String(props.fillColor || "").trim() || color;
          return {
            className: `gis-asset-shape ${isPolygon ? "gis-asset-polygon" : ""}`,
            color,
            fillColor,
            fillOpacity: isPolygon ? 0.1 : 0.18,
            opacity: 0.98,
            weight: isPolygon ? 2.5 : 4,
            dashArray: isPolygon ? "6 4" : undefined,
            lineCap: isPolygon ? "butt" : "round",
            lineJoin: "round"
          };
        },
        pointToLayer: (feature, latlng) => {
          const props = (feature && feature.properties) || {};
          const color = gisLayerColor(props.classId || props.className || props.assetType);
          return L.circleMarker(latlng, {
            className: "gis-asset-marker",
            color: "#ffffff",
            fillColor: color,
            fillOpacity: 0.95,
            opacity: 1,
            radius: 6,
            weight: 2
          });
        },
        onEachFeature: (feature, layer) => {
          const props = (feature && feature.properties) || {};
          const title = props.assetName || props.className || `Asset ${props.assetId || ""}` || "GIS asset";
          const meta = [props.className, props.assetType, props.assetSubType].filter(Boolean).join(" - ");
          if (showSegmentTooltips) {
            layer.bindTooltip(title, {
              className: "gis-asset-tooltip",
              sticky: true
            });
          }
          layer.bindPopup(
            `<strong>${escapeHtml(title)}</strong>${meta ? `<br>${escapeHtml(meta)}` : ""}${props.assetId ? `<br>Asset ${escapeHtml(props.assetId)}` : ""}`
          );
          layer.on("click", (event) => {
            if (event && event.originalEvent && window.L && L.DomEvent) {
              L.DomEvent.stop(event.originalEvent);
            }
          });
          layer.on("mouseover", () => {
            map.getContainer().style.cursor = "pointer";
          });
          layer.on("mouseout", () => {
            map.getContainer().style.cursor = "";
          });
        }
      }
    ).addTo(map);

    gisLayerRef.current = layerGroup;
    if (typeof layerGroup.bringToFront === "function") {
      layerGroup.bringToFront();
    }

    return () => {
      if (gisLayerRef.current === layerGroup) {
        layerGroup.remove();
        gisLayerRef.current = null;
      }
    };
  }, [gisAssetsKey, showSegmentTooltips]);

  useEffect(() => {
    const map = mapRef.current;
    const baseLayers = baseLayersRef.current;
    if (!map || !baseLayers || !Object.keys(baseLayers).length) {
      return;
    }
    const nextBaseLayerId = normalizedMapBaseLayerId(baseLayerId);
    const nextLayer = baseLayers[nextBaseLayerId] || baseLayers.googleRoad;
    if (!nextLayer || (selectedBaseLayerIdRef.current === nextBaseLayerId && map.hasLayer(nextLayer))) {
      return;
    }
    Object.values(baseLayers).forEach((layer) => {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });
    nextLayer.addTo(map);
    selectedBaseLayerIdRef.current = nextBaseLayerId;
    if (typeof onBaseLayerChange === "function" && nextBaseLayerId !== baseLayerId) {
      onBaseLayerChange(nextBaseLayerId);
    }
  }, [baseLayerId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.L) {
      return undefined;
    }
    if (panoAnnotationMapLayerRef.current) {
      panoAnnotationMapLayerRef.current.remove();
      panoAnnotationMapLayerRef.current = null;
    }

    const annotations = (panoMapAnnotations || []).filter(Boolean);
    if (!annotations.length || !detail) {
      return undefined;
    }

    const layerGroup = L.layerGroup();
    annotations.forEach((annotation) => {
      const projection = panoAnnotationMapProjection(annotation, {
        detail,
        panoViewCalibration,
        selectedCalibration: panoMapCalibration
      });
      if (!projection || !projection.latLngs.length) {
        return;
      }
      const active = String(activePanoAnnotationId || "") === String(annotation.id || "");
      const style = panoMapAnnotationStyle(annotation, active);
      const latLngs = projection.latLngs.map((point) => [point.lat, point.lng]);
      const tooltip = panoMeasurementAnnotationLabel(annotation);
      let layer = null;
      if (projection.kind === "point" || latLngs.length === 1) {
        layer = L.circleMarker(latLngs[0], {
          className: "pano-map-annotation-point",
          color: style.color,
          fillColor: style.fillColor,
          fillOpacity: active ? 0.92 : 0.76,
          opacity: 0.96,
          radius: active ? 7 : 5,
          weight: active ? Math.max(2, style.weight) : style.weight
        });
      } else if ((projection.kind === "polygon" || projection.kind === "sam") && latLngs.length >= 3) {
        layer = L.polygon(latLngs, {
          className: "pano-map-annotation-shape",
          color: style.color,
          dashArray: style.dashArray,
          fillColor: style.fillColor,
          fillOpacity: style.fillOpacity,
          opacity: 0.96,
          weight: style.weight
        });
      } else if (latLngs.length >= 2) {
        layer = L.polyline(latLngs, {
          className: "pano-map-annotation-line",
          color: style.color,
          dashArray: style.dashArray,
          opacity: 0.96,
          weight: style.weight
        });
      }
      if (!layer) {
        return;
      }
      if (showSegmentTooltips) {
        layer.bindTooltip(tooltip, {
          className: "pano-map-annotation-tooltip",
          sticky: true
        });
      }
      layer.addTo(layerGroup);
    });

    if (!layerGroup.getLayers().length) {
      return undefined;
    }
    panoAnnotationMapLayerRef.current = layerGroup.addTo(map);
    if (typeof layerGroup.bringToFront === "function") {
      layerGroup.bringToFront();
    }
    return () => {
      if (panoAnnotationMapLayerRef.current === layerGroup) {
        layerGroup.remove();
        panoAnnotationMapLayerRef.current = null;
      }
    };
  }, [mapSegments, detail, currentImage, panoMapAnnotations, activePanoAnnotationId, panoMapCalibration, panoViewCalibration, showSegmentTooltips]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.L) {
      return undefined;
    }

    if (panoFovLayerRef.current) {
      panoFovLayerRef.current.remove();
      panoFovLayerRef.current = null;
    }

    const selectedImage = detail && Array.isArray(detail.images) ? detail.images[currentImage] : null;
    const selectedPanoKey = selectedImage ? panoKeyFromImageFile(selectedImage.file) : "";
    const panoFeatures = detail && Number(detail.id) === Number(selectedId)
      ? detail.panoFeatures || []
      : [];
    if (!selectedPanoKey || !panoFeatures.length || !panoViewState) {
      return undefined;
    }

    const activeFeature = panoFeatures.find((feature) => selectedPanoKey === panoKeyFromFeature(feature));
    const origin = panoFeatureLatLng(activeFeature);
    if (!origin) {
      return undefined;
    }

    const viewCalibration = sanitizePanoViewCalibration(panoViewCalibration);
    const yawValue = numericPanoValue(panoViewState.yaw);
    const viewerYawDegrees = Number.isFinite(yawValue) ? radiansToDegrees(yawValue) : 0;
    const centerBearing = normalizeDegrees(
      panoFeatureHeading(activeFeature, panoFeatures) + viewCalibration.headingOffsetDeg + viewerYawDegrees
    );
    const fovDegrees = Number.isFinite(viewCalibration.fovDeg)
      ? viewCalibration.fovDeg
      : normalizePanoFovDegrees(panoViewState.hFov);
    const radiusMeters = Number.isFinite(viewCalibration.rangeMeters)
      ? Math.max(4, viewCalibration.rangeMeters * PANO_FOV_MAP_RANGE_SCALE)
      : PANO_FOV_AUTO_RANGE_METERS;
    const left = destinationLatLng(origin, centerBearing - fovDegrees / 2, radiusMeters);
    const center = destinationLatLng(origin, centerBearing, radiusMeters * 1.12);
    const right = destinationLatLng(origin, centerBearing + fovDegrees / 2, radiusMeters);
    const layerGroup = L.layerGroup();

    L.polygon(
      [
        [origin.lat, origin.lng],
        [left.lat, left.lng],
        [center.lat, center.lng],
        [right.lat, right.lng]
      ],
      {
        className: "pano-fov-cone",
        color: "#2563eb",
        fillColor: "#38bdf8",
        fillOpacity: 0.2,
        interactive: false,
        opacity: 0.78,
        weight: 2
      }
    ).addTo(layerGroup);
    L.polyline([[origin.lat, origin.lng], [center.lat, center.lng]], {
      className: "pano-fov-centerline",
      color: "#1d4ed8",
      dashArray: "4 4",
      interactive: false,
      opacity: 0.9,
      weight: 2
    }).addTo(layerGroup);
    L.circleMarker([origin.lat, origin.lng], {
      className: "pano-fov-origin",
      color: "#1d4ed8",
      fillColor: "#ffffff",
      fillOpacity: 1,
      interactive: false,
      opacity: 1,
      radius: 5,
      weight: 2
    }).addTo(layerGroup);

    panoFovLayerRef.current = layerGroup.addTo(map);
    if (typeof layerGroup.bringToFront === "function") {
      layerGroup.bringToFront();
    }

    return () => {
      if (panoFovLayerRef.current === layerGroup) {
        layerGroup.remove();
        panoFovLayerRef.current = null;
      }
    };
  }, [detail, selectedId, currentImage, panoViewState, panoViewCalibration]);

  return segments ? (
    <div ref={containerRef} className={`map-panel-shell ${mapMeasureActive || mapArcMeasureActive ? "is-measuring" : ""} ${mapArcMeasureActive ? "is-arc-measuring" : ""}`}>
      <div id={elementId} className="leaflet-map"></div>
      {(showMapMeasureControl || showMapArcMeasureControl) && (
      <div className="map-measure-control" aria-label="Map measurement">
        {showMapMeasureControl && (
          <>
            <button
              className={`map-measure-button ${mapMeasureActive ? "active" : ""}`}
              type="button"
              aria-pressed={mapMeasureActive}
              title={mapMeasureActive ? "Stop map measurement" : "Measure distance on map"}
              onClick={() => {
                const next = !mapMeasureActive;
                setMapMeasureActiveValue(next);
                if (next) {
                  setMapArcMeasureActiveValue(false);
                }
              }}
            >
              {icon("ruler")}
              <span>Measure</span>
            </button>
            <span className="map-measure-readout" title="Use this distance as the known scale for calibration">
              {mapMeasureStatus}
            </span>
          </>
        )}
        {showMapArcMeasureControl && (
          <>
            <button
              className={`map-measure-button ${mapArcMeasureActive ? "active" : ""}`}
              type="button"
              aria-pressed={mapArcMeasureActive}
              title={mapArcMeasureActive ? "Stop arc radius tool" : "Arc radius from three map points"}
              onClick={() => setMapArcMeasureActiveValue((current) => !current)}
            >
              {icon("circle-dot")}
              <span>Arc</span>
            </button>
            <span className={`map-measure-readout ${mapArcMeasureError ? "is-error" : ""}`} title="Click three points on an arc to calculate the circle radius">
              {mapArcMeasureStatus}
            </span>
          </>
        )}
        {showMapMeasureControl && Number.isFinite(Number(mapMeasureDistance)) && (
          <button
            className="map-measure-icon-button"
            type="button"
            title="Copy meters"
            onClick={copyMapMeasureDistance}
          >
            {icon("copy")}
          </button>
        )}
        <button
          className="map-measure-icon-button"
          type="button"
          title="Clear measurement drawings"
          disabled={!mapMeasurePoints.length && !mapArcMeasurePoints.length}
          onClick={clearAllMapMeasurements}
        >
          {icon("x")}
        </button>
      </div>
      )}
    </div>
  ) : <div className="map-empty">Loading map...</div>;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function panoKeyFromImageFile(fileName) {
  const text = String(fileName || "").replace(/\.[^.]+$/, "");
  const match = text.match(/^(.*)_(\d+)$/);
  if (!match) {
    return "";
  }
  return `${match[1]}:${match[2]}`;
}

function panoKeyFromFeature(feature) {
  const props = (feature && feature.properties) || {};
  const videoName = String(props.VideoName || props.VideoNameRaw || "").replace(/\.[^.]+$/, "");
  const fid = props.FID ?? props._target_fid;
  if (!videoName || fid === null || fid === undefined || fid === "") {
    return "";
  }
  return `${videoName}:${fid}`;
}

function findImageIndexForPanoFeature(detail, feature) {
  if (!detail || !Array.isArray(detail.images)) {
    return -1;
  }
  const featureKey = panoKeyFromFeature(feature);
  if (!featureKey) {
    return -1;
  }
  return detail.images.findIndex((image) => panoKeyFromImageFile(image.file) === featureKey);
}

function photoSphereMarkersPluginConfig(MarkersPlugin) {
  const config = { markers: [] };
  return MarkersPlugin && typeof MarkersPlugin.withConfig === "function"
    ? MarkersPlugin.withConfig(config)
    : [MarkersPlugin, config];
}

function panoViewerStateSnapshot(viewer) {
  if (!viewer) {
    return null;
  }
  let position = null;
  try {
    position = typeof viewer.getPosition === "function"
      ? viewer.getPosition()
      : viewer.state && viewer.state.position;
  } catch (_error) {
    position = viewer.state && viewer.state.position;
  }
  const yaw = numericPanoValue(position && position.yaw);
  const pitch = numericPanoValue(position && position.pitch);
  if (!Number.isFinite(yaw) || !Number.isFinite(pitch)) {
    return null;
  }
  const state = viewer.state || {};
  const vFov = numericPanoValue(state.vFov);
  let hFov = numericPanoValue(state.hFov);
  if (!Number.isFinite(hFov) && Number.isFinite(vFov) && viewer.dataHelper && typeof viewer.dataHelper.vFovToHFov === "function") {
    hFov = viewer.dataHelper.vFovToHFov(vFov);
  }
  let zoomLevel = null;
  try {
    zoomLevel = typeof viewer.getZoomLevel === "function"
      ? viewer.getZoomLevel()
      : numericPanoValue(state.zoomLevel ?? state.zoom);
  } catch (_error) {
    zoomLevel = numericPanoValue(state.zoomLevel ?? state.zoom);
  }
  return {
    yaw,
    pitch,
    hFov: Number.isFinite(hFov) ? hFov : null,
    vFov: Number.isFinite(vFov) ? vFov : null,
    zoomLevel: Number.isFinite(Number(zoomLevel)) ? Number(zoomLevel) : null
  };
}

function panoViewerStateKey(state) {
  if (!state) {
    return "";
  }
  return [
    state.yaw,
    state.pitch,
    state.hFov,
    state.vFov,
    state.zoomLevel
  ].map((value) => Number.isFinite(Number(value)) ? Number(value).toFixed(4) : "").join("|");
}

function PanoViewer({
  imageUrl,
  overlays = EMPTY_ARRAY,
  annotations = EMPTY_ARRAY,
  activeAnnotationId = null,
  annotationPlacementMode = false,
  suppressActiveAnnotationFocus = false,
  draftAnnotation = null,
  onAnnotationPlacement = null,
  onSelectAnnotation = null,
  onDeleteAnnotation = null,
  onViewChange = null
}) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const markersPluginRef = useRef(null);
  const placementModeRef = useRef(false);
  const onAnnotationPlacementRef = useRef(null);
  const onSelectAnnotationRef = useRef(null);
  const onViewChangeRef = useRef(null);
  const annotationsRef = useRef([]);
  const draftAnnotationRef = useRef(null);
  const updateAnnotationPointsRef = useRef(() => {});
  const updateViewerMarkersRef = useRef(() => {});
  const scheduleMarkerRenderRef = useRef(() => {});
  const markerRefreshTimersRef = useRef([]);
  const navigationTimerRef = useRef(null);
  const lastViewStateKeyRef = useRef("");
  const [fallback, setFallback] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [annotationRenderItems, setAnnotationRenderItems] = useState([]);
  const [draftRenderItem, setDraftRenderItem] = useState(null);

  placementModeRef.current = Boolean(annotationPlacementMode);
  onAnnotationPlacementRef.current = onAnnotationPlacement;
  onSelectAnnotationRef.current = onSelectAnnotation;
  onViewChangeRef.current = onViewChange;
  annotationsRef.current = annotations;
  draftAnnotationRef.current = draftAnnotation;

  const emitPanoViewChange = (snapshot) => {
    if (typeof onViewChangeRef.current !== "function") {
      return;
    }
    const key = panoViewerStateKey(snapshot);
    if (key === lastViewStateKeyRef.current) {
      return;
    }
    lastViewStateKeyRef.current = key;
    onViewChangeRef.current(snapshot);
  };

  const clearMarkerRefreshTimers = () => {
    markerRefreshTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    markerRefreshTimersRef.current = [];
  };

  const clearNavigationTimer = () => {
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }
    setIsNavigating(false);
  };

  const markViewerNavigating = () => {
    if (!navigationTimerRef.current) {
      setIsNavigating(true);
    }
    clearTimeout(navigationTimerRef.current);
    navigationTimerRef.current = setTimeout(() => {
      navigationTimerRef.current = null;
      setIsNavigating(false);
    }, 180);
  };

  const forceMarkerRender = () => {
    const viewer = viewerRef.current;
    const markersPlugin = markersPluginRef.current;
    if (!viewer || !markersPlugin) {
      return;
    }
    try {
      if (typeof viewer.autoSize === "function") {
        if (viewer.state && viewer.state.size && viewer.container) {
          const width = Math.round(viewer.container.clientWidth || 0);
          const height = Math.round(viewer.container.clientHeight || 0);
          if (width && height && viewer.state.size.width === width && viewer.state.size.height === height) {
            viewer.state.size.width = 0;
            viewer.state.size.height = 0;
          }
        }
        viewer.autoSize();
      }
      if (typeof markersPlugin.renderMarkers === "function") {
        markersPlugin.renderMarkers();
      }
      if (typeof viewer.needsUpdate === "function") {
        viewer.needsUpdate();
      }
      updateAnnotationPointsRef.current();
    } catch (_error) {
      // Rendering can briefly fail while the panorama is being swapped.
    }
  };

  scheduleMarkerRenderRef.current = () => {
    clearMarkerRefreshTimers();
    forceMarkerRender();
    requestAnimationFrame(() => {
      forceMarkerRender();
      requestAnimationFrame(forceMarkerRender);
    });
    [0, 50, 150, 300].forEach((delay) => {
      const timerId = setTimeout(forceMarkerRender, delay);
      markerRefreshTimersRef.current.push(timerId);
    });
  };

  updateViewerMarkersRef.current = () => {
    const markersPlugin = markersPluginRef.current;
    if (!markersPlugin || fallback) {
      return;
    }
    const markers = (annotationsRef.current || [])
      .map((annotation) => panoAnnotationToViewerMarker(annotation, {
        active: String(activeAnnotationId) === String(annotation.id)
      }))
      .filter(Boolean);
    const draft = draftAnnotationRef.current;
    if (draft && Array.isArray(draft.points) && draft.points.length) {
      const draftMarker = panoAnnotationToViewerMarker(
        { id: "__draft", label: "Drawing", type: "other", shape: draft },
        { draft: true }
      );
      if (draftMarker) {
        markers.push(draftMarker);
      }
    }
    try {
      markersPlugin.setMarkers(markers);
      scheduleMarkerRenderRef.current();
    } catch (_error) {
      // Keep the panorama usable even if an old annotation has malformed geometry.
    }
  };

  updateAnnotationPointsRef.current = () => {
    const viewer = viewerRef.current;
    if (!viewer || fallback) {
      setAnnotationRenderItems((current) => current.length ? [] : current);
      setDraftRenderItem((current) => current ? null : current);
      return;
    }

    const currentAnnotations = annotationsRef.current || [];
    const draft = draftAnnotationRef.current;
    const hasDraft = draft && Array.isArray(draft.points) && draft.points.length;
    if (!currentAnnotations.length && !hasDraft) {
      setAnnotationRenderItems((current) => current.length ? [] : current);
      setDraftRenderItem((current) => current ? null : current);
      return;
    }

    const projectPoint = (point, index) => {
      const position = {
        yaw: Number(point && point.yaw),
        pitch: Number(point && point.pitch)
      };
      if (!Number.isFinite(position.yaw) || !Number.isFinite(position.pitch)) {
        return null;
      }
      try {
        const viewerPoint = viewer.dataHelper.sphericalCoordsToViewerCoords(position);
        return {
          index,
          x: viewerPoint.x,
          y: viewerPoint.y,
          visible: viewer.dataHelper.isPointVisible(position)
        };
      } catch (_error) {
        return null;
      }
    };

    const screenCenterForPoints = (points) => {
      const visiblePoints = points.filter((point) => point.visible);
      const source = visiblePoints.length ? visiblePoints : points;
      const center = source.reduce((sum, point) => ({
        x: sum.x + point.x,
        y: sum.y + point.y
      }), { x: 0, y: 0 });
      return {
        index: -1,
        x: center.x / source.length,
        y: center.y / source.length,
        visible: Boolean(visiblePoints.length || source[0]?.visible)
      };
    };

    const projectAnnotation = (annotation, options = {}) => {
      const shape = options.shape || panoAnnotationShape(annotation);
      const points = (shape.points || []).map(projectPoint).filter(Boolean);
      if (!points.length) {
        return null;
      }
      const visiblePoints = points.filter((point) => point.visible);
      const role = panoAnnotationMeasurementRole(annotation);
      const midpoint = role === "measurement" && (shape.kind || "") === "line" && (shape.points || []).length >= 2
        ? panoLineMidpoint(shape.points[0], shape.points[1])
        : null;
      const main = midpoint ? projectPoint(midpoint, -1) || screenCenterForPoints(points) : screenCenterForPoints(points);
      return {
        id: options.id || annotation.id,
        annotation,
        label: options.label || annotation.label,
        kind: shape.kind || "point",
        points,
        visiblePoints,
        main,
        draft: Boolean(options.draft)
      };
    };

    setAnnotationRenderItems(
      currentAnnotations.map((annotation) => projectAnnotation(annotation)).filter(Boolean)
    );
    setDraftRenderItem(
      hasDraft
        ? projectAnnotation(
            { id: "__draft", label: "Drawing", type: "other", shape: draft },
            { id: "__draft", label: "Drawing", shape: draft, draft: true }
          )
        : null
    );
  };

  useEffect(() => {
    let cancelled = false;
    setFallback(false);
    setAnnotationRenderItems([]);
    setDraftRenderItem(null);
    const container = containerRef.current;
    if (!container) {
      return;
    }

    function destroyViewer() {
      clearMarkerRefreshTimers();
      clearNavigationTimer();
      emitPanoViewChange(null);
      if (viewerRef.current) {
        try {
          if (typeof viewerRef.current.__irapAnnotationCleanup === "function") {
            viewerRef.current.__irapAnnotationCleanup();
          }
          if (markersPluginRef.current && typeof markersPluginRef.current.clearMarkers === "function") {
            markersPluginRef.current.clearMarkers(false);
          }
          viewerRef.current.destroy();
        } catch (_error) {
          // The viewer may already be tearing down while React swaps images.
        }
        viewerRef.current = null;
        markersPluginRef.current = null;
      }
    }

    function createViewer() {
      if (cancelled) {
        return;
      }

      const Viewer = window.PhotoSphereViewerCore && window.PhotoSphereViewerCore.Viewer;
      const MarkersPlugin = window.PhotoSphereViewerCore && window.PhotoSphereViewerCore.MarkersPlugin;
      if (!Viewer) {
        return;
      }

      destroyViewer();
      container.innerHTML = "";
      try {
        viewerRef.current = new Viewer({
          container,
          panorama: imageUrl,
          navbar: ["zoom", "move", "caption", "fullscreen"],
          mousewheelCtrlKey: false,
          touchmoveTwoFingers: false,
          plugins: MarkersPlugin ? [photoSphereMarkersPluginConfig(MarkersPlugin)] : []
        });
        const viewer = viewerRef.current;
        try {
          markersPluginRef.current = MarkersPlugin
            ? viewer.getPlugin(MarkersPlugin) || viewer.getPlugin("markers")
            : null;
        } catch (_error) {
          markersPluginRef.current = null;
        }
        const updatePoints = () => updateAnnotationPointsRef.current();
        const updatePanoViewState = () => emitPanoViewChange(panoViewerStateSnapshot(viewer));
        const handleClick = (event) => {
          if (!placementModeRef.current || !onAnnotationPlacementRef.current) {
            return;
          }
          const data = event && event.data ? event.data : {};
          if (data.rightclick) {
            return;
          }
          onAnnotationPlacementRef.current({
            yaw: data.yaw,
            pitch: data.pitch,
            textureX: data.textureX,
            textureY: data.textureY
          });
        };
        const handleSelectMarker = (event) => {
          const annotationId = event && event.marker && event.marker.data && event.marker.data.annotationId;
          if (!annotationId || annotationId === "__draft" || placementModeRef.current || !onSelectAnnotationRef.current) {
            return;
          }
          onSelectAnnotationRef.current(annotationId);
        };
        viewer.addEventListener("click", handleClick);
        if (markersPluginRef.current && typeof markersPluginRef.current.addEventListener === "function") {
          markersPluginRef.current.addEventListener("select-marker", handleSelectMarker);
        }
        const refreshMarkers = () => {
          updateViewerMarkersRef.current();
          scheduleMarkerRenderRef.current();
        };
        ["ready", "render", "position-updated", "zoom-updated", "size-updated"].forEach((eventName) => {
          viewer.addEventListener(eventName, updatePoints);
        });
        ["ready", "position-updated", "zoom-updated", "size-updated"].forEach((eventName) => {
          viewer.addEventListener(eventName, updatePanoViewState);
        });
        ["position-updated", "zoom-updated"].forEach((eventName) => {
          viewer.addEventListener(eventName, markViewerNavigating);
        });
        viewer.addEventListener("ready", refreshMarkers);
        updateViewerMarkersRef.current();
        requestAnimationFrame(updatePoints);
        requestAnimationFrame(updatePanoViewState);
        viewer.__irapAnnotationCleanup = () => {
          viewer.removeEventListener("click", handleClick);
          if (markersPluginRef.current && typeof markersPluginRef.current.removeEventListener === "function") {
            markersPluginRef.current.removeEventListener("select-marker", handleSelectMarker);
          }
          ["ready", "render", "position-updated", "zoom-updated", "size-updated"].forEach((eventName) => {
            viewer.removeEventListener(eventName, updatePoints);
          });
          ["ready", "position-updated", "zoom-updated", "size-updated"].forEach((eventName) => {
            viewer.removeEventListener(eventName, updatePanoViewState);
          });
          ["position-updated", "zoom-updated"].forEach((eventName) => {
            viewer.removeEventListener(eventName, markViewerNavigating);
          });
          viewer.removeEventListener("ready", refreshMarkers);
        };
      } catch (_error) {
        setFallback(true);
      }
    }

    let fallbackTimer = null;

    if (window.PhotoSphereViewerCore && window.PhotoSphereViewerCore.Viewer) {
      createViewer();
    } else {
      window.addEventListener("photo-sphere-viewer-ready", createViewer, { once: true });
      fallbackTimer = setTimeout(() => {
        if (!cancelled && !viewerRef.current) {
          cancelled = true;
          window.removeEventListener("photo-sphere-viewer-ready", createViewer);
          setFallback(true);
        }
      }, 5000);

      return () => {
        cancelled = true;
        clearTimeout(fallbackTimer);
        window.removeEventListener("photo-sphere-viewer-ready", createViewer);
        destroyViewer();
      };
    }

    return () => {
      cancelled = true;
      window.removeEventListener("photo-sphere-viewer-ready", createViewer);
      destroyViewer();
    };
  }, [imageUrl]);

  useEffect(() => {
    updateViewerMarkersRef.current();
    updateAnnotationPointsRef.current();
  }, [annotations, activeAnnotationId, draftAnnotation, fallback]);

  useEffect(() => {
    const handleAnnotationsChanged = () => {
      scheduleMarkerRenderRef.current();
    };
    window.addEventListener("irap-pano-annotations-changed", handleAnnotationsChanged);
    return () => {
      window.removeEventListener("irap-pano-annotations-changed", handleAnnotationsChanged);
      clearMarkerRefreshTimers();
      clearNavigationTimer();
    };
  }, []);

  useEffect(() => {
    if (suppressActiveAnnotationFocus || !activeAnnotationId || !viewerRef.current) {
      return;
    }
    const activeAnnotation = (annotationsRef.current || []).find((annotation) => String(annotation.id) === String(activeAnnotationId));
    if (!activeAnnotation) {
      return;
    }
    const focusPoint = panoAnnotationFocusPoint(activeAnnotation);
    const yaw = Number(focusPoint ? focusPoint.yaw : activeAnnotation.yaw);
    const pitch = Number(focusPoint ? focusPoint.pitch : activeAnnotation.pitch);
    if (!Number.isFinite(yaw) || !Number.isFinite(pitch)) {
      return;
    }
    try {
      viewerRef.current.rotate({ yaw, pitch });
      viewerRef.current.needsUpdate();
      requestAnimationFrame(() => updateAnnotationPointsRef.current());
    } catch (_error) {
      // Ignore focus failures while the panorama is being replaced.
    }
  }, [activeAnnotationId, imageUrl, suppressActiveAnnotationFocus]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !window.ResizeObserver) {
      return;
    }

    let frameId = null;
    const observer = new ResizeObserver(() => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        const viewer = viewerRef.current;
        if (viewer && typeof viewer.autoSize === "function") {
          viewer.autoSize();
        } else {
          window.dispatchEvent(new Event("resize"));
        }
      });
    });

    observer.observe(container);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <div className={`pano-viewer-shell ${annotationPlacementMode ? "is-placing-annotation" : ""} ${isNavigating ? "is-navigating" : ""}`}>
      {fallback ? (
        <div className="pano-fallback"><img src={imageUrl} alt="Selected panorama" /></div>
      ) : (
        <div ref={containerRef} className="pano-viewer"></div>
      )}
      <EvidenceOverlayLayer overlays={overlays} />
      <PanoAnnotationMarkerLayer
        annotations={annotations}
        renderItems={annotationRenderItems}
        draftRenderItem={draftRenderItem}
        activeAnnotationId={activeAnnotationId}
        onSelectAnnotation={onSelectAnnotation}
        onDeleteAnnotation={onDeleteAnnotation}
      />
    </div>
  );
}

function PanoAnnotationMarkerLayer({
  annotations = [],
  renderItems = [],
  draftRenderItem = null,
  activeAnnotationId = null,
  onSelectAnnotation = null,
  onDeleteAnnotation = null
}) {
  if (!annotations.length && !draftRenderItem) {
    return null;
  }

  const renderItemsById = new Map(renderItems.map((item) => [String(item.id), item]));
  return (
    <div className="pano-annotation-marker-layer" aria-label="Pano annotations">
      {draftRenderItem && (draftRenderItem.visiblePoints || []).map((point, index) => (
        <span
          key={`draft-point-${index}`}
          className="pano-annotation-draft-point"
          style={{ left: `${point.x}px`, top: `${point.y}px` }}
        >
          {index + 1}
        </span>
      ))}
      {annotations.map((annotation) => {
        const item = renderItemsById.get(String(annotation.id));
        const isShapeLabel = item && ["line", "polygon", "sam"].includes(item.kind);
        const isMeasurementLabel = panoAnnotationMeasurementRole(annotation) === "measurement";
        const point = isShapeLabel && !isMeasurementLabel ? item?.points?.[0] : item?.main;
        if (!point?.visible) {
          return null;
        }
        const isActive = String(activeAnnotationId) === String(annotation.id);
        const displayLabel = panoMeasurementAnnotationLabel(annotation);
        const markerSource = panoAnnotationMeasurementMeta(annotation).source;
        const canDelete = onDeleteAnnotation && markerSource !== "pano-calibration-profile" && markerSource !== "pano-measurement-preview";
        return (
          <React.Fragment key={annotation.id}>
            <button
              type="button"
              className={`pano-annotation-marker ${isShapeLabel ? "is-shape-label" : ""} ${isMeasurementLabel ? "is-measurement-label" : ""} ${isActive ? "active" : ""}`}
              style={{ left: `${point.x}px`, top: `${point.y}px` }}
              title={displayLabel}
              onClick={(event) => {
                event.stopPropagation();
                if (onSelectAnnotation) {
                  onSelectAnnotation(annotation.id);
                }
              }}
            >
              {!isShapeLabel ? icon(panoAnnotationTypeIcon(annotation.type)) : null}
              <span>{displayLabel}</span>
            </button>
            {isActive && canDelete && (
              <button
                type="button"
                className="pano-annotation-delete"
                style={{ left: `${point.x}px`, top: `${point.y}px` }}
                title={`Delete ${annotation.label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteAnnotation(annotation.id);
                }}
              >
                {icon("trash-2")}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ImageStrip({ detail, currentImage, setCurrentImage, previewImage = null, previewActive = false, onSelectPreview = null }) {
  const images = (detail && detail.images) || [];

  return (
    <div className="image-strip">
      <div className="image-strip__label">
        {icon("images")}
        <span>{images.length ? `${images.length} pano images` : "No pano images"}</span>
      </div>
      <div className="thumbs">
        {previewImage && (
          <button
            key={`candidate-${previewImage.candidateId || previewImage.file}`}
            className={`thumb candidate-preview ${previewActive ? "active" : ""}`}
            onClick={() => onSelectPreview && onSelectPreview(previewImage)}
            title={previewImage.file}
          >
            <img
              src={previewImage.thumbUrl || previewImage.url}
              alt="Candidate pano preview"
              loading="lazy"
              decoding="async"
            />
            <i className="thumb__plus" aria-hidden="true">
              {icon("map-pin")}
            </i>
            <span>C</span>
          </button>
        )}
        {images.map((image, index) => (
          <button
            key={image.file}
            className={`thumb ${!previewActive && index === currentImage ? "active" : ""}`}
            onClick={() => setCurrentImage(index)}
            title={image.file}
          >
            <img
              src={image.thumbUrl || image.url}
              alt={`Pano ${index + 1}`}
              loading="lazy"
              decoding="async"
            />
            <span>{index + 1}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SegmentsGridView({
  health,
  segments,
  fields,
  analysisResults,
  search,
  selectedId = null,
  selectSegment = null,
  updateGridCell = null,
  signResults = {},
  openEvidencePreview = null,
  openEvidenceInPano = null,
  compact = false
}) {
  const [selectedFields, setSelectedFields] = useState(["Road_name", "Distance", "F1", "F2", "F3", "F4", "F5"]);
  const [showOldData, setShowOldData] = useState(true);
  const [showAiData, setShowAiData] = useState(true);
  const [showAiReason, setShowAiReason] = useState(true);
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(false);
  const [filterAllChangedFields, setFilterAllChangedFields] = useState(false);
  const [showSequenceMarkers, setShowSequenceMarkers] = useState(true);
  const [showOnlySequenceChanges, setShowOnlySequenceChanges] = useState(false);
  const [fieldSearch, setFieldSearch] = useState("");
  const [fieldPickerOpen, setFieldPickerOpen] = useState(false);
  const [activeFieldGroup, setActiveFieldGroup] = useState("Mid-block");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const fieldMeta = useMemo(() => {
    const byName = new Map();
    const byProperty = new Map();
    fields.forEach((field) => {
      byName.set(field.field, field);
      if (field.geoProperty) {
        byProperty.set(field.geoProperty, field);
      }
    });
    return { byName, byProperty };
  }, [fields]);

  const availableFields = useMemo(() => {
    if (!segments.length || !segments[0].feature) return [];
    const props = segments[0].feature.properties;
    const propertyFields = Object.keys(props).filter(key => key !== "SegmentId" && key !== "ID");
    const irapProperties = new Set(fields.map((field) => field.geoProperty).filter(Boolean));
    const oldSystemFields = propertyFields.filter((fieldName) => !irapProperties.has(fieldName));
    const irapFieldCodes = fields
      .map((field) => field.field)
      .filter((fieldName) => !oldSystemFields.includes(fieldName));
    return [...oldSystemFields, ...irapFieldCodes];
  }, [segments, fields]);
  const allComparableFields = useMemo(() => fields.map((field) => field.field), [fields]);
  const segmentOrder = useMemo(() => segments.slice().sort((a, b) => a.id - b.id), [segments]);
  const segmentIndexById = useMemo(() => {
    const index = new Map();
    segmentOrder.forEach((segment, position) => {
      index.set(Number(segment.id), position);
    });
    return index;
  }, [segmentOrder]);

  const fieldGroups = useMemo(() => {
    const props = segments.length && segments[0].feature ? segments[0].feature.properties : {};
    const propertyFields = Object.keys(props).filter(key => key !== "SegmentId" && key !== "ID");
    const irapProperties = new Set(fields.map((field) => field.geoProperty).filter(Boolean));
    const oldSystemFields = propertyFields.filter((fieldName) => !irapProperties.has(fieldName));
    const segmentInfoFields = oldSystemFields.filter((fieldName) => CORE_SEGMENT_FIELDS.has(fieldName));
    const legacyFields = oldSystemFields.filter((fieldName) => !CORE_SEGMENT_FIELDS.has(fieldName));
    const groups = [{ name: "Segment info", keys: segmentInfoFields, note: "Road metadata and context" }];
    const orderedGroups = [
      ...CATEGORY_ORDER.filter((groupName) => fields.some((field) => field.group === groupName)),
      ...Array.from(new Set(fields.map((field) => field.group))).filter((groupName) => !CATEGORY_ORDER.includes(groupName))
    ];

    orderedGroups.forEach((groupName) => {
      groups.push({
        name: groupName,
        keys: fields.filter((field) => field.group === groupName).map((field) => field.field),
        note: CATEGORY_META[groupName]?.note || "iRAP fields"
      });
    });

    groups.push({ name: "Old system outputs", keys: legacyFields, note: "Legacy supporting fields, SRS, star ratings, and output values" });

    return groups.filter((group) => group.keys.length);
  }, [segments, fields]);

  const selectedSet = useMemo(() => new Set(selectedFields), [selectedFields]);

  const toggleField = (fieldName) => {
    if (selectedFields.includes(fieldName)) {
      setSelectedFields(selectedFields.filter(f => f !== fieldName));
    } else {
      setSelectedFields([...selectedFields, fieldName]);
    }
  };

  const addFields = (fieldNames) => {
    setSelectedFields([...new Set([...selectedFields, ...fieldNames])]);
  };

  const removeFields = (fieldNames) => {
    const removeSet = new Set(fieldNames);
    setSelectedFields(selectedFields.filter((fieldName) => !removeSet.has(fieldName)));
  };

  const toggleCategoryFields = (fieldNames) => {
    const selectedCount = fieldNames.filter((fieldName) => selectedSet.has(fieldName)).length;
    if (selectedCount) {
      removeFields(fieldNames);
    } else {
      addFields(fieldNames);
    }
  };

  const showOnlyFields = (fieldNames) => {
    setSelectedFields([...new Set(fieldNames)]);
  };

  const getFieldMeta = (fieldName) => {
    return fieldMeta.byName.get(fieldName) || fieldMeta.byProperty.get(fieldName) || null;
  };

  const getFieldEditOptions = (fieldName) => {
    const field = getFieldMeta(fieldName);
    return field && Array.isArray(field.options) && field.options.length ? field.options : [];
  };

  const formatGridValue = (value, field = null) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }
    if (field && Array.isArray(field.options)) {
      const option = field.options.find((item) => String(item.id) === String(value));
      if (option) {
        return `${value}: ${option.label}`;
      }
    }
    if (typeof value === "object") {
      if (value.lat !== undefined && value.lng !== undefined) {
        return `${Number(value.lat).toFixed(6)}, ${Number(value.lng).toFixed(6)}`;
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  const normalizeComparableValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return String(value).trim();
  };

  const getAiDisplay = (aiField) => {
    if (!aiField || aiField.value === null || aiField.value === undefined || aiField.value === "") {
      return "-";
    }
    return `${aiField.value}${aiField.decodedValue ? `: ${aiField.decodedValue}` : ""}`;
  };

  const getOldField = (segment, fieldName) => {
    const field = fieldMeta.byName.get(fieldName);
    const propertyName = field && field.geoProperty ? field.geoProperty : fieldName;
    const value = segment.feature?.properties?.[propertyName];
    return {
      propertyName,
      rawValue: value,
      display: formatGridValue(value, field)
    };
  };

  const getFieldTitle = (fieldName) => {
    if (fieldName === "__centroid") return "Centroid";
    const field = getFieldMeta(fieldName);
    return field ? field.caption : fieldName.replace(/_/g, " ");
  };

  const getFieldCode = (fieldName) => {
    const field = getFieldMeta(fieldName);
    return field ? field.field : null;
  };

  const getFieldCategory = (fieldName) => {
    const field = getFieldMeta(fieldName);
    if (field && field.group) {
      return field.group;
    }
    if (CORE_SEGMENT_FIELDS.has(fieldName)) {
      return "Segment info";
    }
    return "Old system outputs";
  };

  const getFieldCategoryLabel = (fieldName) => {
    const groupName = getFieldCategory(fieldName);
    return (CATEGORY_META[groupName] && CATEGORY_META[groupName].short) || groupName;
  };

  const getFieldCategoryColor = (fieldName) => {
    const groupName = getFieldCategory(fieldName);
    return (CATEGORY_META[groupName] && CATEGORY_META[groupName].color) || "#64748b";
  };

  const getOldPropertyName = (fieldName) => {
    const field = fieldMeta.byName.get(fieldName);
    return field && field.geoProperty ? field.geoProperty : fieldName;
  };

  const getAiField = (segmentId, fieldName) => {
    const result = analysisResults[segmentId];
    if (!result) return null;
    const fieldCode = getFieldCode(fieldName);
    return (
      (fieldCode && result.fields && result.fields[fieldCode]) ||
      (result.fields && result.fields[fieldName]) ||
      null
    );
  };

  const getAiValue = (segmentId, fieldName) => {
    const aiField = getAiField(segmentId, fieldName);
    if (!aiField || aiField.value === null || aiField.value === undefined || aiField.value === "") {
      return null;
    }
    return aiField.value;
  };

  const fieldHasDifference = (segment, fieldName) => {
    if (!analysisResults[segment.id]) return false;
    const aiField = getAiField(segment.id, fieldName);
    if (!aiField) return false;
    const oldField = getOldField(segment, fieldName);
    return normalizeComparableValue(aiField.value) !== normalizeComparableValue(oldField.rawValue);
  };

  const isSequenceField = (fieldName) => {
    return Boolean(fieldMeta.byName.get(fieldName));
  };

  const getNeighborSegment = (segment, direction) => {
    const position = segmentIndexById.get(Number(segment.id));
    if (position === undefined) {
      return null;
    }
    return segmentOrder[position + direction] || null;
  };

  const getSequenceSources = () => {
    const sources = [];
    if (showOldData || !showAiData) {
      sources.push("old");
    }
    if (showAiData) {
      sources.push("ai");
    }
    return sources;
  };

  const getComparableFieldValue = (segment, fieldName, source) => {
    if (!segment) {
      return "";
    }
    if (source === "ai") {
      const aiField = getAiField(segment.id, fieldName);
      return normalizeComparableValue(aiField ? aiField.value : "");
    }
    return normalizeComparableValue(getOldField(segment, fieldName).rawValue);
  };

  const fieldHasSequenceChange = (segment, fieldName, source, direction) => {
    if (!isSequenceField(fieldName)) {
      return false;
    }
    const neighbor = getNeighborSegment(segment, direction);
    if (!neighbor) {
      return false;
    }
    return getComparableFieldValue(segment, fieldName, source) !== getComparableFieldValue(neighbor, fieldName, source);
  };

  const getSequenceChange = (segment, fieldName, source) => {
    return {
      previous: fieldHasSequenceChange(segment, fieldName, source, -1),
      next: fieldHasSequenceChange(segment, fieldName, source, 1)
    };
  };

  const segmentHasSequenceChanges = (segment) => {
    const fieldsToCheck = (filterAllChangedFields ? allComparableFields : selectedFields).filter(isSequenceField);
    const sources = getSequenceSources();
    return fieldsToCheck.some((fieldName) =>
      sources.some((source) =>
        fieldHasSequenceChange(segment, fieldName, source, -1) ||
        fieldHasSequenceChange(segment, fieldName, source, 1)
      )
    );
  };

  const countSequenceChangedSegments = (segmentsToScan) => {
    return segmentsToScan.filter((segment) => segmentHasSequenceChanges(segment)).length;
  };

  const segmentHasDifferences = (segment) => {
    if (!analysisResults[segment.id]) return false;
    const fieldsToCheck = filterAllChangedFields ? allComparableFields : selectedFields;
    return fieldsToCheck.some((fieldName) => fieldHasDifference(segment, fieldName));
  };

  const countDifferences = (segment) => {
    if (!analysisResults[segment.id]) return 0;
    const fieldsToCheck = filterAllChangedFields ? allComparableFields : selectedFields;
    return fieldsToCheck.filter((fieldName) => fieldHasDifference(segment, fieldName)).length;
  };

  const buildDifferenceRows = (segmentsToScan) => {
    const rows = [];
    const fieldsToCheck = filterAllChangedFields ? allComparableFields : selectedFields;
    segmentsToScan.forEach((segment) => {
      if (!analysisResults[segment.id]) return;
      const segmentDifferenceCount = countDifferences(segment);
      if (!segmentDifferenceCount) return;
      fieldsToCheck.forEach((fieldName) => {
        if (!fieldHasDifference(segment, fieldName)) return;
        const oldField = getOldField(segment, fieldName);
        const aiField = getAiField(segment.id, fieldName);
        rows.push({
          segmentId: segment.id,
          roadName: segment.road,
          distance: segment.distance,
          fieldTitle: getFieldTitle(fieldName),
          fieldCode: getFieldCode(fieldName) || "",
          oldProperty: oldField.propertyName,
          oldValue: oldField.rawValue ?? "",
          oldDisplay: oldField.display,
          aiValue: aiField.value ?? "",
          aiDisplay: getAiDisplay(aiField),
          aiConfidence: aiField.confidence ?? "",
          aiNeedsReview: aiField.needsReview ? "Yes" : "No",
          aiEvidence: aiField.evidence || ""
        });
      });
    });
    return rows;
  };

  const escapeCsvValue = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const exportDifferenceRows = () => {
    const exportColumns = [
      { key: "segmentId", label: "Segment ID" },
      { key: "roadName", label: "Road name" },
      { key: "distance", label: "Distance" },
      { key: "fieldTitle", label: "Field" },
      { key: "fieldCode", label: "F code" },
      { key: "oldProperty", label: "Old property" },
      { key: "oldValue", label: "Old value" },
      { key: "oldDisplay", label: "Old display" },
      { key: "aiValue", label: "AI value" },
      { key: "aiDisplay", label: "AI display" },
      { key: "aiConfidence", label: "AI confidence" },
      { key: "aiNeedsReview", label: "AI needs review" },
      { key: "aiEvidence", label: "AI evidence" }
    ];
    const csv = [
      exportColumns.map((column) => escapeCsvValue(column.label)).join(","),
      ...differenceRows.map((row) =>
        exportColumns.map((column) => escapeCsvValue(row[column.key])).join(",")
      )
    ].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `irap-old-ai-differences-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const countAnalyzedSegments = (segmentsToScan) => {
    return segmentsToScan.filter((segment) => analysisResults[segment.id]).length;
  };

  const filteredSegments = useMemo(() => segments.filter((segment) => {
    const hasSearch = Boolean(search.trim());
    const matchesSequence = !showOnlySequenceChanges || segmentHasSequenceChanges(segment);
    if (!hasSearch) {
      if (showOnlyDifferences) {
        return segmentHasDifferences(segment) && matchesSequence;
      }
      return matchesSequence;
    }
    const matchesSearch = segmentMatchesSearch(segment, search);
    if (showOnlyDifferences) {
      return matchesSearch && segmentHasDifferences(segment) && matchesSequence;
    }
    return matchesSearch && matchesSequence;
  }), [
    segments,
    search,
    showOnlyDifferences,
    showOnlySequenceChanges,
    filterAllChangedFields,
    selectedFields,
    allComparableFields,
    analysisResults,
    fieldMeta,
    segmentOrder,
    showOldData,
    showAiData
  ]);

  const differenceRows = useMemo(
    () => buildDifferenceRows(filteredSegments),
    [filteredSegments, selectedFields, filterAllChangedFields, allComparableFields, analysisResults, fieldMeta]
  );
  const analyzedSegmentCount = useMemo(
    () => countAnalyzedSegments(filteredSegments),
    [filteredSegments, analysisResults]
  );
  const sequenceChangedSegmentCount = useMemo(
    () => countSequenceChangedSegments(filteredSegments),
    [filteredSegments, selectedFields, filterAllChangedFields, allComparableFields, analysisResults, fieldMeta, segmentOrder, showOldData, showAiData]
  );

  const normalizedFieldSearch = fieldSearch.trim().toLowerCase();

  const fieldMatchesSearch = (fieldName) => {
    if (!normalizedFieldSearch) return true;
    const title = getFieldTitle(fieldName).toLowerCase();
    const code = String(getFieldCode(fieldName) || "").toLowerCase();
    const propertyName = getOldPropertyName(fieldName).toLowerCase();
    return title.includes(normalizedFieldSearch) || code.includes(normalizedFieldSearch) || fieldName.toLowerCase().includes(normalizedFieldSearch) || propertyName.includes(normalizedFieldSearch);
  };

  const activePickerGroup =
    fieldGroups.find((group) => group.name === activeFieldGroup) || fieldGroups[0] || { name: "", keys: [], note: "" };
  const activePickerKeys = activePickerGroup.keys.filter(fieldMatchesSearch);
  const quickGroups = fieldGroups.filter((group) => group.name !== "Old system outputs");
  const totalPages = Math.max(1, Math.ceil(filteredSegments.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStartIndex = filteredSegments.length ? (safePage - 1) * pageSize : 0;
  const pageEndIndex = Math.min(pageStartIndex + pageSize, filteredSegments.length);
  const pagedSegments = useMemo(
    () => filteredSegments.slice(pageStartIndex, pageEndIndex),
    [filteredSegments, pageStartIndex, pageEndIndex]
  );
  const measureGridText = (value) => {
    if (value === null || value === undefined) {
      return 0;
    }
    return String(value).replace(/\s+/g, " ").trim().length;
  };

  const clampColumnWidth = (width, min, max) => {
    return Math.max(min, Math.min(max, width));
  };

  const estimateColumnWidth = (fieldName, kind) => {
    if (kind === "id") {
      return 98;
    }

    const visibleSegments = pagedSegments.length
      ? pagedSegments
      : filteredSegments.slice(0, Math.min(pageSize, 50));
    const fieldSpan = Math.max(1, Number(showOldData || !showAiData) + Number(showAiData));
    const headerTextLength = Math.max(
      measureGridText(getFieldTitle(fieldName)),
      measureGridText(`${getFieldCode(fieldName) || ""} ${getOldPropertyName(fieldName)}`)
    );
    const headerShareLength = Math.ceil(headerTextLength / fieldSpan);
    const bodyLengths = visibleSegments.map((segment) => {
      if (kind === "old") {
        return measureGridText(getOldField(segment, fieldName).display);
      }

      const aiField = getAiField(segment.id, fieldName);
      const valueLength = measureGridText(getAiDisplay(aiField));
      const reasonLength = showAiReason && aiField && aiField.evidence
        ? Math.min(measureGridText(aiField.evidence), 46)
        : 0;
      return Math.max(valueLength, reasonLength);
    });
    const longestText = Math.max(headerShareLength, ...bodyLengths, 1);
    const contentWidth = Math.ceil(longestText * 7.2) + 34;

    if (kind === "ai") {
      const mostlyEmpty = bodyLengths.length > 0 && bodyLengths.every((length) => length <= 1);
      return clampColumnWidth(contentWidth, mostlyEmpty ? 72 : 110, showAiReason ? 300 : 260);
    }

    return clampColumnWidth(contentWidth, 96, 300);
  };

  const gridColumns = useMemo(() => {
    const columns = [
      {
        key: "__id",
        kind: "id",
        width: `${estimateColumnWidth(null, "id")}px`,
        header: "ID",
        subHeader: "",
        fieldName: null
      }
    ];

    selectedFields.forEach((fieldName) => {
      const title = getFieldTitle(fieldName);
      const code = getFieldCode(fieldName);
      const propertyName = getOldPropertyName(fieldName);
      if (showOldData || !showAiData) {
        columns.push({
          key: `${fieldName}:old`,
          kind: "old",
          width: `${estimateColumnWidth(fieldName, "old")}px`,
          header: title,
          subHeader: "Old",
          fieldName,
          code,
          propertyName
        });
      }
      if (showAiData) {
        columns.push({
          key: `${fieldName}:ai`,
          kind: "ai",
          width: `${estimateColumnWidth(fieldName, "ai")}px`,
          header: title,
          subHeader: "AI",
          fieldName,
          code,
          propertyName
        });
      }
    });

    return columns;
  }, [selectedFields, showOldData, showAiData, showAiReason, fieldMeta, analysisResults, pagedSegments, filteredSegments, pageSize]);
  const gridTemplateColumns = gridColumns.map((column) => column.width).join(" ");

  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <div className={`segments-grid-container ${compact ? "compact-grid" : ""}`}>
      <div className="grid-controls">
        {false && (
        <div className="control-section difference-tools">
          <h3>Extract</h3>
          <button
            className="primary-lite-button"
            onClick={exportDifferenceRows}
            disabled={!differenceRows.length}
            title={filterAllChangedFields
              ? "Export Old and AI mismatch rows for the current search across all iRAP fields"
              : "Export Old and AI mismatch rows for the current search and selected fields"}
          >
            {icon("download")} CSV
          </button>
          <small>{differenceRows.length} Old &lt;&gt; AI rows · {filterAllChangedFields ? "all fields" : "selected fields"}</small>
        </div>
        )}

        <div className="control-section field-selector">
          <div className="field-selector-head">
            <div>
              <h3>Grid Fields ({selectedFields.length})</h3>
              <p>Use quick category actions or open the manager for exact fields.</p>
            </div>
            <div className="field-selector-actions">
              <button className="primary-lite-button" onClick={() => setFieldPickerOpen(true)}>Manage fields</button>
              <button onClick={() => addFields(["Road_name", "Distance"])}>Add core</button>
              <button onClick={() => setSelectedFields(["Road_name", "Distance"])}>Reset</button>
            </div>
          </div>

          <div className="quick-field-row">
            {quickGroups.map((group) => {
              const meta = CATEGORY_META[group.name] || {};
              const selectedCount = group.keys.filter((fieldName) => selectedSet.has(fieldName)).length;
              return (
                <button
                  key={group.name}
                  className={`quick-field-button ${selectedCount === group.keys.length ? "complete" : selectedCount ? "partial" : ""}`}
                  style={{ "--category-color": meta.color || "#64748b" }}
                  onClick={() => toggleCategoryFields(group.keys)}
                  title={selectedCount ? `Remove ${group.name} fields` : `Add ${group.name} fields`}
                >
                  <span>{meta.short || group.name}</span>
                  <small>{selectedCount}/{group.keys.length}</small>
                </button>
              );
            })}
          </div>

        </div>
      </div>

      {fieldPickerOpen && (
        <div className="field-picker-backdrop" onClick={() => setFieldPickerOpen(false)}>
          <div className="field-picker-panel" onClick={(event) => event.stopPropagation()}>
            <header className="field-picker-header">
              <div>
                <h3>Manage Grid Fields</h3>
                <p>{selectedFields.length} selected. Compare old GeoJSON values with new AI values.</p>
              </div>
              <button className="icon-close-button" onClick={() => setFieldPickerOpen(false)} aria-label="Close field manager">
                {icon("x")}
              </button>
            </header>

            <div className="field-picker-body">
              <aside className="field-picker-groups">
                {fieldGroups.map((group) => {
                  const meta = CATEGORY_META[group.name] || {};
                  const selectedCount = group.keys.filter((fieldName) => selectedSet.has(fieldName)).length;
                  return (
                    <button
                      key={group.name}
                      className={activePickerGroup.name === group.name ? "active" : ""}
                      style={{ "--category-color": meta.color || "#64748b" }}
                      onClick={() => setActiveFieldGroup(group.name)}
                    >
                      <strong>{meta.short || group.name}</strong>
                      <span>{selectedCount}/{group.keys.length}</span>
                    </button>
                  );
                })}
              </aside>

              <section className="field-picker-fields">
                <div className="field-picker-tools">
                  <div>
                    <h4>{CATEGORY_META[activePickerGroup.name]?.short || activePickerGroup.name}</h4>
                    <p>{activePickerGroup.note}</p>
                  </div>
                  <input
                    value={fieldSearch}
                    onChange={(event) => setFieldSearch(event.target.value)}
                    placeholder="Search title, F code, or property"
                  />
                </div>

                <div className="field-picker-actions">
                  <button onClick={() => addFields(activePickerGroup.keys)}>Add all in category</button>
                  <button onClick={() => removeFields(activePickerGroup.keys)}>Remove category</button>
                  <button onClick={() => showOnlyFields(["Road_name", "Distance", ...activePickerGroup.keys])}>Show only this category</button>
                  <button onClick={() => setSelectedFields([])}>Clear all</button>
                </div>

                <div className="field-picker-list">
                  {activePickerKeys.length ? (
                    activePickerKeys.map((fieldName) => {
                      const fieldCode = getFieldCode(fieldName);
                      const oldProperty = getOldPropertyName(fieldName);
                      return (
                        <label className="field-picker-row" key={fieldName}>
                          <input
                            type="checkbox"
                            checked={selectedSet.has(fieldName)}
                            onChange={() => toggleField(fieldName)}
                          />
                          <span>
                            <strong>{getFieldTitle(fieldName)}</strong>
                            <small>
                              {fieldCode ? `${fieldCode} / ${oldProperty}` : oldProperty}
                            </small>
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="field-picker-empty">No fields match the search.</div>
                  )}
                </div>
              </section>
            </div>

            <footer className="field-picker-footer">
              <button onClick={() => setFieldPickerOpen(false)}>Done</button>
            </footer>
          </div>
        </div>
      )}

      <FastSegmentsDataGrid
        columns={gridColumns}
        gridTemplateColumns={gridTemplateColumns}
        selectedFields={selectedFields}
        segments={pagedSegments}
        analysisResults={analysisResults}
        signResults={signResults}
        showAiReason={showAiReason}
        getFieldTitle={getFieldTitle}
        getFieldCode={getFieldCode}
        getOldPropertyName={getOldPropertyName}
        getOldField={getOldField}
        getAiField={getAiField}
        getAiValue={getAiValue}
        getAiDisplay={getAiDisplay}
        fieldHasDifference={fieldHasDifference}
        getSequenceChange={getSequenceChange}
        showSequenceMarkers={showSequenceMarkers}
        getFieldCategoryLabel={getFieldCategoryLabel}
        getFieldCategoryColor={getFieldCategoryColor}
        selectedId={selectedId}
        selectSegment={selectSegment}
        updateGridCell={updateGridCell}
        openEvidencePreview={openEvidencePreview}
        openEvidenceInPano={openEvidenceInPano}
        getFieldEditOptions={getFieldEditOptions}
      />

      <GridPagination
        page={safePage}
        totalPages={totalPages}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setPage={setPage}
        start={pageStartIndex + 1}
        end={pageEndIndex}
        total={filteredSegments.length}
      />

      {filteredSegments.length === 0 && (
        <div className="no-results">
          No segments match your search criteria.
        </div>
      )}
    </div>
  );
}

function FastSegmentsDataGrid({
  columns,
  gridTemplateColumns,
  selectedFields,
  segments,
  analysisResults,
  signResults = {},
  showAiReason,
  getFieldTitle,
  getFieldCode,
  getOldPropertyName,
  getOldField,
  getAiField,
  getAiValue,
  getAiDisplay,
  fieldHasDifference,
  getSequenceChange,
  showSequenceMarkers,
  getFieldCategoryLabel,
  getFieldCategoryColor,
  selectedId,
  selectSegment,
  updateGridCell,
  openEvidencePreview,
  openEvidenceInPano,
  getFieldEditOptions
}) {
  const gridStyle = { gridTemplateColumns };
  const canSelectRows = typeof selectSegment === "function";
  const canEditCells = typeof updateGridCell === "function";

  const spanForField = (fieldName) => {
    const span = columns.filter((column) => column.fieldName === fieldName).length;
    return Math.max(1, span);
  };

  return (
    <div className="data-grid-wrapper">
      <div className="fast-data-grid" role="grid" aria-rowcount={segments.length + 2}>
        <div className="fast-data-grid-inner">
          <div className="fast-data-grid-header">
            <div className="fast-data-grid-row group-row" style={gridStyle} role="row">
              <div className="data-grid-cell header-cell sticky-grid-col" role="columnheader">ID</div>
              {selectedFields.map((fieldName) => (
                (() => {
                  const categoryColor = getFieldCategoryColor(fieldName);
                  return (
                    <div
                      key={fieldName}
                      className="data-grid-cell header-cell field-group-cell"
                      role="columnheader"
                      style={{
                        gridColumn: `span ${spanForField(fieldName)}`,
                        "--category-color": categoryColor
                      }}
                    >
                      <span className="grid-field-category">{getFieldCategoryLabel(fieldName)}</span>
                      <span className="grid-field-title">{getFieldTitle(fieldName)}</span>
                      {getOldPropertyName(fieldName) && (
                        <small>{getOldPropertyName(fieldName)}</small>
                      )}
                    </div>
                  );
                })()
              ))}
            </div>
            <div className="fast-data-grid-row sub-row" style={gridStyle} role="row">
              {columns.map((column) => (
                <div
                  key={column.key}
                  className={`data-grid-cell sub-header-cell ${column.kind === "id" ? "sticky-grid-col" : ""}`}
                  role="columnheader"
                >
                  {column.subHeader}
                </div>
              ))}
            </div>
          </div>

          <div className="fast-data-grid-body">
            {segments.map((segment) => {
              const hasAiResults = analysisResults[segment.id];
              const isActive = Number(selectedId) === Number(segment.id);
              return (
                <div
                  key={segment.id}
                  className={`fast-data-grid-row body-row ${hasAiResults ? "has-ai-data" : ""} ${canSelectRows ? "selectable-row" : ""} ${isActive ? "active-row" : ""}`}
                  style={gridStyle}
                  role="row"
                  tabIndex={canSelectRows ? 0 : undefined}
                  onClick={canSelectRows ? () => selectSegment(segment.id) : undefined}
                  onKeyDown={canSelectRows ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      selectSegment(segment.id);
                    }
                  } : undefined}
                >
                  {columns.map((column) => {
                    if (column.kind === "id") {
                      return (
                        <div key={column.key} className="data-grid-cell body-cell segment-id sticky-grid-col" role="gridcell">
                          <span className="segment-id-value">{segment.id}</span>
                          <button
                            type="button"
                            className="aerial-overlay-button"
                            title={`Open aerial overlay for segment ${segment.id}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              openSegmentAerialOverlay(segment.id);
                            }}
                          >
                            {icon("map")}
                          </button>
                        </div>
                      );
                    }

                    if (column.kind === "old") {
                      const oldField = getOldField(segment, column.fieldName);
                      const isDifferent = fieldHasDifference(segment, column.fieldName);
                      const sequenceChange = getSequenceChange(segment, column.fieldName, "old");
                      const hasSequenceChange = showSequenceMarkers && (sequenceChange.previous || sequenceChange.next);
                      return (
                        <EditableGridCell
                          key={column.key}
                          className={`data-grid-cell body-cell current-cell ${isDifferent ? "different-pair" : ""} ${hasSequenceChange ? "sequence-change-cell" : ""}`}
                          title={oldField.propertyName}
                          rawValue={oldField.rawValue}
                          editOptions={getFieldEditOptions(column.fieldName)}
                          disabled={!canEditCells || oldField.propertyName === "__centroid" || typeof oldField.rawValue === "object"}
                          onSave={(value) => updateGridCell({
                            segmentId: segment.id,
                            source: "old",
                            fieldName: column.fieldName,
                            value
                          })}
                        >
                          <span className="old-grid-value">{oldField.display}</span>
                        </EditableGridCell>
                      );
                    }

                    const aiValue = getAiValue(segment.id, column.fieldName);
                    const aiField = getAiField(segment.id, column.fieldName);
                    const aiDisplay = getAiDisplay(aiField);
                    const aiReason = aiField && aiField.evidence ? aiField.evidence : "";
                    const isDifferent = fieldHasDifference(segment, column.fieldName);
                    const sequenceChange = getSequenceChange(segment, column.fieldName, "ai");
                    const hasSequenceChange = showSequenceMarkers && (sequenceChange.previous || sequenceChange.next);
                    const linkedEvidence = evidenceForGridField(
                      signResults,
                      segment.id,
                      column.fieldName,
                      column.code
                    );

                    return (
                      <EditableGridCell
                        key={column.key}
                        className={`data-grid-cell body-cell ai-cell ${aiField ? "has-ai-field" : ""} ${aiValue !== null && aiValue !== undefined ? "has-value" : ""} ${isDifferent ? "different" : ""} ${hasSequenceChange ? "sequence-change-cell" : ""}`}
                        title={aiReason}
                        rawValue={aiField ? aiField.value : ""}
                        editOptions={getFieldEditOptions(column.fieldName)}
                        disabled={!canEditCells || !column.code}
                        onSave={(value) => updateGridCell({
                          segmentId: segment.id,
                          source: "ai",
                          fieldName: column.fieldName,
                          value
                        })}
                      >
                        <span className="ai-grid-value">
                          {aiDisplay}
                        </span>
                        {showAiReason && aiReason && <small className="ai-grid-reason">{aiReason}</small>}
                        {linkedEvidence.length > 0 && (
                          <div className="ai-grid-evidence-links" aria-label="Linked segmentation evidence">
                            {linkedEvidence.slice(0, 2).map((item) => {
                              const label = evidenceItemLabel(item);
                              const imageUrl = evidenceMarkedImageUrl(item, segment);
                              return (
                                <button
                                  key={item.id || item.markedCropUrl || item.cropUrl || label}
                                  type="button"
                                  className="ai-grid-evidence-link"
                                  title={`Open evidence: ${label}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    const openEvidence =
                                      typeof openEvidencePreview === "function"
                                        ? openEvidencePreview
                                        : openEvidenceInPano;
                                    if (typeof openEvidence === "function") {
                                      openEvidence(item, segment);
                                    }
                                  }}
                                >
                                  {imageUrl ? (
                                    <img src={imageUrl} alt="" loading="lazy" />
                                  ) : (
                                    <span className="ai-grid-evidence-icon">{icon("scan-search")}</span>
                                  )}
                                  <span>{label}</span>
                                </button>
                              );
                            })}
                            {linkedEvidence.length > 2 && (
                              <span className="ai-grid-evidence-more">+{linkedEvidence.length - 2}</span>
                            )}
                          </div>
                        )}
                      </EditableGridCell>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditableGridCell({ className, title, rawValue, editOptions = [], disabled, onSave, children }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const rawText = rawValue === null || rawValue === undefined ? "" : String(rawValue);
  const hasOptions = Array.isArray(editOptions) && editOptions.length > 0;

  useEffect(() => {
    if (!editing) {
      setDraft(rawText);
    }
  }, [rawText, editing]);

  const startEditing = (event) => {
    if (event) {
      event.stopPropagation();
    }
    if (disabled || saving) {
      return;
    }
    setDraft(rawText);
    setEditError("");
    setEditing(true);
  };

  const cancelEditing = () => {
    setDraft(rawText);
    setEditError("");
    setEditing(false);
  };

  const commitEditing = async (nextValue = draft) => {
    if (saving) {
      return;
    }
    if (nextValue === rawText) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setEditError("");
    try {
      await onSave(nextValue);
      setEditing(false);
    } catch (error) {
      setEditError(error.message || "Could not save value.");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div
        className={`${className} editable-grid-cell editing`}
        role="gridcell"
        title={editError || (hasOptions ? "Choose a valid value" : "Press Enter to save, Escape to cancel")}
        onClick={(event) => event.stopPropagation()}
      >
        {hasOptions ? (
          <select
            autoFocus
            value={draft}
            disabled={saving}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => commitEditing()}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitEditing();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                cancelEditing();
              }
            }}
          >
            <option value="">Blank</option>
            {editOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.id}: {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            autoFocus
            value={draft}
            disabled={saving}
            onChange={(event) => setDraft(event.target.value)}
            onFocus={(event) => event.target.select()}
            onBlur={() => commitEditing()}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitEditing();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                cancelEditing();
              }
            }}
          />
        )}
        <small>{editError || (saving ? "Saving..." : hasOptions ? "Select a valid coded value" : "Enter saves, Esc cancels")}</small>
      </div>
    );
  }

  return (
    <div
      className={`${className} editable-grid-cell ${disabled ? "" : "can-edit"}`}
      role="gridcell"
      title={disabled ? title : `${title || "Cell"} - double-click to edit`}
      onDoubleClick={startEditing}
    >
      <div className="editable-grid-cell__content">
        {children}
      </div>
      {!disabled && (
        <button
          className="grid-cell-edit-button"
          type="button"
          title="Edit value"
          onClick={startEditing}
        >
          {icon("pencil")}
        </button>
      )}
    </div>
  );
}

function GridPagination({ page, totalPages, pageSize, setPageSize, setPage, start, end, total }) {
  const safeStart = total ? start : 0;
  return (
    <div className="grid-pagination">
      <div className="grid-pagination__summary">
        Showing <strong>{safeStart}-{end}</strong> of <strong>{total}</strong> segments
      </div>
      <div className="grid-pagination__controls">
        <label>
          Rows
          <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
            {[25, 50, 100, 200].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
        <button onClick={() => setPage(1)} disabled={page <= 1}>First</button>
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>Previous</button>
        <span>Page {page} of {totalPages}</span>
        <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}>Next</button>
        <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}>Last</button>
      </div>
    </div>
  );
}

function formatFeedbackDate(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

function fieldDiscussionPreview(discussion) {
  const messages = Array.isArray(discussion && discussion.messages) ? discussion.messages : [];
  const latest = messages.length ? messages[messages.length - 1] : null;
  return String(
    (discussion && discussion.feedbackText) ||
      (latest && latest.content) ||
      (discussion && discussion.suggestionReason) ||
      (discussion && discussion.suggestedPrompt) ||
      ""
  ).trim();
}

function discussionUserPayload(authUser) {
  return {
    id: authUser && (authUser.id || authUser.userId || authUser.nid || authUser.username || authUser.userName || authUser.email) || "anonymous",
    name: authUser ? userDisplayName(authUser) : "Anonymous user",
    email: authUser && (authUser.email || authUser.mail) || "",
    isEditor: userCanEditIrap(authUser)
  };
}

function SegmentFeedbackView({
  authUser = null,
  fields = [],
  selectSegment = null,
  setLayoutMode = null,
  setMessage = null,
  setError = null
}) {
  const isEditor = userCanEditIrap(authUser);
  const fieldTitleMap = useMemo(() => makeFieldTitleMap(fields), [fields]);
  const [statusFilter, setStatusFilter] = useState("feedback");
  const [query, setQuery] = useState("");
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [actionBusyId, setActionBusyId] = useState("");

  const loadFeedbacks = async () => {
    if (!isEditor) {
      return;
    }
    const user = discussionUserPayload(authUser);
    const params = new URLSearchParams({
      userId: user.id,
      userName: user.name,
      userEmail: user.email || "",
      isEditor: "1",
      status: statusFilter,
      limit: "500"
    });
    setLoading(true);
    setLocalError("");
    try {
      const data = await apiFetch(`/api/field-discussions?${params.toString()}`);
      const discussions = Array.isArray(data && data.discussions) ? data.discussions : [];
      setFeedbacks(discussions);
      setSelectedId((current) => current && discussions.some((item) => item.id === current) ? current : (discussions[0] && discussions[0].id) || "");
    } catch (error) {
      setLocalError(error.message || "Could not load segment feedback.");
      if (typeof setError === "function") {
        setError(error.message || "Could not load segment feedback.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedbacks();
  }, [isEditor, statusFilter]);

  const filteredFeedbacks = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) {
      return feedbacks;
    }
    return feedbacks.filter((discussion) => {
      const fieldMeta = fieldTitleMap.get(String(discussion.fieldCode || ""));
      const haystack = [
        discussion.id,
        discussion.segmentId,
        discussion.fieldCode,
        discussion.fieldCaption,
        fieldMeta && fieldMeta.title,
        discussion.groupName,
        discussion.status,
        discussion.user && discussion.user.name,
        discussion.user && discussion.user.email,
        discussion.feedbackText,
        discussion.suggestedPrompt,
        fieldDiscussionPreview(discussion)
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(text);
    });
  }, [feedbacks, fieldTitleMap, query]);

  const selectedFeedback = filteredFeedbacks.find((discussion) => discussion.id === selectedId) || filteredFeedbacks[0] || null;
  const segmentCount = new Set(filteredFeedbacks.map((discussion) => discussion.segmentId).filter(Boolean)).size;
  const fieldCount = new Set(filteredFeedbacks.map((discussion) => discussion.fieldCode).filter(Boolean)).size;
  const userCount = new Set(filteredFeedbacks.map((discussion) => discussion.user && (discussion.user.id || discussion.user.email || discussion.user.name)).filter(Boolean)).size;

  const acceptPrompt = async (discussion) => {
    if (!discussion || !discussion.id || !discussion.suggestedPrompt) {
      return;
    }
    const user = discussionUserPayload(authUser);
    setActionBusyId(discussion.id);
    setLocalError("");
    try {
      const response = await apiFetch(`/api/field-discussions/${encodeURIComponent(discussion.id)}/accept-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user,
          prompt: discussion.suggestedPrompt,
          applyToAgentPrompt: false
        })
      });
      if (response && response.discussion) {
        setFeedbacks((current) => current.map((item) => item.id === response.discussion.id ? response.discussion : item));
        setSelectedId(response.discussion.id);
      }
      if (typeof setMessage === "function") {
        setMessage(`Prompt accepted for ${discussion.fieldCaption || discussion.fieldCode}.`);
      }
    } catch (error) {
      setLocalError(error.message || "Could not accept this prompt.");
      if (typeof setError === "function") {
        setError(error.message || "Could not accept this prompt.");
      }
    } finally {
      setActionBusyId("");
    }
  };

  const openSegment = (discussion) => {
    if (!discussion || !discussion.segmentId) {
      return;
    }
    if (typeof selectSegment === "function") {
      selectSegment(discussion.segmentId);
    }
    if (typeof setLayoutMode === "function") {
      setLayoutMode("specs");
    }
  };

  if (!isEditor) {
    return (
      <div className="feedback-view">
        <header className="feedback-header">
          <h2>{icon("messages-square")} Segment feedback</h2>
        </header>
        <div className="feedback-empty">
          {icon("lock")}
          <strong>Editor access required</strong>
          <span>Only iRAP editors can review field discussion feedback.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-view">
      <header className="feedback-header">
        <div>
          <h2>{icon("messages-square")} Segment feedback</h2>
          <p>Review chat feedback by segment, field, user, and submission date.</p>
        </div>
        <div className="feedback-controls">
          <label className="feedback-search">
            {icon("search")}
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search segment, field, user, or text"
            />
          </label>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="feedback">Feedback</option>
            <option value="suggested">Suggested</option>
            <option value="accepted">Accepted</option>
            <option value="needs-review">Needs review</option>
            <option value="all">All</option>
          </select>
          <button className="btn-secondary" type="button" onClick={loadFeedbacks} disabled={loading}>
            {icon("refresh-cw")} Refresh
          </button>
        </div>
      </header>

      <div className="feedback-stats">
        <span>{filteredFeedbacks.length} feedbacks</span>
        <span>{segmentCount} segments</span>
        <span>{fieldCount} fields</span>
        <span>{userCount} users</span>
      </div>

      {localError && <div className="feedback-error">{localError}</div>}

      <div className="feedback-layout">
        <section className="feedback-list-panel">
          <div className="feedback-list-head">
            <span>Segment</span>
            <span>Field</span>
            <span>User</span>
            <span>Date</span>
            <span>Status</span>
          </div>
          <div className="feedback-list">
            {loading && <div className="feedback-empty inline">Loading feedback...</div>}
            {!loading && filteredFeedbacks.length === 0 && (
              <div className="feedback-empty inline">No feedback found for this filter.</div>
            )}
            {!loading && filteredFeedbacks.map((discussion) => {
              const fieldMeta = fieldTitleMap.get(String(discussion.fieldCode || ""));
              const fieldTitle = discussion.fieldCaption || (fieldMeta && fieldMeta.title) || discussion.fieldCode || "Field";
              return (
                <button
                  key={discussion.id}
                  type="button"
                  className={`feedback-row ${selectedFeedback && selectedFeedback.id === discussion.id ? "active" : ""}`}
                  onClick={() => setSelectedId(discussion.id)}
                >
                  <span>#{discussion.segmentId}</span>
                  <span>
                    <strong>{fieldTitle}</strong>
                    <small>{discussion.fieldCode}{discussion.groupName ? ` - ${discussion.groupName}` : ""}</small>
                  </span>
                  <span>
                    <strong>{discussion.user && discussion.user.name ? discussion.user.name : "Reviewer"}</strong>
                    <small>{discussion.user && discussion.user.email ? discussion.user.email : ""}</small>
                  </span>
                  <span>{formatFeedbackDate(discussion.updatedAt || discussion.createdAt)}</span>
                  <span><em className={`feedback-status status-${discussion.status || "open"}`}>{discussion.status || "open"}</em></span>
                </button>
              );
            })}
          </div>
        </section>

        <aside className="feedback-detail-panel">
          {selectedFeedback ? (
            <>
              <header>
                <div>
                  <span>Segment #{selectedFeedback.segmentId}</span>
                  <h3>{selectedFeedback.fieldCaption || displayFieldTitle(selectedFeedback.fieldCode, fieldTitleMap)}</h3>
                  <small>{selectedFeedback.fieldCode}{selectedFeedback.groupName ? ` - ${selectedFeedback.groupName}` : ""}</small>
                </div>
                <em className={`feedback-status status-${selectedFeedback.status || "open"}`}>{selectedFeedback.status || "open"}</em>
              </header>
              <div className="feedback-detail-meta">
                <span>{icon("user")} {selectedFeedback.user && selectedFeedback.user.name ? selectedFeedback.user.name : "Reviewer"}</span>
                <span>{icon("calendar")} {formatFeedbackDate(selectedFeedback.updatedAt || selectedFeedback.createdAt)}</span>
              </div>
              <section>
                <h4>Feedback</h4>
                <p>{selectedFeedback.feedbackText || fieldDiscussionPreview(selectedFeedback) || "No feedback text recorded."}</p>
              </section>
              {selectedFeedback.imageFiles && selectedFeedback.imageFiles.length > 0 && (
                <section>
                  <h4>Source images</h4>
                  <div className="feedback-image-list">
                    {selectedFeedback.imageFiles.map((file) => <span key={file}>{file}</span>)}
                  </div>
                </section>
              )}
              <section>
                <h4>Chat history</h4>
                <div className="feedback-chat-history">
                  {(Array.isArray(selectedFeedback.messages) ? selectedFeedback.messages : []).map((message, index) => (
                    <div key={`${message.role}-${index}`} className={message.role === "assistant" ? "assistant" : "user"}>
                      <b>{message.role === "assistant" ? "AiRAP Agent" : "User"}</b>
                      <span>{message.content}</span>
                    </div>
                  ))}
                </div>
              </section>
              <footer>
                <button className="btn-secondary" type="button" onClick={() => openSegment(selectedFeedback)}>
                  {icon("image")} Open segment
                </button>
                {selectedFeedback.suggestedPrompt && (
                  <button
                    className="primary-button"
                    type="button"
                    disabled={actionBusyId === selectedFeedback.id || selectedFeedback.status === "accepted"}
                    onClick={() => acceptPrompt(selectedFeedback)}
                  >
                    {icon("check")} {selectedFeedback.status === "accepted" ? "Accepted" : "Accept Feedback"}
                  </button>
                )}
              </footer>
            </>
          ) : (
            <div className="feedback-empty">
              {icon("inbox")}
              <strong>No item selected</strong>
              <span>Select a feedback row to inspect its chat history.</span>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function LogViewer({ logs = [], fields = [], loadLogs, clearLogs }) {
  const [expandedLog, setExpandedLog] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all"); // "all", "success", "error"
  const fieldTitleMap = useMemo(() => makeFieldTitleMap(fields), [fields]);

  // Ensure logs is always an array
  const safeLogs = Array.isArray(logs) ? logs : [];

  console.log("LogViewer rendering with", safeLogs.length, "logs");

  const filteredLogs = safeLogs.filter((log) => {
    if (filterStatus === "all") return true;
    return log.status === filterStatus;
  });

  const toggleExpand = (timestamp) => {
    setExpandedLog(expandedLog === timestamp ? null : timestamp);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="log-viewer">
      <div className="log-header">
        <h2>{icon("scroll-text")} OpenAI Prompt Logs</h2>
        <div className="log-controls">
          <div className="filter-buttons">
            <button 
              className={filterStatus === "all" ? "active" : ""}
              onClick={() => setFilterStatus("all")}
            >
              All ({safeLogs.length})
            </button>
            <button 
              className={filterStatus === "success" ? "active" : ""}
              onClick={() => setFilterStatus("success")}
            >
              {icon("check-circle")} Success ({safeLogs.filter(l => l.status === "success").length})
            </button>
            <button 
              className={filterStatus === "error" ? "active" : ""}
              onClick={() => setFilterStatus("error")}
            >
              {icon("x-circle")} Error ({safeLogs.filter(l => l.status === "error").length})
            </button>
          </div>
          <button className="btn-secondary" onClick={loadLogs}>
            {icon("refresh-cw")} Refresh
          </button>
          <button className="btn-danger" onClick={clearLogs}>
            {icon("trash-2")} Clear All
          </button>
        </div>
      </div>

      <div className="log-list">
        {filteredLogs.length === 0 && (
          <div className="no-logs">
            <p>No logs to display. Analyze a segment to generate logs.</p>
          </div>
        )}

        {filteredLogs.map((log) => {
          const isExpanded = expandedLog === log.timestamp;
          
          return (
            <div key={log.timestamp} className={`log-entry ${log.status} ${isExpanded ? "expanded" : ""}`}>
              <div className="log-summary" onClick={() => toggleExpand(log.timestamp)}>
                <div className="log-meta">
                  <span className="log-status-icon">
                    {log.status === "success" ? icon("check-circle") : 
                     log.status === "error" ? icon("x-circle") : 
                     icon("clock")}
                  </span>
                  <span className="log-segment">Segment {log.segmentId}</span>
                  <span className="log-time">{formatTimestamp(log.timestamp)}</span>
                  {log.images && <span className="log-images">{log.images.length} images</span>}
                  {log.status === "success" && log.savedCount !== undefined && (
                    <span className="log-fields">{log.savedCount}/{log.totalCount} fields saved</span>
                  )}
                </div>
                <div className="log-expand-icon">
                  {isExpanded ? icon("chevron-up") : icon("chevron-down")}
                </div>
              </div>

              {isExpanded && (
                <div className="log-details">
                  <div className="log-section">
                    <h4>{icon("image")} Images ({log.images?.length || 0})</h4>
                    <ul className="log-images-list">
                      {log.images?.map((img, idx) => (
                        <li key={idx}>{img}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="log-section">
                    <h4>{icon("file-text")} Prompt</h4>
                    <pre className="log-prompt">{log.prompt}</pre>
                  </div>

                  {log.response && (
                    <div className="log-section">
                      <h4>{icon("code")} Raw API Response</h4>
                      <pre className="log-response">{JSON.stringify(log.response, null, 2)}</pre>
                    </div>
                  )}

                  {log.parsedResult && (
                    <div className="log-section">
                      <h4>{icon("list")} Parsed Result</h4>
                      <pre className="log-parsed">{JSON.stringify(log.parsedResult, null, 2)}</pre>
                    </div>
                  )}

                  {log.fieldAnalysis && log.fieldAnalysis.length > 0 && (
                    <div className="log-section">
                      <h4>{icon("bar-chart")} Field Analysis</h4>
                      <div className="field-analysis-grid">
                        {log.fieldAnalysis.map((field, idx) => (
                          <div key={idx} className={`field-analysis-item ${field.validOption ? "valid" : "invalid"}`}>
                            <div className="field-header">
                              <span className="field-name">{displayFieldTitle(field.fieldName, fieldTitleMap)}</span>
                              <span className="field-confidence">{(field.confidence * 100).toFixed(0)}%</span>
                            </div>
                            <div className="field-value">
                              {field.decodedValue || field.value || "null"}
                            </div>
                            {field.evidence && (
                              <div className="field-evidence">{field.evidence}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {log.error && (
                    <div className="log-section log-error">
                      <h4>{icon("alert-circle")} Error</h4>
                      <p>{log.error}</p>
                      {log.attempt && <p>Failed on attempt {log.attempt}</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <AppErrorBoundary>
    <App />
  </AppErrorBoundary>
);
